import React, { useState, useRef, useEffect } from 'react';
import ReportEngine from '../components/ReportEngine';
import { GlobalStyles, CyberBackground, IconShieldBig } from '../components/BaseComponents';
import html2pdf from 'html2pdf.js';
import * as XLSX from 'xlsx';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import {
   History, User, Zap, ShieldCheck, Lock, Shield,
   Loader2, UploadCloud, Keyboard, TrendingUp, ArrowLeft,
   Activity, Download, LayoutPanelLeft, BarChart3,
   Binary, AlertTriangle, Target, Settings, LogOut, CheckCircle2,
   Trash2, FileText, ChevronDown, ChevronUp, FileSpreadsheet, Gauge,
   Fingerprint, X, PieChart, Briefcase, FileSignature, Landmark, Wand, Coins,
   Monitor, Palette, Crown, ArrowRight, Mail, Check, Info, Circle, Plus, Wallet, Bot,
   ShieldAlert, XOctagon
} from 'lucide-react';
import { COMPANY_INFO, DISCLAIMER_FULL } from '../constants/constants';
import ConsultorReportTabs from './ConsultorReportTabs';
import { METODO_PARECER, calculateCoreMetrics, calculateHealthStatus, calculateExtendedMetrics, calculateProjection, calculateStochasticProjection, getSectorCycleBenchmark, getFinancialFieldLabels } from '../components/financeEngine';
import { gerarInsights } from '../components/parecerEngine';
import { gerarParecerIA } from '../services/parecerIAService';
import { nomeArquivoRelatorio } from '../services/nomeArquivoPdf';

// --- IMPORTAÇÕES DO FIREBASE ---
import { db, auth, gerarLinkDashboard, app } from '../services/firebase';
import AceiteAtivo, { registrarAceite } from '../components/AceiteAtivo';
import { canalDePagamento, linkGerenciarAssinaturaGoogle } from '../services/canalPagamento';
import { mensagemErroCriarConta } from '../services/mensagensErro';
import AvisoCustoNaoRecorrente from '../components/AvisoCustoNaoRecorrente';
import { comprarPeloGoogle, recuperarComprasGoogle } from '../services/googleBillingService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, addDoc, doc, setDoc, getDoc, getDocs, deleteDoc, query, where, arrayUnion, onSnapshot } from 'firebase/firestore';
import { signInWithEmailAndPassword, sendPasswordResetEmail, createUserWithEmailAndPassword, deleteUser, linkWithCredential, EmailAuthProvider } from 'firebase/auth';
import { criarClienteAsaas, criarAssinaturaAsaas, criarCobrancaAvulsaAsaas, cancelarAssinaturaAsaas, aplicarDescontoDuplaAssinatura } from '../services/asaasService';
import { extrairRaizCNPJ, ehMatriz, rotuloUnidade } from '../components/filiaisEngine';
import { migrarSessaoAnonima } from '../services/migrarContaService';
import QRCode from 'qrcode';

// Função Mestra de Formatação Financeira
const formatCurrency = (value) => {
   return Number(value).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });
};

const parseCurrencyToNumber = (val) => {
   if (!val) return 0;
   if (typeof val === 'number') return val;
   return Number(val.replace(/\./g, '').replace(',', '.')) || 0;
};

// ==========================================
// 📅 HORIZONTES LIBERADOS POR PLANO — mesma regra usada na tela (grade de
// cards) e agora TAMBÉM na geração da IA. Antes, a IA sempre recebia os
// 5 horizontes completos e escrevia sobre todos eles no texto corrido
// ("Análise"), vazando conteúdo de 12/24/36 meses pra quem só pagou por
// 3/6 — mesmo com os CARDS numéricos corretamente bloqueados. Usando este
// mesmo mapa pra FILTRAR o que é mandado pra IA, o texto gerado nunca
// menciona um horizonte que a pessoa não tem acesso.
// ==========================================
const HORIZONTES_POR_PLANO_GESTOR = {
   tactical: [3, 6],
   plus: [3, 6, 12],
   total: [3, 6, 12, 24, 36],
};
// 🔥 Decisão do CTO (mesma aplicada no Dashboard Consultor): antes o
// Advisor PRO não liberava NENHUM horizonte de projeção — o plano de
// entrada do Consultor (mais caro que o Tactical do Gestor) entregava
// menos. Agora segue a mesma progressão do Gestor: Advisor PRO = 3 e 6
// meses, Boutique Elite = +12, Enterprise Ultra = +24 e 36.
const HORIZONTES_POR_PLANO_CONSULTOR = {
   advisor: [3, 6],
   boutique: [3, 6, 12],
   enterprise: [3, 6, 12, 24, 36],
};
function getHorizontesLiberados(tipoUsuario, plano) {
   const mapa = String(tipoUsuario).toLowerCase() === 'consultor' ? HORIZONTES_POR_PLANO_CONSULTOR : HORIZONTES_POR_PLANO_GESTOR;
   return mapa[String(plano || '').toLowerCase()] || [];
}

// ==========================================
// SANITIZAÇÃO PARA O FIRESTORE
// O Firestore trava com "INTERNAL ASSERTION FAILED" (erro obscuro, sem
// mensagem clara) quando recebe NaN, Infinity/-Infinity ou undefined dentro
// de um objeto — coisas que podem acontecer em cálculos financeiros com
// divisão por zero. Essa função varre o objeto inteiro e troca esses valores
// por null antes de qualquer gravação, então nunca mais quebra por isso.
// ==========================================
// ==========================================
// JSON.stringify SEGURO
// Faz o mesmo que JSON.stringify, mas nunca quebra por causa de referência
// circular (ex: um elemento DOM/nó do React que tenha entrado sem querer
// nos dados, como acontece às vezes com upload de imagem/logo). Qualquer
// coisa "estranha" vira null em vez de travar a tela inteira.
// 🔥 Cada dossiê do histórico é serializado com seu PRÓPRIO controle de
// referência circular — assim um objeto repetido em dois dossiês diferentes
// (comum, não é bug) não faz um deles virar "null" à toa.
// ==========================================
// ==========================================
// GERADOR DE ID ÚNICO PARA DOSSIÊS
// Antes usava só Date.now() — se duas ações disparassem no mesmo milissegundo
// (ex: duplo clique, ou dois efeitos rodando quase juntos), gerava o MESMO id
// duas vezes, e dois dossiês diferentes acabavam colidindo na lista.
// ==========================================
// ==========================================
// REMOVE DOSSIÊS DUPLICADOS (MESMO ID) — mantém o último de cada um
// ==========================================
function dedupeById(lista) {
   const mapa = new Map();
   lista.forEach(item => { if (item?.id) mapa.set(item.id, item); });
   return Array.from(mapa.values());
}

function gerarIdUnico() {
   return `${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
}

// Confere o dígito verificador de verdade — evita mandar CPF/CNPJ com
// 11 ou 14 números aleatórios que passariam numa checagem só de
// quantidade, mas seriam recusados pelo Asaas mesmo assim.
function cpfCnpjValido(valor) {
   const numeros = String(valor || '').replace(/\D/g, '');

   if (numeros.length === 11) {
      if (/^(\d)\1{10}$/.test(numeros)) return false; // todos os dígitos iguais
      let soma = 0;
      for (let i = 0; i < 9; i++) soma += parseInt(numeros[i]) * (10 - i);
      let resto = (soma * 10) % 11;
      if (resto === 10) resto = 0;
      if (resto !== parseInt(numeros[9])) return false;

      soma = 0;
      for (let i = 0; i < 10; i++) soma += parseInt(numeros[i]) * (11 - i);
      resto = (soma * 10) % 11;
      if (resto === 10) resto = 0;
      return resto === parseInt(numeros[10]);
   }

   if (numeros.length === 14) {
      if (/^(\d)\1{13}$/.test(numeros)) return false;
      const calcularDigito = (base) => {
         let peso = base.length - 7;
         let soma = 0;
         for (let i = 0; i < base.length; i++) {
            soma += parseInt(base[i]) * peso;
            peso = peso === 2 ? 9 : peso - 1;
         }
         const resto = soma % 11;
         return resto < 2 ? 0 : 11 - resto;
      };
      const digito1 = calcularDigito(numeros.slice(0, 12));
      if (digito1 !== parseInt(numeros[12])) return false;
      const digito2 = calcularDigito(numeros.slice(0, 12) + digito1);
      return digito2 === parseInt(numeros[13]);
   }

   return false;
}

function safeStringifyOne(value) {
   const seen = new WeakSet();
   return JSON.stringify(value, (key, val) => {
      if (val instanceof Node || val instanceof Window) return null; // elemento do DOM
      if (typeof val === 'function') return null;
      if (val !== null && typeof val === 'object') {
         if (seen.has(val)) return null; // referência circular de verdade
         seen.add(val);
      }
      return val;
   });
}

function safeStringify(value) {
   if (Array.isArray(value)) {
      return '[' + value.map(item => safeStringifyOne(item)).join(',') + ']';
   }
   return safeStringifyOne(value);
}

function sanitizeForFirestore(value, seen = new WeakSet(), dentroDeArray = false) {
   if (value === undefined) return null;
   if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
   }
   // 🔥 Funções e elementos React (ícones JSX, componentes) não existem no
   // Firestore — cortamos aqui em vez de deixar passar e travar lá na frente.
   if (typeof value === 'function' || typeof value === 'symbol') return null;
   if (value !== null && typeof value === 'object' && value.$$typeof) return null; // elemento React
   if (Array.isArray(value)) {
      if (seen.has(value)) return null; // referência circular — corta aqui
      seen.add(value);
      const limpo = value.map(item => sanitizeForFirestore(item, seen, true));
      // 🔥 Firestore não aceita array DENTRO de array (ex: matriz [[1,2],[3,4]]).
      // Se estivermos processando um array e o resultado ainda for um array,
      // convertemos em objeto {0: ..., 1: ...} pra não quebrar a gravação.
      if (dentroDeArray) {
         const comoObjeto = {};
         limpo.forEach((v, i) => { comoObjeto[i] = v; });
         return comoObjeto;
      }
      return limpo;
   }
   if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
      if (seen.has(value)) return null; // referência circular — corta aqui
      seen.add(value);
      const cleaned = {};
      for (const key in value) {
         if (Object.prototype.hasOwnProperty.call(value, key)) {
            cleaned[key] = sanitizeForFirestore(value[key], seen);
         }
      }
      return cleaned;
   }
   return value;
}

// 🤖 Formata o texto vindo da IA (Markdown simples: #, ##, **negrito**, ---)
// em elementos visuais coerentes com o resto do relatório — sem precisar de
// uma biblioteca de Markdown inteira só pra isso. Fontes maiores que o resto
// do relatório de propósito: esse texto é lido em bloco, não em cards
// curtos, então precisa de mais conforto de leitura numa tela de celular.
// 🔮 Interpreta os números da projeção em uma frase simples, pra quem não
// tem bagagem técnica entender de cara o que aquilo significa — sem
// precisar de outra chamada de IA (calculado na hora, sem custo extra).
function interpretarHorizonte(dado, meses) {
   if (!dado) return '';
   const faixa = dado.otimista - dado.pessimista;
   const faixaPct = dado.provavel !== 0 ? Math.abs(faixa / dado.provavel) : 0;
   if (dado.pessimista < 0) {
      return `Mesmo no cenário mais pessimista, o resultado pode ficar negativo em ${meses} meses — vale reforçar o caixa antes desse prazo.`;
   }
   if (faixaPct > 0.5) {
      return `A incerteza pra ${meses} meses é alta — mesmo no pior cenário, o resultado segue positivo, mas os números podem variar bastante.`;
   }
   if (faixaPct > 0.25) {
      return `Incerteza moderada pra ${meses} meses — o resultado tende a se manter estável, com variação razoável entre os cenários.`;
   }
   return `Boa previsibilidade pra ${meses} meses — os 3 cenários ficam próximos, sinal de negócio mais estável nesse prazo.`;
}

function formatarParecerIA(texto) {
   if (!texto) return null;
   const linhas = texto.split('\n').filter(l => l.trim() !== '');

   const formatarNegrito = (linha) => {
      const partes = linha.split(/(\*\*[^*]+\*\*)/g);
      return partes.map((parte, i) =>
         parte.startsWith('**') && parte.endsWith('**')
            ? <strong key={i} className="text-lime-300 font-black">{parte.slice(2, -2)}</strong>
            : parte
      );
   };

   return linhas.map((linha, i) => {
      const l = linha.trim();
      if (l === '---') {
         return <div key={i} className="h-px bg-white/10 my-6" />;
      }
      if (l.startsWith('## ')) {
         return <h4 key={i} className="text-[11px] sm:text-xs font-black text-lime-400 uppercase italic tracking-wide mt-7 mb-2 leading-snug text-left">{l.replace(/^##\s*/, '')}</h4>;
      }
      if (l.startsWith('# ')) {
         return <h3 key={i} className="text-base sm:text-lg font-black text-white uppercase italic tracking-wide mb-3 leading-snug text-left">{l.replace(/^#\s*/, '')}</h3>;
      }
      return <p key={i} className="text-[13px] sm:text-sm text-slate-300 font-medium leading-relaxed tracking-tight text-justify mb-4">{formatarNegrito(l)}</p>;
   });
}

export default function MobileInterface({ user, onLoginSuccess, onLogout, onOpenDashboardGestor, onOpenDashboardConsultor }) {

   const [view, setView] = useState('splash');
   const [isUploading, setIsUploading] = useState(false);
   const [isUploadingPDF, setIsUploadingPDF] = useState(false);
   const [step, setStep] = useState(1);
   const [reportTab, setReportTab] = useState('visao');

   // 🔥 CORREÇÃO: sempre que troca de tela (view) ou de aba dentro do
   // relatório (reportTab), o app abria "no meio" ou "no fim" da página
   // nova — porque a posição de rolagem antiga ficava guardada. Isso
   // força a rolagem pro topo toda vez que qualquer uma das duas muda.
   const mainScrollRef = useRef(null);
   useEffect(() => {
      if (mainScrollRef.current) {
         mainScrollRef.current.scrollTop = 0;
      }
   }, [view, reportTab]);

   // Faixa fixa com o nome da empresa: aparece só depois que o título do relatório sai da tela ao rolar.
   const [rolouAlemDoTitulo, setRolouAlemDoTitulo] = useState(false);
   useEffect(() => {
      setRolouAlemDoTitulo(false);
      const el = mainScrollRef.current;
      if (view !== 'report' || !el) return undefined;
      const aoRolar = () => {
         const titulo = el.querySelector('[data-titulo-relatorio]');
         if (!titulo) { setRolouAlemDoTitulo(false); return; }
         setRolouAlemDoTitulo(titulo.getBoundingClientRect().bottom < el.getBoundingClientRect().top + 8);
      };
      el.addEventListener('scroll', aoRolar, { passive: true });
      return () => el.removeEventListener('scroll', aoRolar);
   }, [view, reportTab]);
   // 🤖 TEASER "AGENTES DE IA" (Em Breve) — sem funcionalidade real, só
   // antecipa a feature. O contador é compartilhado entre TODOS os
   // usuários (não é "quantos cliques EU dei"), por isso vem direto do
   // Firestore em tempo real. "jaRegistrouInteresse" é só LOCAL (guarda
   // no aparelho), pra trocar o botão por uma confirmação depois do
   // clique — não precisa de conta nem e-mail pra isso, como decidido.
   const [contadorInteresseAgentes, setContadorInteresseAgentes] = useState(null);
   const [jaRegistrouInteresseAgentes, setJaRegistrouInteresseAgentes] = useState(
      () => localStorage.getItem('phoenix_interesse_agentes_ia') === '1'
   );
   const [registrandoInteresseAgentes, setRegistrandoInteresseAgentes] = useState(false);

   // 🤖 Escuta o contador compartilhado em tempo real — assim, se muita
   // gente clicar enquanto a pessoa está com a tela aberta, o número sobe
   // sozinho na hora, sem precisar recarregar nada.
   useEffect(() => {
      const unsubscribe = onSnapshot(doc(db, 'metricas', 'interesseAgentesIA'), (snap) => {
         if (snap.exists()) setContadorInteresseAgentes(snap.data());
      }, () => { });
      return () => unsubscribe();
   }, []);

   const registrarInteresseAgentesIA = async () => {
      if (jaRegistrouInteresseAgentes || registrandoInteresseAgentes) return;
      setRegistrandoInteresseAgentes(true);
      try {
         const functionsInstance = getFunctions(app, 'southamerica-east1');
         const chamar = httpsCallable(functionsInstance, 'registrarInteresseAgentesIA');
         const perfil = formData.userType === 'Consultor' ? 'consultor' : 'gestor';
         await chamar({ perfil });
         localStorage.setItem('phoenix_interesse_agentes_ia', '1');
         setJaRegistrouInteresseAgentes(true);
      } catch (e) {
         console.error('[PHOENIX] Erro ao registrar interesse em Agentes de IA:', e);
         setToastMessage('Não foi possível registrar agora. Tente de novo em instantes.');
      } finally {
         setRegistrandoInteresseAgentes(false);
      }
   };
   const [formData, setFormData] = useState({
      method: '', profile: '', companyName: '', segment: '', niche: '', revenue: '', costs: '', debt: '', expertise: '', userType: ''
   });

   const [activePlan, setActivePlan] = useState('entry');
   const [gestorPlan, setGestorPlan] = useState('entry');
   const [pagamentoAtrasado, setPagamentoAtrasado] = useState(null); // { tipo, desde } — vem direto do Firestore, só leitura
   const [acessoSuspensoPorAtraso, setAcessoSuspensoPorAtraso] = useState(null); // { tipo } — idem, só depois das 48h
   const [consultorPlan, setConsultorPlan] = useState('free');
   const [recuperando, setRecuperando] = useState(false);
   // ✅ Selo visual "Sincronizado com o Servidor" — a sincronização em si
   // agora roda sozinha (useEffect abaixo), o botão antigo virou só um
   // indicador de status, sem precisar de clique.
   const [ultimaSincronizacao, setUltimaSincronizacao] = useState(null);
   const [showGestorCreateAccount, setShowGestorCreateAccount] = useState(false);
   const [showEditEntryWarning, setShowEditEntryWarning] = useState(false);
   // ✅ Aviso pós-assinatura: explica que o mesmo login já dá acesso ao
   // Painel Desktop (Dashboard). É um pedido de escopo separado do toast
   // rápido — precisa ficar visível até a pessoa fechar de propósito.
   const [showDashboardUnlockedNotice, setShowDashboardUnlockedNotice] = useState(false);
   const [nomeParaAvisoDashboard, setNomeParaAvisoDashboard] = useState('');
   const [landingEmail, setLandingEmail] = useState('');
   const [landingPassword, setLandingPassword] = useState('');
   const [landingLoginError, setLandingLoginError] = useState('');
   const [landingLoginLoading, setLandingLoginLoading] = useState(false);
   const [showForgotPassword, setShowForgotPassword] = useState(false);
   const [gestorAccountMode, setGestorAccountMode] = useState('create'); // 'create' | 'login'
   const [gestorLoginEmail, setGestorLoginEmail] = useState('');
   const [gestorLoginPassword, setGestorLoginPassword] = useState('');
   const [gestorLoginError, setGestorLoginError] = useState('');
   const [gestorLoginLoading, setGestorLoginLoading] = useState(false);
   const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
   const [pendingGestorPlan, setPendingGestorPlan] = useState(null);
   const [userLevel, setUserLevel] = useState(1); // Nível de acesso (1 a 4)
   const [isUpgrading, setIsUpgrading] = useState(false);
   const [upgradeTarget, setUpgradeTarget] = useState('');
   const [showCompleteDataPrompt, setShowCompleteDataPrompt] = useState(false);
   const [pendingCompleteDossierId, setPendingCompleteDossierId] = useState(null);
   const [expandedFaq, setExpandedFaq] = useState(null);

   const [simRevenue, setSimRevenue] = useState(0);
   const [simCosts, setSimCosts] = useState(0);
   const [aiJustification, setAiJustification] = useState('');

   const [historyList, setHistoryList] = useState([]);
   const [activeDossierId, setActiveDossierId] = useState(null);

   // 🔥 CORRIGIDO — TRAVA UNIVERSAL, DIRETO NA RENDERIZAÇÃO DO RELATÓRIO
   // (2026-09-28): depois de achar e corrigir várias funções diferentes
   // que abriam o relatório sem checar pagamento (unlockDossier,
   // loadDossier, modais de criação de conta), ainda restava a pergunta
   // "e se existir mais algum caminho que eu não achei?" — o app é grande
   // demais pra garantir isso só corrigindo pontos de entrada um por um.
   // Esta trava resolve isso na raiz: não importa COMO 'view' virou
   // 'report', se o dossiê ativo é Entry (avulso) e o pagamento real
   // dele ainda não foi confirmado pelo webhookAsaas, o conteúdo do
   // relatório simplesmente não é mostrado — sempre. Atualizado em tempo
   // real (onSnapshot no dossiê ativo) logo abaixo.
   const [entryStatusPagamento, setEntryStatusPagamento] = useState(null);
   // Distingue "ainda carregando" de "carregou e NÃO está pago": sem isso, o
   // aviso de "Aguardando Pagamento" piscava por uma fração de segundo até
   // mesmo em relatórios já pagos, enquanto o Firestore respondia.
   const [entryStatusCarregado, setEntryStatusCarregado] = useState(false);
   // 🔒 A trava não pode depender só de 'activePlan' (cópia em memória que muda com assinatura na mesma sessão):
   // lê também o próprio documento do dossiê. Se o dossiê é do tipo Entry e o pagamento dele não foi confirmado,
   // continua bloqueado MESMO que a pessoa tenha assinado um plano depois (assinar não "paga" o Entry pendente).
   const [entryDocPlano, setEntryDocPlano] = useState(null);
   useEffect(() => {
      if (!activeDossierId) {
         setEntryStatusPagamento(null);
         setEntryDocPlano(null);
         setEntryStatusCarregado(false);
         return;
      }
      setEntryStatusPagamento(null); // enquanto carrega, trata como não confirmado
      setEntryDocPlano(null);
      setEntryStatusCarregado(false);
      const unsubscribe = onSnapshot(doc(db, 'cenarios', String(activeDossierId)), (snap) => {
         setEntryStatusPagamento(snap.exists() ? (snap.data().statusPagamento || null) : null);
         setEntryDocPlano(snap.exists() ? String(snap.data().plan || '').toLowerCase() : null);
         setEntryStatusCarregado(true);
      }, () => { setEntryStatusPagamento(null); setEntryDocPlano(null); setEntryStatusCarregado(true); });
      return () => unsubscribe();
   }, [activeDossierId]);
   const relatorioEntryBloqueado = (activePlan === 'entry' || entryDocPlano === 'entry') && entryStatusPagamento !== 'pago';
   // Dossiê Entry sem pagamento confirmado (pelo próprio documento): NENHUMA geração de IA pode rodar pra ele,
   // nem por "completar blocos" depois de assinar um plano. Ref porque é lido de dentro de callbacks assíncronos.
   const entryPendenteRef = useRef(false);
   entryPendenteRef.current = entryDocPlano === 'entry' && entryStatusPagamento !== 'pago';

   // 🔧 Compra feita NA MESMA SESSÃO (sem recarregar o app): 'gestorPlan'/'consultorPlan' atualizam sozinhos pelo
   // Firestore, mas 'activePlan' só era recalculado no login — ficava preso em 'entry' e a trava do Entry acima
   // mostrava "Aguardando Pagamento" até dentro da Carteira do Consultor recém-assinada (e ainda herdava o dossiê
   // Entry pendente que estava aberto). Na Carteira não há dossiê individual a proteger: ao entrar com plano de
   // Consultor pago, alinha o plano ativo e solta o dossiê antigo. (A trava continua valendo ao abrir um dossiê Entry.)
   useEffect(() => {
      if (view !== 'report' || formData.userType !== 'Consultor' || reportTab !== 'carteira') return;
      if (!consultorPlan || consultorPlan === 'free' || activePlan !== 'entry') return;
      setActiveDossierId(null);
      setActivePlan(consultorPlan);
      // eslint-disable-next-line
   }, [view, reportTab, formData.userType, consultorPlan, activePlan]);

   // ✅ CHECKLIST DUE DILIGENCE (Boardroom) — mesmo campo do Firestore usado
   // pelo Dashboard Gestor (dueDiligenceChecklist), marcar aqui reflete lá
   // e vice-versa. Carrega ao entrar num dossiê e grava direto no clique.
   const [dueDiligenceChecklist, setDueDiligenceChecklist] = useState({ auditoria: false, acordoSocios: false });

   const skipNextSaveRef = useRef(true);
   const skipNextLoadRef = useRef(false);

   // ==========================================
   // ✨ SPLASH DE ENTRADA — mesmo princípio de apps de banco/loja: mostra a
   // marca por um instante antes de cair na tela inicial, em vez de abrir
   // direto sem nenhuma introdução. Puramente visual, não bloqueia nada —
   // depois de ~1,8s troca sozinho pra 'landing'.
   // ==========================================
   useEffect(() => {
      if (view !== 'splash') return;
      const timer = setTimeout(() => setView('landing'), 3200);
      return () => clearTimeout(timer);
   }, [view]);

   // EFEITO A — Roda toda vez que a CONTA ATIVA muda (login, logout, ou troca de e-mail).
   // Ele CARREGA a lista certa do dono certo.
   useEffect(() => {
      // 🔥 Se a própria criação de conta já colocou os dados certos na memória
      // e no localStorage, NÃO recarrega — evita sobrescrever com dado velho/vazio
      if (skipNextLoadRef.current) {
         skipNextLoadRef.current = false;
         skipNextSaveRef.current = true;
         return;
      }
      const escopo = user?.uid ? `_${user.uid}` : '_avulso';
      let carregouLocal = false;
      try {
         const saved = localStorage.getItem(`phoenix_history${escopo}`);
         if (saved) {
            setHistoryList(dedupeById(JSON.parse(saved).filter(Boolean)));
            carregouLocal = true;
         } else {
            setHistoryList([]);
         }
      } catch (e) {
         setHistoryList([]);
      }
      skipNextSaveRef.current = true;

      // 🔥 Se este navegador não tinha nada salvo localmente (aparelho novo, cache
      // limpo, aba anônima), busca o backup no Firestore — mesmo princípio já
      // usado para restaurar o plano.
      if (!carregouLocal && user?.uid) {
         getDoc(doc(db, 'usuarios', user.uid)).then(async userDoc => {
            if (userDoc.exists() && userDoc.data().historyBackup) {
               try {
                  const indice = JSON.parse(userDoc.data().historyBackup).filter(Boolean);
                  // 🔥 O historyBackup agora é só um ÍNDICE LEVE (nome/data/plano) —
                  // os dados pesados de cada dossiê vêm da coleção 'cenarios'.
                  // Busca cada um em paralelo pra reconstruir a lista completa.
                  const restaurado = await Promise.all(indice.map(async (item) => {
                     try {
                        const cenarioDoc = await getDoc(doc(db, 'cenarios', String(item.id)));
                        if (cenarioDoc.exists()) {
                           const dados = cenarioDoc.data();
                           // 🔥 CORREÇÃO — CAUSA RAIZ DE VERDADE: esse trecho já buscava
                           // o documento fresco em 'cenarios', mas só usava ele pra
                           // atualizar o `.snapshot` — o `.name` continuava vindo do
                           // índice leve (historyBackup), que pode estar desatualizado
                           // se a empresa foi renomeada pelo Dashboard depois do último
                           // backup salvo. Por isso o nome só "colava" quando a pessoa
                           // abria o relatório manualmente (loadDossier tem sua própria
                           // correção). Agora atualiza o nome aqui também, na restauração.
                           const origemFresca = dados.snapshot?.formData || dados;
                           const nomeFresco = dados.companyName || origemFresca.companyName || item.name;
                           return { ...item, name: nomeFresco, snapshot: dados.snapshot || item.snapshot || {} };
                        }
                     } catch (e) { /* mantém o item leve se der erro pontual */ }
                     return item;
                  }));
                  const restauradoSemDuplicata = dedupeById(restaurado);
                  skipNextSaveRef.current = true;
                  setHistoryList(restauradoSemDuplicata);
                  localStorage.setItem(`phoenix_history${escopo}`, safeStringify(restauradoSemDuplicata));
               } catch (e) {
                  console.error('[PHOENIX] Erro ao restaurar histórico do Firestore:', e);
               }
            }
         }).catch(e => console.error('[PHOENIX] Erro ao buscar histórico no Firestore:', e));
      }
   }, [user?.uid]);

   // EFEITO B — Roda toda vez que a LISTA muda. SÓ salva se a mudança foi uma ação real
   // (criar, editar, excluir dossiê) — nunca no instante da troca de conta.
   useEffect(() => {
      if (skipNextSaveRef.current) {
         skipNextSaveRef.current = false;
         return;
      }
      const escopo = user?.uid ? `_${user.uid}` : '_avulso';
      const historyJson = safeStringify(historyList);
      localStorage.setItem(`phoenix_history${escopo}`, historyJson);

      // 🔥 Backup automático no Firestore (só pra quem está logado — 'avulso'
      // continua só local, pois não tem conta pra vincular).
      // 🔥 IMPORTANTE: aqui manda só um ÍNDICE LEVE (id/nome/data/plano/tipo),
      // SEM o snapshot completo — isso já vai separadamente pra 'cenarios'
      // logo abaixo. Guardar tudo duas vezes foi o que estourou o limite de
      // 1MB do Firestore quando a carteira cresceu.
      if (user?.uid) {
         const indiceLeve = historyList.map(item => ({
            id: item.id,
            name: item.name,
            date: item.date,
            plan: item.plan,
            snapshot: { userType: item.snapshot?.userType || 'Gestor' } // só o tipo, sem os dados pesados
         }));
         setDoc(doc(db, 'usuarios', user.uid), { historyBackup: safeStringify(indiceLeve) }, { merge: true })
            .catch(e => console.error('[PHOENIX] Erro ao sincronizar histórico com Firestore:', e));

         // 🔥 REMOVIDO o "reenviar todos os dossiês toda vez que a lista muda"
         // — isso fazia o app reescrever a carteira INTEIRA no Firestore a
         // cada pequena edição, causando lentidão e estourando o limite de
         // tamanho de requisição. Agora cada dossiê só é sincronizado
         // individualmente no exato momento em que É criado/editado
         // (função sincronizarDossieUnico, chamada em unlockDossier).
      }
   }, [historyList]);

   // EFEITO C — 🔥 CORREÇÃO: até aqui, um dossiê editado pelo Dashboard só
   // atualizava o nome na lista "Meus Dossiês" quando a pessoa ABRIA aquele
   // dossiê específico no app (dentro de loadDossier/aplicarDadosFrescos).
   // Isso parecia bug pro usuário: "mudei o nome no Dashboard mas a lista
   // continua com o nome antigo". Agora existe um listener ao vivo na
   // coleção 'cenarios' (só os dossiês do usuário logado) que atualiza o
   // nome na lista sozinho, sem precisar abrir nada — mesmo princípio do
   // Dashboard, que já lê tudo via onSnapshot.
   const ultimoSnapshotCenariosRef = useRef(null);

   const reconciliarNomesComFirestore = (snapshot) => {
      setHistoryList(prev => {
         if (prev.length === 0) return prev; // lista ainda não carregada — não interfere
         let mudou = false;
         const atualizado = prev.map(item => {
            const docSnap = snapshot.docs.find(d => d.id === String(item.id));
            if (!docSnap) return item;
            const dados = docSnap.data();
            const origem = dados.snapshot?.formData || dados;
            const nomeFresco = dados.companyName || origem.companyName;
            if (nomeFresco && nomeFresco !== item.name) {
               mudou = true;
               return { ...item, name: nomeFresco };
            }
            return item;
         });
         return mudou ? atualizado : prev;
      });
   };

   useEffect(() => {
      if (!user?.uid) return;
      let cancelado = false;
      let tentativas = 0;

      // 🔥 CORREÇÃO: bem no instante do login, o Firestore às vezes recusa o
      // listener com "Missing or insufficient permissions" porque a
      // autenticação ainda não "assentou" de verdade — e um onSnapshot que
      // erra MORRE pra sempre (o Firestore não tenta de novo sozinho). Sem
      // isso, a lista "Meus Dossiês" ficava travada com nomes antigos até a
      // pessoa abrir um relatório manualmente. Agora, se cair por erro,
      // tenta de novo sozinho (com um pequeno atraso crescente).
      const assinar = () => {
         if (cancelado) return () => { };
         return onSnapshot(
            query(collection(db, 'cenarios'), where('userId', '==', user.uid)),
            (snapshot) => {
               tentativas = 0; // conexão funcionou, zera o contador de retentativas
               ultimoSnapshotCenariosRef.current = snapshot;
               reconciliarNomesComFirestore(snapshot);
            },
            (error) => {
               console.error('[PHOENIX] Erro no listener de sincronia da lista Meus Dossiês:', error);
               if (cancelado || tentativas >= 5) return;
               tentativas += 1;
               setTimeout(() => { if (!cancelado) unsubscribeAtual = assinar(); }, 1000 * tentativas);
            }
         );
      };

      let unsubscribeAtual = assinar();
      return () => { cancelado = true; if (typeof unsubscribeAtual === 'function') unsubscribeAtual(); };
   }, [user?.uid]);

   // 🔥 CORREÇÃO — CAUSA RAIZ DO "NOME VOLTA AO ANTIGO NO LOGIN": o listener
   // acima só reage quando algo muda DE NOVO no Firestore. Mas se o login
   // recarrega a lista a partir do cache local ou do backup (nomes velhos)
   // DEPOIS que o listener já rodou uma vez, não tem mudança nova pra
   // disparar a correção de novo — o nome antigo ficava "preso" até a
   // pessoa abrir um relatório manualmente. Agora, toda vez que a lista
   // for (re)carregada, reconcilia de novo usando o snapshot mais recente
   // que já temos guardado — sem precisar esperar uma mudança nova.
   useEffect(() => {
      if (historyList.length > 0 && ultimoSnapshotCenariosRef.current) {
         reconciliarNomesComFirestore(ultimoSnapshotCenariosRef.current);
      }
   }, [historyList.length]);

   // ==========================================
   // SINCRONIZA UM ÚNICO DOSSIÊ COM O FIRESTORE
   // Chamada só no momento em que aquele dossiê específico é criado ou
   // salvo — nunca em massa. Isso é o que mantém a coleção 'cenarios'
   // atualizada automaticamente, sem reescrever a carteira toda a cada
   // mudança de estado.
   // ==========================================
   const sincronizarDossieUnico = (item) => {
      if (!user?.uid || !item?.id) return Promise.resolve();
      const dadosParaSalvar = sanitizeForFirestore({
         name: item.name,
         date: item.date,
         plan: item.plan,
         userId: user.uid,
         // 🔥 Prioriza o e-mail digitado no checkout (item.emailComprador) —
         // sessões anônimas não têm user.email, então sem isso o campo
         // ficaria vazio e a migração de dossiês órfãos nunca encontraria
         // esse documento depois.
         userEmail: item.emailComprador || user.email,
         cpfComprador: item.cpfComprador || null,
         userType: item.snapshot?.userType || 'Gestor',
         snapshot: item.snapshot || {},
         timestamp: new Date().toISOString()
      });

      // 🔥 HISTÓRICO PARA O GRÁFICO DE EVOLUÇÃO (mesmo recurso do Dashboard) —
      // toda vez que um dossiê é criado/editado no APP, também guarda um
      // retrato dos números. Anexado DEPOIS do sanitizeForFirestore, porque
      // o arrayUnion() é um objeto especial do Firestore — sanitizar ele
      // junto com o resto quebraria o mecanismo de "adicionar ao array".
      // Isolado em try/catch pra nunca travar a sincronização principal.
      try {
         const fd = item.snapshot?.formData || {};
         const parseValorBR = (v) => {
            if (!v) return 0;
            if (typeof v === 'number') return v;
            return Number(v.toString().replace(/\./g, '').replace(',', '.')) || 0;
         };
         const core = calculateCoreMetrics(
            parseValorBR(fd.revenue), parseValorBR(fd.costs), parseValorBR(fd.debt), fd.segment,
            { custoNaoRecorrente: parseValorBR(fd.custoNaoRecorrente), custoDivida: parseValorBR(fd.custoDivida) }
         );
         dadosParaSalvar.historico = arrayUnion({
            date: new Date().toLocaleDateString('pt-BR'),
            revenue: parseValorBR(fd.revenue), costs: parseValorBR(fd.costs), debt: parseValorBR(fd.debt),
            ebitda: core.ebitdaAjustado ?? 0,
            equityValue: core.equityValue ?? 0,
            alavancagem: typeof core.alavancagem === 'number' ? core.alavancagem : 0,
            currentMargin: core.currentMargin ?? 0
         });
      } catch (erroHistorico) {
         console.error('[PHOENIX] Não foi possível salvar o retrato do histórico:', erroHistorico);
      }

      return setDoc(doc(db, 'cenarios', String(item.id)), dadosParaSalvar, { merge: true }).catch(e => console.error('[PHOENIX] Erro ao sincronizar dossiê no Firestore:', e));
   };

   const [itemToDelete, setItemToDelete] = useState(null);

   const confirmDelete = async () => {
      const idParaExcluir = itemToDelete;
      setHistoryList(prev => prev.filter(item => item.id !== idParaExcluir));
      if (activeDossierId === idParaExcluir) {
         setActiveDossierId(null);
         setStep(1);
         setView('landing');
      }
      setItemToDelete(null);

      // 🔥 Apaga também da nuvem (coleção 'cenarios') — antes só apagava
      // localmente, e o dossiê "ressuscitava" na próxima sincronização.
      try {
         await deleteDoc(doc(db, 'cenarios', String(idParaExcluir)));
      } catch (e) {
         console.error('[PHOENIX] Erro ao excluir dossiê do Firestore:', e);
      }
   };
   const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
   const [downloadConfirmEhSuper, setDownloadConfirmEhSuper] = useState(false);
   const [saveBtnText, setSaveBtnText] = useState('Salvar Cenário Preditivo');
   // Aviso visível de salvamento automático — diferente do saveBtnText
   // (que só aparece no botão da tela de Relatório), esse indicador
   // aparece direto nas telas do Assistente, onde os dados da empresa
   // (CNPJ, nome etc.) são editados.
   const [statusAutoSave, setStatusAutoSave] = useState('inativo'); // 'inativo' | 'salvando' | 'salvo'

   // ==========================================
   // 🎯📝✅ META FINANCEIRA + TAREFAS + ANOTAÇÕES — trazido do Dashboard
   // Gestor pra paridade. MESMOS campos do Firestore que o Dashboard já
   // usa (metaFinanceira, tarefasGestor, notasGestor), então ficam
   // sincronizados nos dois lugares automaticamente — sem precisar de
   // nenhuma lógica nova de sincronia, só ler/gravar os mesmos campos.
   // ==========================================
   const [metaFinanceira, setMetaFinanceira] = useState(null);
   const [tarefasGestor, setTarefasGestor] = useState([]);
   const [notasGestor, setNotasGestor] = useState([]);
   const [editandoMetaApp, setEditandoMetaApp] = useState(false);
   const [metaTipoFormApp, setMetaTipoFormApp] = useState('faturamento');
   const [metaValorFormApp, setMetaValorFormApp] = useState('');
   const [novaTarefaTextoApp, setNovaTarefaTextoApp] = useState('');
   const [novaTarefaPrazoApp, setNovaTarefaPrazoApp] = useState('');
   const [novaNotaApp, setNovaNotaApp] = useState('');

   // 🔥 CORREÇÃO: o Dashboard Consultor usa os campos 'tarefasConsultor' /
   // 'notasConsultor' no Firestore, diferente do 'tarefasGestor' /
   // 'notasGestor' usados aqui. Tarefas e Anotações estavam liberadas só
   // pro Gestor no app — Consultor nunca via essa seção. Agora os dois
   // perfis têm acesso, e cada um lê/grava no campo certo, sincronizando
   // de verdade com o que aparece no respectivo Dashboard.
   const campoTarefas = formData.userType === 'Consultor' ? 'tarefasConsultor' : 'tarefasGestor';
   const campoNotas = formData.userType === 'Consultor' ? 'notasConsultor' : 'notasGestor';

   // Escuta em tempo real o dossiê ativo — qualquer mudança feita pelo
   // Dashboard (ou por outra aba do app) aparece aqui sozinha.
   useEffect(() => {
      if (!activeDossierId) {
         setMetaFinanceira(null);
         setTarefasGestor([]);
         setNotasGestor([]);
         return;
      }
      const unsubscribe = onSnapshot(doc(db, 'cenarios', String(activeDossierId)), (snap) => {
         if (snap.exists()) {
            const dados = snap.data();
            setMetaFinanceira(dados.metaFinanceira || null);
            setTarefasGestor(dados[campoTarefas] || []);
            setNotasGestor(dados[campoNotas] || []);
         }
      }, (e) => console.error('[PHOENIX] Erro ao escutar meta/tarefas/anotações:', e));
      return () => unsubscribe();
   }, [activeDossierId, campoTarefas, campoNotas]);

   const salvarMetaApp = async () => {
      if (!activeDossierId || !metaValorFormApp) return;
      const valorNum = parseCurrencyToNumber(metaValorFormApp);
      if (valorNum <= 0) return;
      try {
         await setDoc(doc(db, 'cenarios', String(activeDossierId)), {
            metaFinanceira: { tipo: metaTipoFormApp, valor: valorNum, criadaEm: new Date().toISOString() }
         }, { merge: true });
         setEditandoMetaApp(false);
      } catch (e) {
         console.error('[PHOENIX] Erro ao salvar meta financeira:', e);
      }
   };

   const gerarIdTarefaApp = () => `${Date.now()}${Math.random().toString(36).slice(2, 8)}`;

   const adicionarTarefaApp = async () => {
      const texto = novaTarefaTextoApp.trim();
      if (!texto || !activeDossierId) return;
      const nova = { id: gerarIdTarefaApp(), texto, prazo: novaTarefaPrazoApp || null, concluida: false };
      try {
         await setDoc(doc(db, 'cenarios', String(activeDossierId)), { [campoTarefas]: [nova, ...tarefasGestor] }, { merge: true });
         setNovaTarefaTextoApp('');
         setNovaTarefaPrazoApp('');
      } catch (e) {
         console.error('[PHOENIX] Erro ao adicionar tarefa:', e);
      }
   };

   const alternarTarefaApp = async (id) => {
      if (!activeDossierId) return;
      const atualizado = tarefasGestor.map(t => t.id === id ? { ...t, concluida: !t.concluida } : t);
      try {
         await setDoc(doc(db, 'cenarios', String(activeDossierId)), { [campoTarefas]: atualizado }, { merge: true });
      } catch (e) {
         console.error('[PHOENIX] Erro ao atualizar tarefa:', e);
      }
   };

   const excluirTarefaApp = async (id) => {
      if (!activeDossierId) return;
      try {
         await setDoc(doc(db, 'cenarios', String(activeDossierId)), { [campoTarefas]: tarefasGestor.filter(t => t.id !== id) }, { merge: true });
      } catch (e) {
         console.error('[PHOENIX] Erro ao excluir tarefa:', e);
      }
   };

   const adicionarNotaApp = async () => {
      const texto = novaNotaApp.trim();
      if (!texto || !activeDossierId) return;
      const nova = { texto, data: new Date().toISOString(), autor: user?.email || 'Gestor' };
      try {
         await setDoc(doc(db, 'cenarios', String(activeDossierId)), { [campoNotas]: [nova, ...notasGestor] }, { merge: true });
         setNovaNotaApp('');
      } catch (e) {
         console.error('[PHOENIX] Erro ao adicionar anotação:', e);
      }
   };

   const excluirNotaApp = async (index) => {
      if (!activeDossierId) return;
      try {
         await setDoc(doc(db, 'cenarios', String(activeDossierId)), { [campoNotas]: notasGestor.filter((_, i) => i !== index) }, { merge: true });
      } catch (e) {
         console.error('[PHOENIX] Erro ao excluir anotação:', e);
      }
   };

   // ==========================================
   // 💰🏦 CALCULADORA DE PRECIFICAÇÃO + SIMULADOR DE EMPRÉSTIMO — trazido
   // do Dashboard Gestor. Ferramentas independentes, não gravam nada no
   // Firestore (só cálculo local, como no Dashboard).
   // ==========================================
   const [precoCustoApp, setPrecoCustoApp] = useState('');
   const [precoDespesasApp, setPrecoDespesasApp] = useState(8);
   const [precoMargemApp, setPrecoMargemApp] = useState(20);

   const mascaraMoedaApp = (valor) => {
      const apenasNumeros = valor.replace(/\D/g, '');
      if (apenasNumeros === '') return '';
      return (parseInt(apenasNumeros, 10) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
   };

   const calculoPrecoApp = (() => {
      const custo = parseCurrencyToNumber(precoCustoApp);
      const divisor = 1 - ((Number(precoDespesasApp) + Number(precoMargemApp)) / 100);
      if (custo <= 0 || divisor <= 0) return { valido: false };
      const precoSugerido = custo / divisor;
      return {
         valido: true,
         precoSugerido,
         lucroReais: precoSugerido * (Number(precoMargemApp) / 100),
         despesasReais: precoSugerido * (Number(precoDespesasApp) / 100)
      };
   })();

   const [emprestimoValorApp, setEmprestimoValorApp] = useState('');
   const [emprestimoJurosApp, setEmprestimoJurosApp] = useState(24);
   const [emprestimoPrazoApp, setEmprestimoPrazoApp] = useState(24);

   const [progress, setProgress] = useState(0);
   const [showSettings, setShowSettings] = useState(false);

   // 🔗 PAREAMENTO SEM SENHA — gera um link de uso único (5 minutos) que,
   // escaneado pelo Dashboard no computador, autentica lá sem pedir
   // e-mail/senha nenhum.
   const [showPareamento, setShowPareamento] = useState(false);
   const [linkPareamento, setLinkPareamento] = useState('');
   // 🔒 QR gerado AQUI, no aparelho (biblioteca qrcode). Antes o link com o token de
   // pareamento era enviado a api.qrserver.com (serviço de terceiros) só pra desenhar o QR.
   const [qrPareamentoImg, setQrPareamentoImg] = useState('');
   useEffect(() => {
      let cancelado = false;
      if (!linkPareamento) { setQrPareamentoImg(''); return; }
      QRCode.toDataURL(linkPareamento, { width: 400, margin: 1, errorCorrectionLevel: 'M' })
         .then(url => { if (!cancelado) setQrPareamentoImg(url); })
         .catch(e => { console.error('[PHOENIX] Erro ao gerar QR de pareamento:', e); if (!cancelado) setQrPareamentoImg(''); });
      return () => { cancelado = true; };
   }, [linkPareamento]);
   const [gerandoPareamento, setGerandoPareamento] = useState(false);

   const abrirPareamentoQRCode = async () => {
      if (!user) return;
      setShowPareamento(true);
      setGerandoPareamento(true);
      setLinkPareamento('');
      try {
         const link = await gerarLinkDashboard(user.uid);
         setLinkPareamento(link);
      } catch (erro) {
         console.error('[PHOENIX] Erro ao gerar link de pareamento:', erro);
         setToastMessage('Não foi possível gerar o QR Code agora. Tente novamente.');
         setShowPareamento(false);
      } finally {
         setGerandoPareamento(false);
      }
   };
   const [confirmandoExclusaoConta, setConfirmandoExclusaoConta] = useState(false);
   const [excluindoConta, setExcluindoConta] = useState(false);

   // 🔥 NOVO: exclusão de conta de verdade — antes o botão não tinha onClick
   // nenhum. Apaga todos os dossiês do usuário (coleção 'cenarios'), o
   // documento de perfil ('usuarios') e por fim a conta no Firebase Auth.
   // 🔥 CORREÇÃO IMPORTANTE que fiz por conta própria (mesma corrigida nos
   // dois Dashboards): antes de apagar a conta, agora CANCELA a assinatura
   // ativa no Asaas primeiro. Sem isso, excluir a conta apagava o login
   // mas a cobrança recorrente continuava rodando pra sempre no Asaas — e
   // a pessoa perderia o acesso à própria conta pra conseguir cancelar,
   // sem nenhum jeito de parar de ser cobrada.
   const excluirContaDefinitivamente = async () => {
      if (!auth.currentUser) return;
      setExcluindoConta(true);
      try {
         const uid = auth.currentUser.uid;
         const perfilSnap = await getDoc(doc(db, 'usuarios', uid));
         const dadosPerfil = perfilSnap.exists() ? perfilSnap.data() : {};
         if (dadosPerfil.consultorAsaasSubscriptionId) {
            await cancelarAssinaturaAsaas({ subscriptionId: dadosPerfil.consultorAsaasSubscriptionId, imediato: true }).catch(e =>
               console.error('[PHOENIX] Erro ao cancelar assinatura Consultor no Asaas (excluindo mesmo assim):', e)
            );
         }
         if (dadosPerfil.gestorAsaasSubscriptionId) {
            await cancelarAssinaturaAsaas({ subscriptionId: dadosPerfil.gestorAsaasSubscriptionId, imediato: true }).catch(e =>
               console.error('[PHOENIX] Erro ao cancelar assinatura Gestor no Asaas (excluindo mesmo assim):', e)
            );
         }
         const q = query(collection(db, 'cenarios'), where('userId', '==', uid));
         const snapshot = await getDocs(q);
         await Promise.all(snapshot.docs.map((d) => deleteDoc(doc(db, 'cenarios', d.id))));
         await deleteDoc(doc(db, 'usuarios', uid)).catch(() => { });
         await deleteUser(auth.currentUser);
         setConfirmandoExclusaoConta(false);
         setShowSettings(false);
         if (typeof onLogout === 'function') onLogout();
      } catch (e) {
         console.error('[PHOENIX] Erro ao excluir conta:', e);
         if (e?.code === 'auth/requires-recent-login') {
            setToastMessage('Por segurança, saia e faça login novamente antes de excluir sua conta.');
         } else {
            setToastMessage('Não foi possível excluir a conta agora. Tente novamente.');
         }
      } finally {
         setExcluindoConta(false);
      }
   };


   const superPdfRef = useRef(null);
   const basicPdfRef = useRef(null);

   const [toastMessage, setToastMessage] = useState('');

   // 🔥 CORREÇÃO IMPORTANTE: esse "toast" era na verdade um modal que
   // travava a tela inteira e exigia clique pra fechar — toda vez que
   // QUALQUER parte do app chamava setToastMessage (login, criar conta,
   // salvar, etc). Isso multiplicava cliques desnecessários. Agora ele
   // some sozinho depois de alguns segundos, sem travar nada.
   useEffect(() => {
      if (!toastMessage) return;
      const timer = setTimeout(() => setToastMessage(''), 3200);
      return () => clearTimeout(timer);
   }, [toastMessage]);

   // 🔥 SEPARADO do toast comum: confirmação de PAGAMENTO/ASSINATURA é
   // importante demais pra sumir sozinha — fica visível até a pessoa
   // clicar em "Entendi", com destaque visual maior (é a prova de que o
   // dinheiro foi processado direito).
   const [confirmacaoPagamento, setConfirmacaoPagamento] = useState('');
   // 🔥 Guarda pra onde o botão "Entendi" deve levar depois de uma
   // confirmação de pagamento. Antes o botão só fechava o aviso e deixava
   // a pessoa parada na TELA DE VENDA (a mesma que ela acabou de comprar),
   // com a cobrança ainda aberta embaixo — parecia que não tinha
   // funcionado, e obrigava a cancelar tudo na mão pra chegar no relatório.
   const [destinoAposConfirmacao, setDestinoAposConfirmacao] = useState(null);

   // ===================================================================
   // 🎨 ESTADOS DO WHITE-LABEL (CONSULTOR)
   // ===================================================================
   const [wlName, setWlName] = useState('');
   const [wlColor, setWlColor] = useState('#a855f7');
   const [wlLogo, setWlLogo] = useState(null);

   useEffect(() => {
      if (toastMessage) {
         const t = setTimeout(() => setToastMessage(''), 4000);
         return () => clearTimeout(t);
      }
   }, [toastMessage]);

   // 🔒 Sem usuário logado (nunca logou, ou sessão expirou por inatividade):
   // sempre mostra a tela inicial do app, onde está o formulário de login.
   // 🔥 CORREÇÃO: esse efeito rodava assim que o app carregava (já que
   // `user` normalmente começa vazio até o Firebase confirmar a sessão) e
   // forçava a tela pra 'landing' na hora — atropelando a splash antes
   // mesmo do temporizador dela terminar, não importava o tempo configurado.
   // Agora ele ignora a troca enquanto a splash ainda está na tela.
   useEffect(() => {
      if (!user && view !== 'splash') {
         setView('landing');
         setShowSettings(false);
      }
   }, [user]);


   // ==========================================
   // CARREGAR PLANOS SALVOS DO LOCALSTORAGE (GESTOR E CONSULTOR, EM PARALELO)
   // 🔥 CORRIGIDO: antes, este efeito apagava o plano do "perfil oposto" toda vez
   // que rodava (ex: se o último perfil ativo era Gestor, ele deletava
   // phoenix_consultor_plan). Isso quebrava a coexistência dos dois perfis na
   // mesma conta — exatamente o cenário que você reportou (plano do Consultor
   // sumindo no logout). Agora cada perfil é carregado de forma independente,
   // sem um apagar o outro.
   // ==========================================
   useEffect(() => {
      const userType = localStorage.getItem('phoenix_user_type') || 'gestor';

      // 🔥 CORRIGIDO (2026-09-30): o plano guardado no localStorage é um
      // cache DESTE APARELHO e não dizia de quem era. Logando numa conta
      // Entry logo depois de outra conta (de teste) que tinha Total/
      // Enterprise, o app lia esse cache velho e mostrava tudo como "já
      // adquirido" / "protocolo máximo" até dar logout e entrar de novo.
      // Agora o cache só vale se pertencer ao usuário atual
      // (phoenix_plan_owner_uid); senão é descartado e o plano de verdade
      // chega em instantes pelo onSnapshot de usuarios/{uid}.
      if (user?.uid && !user.isAnonymous && localStorage.getItem('phoenix_plan_owner_uid') !== user.uid) {
         localStorage.removeItem('phoenix_gestor_plan');
         localStorage.removeItem('phoenix_consultor_plan');
         localStorage.setItem('phoenix_plan_owner_uid', user.uid);
      }

      const savedGestorPlan = localStorage.getItem('phoenix_gestor_plan');
      const savedConsultorPlan = localStorage.getItem('phoenix_consultor_plan');

      const isValidGestorPlan = (plan) => plan === 'tactical' || plan === 'plus' || plan === 'total';
      const isValidConsultorPlan = (plan) => plan === 'advisor' || plan === 'boutique' || plan === 'enterprise';

      // 🔥 CORREÇÃO DE SEGURANÇA: depois de um logout, o Firebase entra
      // automaticamente numa sessão ANÔNIMA nova (necessário pro app
      // funcionar sem login) — mas essa sessão nova NÃO tem nenhum plano
      // de verdade. Sem essa checagem, o app "herdava" o plano salvo no
      // localStorage deste aparelho mesmo sem ninguém logado, deixando
      // criar uma análise paga sem conta nenhuma por trás. Agora, só
      // confiamos no plano salvo quando existe uma CONTA de verdade
      // (usuário logado, não anônimo) por trás da sessão atual.
      const contaDeVerdade = !!user && !user.isAnonymous;
      const houveContaAntesDesteAparelho = !!(savedGestorPlan || savedConsultorPlan);

      if (!contaDeVerdade) {
         setGestorPlan('entry');
         setConsultorPlan('free');
         setActivePlan('entry');
         setUserLevel(1);
         // Se existe plano salvo no aparelho mas ninguém está logado de
         // verdade, é sinal de que a pessoa tinha conta antes — convida
         // a fazer login pra recuperar o acesso, em vez de deixar parecer
         // que ela nunca teve nada.
         if (houveContaAntesDesteAparelho && user?.isAnonymous) {
            setToastMessage('Você tinha um plano ativo — faça login pra recuperar o acesso.');
         }
         return;
      }

      // Carrega (ou reseta, individualmente) cada perfil — nunca um mexe no outro
      setGestorPlan(isValidGestorPlan(savedGestorPlan) ? savedGestorPlan : 'entry');
      setConsultorPlan(isValidConsultorPlan(savedConsultorPlan) ? savedConsultorPlan : 'free');

      // activePlan/userLevel seguem o PERFIL ATIVO no momento (o que está na tela)
      const planLevelMap = {
         'entry': 1, 'tactical': 2, 'plus': 3, 'total': 4, 'boardroom': 4,
         'advisor': 5, 'boutique': 6, 'enterprise': 7
      };
      const isGestorProfile = userType !== 'consultor';
      const planDoPerfilAtivo = isGestorProfile ? savedGestorPlan : savedConsultorPlan;
      const isValidoNoPerfilAtivo = isGestorProfile ? isValidGestorPlan(planDoPerfilAtivo) : isValidConsultorPlan(planDoPerfilAtivo);

      if (isValidoNoPerfilAtivo) {
         setActivePlan(planDoPerfilAtivo);
         setUserLevel(isGestorProfile ? (planLevelMap[planDoPerfilAtivo] || 1) : 1);
      } else {
         setActivePlan('entry');
         setUserLevel(1);
      }
   }, [user]);

   // 🔥 CORREÇÃO — CAUSA RAIZ DO PLANO DESSINCRONIZADO: antes, o app só
   // buscava o plano certo no Firestore no momento do LOGIN. Numa sessão
   // já ativa (usuário não saiu e entrou de novo — só reabriu o app, ou o
   // plano mudou por fora, ex: editado direto no Firestore ou futuramente
   // pela tela de pagamento), o app continuava usando o valor antigo salvo
   // no localStorage DESTE aparelho — e o efeito de sincronização
   // regravava esse valor desatualizado por cima do valor certo no
   // Firestore, causando a dessincronia. Agora, toda vez que houver uma
   // sessão de usuário ativa (login novo OU sessão restaurada ao abrir o
   // app), buscamos o plano direto do documento 'usuarios/{uid}' — a
   // fonte da verdade real — e atualizamos o localStorage e a tela.
   // 🔥 Trava usada pela correção abaixo — impede que o efeito de "salvar
   // plano no Firestore" dispare com um valor ainda desatualizado (vindo
   // do localStorage deste aparelho) antes da leitura fresca do Firestore
   // (logo abaixo) ter chance de terminar. Sem essa trava, existia uma
   // corrida real: os dois efeitos disparam juntos no login/abertura do
   // app, um lendo do Firestore (async, mais lento) e outro já gravando de
   // volta o valor antigo do localStorage (mais rápido) — quando a leitura
   // fresca terminava depois, às vezes o valor antigo já tinha sido regravado
   // por cima da compra de verdade no Firestore. Era essa a causa raiz de
   // "comprei mas o app pede assinatura de novo depois do logout".
   const planoInicialCarregadoRef = useRef(false);

   // 🔒 Escuta o plano EM TEMPO REAL (antes era uma leitura única no login).
   // Agora que só o webhookAsaas (servidor) libera plano, o app precisa
   // ficar ouvindo: quando o pagamento confirma no Asaas, o webhook grava
   // aqui e esta tela reage sozinha, sem a pessoa precisar recarregar.
   // O campo do Gestor no Firestore é 'plan' (é assim que o webhook grava),
   // mas aceitamos 'gestorPlan' também por compatibilidade com dados antigos.
   useEffect(() => {
      if (!user?.uid) return;
      // 🔥 CORRIGIDO — CAUSA RAIZ DO "ASSINATURA REALIZADA" FALSO NO LOGIN:
      // antes, o estado "qual plano estava valendo antes" ficava num useRef
      // do COMPONENTE INTEIRO — compartilhado entre TODAS as vezes que este
      // efeito monta. Em desenvolvimento (React.StrictMode) e em algumas
      // trocas de sessão (login anônimo → login de verdade), esse efeito
      // pode montar mais de uma vez em sequência rápida, cada montagem
      // criando sua PRÓPRIA assinatura no Firestore (onSnapshot) — mas
      // todas escrevendo no mesmo ref compartilhado. Quando duas dessas
      // assinaturas ficavam ativas ao mesmo tempo, uma podia ler o "anterior"
      // que a outra tinha acabado de gravar, achando (errado) que o plano
      // tinha acabado de MUDAR — e disparava o aviso de assinatura sem
      // nenhuma compra ter acontecido.
      // A correção: guardar o "anterior" como variável LOCAL desta instância
      // do efeito (não um ref do componente) — assim cada montagem tem sua
      // própria memória isolada, nunca contaminada por uma assinatura irmã.
      let planoAnterior = { gestor: null, consultor: null, inicializado: false };
      const unsubscribe = onSnapshot(doc(db, 'usuarios', user.uid), (snap) => {
         if (!snap.exists()) {
            planoInicialCarregadoRef.current = true;
            return;
         }
         // 🔥 CORRIGIDO — SEGUNDA CAUSA DO "ASSINATURA REALIZADA" FALSO NO
         // LOGIN: a PRIMEIRA entrega deste onSnapshot muitas vezes chega
         // como um snapshot local PENDENTE (hasPendingWrites=true, direto do
         // buffer de escrita do próprio SDK, antes do servidor confirmar) —
         // e vem com os campos de plano ainda `undefined`, mesmo que o plano
         // de verdade já exista no servidor. Sem essa trava, o código usava
         // esse snapshot vazio como "estado inicial" (plano = null) e, ao
         // chegar a leitura de verdade logo em seguida (plano = 'total'),
         // interpretava isso como "acabou de assinar agora" — mesmo sendo
         // só a leitura normal do plano já existente. Ignorando snapshots
         // pendentes por completo, só a primeira leitura CONFIRMADA pelo
         // servidor vira a linha de base de comparação.
         if (snap.metadata.hasPendingWrites) return;
         const dados = snap.data();
         const planoGestor = dados.plan || dados.gestorPlan;
         const planoConsultor = dados.consultorPlan;
         // 🔥 NOVO: aviso de pagamento atrasado — mostra no app enquanto a
         // Cloud Function não suspender de vez (prazo de 48h). Vem direto
         // do Firestore, então some sozinho assim que a pessoa pagar ou
         // assim que o prazo vencer e o acesso for suspenso.
         setPagamentoAtrasado(dados.pagamentoAtrasado || null);
         setAcessoSuspensoPorAtraso(dados.acessoSuspensoPorAtraso || null);
         setCancelamentoGestor(dados.cancelamentoGestor || null);
         setCancelamentoConsultor(dados.cancelamentoConsultor || null);
         setOrigemGestor(dados.gestorOrigem || null);
         setOrigemConsultor(dados.consultorOrigem || null);
         setProdutoGoogleGestor((dados.gestorGoogle && dados.gestorGoogle.productId) || null);
         setProdutoGoogleConsultor((dados.consultorGoogle && dados.consultorGoogle.productId) || null);

         if (planoGestor && ['tactical', 'plus', 'total'].includes(planoGestor)) {
            localStorage.setItem('phoenix_plan_owner_uid', user.uid);
            localStorage.setItem('phoenix_gestor_plan', planoGestor);
            setGestorPlan(planoGestor);
         }
         if (planoConsultor && ['advisor', 'boutique', 'enterprise'].includes(planoConsultor)) {
            localStorage.setItem('phoenix_plan_owner_uid', user.uid);
            localStorage.setItem('phoenix_consultor_plan', planoConsultor);
            setConsultorPlan(planoConsultor);
         }
         // Conta real SEM plano pago no servidor: garante que nenhum plano
         // velho (de outra conta neste aparelho) fique na tela.
         if (!user.isAnonymous) {
            if (!(planoGestor && ['tactical', 'plus', 'total'].includes(planoGestor))) setGestorPlan('entry');
            if (!(planoConsultor && ['advisor', 'boutique', 'enterprise'].includes(planoConsultor))) setConsultorPlan('free');
         }

         // 🔥 ASSINATURA CONFIRMADA: se o plano MUDOU agora (e não é a
         // primeira leitura desta assinatura), foi o webhook confirmando um
         // pagamento — avisa a pessoa e manda pro lugar certo, em vez de
         // deixá-la parada na tela de vendas achando que não funcionou.
         // `planoAnterior` é local a ESTA instância do efeito — nunca é
         // lido nem escrito por nenhuma outra assinatura concorrente, então
         // a primeira leitura desta instância nunca pode ser confundida com
         // uma mudança de plano.
         if (planoAnterior.inicializado) {
            const gestorMudou = planoGestor && ['tactical', 'plus', 'total'].includes(planoGestor) && planoGestor !== planoAnterior.gestor;
            const consultorMudou = planoConsultor && ['advisor', 'boutique', 'enterprise'].includes(planoConsultor) && planoConsultor !== planoAnterior.consultor;
            if (gestorMudou || consultorMudou) {
               setShowInAppPurchase(false); // fecha o checkout que ficou aberto embaixo
               setConfirmacaoPagamento('Assinatura realizada com sucesso! Seu plano já está ativo.');
               setDestinoAposConfirmacao('history');
            }
         }
         planoAnterior = { gestor: planoGestor || null, consultor: planoConsultor || null, inicializado: true };

         planoInicialCarregadoRef.current = true;
      }, (e) => {
         console.error('[PHOENIX] Erro ao escutar plano no Firestore:', e);
         planoInicialCarregadoRef.current = true; // não trava pra sempre se falhar
      });
      return () => unsubscribe();
   }, [user?.uid]);

   // ==========================================
   // MOTOR DO SCANNER (ANIMAÇÃO E TRANSIÇÃO)
   // ==========================================
   useEffect(() => {
      let timer;
      if (view === 'scanning') {
         timer = setInterval(() => {
            setProgress(prev => {
               if (prev >= 100) {
                  clearInterval(timer);
                  return 100;
               }
               return prev + 1;
            });
         }, 180); // Velocidade do scanner
      }
      return () => clearInterval(timer);
   }, [view]);

   useEffect(() => {
      if (view === 'scanning' && progress === 100) {
         const timeout = setTimeout(() => setView('ready'), 800);
         return () => clearTimeout(timeout);
      }
   }, [view, progress]);

   // 🔥 CORREÇÃO: essa correção só existia pro Gestor (mais abaixo). Do lado
   // Consultor, o plano do dossiê era corrigido só na memória local
   // (historyList), nunca gravado de volta no Firestore — então continuava
   // errado pra sempre no Dashboard e em qualquer outro aparelho/aba, e
   // voltava a ficar errado assim que o app recarregasse. Agora corrige
   // direto na fonte, igual já fazíamos pro Gestor.
   useEffect(() => {
      if (!user?.uid || !consultorPlan || consultorPlan === 'free') return;
      const novoPlano = String(consultorPlan).toUpperCase();

      (async () => {
         try {
            const q = query(collection(db, 'cenarios'), where('userId', '==', user.uid));
            const snap = await getDocs(q);
            const escritas = [];
            snap.forEach(docSnap => {
               const dados = docSnap.data();
               const isConsultorDossier = String(dados.userType || dados.snapshot?.userType || 'Gestor').toLowerCase() === 'consultor';
               if (!isConsultorDossier) return; // dossiês de Gestor seguem o plano do Gestor, não mexe aqui
               if (dados.plan === novoPlano) return; // já está correto
               escritas.push(setDoc(doc(db, 'cenarios', docSnap.id), { plan: novoPlano }, { merge: true }));
            });
            if (escritas.length > 0) await Promise.all(escritas);
         } catch (e) {
            console.error('[PHOENIX] Erro ao sincronizar plano dos dossiês de Consultor com o Firestore:', e);
         }
      })();

      setHistoryList(prev => prev.map(item => {
         const isConsultorDossier = String(item.snapshot?.userType || item.snapshot?.formData?.userType || '').toLowerCase() === 'consultor';
         return isConsultorDossier ? { ...item, plan: novoPlano } : item;
      }));
   }, [consultorPlan, user?.uid]);

   // (Removido em 2026-10-12) Existia aqui um efeito que reescrevia o rótulo de TODOS os dossiês do Gestor para o plano
   // atual. As Regras do Firestore já recusavam a gravação; o que sobrava era só trocar o rótulo na lista local — e isso
   // fazia Entry parecer "do plano" e deixava o comportamento ao subir/descer de plano diferente entre aparelhos. Agora o
   // nível de abertura de cada relatório é decidido em loadDossier, pelo plano ATUAL.

   // 🔒 REMOVIDO (segurança): aqui existia um efeito que gravava
   // gestorPlan/consultorPlan de volta no Firestore a partir do estado
   // local. Isso era o outro lado da mesma brecha: bastava manipular o
   // estado no navegador pra "sincronizar" um plano que nunca foi pago.
   // Não é mais necessário — o listener em tempo real (mais acima) já
   // mantém app e banco alinhados, e a fonte da verdade passou a ser o
   // servidor. As novas regras do Firestore inclusive REJEITAM essa
   // escrita vinda do navegador.

   // ===================================================================
   // 🚀 MOTOR DE PAGAMENTO E LOGICA DE PRODUTOS
   // ===================================================================
   const [showInAppPurchase, setShowInAppPurchase] = useState(false);
   const [emailPagamento, setEmailPagamento] = useState('');

   // 🔥 Se já existe conta de verdade logada (não anônima), o e-mail do
   // checkout já é conhecido — não faz sentido pedir de novo. Só preenche
   // automaticamente quando o campo ainda está vazio (não sobrescreve se
   // a pessoa já tiver digitado outro e-mail de propósito).
   useEffect(() => {
      if (user && !user.isAnonymous && user.email && !emailPagamento) {
         setEmailPagamento(user.email);
      }
      // eslint-disable-next-line
   }, [user?.uid]);
   const [cpfCnpjPagamento, setCpfCnpjPagamento] = useState('');
   // 🔥 Mesmo princípio usado no checkout dos Dashboards: mostra o aviso
   // de CPF inválido direto embaixo do campo, assim que a pessoa termina
   // de digitar — não só depois de tentar confirmar o pagamento.
   const [cpfPagamentoTocado, setCpfPagamentoTocado] = useState(false);
   const [formaPagamentoEscolhida, setFormaPagamentoEscolhida] = useState('PIX');
   const [processandoPagamento, setProcessandoPagamento] = useState(false);
  const [aceiteCheckout, setAceiteCheckout] = useState(false);

  // Google Play Billing: ao abrir o app logado, confirma no servidor compras que ficaram pagas mas não confirmadas (app fechou no meio, sem internet…).
  useEffect(() => {
    if (user && !user.isAnonymous && canalDePagamento() === 'google') {
      recuperarComprasGoogle().catch((e) => console.warn('[PHOENIX] Recuperação de compras do Google falhou:', e));
    }
  }, [user]);

   // 🔥 PROMOÇÃO DE CONTA ANÔNIMA — depois que a pessoa já pagou e já tem
   // o relatório na mão (nenhuma barreira antes), oferecemos um convite
   // rápido pra virar conta de verdade, sem perder nada do que já fez.
   const [mostrarConviteConta, setMostrarConviteConta] = useState(false);
   const [emailConviteConta, setEmailConviteConta] = useState('');
   const [senhaConviteConta, setSenhaConviteConta] = useState('');
   const [salvandoConviteConta, setSalvandoConviteConta] = useState(false);
  const [aceiteConvite, setAceiteConvite] = useState(false);
   const [erroConviteConta, setErroConviteConta] = useState('');

   const promoverContaAnonima = async () => {
      if (!user || !user.isAnonymous) return;
      if (!aceiteConvite) { setErroConviteConta('Marque a caixa de aceite para continuar.'); return; }
      registrarAceite('convite_conta', null);
      if (!emailConviteConta || senhaConviteConta.length < 6) {
         setErroConviteConta('Informe um e-mail e uma senha com pelo menos 6 caracteres.');
         return;
      }
      setSalvandoConviteConta(true);
      setErroConviteConta('');
      try {
         // 🔥 CORREÇÃO: antes de promover, força a renovação do token de
         // acesso. Isso evita o erro "auth/user-token-expired" em sessões
         // que ficaram um tempo abertas (ex: usuário demorou pra preencher
         // o formulário) — o Firebase consegue renovar sozinho na maioria
         // dos casos, sem precisar recriar a sessão do zero (o que perderia
         // o vínculo com os dados já salvos nesse UID).
         await user.getIdToken(true);

         const credencial = EmailAuthProvider.credential(emailConviteConta, senhaConviteConta);
         await linkWithCredential(user, credencial);
         // O mesmo uid continua — nada do que já foi salvo se perde.
         setMostrarConviteConta(false);
         setToastMessage('Conta criada! Você já pode acessar seus relatórios de qualquer aparelho.');
      } catch (erro) {
         console.error('[PHOENIX] Erro ao promover conta anônima:', erro);
         if (erro.code === 'auth/email-already-in-use') {
            // 🔥 Em vez de deixar a pessoa travada com só uma mensagem de
            // erro, guarda o UID anônimo atual (pra migrar depois) e já
            // abre o login de verdade, com o e-mail preenchido.
            setUidAnonimoParaMigrar(user.uid);
            setMostrarConviteConta(false);
            setLoginModalEmail(emailConviteConta);
            setMostrarLoginModal(true);
            setToastMessage('Esse e-mail já é sua conta — faça login pra continuar de onde parou.');
         } else if (erro.code === 'auth/user-token-expired' || erro.code === 'auth/user-token-expired' || erro.code === 'auth/invalid-user-token') {
            // 🔥 Se mesmo a renovação forçada falhou, a sessão está realmente
            // inválida (não só "velha"). Nesse caso é mais seguro orientar o
            // usuário a atualizar a página ANTES de perder o vínculo com os
            // dados, em vez de deixar o app criar uma sessão nova silenciosa.
            setErroConviteConta('Sua sessão expirou. Atualize a página (F5) e tente criar a conta de novo — seus dados preenchidos continuam salvos.');
         } else {
            setErroConviteConta('Não foi possível criar a conta agora. Tente novamente.');
         }
      } finally {
         setSalvandoConviteConta(false);
      }
   };
   const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);

   // 🔥 LOGIN SEMPRE ACESSÍVEL — antes, quem caía numa sessão anônima
   // (ex: depois de 10 minutos de inatividade) ficava "preso" ali, sem
   // achar onde digitar e-mail/senha de novo pra voltar pra própria
   // conta de verdade. O formulário de login já existia (na tela
   // inicial), mas ficava escondido, já que agora SEMPRE existe um
   // "usuário" (mesmo anônimo). Esse modal fica disponível a qualquer
   // momento pela tela de Perfil.
   const [mostrarLoginModal, setMostrarLoginModal] = useState(false);
   const [loginModalEmail, setLoginModalEmail] = useState('');
   const [loginModalSenha, setLoginModalSenha] = useState('');
   const [loginModalCarregando, setLoginModalCarregando] = useState(false);
   const [loginModalErro, setLoginModalErro] = useState('');
   const [loginModalMsg, setLoginModalMsg] = useState('');
   // 🔥 Guarda o UID da sessão anônima quando a promoção de conta falha por
   // "e-mail já em uso" — usado pra migrar os dados (plano, dossiês) pra
   // conta real assim que o login for concluído com sucesso.
   const [uidAnonimoParaMigrar, setUidAnonimoParaMigrar] = useState(null);

   // 🔥 CANCELAMENTO COM ACESSO ATÉ O FIM DO PERÍODO PAGO. O servidor guarda {acessoAte, canceladoEm} em
   // usuarios/{uid}.cancelamentoGestor/Consultor; aqui só lemos e mostramos. Quem encerra o acesso na data é a
   // função agendada (revogarCancelamentosVencidos).
   const [cancelamentoGestor, setCancelamentoGestor] = useState(null);
   const [cancelamentoConsultor, setCancelamentoConsultor] = useState(null);
   const [origemGestor, setOrigemGestor] = useState(null); // 'asaas' | 'google' | null — canal de pagamento da assinatura
   const [origemConsultor, setOrigemConsultor] = useState(null);
   const [produtoGoogleGestor, setProdutoGoogleGestor] = useState(null);
   const [produtoGoogleConsultor, setProdutoGoogleConsultor] = useState(null);
   const [cancelarAssinaturaAlvo, setCancelarAssinaturaAlvo] = useState(null); // 'gestor' | 'consultor' | null
   const [cancelandoAssinatura, setCancelandoAssinatura] = useState(false);
   const formatarDataBR = (iso) => {
      try { return new Date(iso).toLocaleDateString('pt-BR'); } catch (e) { return ''; }
   };
   const confirmarCancelamentoAssinatura = async () => {
      if (!user || !cancelarAssinaturaAlvo) return;
      setCancelandoAssinatura(true);
      try {
         const perfilSnap = await getDoc(doc(db, 'usuarios', user.uid));
         const dadosPerfil = perfilSnap.exists() ? perfilSnap.data() : {};
         const subscriptionId = cancelarAssinaturaAlvo === 'consultor' ? dadosPerfil.consultorAsaasSubscriptionId : dadosPerfil.gestorAsaasSubscriptionId;
         if (!subscriptionId) {
            setToastMessage('Não encontramos uma assinatura ativa para cancelar. Fale com o suporte: suporte@phoenixos.com.br');
            return;
         }
         const r = await cancelarAssinaturaAsaas({ subscriptionId });
         const nome = cancelarAssinaturaAlvo === 'consultor' ? 'Consultor' : 'Gestor';
         if (r && r.acessoAte) {
            if (cancelarAssinaturaAlvo === 'consultor') setCancelamentoConsultor({ acessoAte: r.acessoAte });
            else setCancelamentoGestor({ acessoAte: r.acessoAte });
            setToastMessage('Assinatura do ' + nome + ' cancelada. Seu acesso continua até ' + formatarDataBR(r.acessoAte) + '.');
         } else {
            if (cancelarAssinaturaAlvo === 'consultor') { setConsultorPlan('free'); localStorage.setItem('phoenix_consultor_plan', 'free'); }
            else { setGestorPlan('entry'); localStorage.setItem('phoenix_gestor_plan', 'entry'); }
            setToastMessage('Assinatura do ' + nome + ' cancelada.');
         }
         setCancelarAssinaturaAlvo(null);
      } catch (e) {
         console.error('[PHOENIX] Erro ao cancelar assinatura:', e);
         setToastMessage('Não foi possível cancelar agora. Tente novamente ou fale com o suporte.');
      } finally {
         setCancelandoAssinatura(false);
      }
   };

   // 🔒 O convite persistente de "criar conta" só aparece pra sessão anônima que JÁ pagou algum dossiê.
   // Antes aparecia com qualquer dossiê (inclusive cobrança gerada e não paga), e a pessoa podia criar uma
   // conta real sem ter pago nada. (O relatório continuava bloqueado, mas ficava conta criada à toa.)
   const [anonimoTemDossiePago, setAnonimoTemDossiePago] = useState(false);
   useEffect(() => {
      if (!user || !user.isAnonymous) { setAnonimoTemDossiePago(false); return; }
      let cancelado = false;
      getDocs(query(collection(db, 'cenarios'), where('userId', '==', user.uid), where('statusPagamento', '==', 'pago')))
         .then((snap) => { if (!cancelado) setAnonimoTemDossiePago(!snap.empty); })
         .catch(() => { if (!cancelado) setAnonimoTemDossiePago(false); });
      return () => { cancelado = true; };
      // eslint-disable-next-line
   }, [user?.uid, user?.isAnonymous, historyList.length, entryStatusPagamento, mostrarConviteConta]);

   // 🔥 REMOVIDO (2026-09-28) — `ativarPerfilConsultorDeVerdade()` gravava
   // `consultorPlan` direto no Firestore pelo navegador, sem nenhuma
   // verificação de pagamento (vulnerabilidade crítica achada em teste real
   // com Asaas de produção). O único botão que a chamava agora manda pro
   // checkout de verdade (`setView('consultor_showcase')`) — plano só é
   // liberado pelo webhookAsaas depois de pagamento confirmado.

   // 🔥 DECISÃO DE DUPLA ASSINATURA — quando alguém que já é Gestor pago
   // tenta ativar o Consultor (ou vice-versa), avisa ANTES de qualquer
   // cobrança que as duas assinaturas somam, em vez da pessoa descobrir
   // isso só na fatura. Evita reclamação de "cobrança inesperada".
   const [mostrarDecisaoDuplaAssinatura, setMostrarDecisaoDuplaAssinatura] = useState(false);
   const [processandoDecisaoDupla, setProcessandoDecisaoDupla] = useState(false);

   // Botão 2 do card de decisão: cancela a assinatura do Gestor (guarda
   // o histórico/dados, só para a cobrança recorrente) e segue pra
   // ativar o Consultor normalmente.
   // 🔥 GENERALIZADA: antes só cancelava Gestor → ativava Consultor (sem
   // cobrar, direto). Agora funciona nas duas direções (com base em qual
   // plano ficou pendente) e sempre segue pro checkout de VERDADE (Asaas)
   // no final — consistente com "Assinar os dois", nunca ativa de graça.
   const cancelarPlanoAtualEIrParaCheckoutPendente = async () => {
      if (!planoPendenteAposAviso) return;
      const ehPendenteConsultor = ['advisor', 'boutique', 'enterprise'].includes(planoPendenteAposAviso.planId) || planoPendenteAposAviso.planType === 'CONSULTOR';

      setProcessandoDecisaoDupla(true);
      try {
         const perfilSnap = await getDoc(doc(db, 'usuarios', user.uid));
         const dadosPerfil = perfilSnap.exists() ? perfilSnap.data() : {};

         if (ehPendenteConsultor) {
            // Vai assinar Consultor — cancela a assinatura ATUAL do Gestor.
            const subscriptionId = dadosPerfil?.gestorAsaasSubscriptionId;
            // 🔥 CORRIGIDO: não gravamos mais 'gestorPlan' aqui — o Firestore
            // bloqueia o navegador de mexer nesse campo (de propósito) e
            // essa escrita nunca funcionava de verdade. Agora é a própria
            // Cloud Function `cancelarAssinaturaAsaas` que revoga o acesso
            // no servidor; aqui só atualizamos a TELA na hora, pra pessoa
            // ver o resultado sem esperar a próxima sincronização.
            const rGestor = subscriptionId ? await cancelarAssinaturaAsaas({ subscriptionId }) : null;
            if (rGestor && rGestor.acessoAte) {
               setCancelamentoGestor({ acessoAte: rGestor.acessoAte });
               setToastMessage('Assinatura do Gestor cancelada (acesso até ' + formatarDataBR(rGestor.acessoAte) + '). Abrindo checkout do Consultor...');
            } else {
               setGestorPlan('entry');
               localStorage.setItem('phoenix_gestor_plan', 'entry');
               setToastMessage('Assinatura do Gestor cancelada. Abrindo checkout do Consultor...');
            }
         } else {
            // Vai assinar Gestor — cancela a assinatura ATUAL do Consultor.
            const subscriptionId = dadosPerfil?.consultorAsaasSubscriptionId;
            const rConsultor = subscriptionId ? await cancelarAssinaturaAsaas({ subscriptionId }) : null;
            if (rConsultor && rConsultor.acessoAte) {
               setCancelamentoConsultor({ acessoAte: rConsultor.acessoAte });
               setToastMessage('Assinatura do Consultor cancelada (acesso até ' + formatarDataBR(rConsultor.acessoAte) + '). Abrindo checkout do Gestor...');
            } else {
               setConsultorPlan('free');
               localStorage.setItem('phoenix_consultor_plan', 'free');
               setToastMessage('Assinatura do Consultor cancelada. Abrindo checkout do Gestor...');
            }
         }

         setMostrarDecisaoDuplaAssinatura(false);
         setSelectedPlanDetails({
            id: planoPendenteAposAviso.planId,
            name: planoPendenteAposAviso.planName,
            price: planoPendenteAposAviso.planPrice,
            type: planoPendenteAposAviso.planType
         });
         setShowInAppPurchase(true);
         setPlanoPendenteAposAviso(null);
      } catch (erro) {
         console.error('[PHOENIX] Erro ao cancelar plano atual:', erro);
         setToastMessage('Não foi possível cancelar a assinatura atual agora. Tente novamente.');
      } finally {
         setProcessandoDecisaoDupla(false);
      }
   };

   const fazerLoginModal = async () => {
      if (!loginModalEmail || !loginModalSenha) {
         setLoginModalErro('Preencha e-mail e senha.');
         return;
      }
      setLoginModalCarregando(true);
      setLoginModalErro('');
      try {
         // Troca a sessão anônima pela conta de verdade — perde só o que
         // não tiver sido salvo ainda nessa sessão anônima (normal, já
         // que a conta de verdade tem seu próprio histórico).
         await signInWithEmailAndPassword(auth, loginModalEmail, loginModalSenha);
         setMostrarLoginModal(false);

         // 🔥 CORREÇÃO: qualquer dossiê que estivesse aberto na tela nesse
         // momento pertencia à sessão ANTERIOR (userId diferente do que
         // acabou de logar). Continuar "escutando" esse mesmo documento
         // gera "Missing or insufficient permissions" em cascata (o
         // Firestore nega, porque o dono mudou) e a tela mostra "Empresa
         // Não Informada" (formulário vazio). Mais seguro: sempre volta
         // pra "Meus Dossiês" depois do login, com a lista já atualizada
         // — a pessoa escolhe o que abrir, em vez do app adivinhar.
         setActiveDossierId(null);
         setView('history');

         // 🔥 Se esse login veio de um "esse e-mail já é sua conta" (ver
         // promoverContaAnonima), migra pra cá o que ficou preso na sessão
         // anônima — plano recém-comprado, dossiê criado — antes de avisar
         // sucesso, pra pessoa realmente ver tudo junto.
         if (uidAnonimoParaMigrar) {
            try {
               const resultado = await migrarSessaoAnonima({ uidAntigo: uidAnonimoParaMigrar });
               if (resultado.migrado && resultado.dossiesMigrados > 0) {
                  setToastMessage(`Login realizado! ${resultado.dossiesMigrados} dossiê(s) reunido(s) na sua conta.`);
               } else {
                  setToastMessage('Login realizado com sucesso!');
               }
            } catch (erroMigracao) {
               console.error('[PHOENIX] Erro ao migrar sessão anônima:', erroMigracao);
               setToastMessage('Login realizado com sucesso!');
            } finally {
               setUidAnonimoParaMigrar(null);
            }
         } else {
            setToastMessage('Login realizado com sucesso!');
         }

         // Recarrega a lista de dossiês já com o que foi migrado (se houve).
         recuperarDossiesPerdidos(true);
      } catch (erro) {
         console.error('[PHOENIX] Erro ao fazer login:', erro);
         if (erro.code === 'auth/invalid-credential' || erro.code === 'auth/wrong-password' || erro.code === 'auth/user-not-found') {
            setLoginModalErro('E-mail ou senha incorretos.');
         } else {
            setLoginModalErro('Não foi possível entrar agora. Tente novamente.');
         }
      } finally {
         setLoginModalCarregando(false);
      }
   };

   const [planoPendenteAposAviso, setPlanoPendenteAposAviso] = useState(null);
   // 🔥 Precisa ser marcado manualmente antes de liberar o botão de
   // "cancelar o outro plano" — essa ação bloqueia acesso aos relatórios
   // do perfil cancelado, então exige ciência explícita (não é reversível
   // com um clique — precisa assinar de novo pra desbloquear).
   const [confirmouIrreversibilidade, setConfirmouIrreversibilidade] = useState(false);

   const handleProtocolSelection = (planId, planName, planPrice, planType) => {
      // 🔥 CORREÇÃO: o aviso de assinatura dupla existia, mas só estava
      // conectado num botão específico de outra tela — comprando direto
      // da lista de planos, o checkout abria sem passar pelo aviso. Agora
      // a checagem roda ANTES de abrir o checkout, pra qualquer caminho.
      const ehPlanoConsultor = ['advisor', 'boutique', 'enterprise'].includes(planId) || planType === 'CONSULTOR';
      const ehPlanoGestorPago = ['tactical', 'plus', 'total'].includes(planId);

      const jaTemGestorPago = gestorPlan && gestorPlan !== 'entry';
      const jaTemConsultorPago = consultorPlan && consultorPlan !== 'free';

      if (ehPlanoConsultor && jaTemGestorPago && !jaTemConsultorPago) {
         setPlanoPendenteAposAviso({ planId, planName, planPrice, planType });
         setMostrarDecisaoDuplaAssinatura(true);
         return;
      }
      if (ehPlanoGestorPago && jaTemConsultorPago && !jaTemGestorPago) {
         setPlanoPendenteAposAviso({ planId, planName, planPrice, planType });
         setMostrarDecisaoDuplaAssinatura(true);
         return;
      }

      setSelectedPlanDetails({ id: planId, name: planName, price: planPrice, type: planType });
      setShowInAppPurchase(true);
   };

   // =======================================================
   // FLUXO DE LIBERAÇÃO DO GESTOR E DOSSIÊ - PADRÃO ENTERPRISE
   // =======================================================

   const unlockDossier = async (overridePlan, overrideLevel) => {
      // 🔥 CORRIGIDO — VULNERABILIDADE CRÍTICA (achada em teste real com
      // Asaas de produção, 2026-09-28): esta função confiava cegamente em
      // 'activePlan' (só uma cópia LOCAL de 'gestorPlan'/'consultorPlan',
      // feita em startNewDiagnosis) pra decidir se mostrava "Acesso
      // Liberado" e abria o relatório de graça. Como isso é estado do
      // React em memória, bastava ficar desatualizado — por exemplo,
      // sobrando de um teste anterior na mesma sessão do app sem
      // reiniciar — pra abrir o Phoenix Entry (R$49,90) sem pagar nada.
      // Agora, antes de liberar QUALQUER coisa, busca o documento de
      // verdade no Firestore (onde só o webhookAsaas grava o plano
      // depois de pagamento confirmado) e usa ESSE valor — nunca o que
      // já estava em memória.
      if (!user?.uid) return;
      let planoRealGestor = null;
      let planoRealConsultor = null;
      try {
         const perfilSnap = await getDoc(doc(db, 'usuarios', user.uid));
         if (perfilSnap.exists()) {
            const dados = perfilSnap.data();
            planoRealGestor = dados.plan || dados.gestorPlan || null;
            planoRealConsultor = dados.consultorPlan || null;
         }
      } catch (e) {
         console.error('[PHOENIX] Erro ao reverificar plano antes de liberar dossiê:', e);
      }

      // 1. Tratamento isolado B2B
      if (formData.userType === 'Consultor') {
         if (!(planoRealConsultor && planoRealConsultor !== 'free')) {
            setToastMessage('Assine um plano Consultor pra acessar este dossiê.');
            setView('consultor_showcase');
            return;
         }
         setUserLevel(1);
         setReportTab('visao'); // 🔥 mesma correção do lado Gestor: sempre abre na aba principal (rótulo "Relatório" na tela)
         dispararGeracaoIA(activeDossierId, 'Consultor', planoRealConsultor);
         setView('report');
         return;
      }

      // 2. Proteção contra Stale Closures
      const currentPlan = overridePlan || activePlan;
      // 🔒 Dossiê Entry (avulso) só libera se a conta tiver plano Gestor
      // pago DE VERDADE agora (recém-checado no servidor) — nunca confia
      // só em 'activePlan'/'gestorPlan' de memória pra decidir isso.
      if (currentPlan === 'entry' && !(planoRealGestor && planoRealGestor !== 'entry')) {
         setToastMessage('Este dossiê ainda não teve o pagamento confirmado. Finalize o pagamento pra desbloquear.');
         return;
      }
      const level = overrideLevel || userLevel;

      setUserLevel(level);

      // 3. Criação ou Atualização Atômica do Dossiê Ativo
      // 🔥 CORREÇÃO: mesma causa raiz do lado Consultor — a escuta em tempo
      // real (setActiveDossierId) ligava ANTES do documento existir de
      // verdade no Firestore, causando "permissão negada" e matando a
      // escuta pro resto da sessão. Agora espera sincronizarDossieUnico
      // terminar antes de ligar a escuta.
      if (!activeDossierId) {
         const newId = gerarIdUnico();
         const novoItem = {
            id: newId,
            name: formData.companyName || 'Nova Análise',
            date: new Date().toLocaleDateString('pt-BR'),
            plan: (currentPlan === 'entry') ? 'ENTRY' : String(formData.userType === 'Consultor' ? (consultorPlan || activePlan) : (gestorPlan || activePlan) || '').toUpperCase(),
            snapshot: {
               formData,
               simRevenue,
               simCosts,
               userType: formData.userType || 'Gestor'
            }
         };
         setHistoryList(prev => [novoItem, ...prev]);
         await sincronizarDossieUnico(novoItem);
         setActiveDossierId(newId);
         dispararGeracaoIA(newId, formData.userType, currentPlan === 'entry' ? 'entry' : (gestorPlan || activePlan));
      } else {
         let itemAtualizado = null;
         setHistoryList(prev => prev.map(item => {
            if (item.id !== activeDossierId) return item;
            itemAtualizado = {
               ...item,
               name: formData.companyName || item.name,
               plan: (currentPlan === 'entry') ? 'ENTRY' : String(formData.userType === 'Consultor' ? (consultorPlan || activePlan) : (gestorPlan || activePlan) || '').toUpperCase(),
               snapshot: {
                  ...item.snapshot,
                  formData,
                  simRevenue,
                  simCosts,
                  userType: formData.userType || 'Gestor'
               }
            };
            return itemAtualizado;
         }));
         if (itemAtualizado) await sincronizarDossieUnico(itemAtualizado);
         dispararGeracaoIA(activeDossierId, formData.userType, currentPlan === 'entry' ? 'entry' : (gestorPlan || activePlan));
      }

      setView('report');
      setReportTab('visao'); // 🔥 evita abrir numa aba que não existe pro novo dossiê (ex: 'carteira' do Consultor)
   };

   const handleConfirmPurchase = async () => {
      if (!selectedPlanDetails) return;
      if (!aceiteCheckout) { setToastMessage('Marque a caixa de aceite para continuar.'); return; }
      registrarAceite('checkout', selectedPlanDetails.id);

      // 🔥 CORREÇÃO: antes, sem estar logado, o botão não fazia nada —
      // sem erro, sem aviso, só ficava mudo. Isso é esperado agora: o
      // Asaas precisa saber QUEM está comprando (pra Cloud Function
      // autenticar a chamada), então login vira pré-requisito real pra
      // pagar de verdade — mas precisa avisar, não travar em silêncio.
      if (!user) {
         setToastMessage('Crie uma conta ou faça login antes de finalizar a compra.');
         setShowInAppPurchase(false);
         setView('landing');
         return;
      }

      // Canal de pagamento: no app Android com o Billing ligado, a compra é pelo Google Play (sem e-mail/CPF/forma de pagamento
      // do Asaas: o Google cuida disso). Em qualquer outro lugar, segue o Asaas como sempre.
      const usandoGoogle = canalDePagamento() === 'google';

      // 🔥 E-MAIL: como agora a pessoa entra numa sessão anônima (sem
      // e-mail nenhum vinculado), precisa coletar aqui dentro do próprio
      // checkout — sem isso o Asaas nem cria o cliente. Confere o
      // formato de verdade (não só se tem "@" em algum lugar), pra pegar
      // erro de digitação na hora, sem esperar o Asaas recusar.
      if (!usandoGoogle) {
      const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailPagamento.trim());
      if (!emailValido) {
         setToastMessage('Informe um e-mail válido (ex: nome@exemplo.com) pra continuar.');
         return;
      }

      // 🔥 CPF/CNPJ é exigido pelo Asaas pra criar o cliente/cobrança —
      // confere o dígito verificador de verdade, não só a quantidade de
      // números, pra pegar erro de digitação na hora, sem esperar o
      // Asaas recusar.
      if (!cpfCnpjValido(cpfCnpjPagamento)) {
         setToastMessage('CPF ou CNPJ inválido — confira os números digitados.');
         return;
      }
      }
      const documentoLimpo = usandoGoogle ? '' : cpfCnpjPagamento.replace(/\D/g, '');

      const planId = selectedPlanDetails.id;
      const valorNumerico = parseFloat(String(selectedPlanDetails.price).replace(/\./g, '').replace(',', '.'));
      const ehAvulso = planId === 'report_avulso' || planId === 'entry' || selectedPlanDetails.type === 'PAGAMENTO ÚNICO';

      // 🔒 CORREÇÃO: quando tiramos a liberação instantânea (insegura) do
      // navegador, isso quebrou sem querer o fluxo de PAGAMENTO ÚNICO
      // (Entry / Relatório Avulso) de duas formas:
      // 1) Os dados digitados nunca chegavam a ser salvos em lugar nenhum
      //    — ficavam só na tela, e se a pessoa saísse, perdia tudo.
      // 2) Não existia mais NENHUM mecanismo pra "destravar" aquele
      //    relatório específico depois do pagamento — o webhook só sabia
      //    mexer no plano da conta (usuarios/{uid}), nunca num dossiê
      //    específico.
      // Agora, ANTES de abrir a cobrança, já salvamos o dossiê no
      // Firestore com status "aguardando_pagamento" — os dados nunca mais
      // se perdem, pagando ou não. O webhook (rodando no servidor) marca
      // esse MESMO dossiê como "pago" assim que o Asaas confirmar, e o
      // app fica de olho nele em tempo real pra reagir sozinho.
      let dossieIdAvulso = null;
      if (ehAvulso && user) {
         try {
            dossieIdAvulso = activeDossierId ? String(activeDossierId) : gerarIdUnico();
            await setDoc(doc(db, 'cenarios', dossieIdAvulso), sanitizeForFirestore({
               ...formData,
               userId: user.uid,
               // 🔥 CORRIGIDO (2026-09-29): 'user.email' é sempre vazio numa
               // sessão anônima — o e-mail de verdade é o que a pessoa
               // digitou AGORA no checkout (emailPagamento). Sem salvar isso
               // em algum lugar persistente, o convite pra criar conta não
               // tinha como saber (com segurança) qual e-mail sugerir depois
               // de o app reiniciar — precisa ser o MESMO e-mail usado na
               // cobrança do Asaas, senão a conta some.
               userEmail: user.email || emailPagamento || null,
               emailComprador: emailPagamento || null,
               dataCriacao: new Date().toISOString(),
               plan: String(planId).toUpperCase(),
               statusPagamento: 'aguardando_pagamento'
            }), { merge: true });
            if (!activeDossierId) setActiveDossierId(dossieIdAvulso);

            // 🔥 NOVO (2026-09-29): antes, o dossiê Entry só entrava na lista
            // "Meus Dossiês" depois do pagamento confirmado — enquanto isso,
            // a pessoa que fechava a página de cobrança não via o dossiê em
            // lugar nenhum (parecia ter sumido). Agora aparece na hora,
            // marcado como Entry pendente; a tela de "Meus Dossiês" (pra
            // onde o app leva logo abaixo) já mostra ele, e clicar nele
            // manda pra tela de "Aguardando Pagamento" com um botão pra
            // retomar o pagamento do MESMO dossiê (não cria um novo).
            if (planId === 'entry') {
               setHistoryList(prev => {
                  const itemPendente = {
                     id: dossieIdAvulso,
                     name: formData.companyName || 'Nova Análise',
                     date: new Date().toLocaleDateString('pt-BR'),
                     plan: 'ENTRY',
                     emailComprador: emailPagamento || null,
                     snapshot: { formData, userType: formData.userType || 'Gestor' }
                  };
                  return prev.some(i => i.id === dossieIdAvulso)
                     ? prev.map(i => i.id === dossieIdAvulso ? itemPendente : i)
                     : [itemPendente, ...prev];
               });
            }
         } catch (e) {
            console.error('[PHOENIX] Erro ao salvar dossiê antes do pagamento avulso:', e);
            setToastMessage('Não foi possível salvar seus dados agora. Tente novamente.');
            return; // não abre cobrança se nem os dados salvaram
         }
      }

      // ===== GOOGLE PLAY BILLING (app Android): compra pelo Google, confirmação pelo servidor =====
      if (usandoGoogle) {
         setProcessandoPagamento(true);
         try {
            const r = await comprarPeloGoogle({ planId, uid: user.uid, dossieId: dossieIdAvulso });
            if (r.cancelado) { setToastMessage('Compra cancelada.'); return; }
            setShowInAppPurchase(false);
            if (!ehAvulso) {
               setConfirmacaoPagamento('Assinatura confirmada! Seu plano já está liberado.');
               return;
            }
            if (planId === 'report_avulso') {
               setConfirmacaoPagamento('Pagamento confirmado! Gerando seu Super Relatório 360º...');
               handleExportPDF(true);
               return;
            }
            // Entry: o servidor já marcou o dossiê como pago; segue o mesmo caminho do fluxo do Asaas.
            const snapPago = await getDoc(doc(db, 'cenarios', dossieIdAvulso));
            const dados = snapPago.exists() ? snapPago.data() : {};
            setConfirmacaoPagamento('Pagamento realizado com sucesso! Seu dossiê está liberado.');
            setDestinoAposConfirmacao('history');
            setView('history');
            setHistoryList(prev => {
               if (prev.some(item => item.id === dossieIdAvulso)) return prev;
               return [{
                  id: dossieIdAvulso,
                  name: dados.companyName || 'Nova Análise',
                  date: new Date().toLocaleDateString('pt-BR'),
                  plan: 'ENTRY',
                  emailComprador: dados.emailComprador || null,
                  snapshot: { formData: dados, userType: dados.userType || 'Gestor' }
               }, ...prev];
            });
            gerarParecerComIA(dossieIdAvulso, dados.userType || 'Gestor', dados).catch(e =>
               console.error('[PHOENIX] Erro ao gerar parecer do avulso após pagamento (Google):', e)
            );
            if (user.isAnonymous) {
               setEmailConviteConta(emailPagamento || '');
               setMostrarConviteConta(true);
            }
         } catch (erroGoogle) {
            setToastMessage((erroGoogle && erroGoogle.message) || 'Não foi possível concluir a compra agora.');
         } finally {
            setProcessandoPagamento(false);
         }
         return;
      }

      // 🔥 CORREÇÃO DEFINITIVA (dentro do app nativo): a técnica de abrir
      // uma aba em branco no clique e "apontar" ela depois só funciona em
      // navegador de verdade — dentro do Capacitor ela fica presa
      // mostrando "Gerando sua cobrança..." pra sempre, mesmo depois do
      // pagamento confirmado, porque essa aba nunca existiu de verdade
      // pro sistema. Por isso, dentro do app, pulamos a aba em branco por
      // completo e abrimos o navegador nativo assim que o link estiver
      // pronto (mais abaixo).
      const ehAppNativo = Capacitor.isNativePlatform();
      let abaFatura = null;
      if (!ehAppNativo) {
         // 🔥 CORREÇÃO DE POP-UP BLOQUEADO (só se aplica no navegador):
         // abrir a aba SÓ depois de esperar a resposta do Asaas (com um
         // "await" no meio) faz o navegador achar que não foi um clique
         // direto da pessoa, e bloqueia a aba silenciosamente — só um
         // ícone discreto avisa, fácil de não notar. Abrindo a aba em
         // branco JÁ AQUI (ainda dentro do clique, sem nenhum await
         // antes), o navegador confia que foi gesto do usuário e libera —
         // só "apontamos" ela pro link certo depois que a resposta chegar.
         abaFatura = window.open('', '_blank');
         if (abaFatura) {
            abaFatura.document.write('<p style="font-family:sans-serif;padding:40px;color:#666">Gerando sua cobrança, aguarde...</p>');
         }
      }

      setProcessandoPagamento(true);
      try {
         // 1. Garante/cria o cliente no Asaas (reaproveita se já existir)
         const { customerId } = await criarClienteAsaas({
            nome: user.displayName || emailPagamento,
            email: emailPagamento,
            cpfCnpj: documentoLimpo
         });

         // 2. Gera a cobrança certa: avulsa (Entry / relatório único / Super
         //    360 avulso) ou assinatura recorrente (todos os outros planos)
         const resultado = ehAvulso
            ? await criarCobrancaAvulsaAsaas({
               customerId,
               valor: valorNumerico,
               descricao: `Phoenix OS — ${selectedPlanDetails.name}`,
               formaPagamento: formaPagamentoEscolhida,
               planId,
               dossieId: dossieIdAvulso
            })
            : await criarAssinaturaAsaas({
               customerId,
               valor: valorNumerico,
               descricao: `Phoenix OS — Assinatura ${selectedPlanDetails.name}`,
               formaPagamento: formaPagamentoEscolhida,
               planId
            });

         // 🔥 Salva o ID da assinatura — sem isso não dá pra cancelar
         // depois (ex: quando a pessoa decide manter só um dos dois
         // perfis, no card de decisão de dupla assinatura).
         if (!ehAvulso && resultado.subscriptionId && user?.uid) {
            const isConsultorPlanAgora = planId === 'advisor' || planId === 'boutique' || planId === 'enterprise' || selectedPlanDetails.type === 'CONSULTOR';
            const campoId = isConsultorPlanAgora ? 'consultorAsaasSubscriptionId' : 'gestorAsaasSubscriptionId';
            await setDoc(doc(db, 'usuarios', user.uid), { [campoId]: resultado.subscriptionId }, { merge: true });
         }

         // 3. Abre a cobrança: dentro do app nativo, no navegador de
         //    verdade do sistema (Capacitor Browser); num navegador comum,
         //    aponta a aba já aberta desde o clique. Nos dois casos é o
         //    Asaas quem hospeda a página de pagamento (PIX ou cartão) —
         //    o Phoenix OS nunca vê o número do cartão, só o Asaas.
         if (resultado.linkFatura) {
            if (ehAppNativo) {
               await Browser.open({ url: resultado.linkFatura });
            } else if (abaFatura) {
               abaFatura.location.href = resultado.linkFatura;
            } else {
               // Fallback: se por algum motivo a aba não abriu no início
               // (bloqueio muito agressivo de algum navegador), tenta de
               // novo aqui — pode falhar, mas é melhor que nada.
               window.open(resultado.linkFatura, '_blank');
            }
         }

         // 🔒 SEGURANÇA: aqui o plano era liberado NA HORA, sem esperar o
         // pagamento. Agora quem libera é só o webhookAsaas, depois da
         // confirmação real. O app fica escutando 'usuarios/{uid}' e a tela
         // se atualiza sozinha assim que o pagamento cair.
         setShowInAppPurchase(false);
         setConfirmacaoPagamento('Cobrança gerada! Assim que o pagamento for confirmado, seu plano libera automaticamente aqui — pode deixar essa tela aberta.');

         // 🔥 NOVO (2026-09-29): pra Entry avulso, manda pra "Meus Dossiês"
         // em vez de deixar a pessoa na tela de "Análise Pronta" (que ainda
         // mostra o botão de pagar de novo, dando a impressão de que nada
         // aconteceu). Em "Meus Dossiês" o dossiê já aparece marcado como
         // pendente (ver acima), e clicar nele leva à tela de "Aguardando
         // Pagamento" — que tem, ela mesma, um jeito de retomar o pagamento
         // se a pessoa fechou a página do Asaas sem querer.
         if (planId === 'entry') {
            setView('history');
         }

         // 🔒 Escuta ESSE dossiê específico em tempo real — assim que o
         // webhookAsaas confirmar o pagamento e marcar statusPagamento como
         // 'pago', a tela reage sozinha (mostra sucesso, gera o parecer,
         // some da lista de "aguardando"), sem precisar recarregar nada.
         if (dossieIdAvulso) {
            const unsubscribePagamento = onSnapshot(doc(db, 'cenarios', dossieIdAvulso), (snap) => {
               if (!snap.exists()) return;
               const dados = snap.data();
               if (dados.statusPagamento === 'pago') {
                  unsubscribePagamento();
                  if (planId === 'report_avulso') {
                     // Relatório Avulso é o PDF premium de um dossiê que já
                     // existe (não um diagnóstico novo) — a ação certa aqui
                     // é baixar o PDF, não gerar parecer de novo.
                     setConfirmacaoPagamento('Pagamento confirmado! Gerando seu Super Relatório 360º...');
                     handleExportPDF(true);
                  } else {
                     // 🔥 CORRIGIDO: faltava justamente isso — sem adicionar
                     // na lista LOCAL (historyList), o dossiê só aparecia em
                     // "Meus Dossiês" depois de algo forçar uma releitura do
                     // zero (ex: abrir outro checkout e cancelar). Agora
                     // aparece na hora, sem precisar de nenhum truque.
                     setShowInAppPurchase(false); // fecha o checkout que ficou aberto embaixo
                     setConfirmacaoPagamento('Pagamento realizado com sucesso! Seu dossiê está liberado.');
                     setDestinoAposConfirmacao('history');
                     setHistoryList(prev => {
                        if (prev.some(item => item.id === dossieIdAvulso)) return prev; // já está lá, não duplica
                        return [{
                           id: dossieIdAvulso,
                           name: dados.companyName || 'Nova Análise',
                           date: new Date().toLocaleDateString('pt-BR'),
                           plan: 'ENTRY',
                           emailComprador: dados.emailComprador || null,
                           snapshot: { formData: dados, userType: dados.userType || 'Gestor' }
                        }, ...prev];
                     });
                     gerarParecerComIA(dossieIdAvulso, dados.userType || 'Gestor', dados).catch(e =>
                        console.error('[PHOENIX] Erro ao gerar parecer do avulso após pagamento:', e)
                     );
                  }

                  // 🔥 CORRIGIDO — o convite pra virar conta de verdade dizia
                  // "Depois do pagamento confirmado" no comentário, mas na
                  // prática disparava ASSIM QUE a cobrança era gerada — antes
                  // de qualquer pagamento acontecer. Resultado: gente que
                  // desistia, tinha o cartão recusado ou deixava o PIX
                  // expirar acabava com uma conta de e-mail/senha criada à
                  // toa (e ainda achava, ao fechar a cobrança, que já tinha
                  // "liberado" o relatório). Agora só aparece aqui dentro,
                  // depois que 'statusPagamento' virou 'pago' de verdade.
                  if (user.isAnonymous) {
                     setEmailConviteConta(emailPagamento);
                     setMostrarConviteConta(true);
                  }
               }
            }, (e) => console.error('[PHOENIX] Erro ao escutar confirmação do pagamento avulso:', e));
         }
      } catch (erro) {
         console.error('[PHOENIX] Erro ao processar pagamento no Asaas:', erro);
         setToastMessage('Não foi possível processar o pagamento agora. Tente novamente em instantes.');
         // Fecha a aba em branco que abrimos no início — senão fica
         // parada pra sempre com a mensagem de "Gerando sua cobrança...".
         if (abaFatura && !abaFatura.closed) abaFatura.close();
      } finally {
         setProcessandoPagamento(false);
      }
   };

   // 🔥 REMOVIDO (limpeza pré-lançamento): existia aqui uma função
   // chamada 'finalizarLiberacaoDoPlano' (233 linhas) que ficou morta
   // desde a correção de segurança do checkout — ninguém mais chama ela.
   // Ela fazia a liberação INSTANTÂNEA do plano pelo navegador, que era
   // justamente a brecha que fechamos (agora só o webhookAsaas libera,
   // depois do pagamento confirmado de verdade). Removida de propósito
   // pra ninguém religar sem querer no futuro.


   // Sempre que o dossiê ativo mudar (abriu um relatório salvo, ou trocou
   // de dossiê), passa a ESCUTAR (não só ler uma vez) o checklist E o
   // parecer da IA direto no Firestore. Assim, qualquer marcação feita no
   // Dashboard Gestor aparece no app na hora, e o parecer da IA aparece
   // sozinho assim que a Cloud Function termina de gerar (sem precisar
   // recarregar a página).
   const [parecerIATexto, setParecerIATexto] = useState(null);
   // 🔥 Leitura da PROJEÇÃO (futuro), agora em 3 BLOCOS separados por prazo
   // — Curto (3-6m, Tactical), Médio (12m, Plus), Longo (24-36m, Total).
   // Cada um só existe no Firestore se o plano correspondente já foi
   // usado pra gerar (a IA nunca escreve sobre um prazo não liberado).
   const [leituraProjecaoCurto, setLeituraProjecaoCurto] = useState(null);
   const [leituraProjecaoMedio, setLeituraProjecaoMedio] = useState(null);
   const [leituraProjecaoLongo, setLeituraProjecaoLongo] = useState(null);
   // 🔥 Seção premium, só pro plano mais caro (Total no Gestor / Enterprise
   // Ultra no Consultor) — oportunidades estratégicas concretas, não só
   // diagnóstico/projeção.
   const [oportunidadesTexto, setOportunidadesTexto] = useState(null);
   // 🔥 Evita o "pisca" do texto fixo aparecendo por uma fração de segundo
   // antes do Firestore confirmar se já existe parecer de IA salvo. Enquanto
   // essa primeira resposta não chega, mostra um estado de carregamento em
   // vez de decidir errado (mostrar fixo) e trocar de conteúdo na tela.
   const [carregandoParecer, setCarregandoParecer] = useState(true);
   // 🔥 SEPARADO do de cima: cobre o tempo de VERDADE que a IA leva pra
   // gerar (vários segundos) — não só o tempo de buscar o documento no
   // Firestore (rápido). Fica "true" desde o momento que disparamos a
   // chamada de IA até o texto realmente chegar (via onSnapshot) ou até
   // um tempo limite de segurança (nunca trava esperando pra sempre).
   const [iaGerando, setIaGerando] = useState(false);
   // Quantas gerações de IA estão em andamento agora. O snapshot do Firestore trazia o texto ANTIGO e
   // desligava o aviso "gerando" cedo demais; agora só desliga quando nenhuma geração está rodando.
   const geracoesIAEmAndamentoRef = useRef(0);
   // Tela atual, lida de dentro de callbacks assíncronos (o autosave) sem ficar com valor velho.
   const viewAtualRef = useRef('splash');
   // Evita disparar duas chamadas de "completar bloco" ao mesmo tempo se
   // o onSnapshot disparar de novo enquanto a primeira ainda está rodando.
   const completandoBlocosRef = useRef(false);
   // 🔥 CORREÇÃO CRÍTICA: sem isso, se um bloco voltar vazio da IA por
   // qualquer motivo (formato inesperado, falha pontual), o código achava
   // que "ainda falta gerar" e tentava de novo pra sempre — loop infinito
   // gastando IA sem parar. Agora cada combinação dossiê+bloco só é
   // tentada automaticamente UMA VEZ por sessão (F5 reseta o controle).
   const blocosJaTentadosRef = useRef(new Set());

   // 🔥 COMPLEMENTA blocos que faltam quando o plano é atualizado (upgrade)
   // — sem regerar parecer nem os blocos que já existem. Dispara sozinho
   // ao abrir um dossiê, se detectar que o plano atual libera um prazo
   // cujo bloco ainda não foi gerado.
   const completarBlocosFaltantes = async (dossieId, tipoUsuario, formDataAlvo, blocosFaltantes, inputSnapshotExistente) => {
      if (!blocosFaltantes.length) return;
      if (String(dossieId) === String(activeDossierId) && entryPendenteRef.current) return; // Entry não pago: sem IA
      const rev = parseCurrencyToNumber(formDataAlvo.revenue);
      const cos = parseCurrencyToNumber(formDataAlvo.costs);
      const deb = parseCurrencyToNumber(formDataAlvo.debt);
      const core = calculateCoreMetrics(rev, cos, deb, formDataAlvo.segment, {
         custoNaoRecorrente: parseCurrencyToNumber(formDataAlvo.custoNaoRecorrente),
         custoDivida: formDataAlvo.custoDivida
      });
      const health = calculateHealthStatus({ ebitda: core.ebitdaAjustado, alavancagem: core.alavancagem, deb, rev, sector: formDataAlvo.segment });
      const extended = calculateExtendedMetrics({
 sector: formDataAlvo.segment,
         rev, cos, ebitda: core.ebitdaAjustado, alavancagem: core.alavancagem, currentMargin: core.currentMargin,
         prazoRecebimento: formDataAlvo.prazoRecebimento, prazoPagamento: formDataAlvo.prazoPagamento,
         dividaCurtoPrazo: parseCurrencyToNumber(formDataAlvo.dividaCurtoPrazo), proLabore: parseCurrencyToNumber(formDataAlvo.proLabore)
      });
      const projecao = calculateStochasticProjection({ ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado, currentMargin: core.currentMargin }, rev);
      const metricas = {
         receita: rev, custos: cos, divida: deb,
         ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado,
         currentMargin: core.currentMargin, alavancagem: core.alavancagem,
         coberturaJuros: core.coberturaJuros, ruptureRisk: core.ruptureRisk,
         statusSaude: health.label,
         ncg: extended.ncg, reservaEmergencia: extended.emergencyReserve,
         dividendosSeguros: extended.safeDividends, scoreMA: extended.maScore,
         coberturaCurtoPrazo: extended.coberturaCurtoPrazo, caixaMinimo: extended.caixaMinimo
      };

      setIaGerando(true);
      geracoesIAEmAndamentoRef.current += 1;
      const tempoLimiteId = setTimeout(() => { geracoesIAEmAndamentoRef.current = 0; setIaGerando(false); }, 90000);
      try {
         await gerarParecerIA({
            dossieId, tipoUsuario,
            nicho: ((formDataAlvo.niche && formDataAlvo.niche !== '-') ? `${formDataAlvo.niche} (setor: ${formDataAlvo.segment || 'não informado'})` : (formDataAlvo.segment || 'negócio geral')),
            metricas, projecao,
            // Mantém a MESMA impressão digital já salva — isso não é uma
            // mudança de dado financeiro, é só um bloco novo liberado
            // pelo plano. Sem isso, o próximo salvamento acharia que os
            // dados "mudaram" (snapshot ficaria null) e chamaria a IA de
            // novo à toa.
            inputSnapshot: inputSnapshotExistente || null,
            blocosProjecao: blocosFaltantes,
            apenasComplementar: true
         });
      } catch (e) {
         console.error('[PHOENIX] Erro ao completar blocos de projeção após upgrade:', e);
      } finally {
         clearTimeout(tempoLimiteId);
         geracoesIAEmAndamentoRef.current = Math.max(0, geracoesIAEmAndamentoRef.current - 1);
         if (geracoesIAEmAndamentoRef.current === 0) setIaGerando(false);
      }
   };
   const ultimoDocPareceRef = useRef(null);
   // Completa só os blocos de projeção que o plano atual libera e ainda não existem (ex.: acabou de virar Plus).
   const completarSeFaltarBloco = (dados) => {
      if (dados.parecerIA && !completandoBlocosRef.current) {
       const tipoUsuarioDoc = dados.userType || dados.snapshot?.userType || 'Gestor';
       const planoAtual = tipoUsuarioDoc === 'Consultor' ? consultorPlan : gestorPlan;
       const horizontes = getHorizontesLiberados(tipoUsuarioDoc, planoAtual);
       const blocosLiberados = [];
       if (horizontes.includes(3) || horizontes.includes(6)) blocosLiberados.push('curto');
       if (horizontes.includes(12)) blocosLiberados.push('medio');
       if (horizontes.includes(24) || horizontes.includes(36)) blocosLiberados.push('longo');
       const camposPorBloco = { curto: 'leituraProjecaoCurto', medio: 'leituraProjecaoMedio', longo: 'leituraProjecaoLongo' };
       const blocosFaltantes = blocosLiberados
          .filter(b => !dados[camposPorBloco[b]])
          .filter(b => !blocosJaTentadosRef.current.has(`${activeDossierId}:${b}`)); // já tentou uma vez, não tenta de novo sozinho

       if (blocosFaltantes.length > 0) {
          completandoBlocosRef.current = true;
          blocosFaltantes.forEach(b => blocosJaTentadosRef.current.add(`${activeDossierId}:${b}`));
          const origem = dados.snapshot?.formData || dados;
          completarBlocosFaltantes(activeDossierId, tipoUsuarioDoc, origem, blocosFaltantes, dados.parecerIAInputSnapshot)
             .finally(() => { completandoBlocosRef.current = false; });
       }
    }
   };
   // Entrou no relatório: confere o último documento recebido (que pode ter chegado durante a edição).
   useEffect(() => {
      if (view === 'report' && ultimoDocPareceRef.current && activeDossierId) completarSeFaltarBloco(ultimoDocPareceRef.current);
   }, [view]);

   useEffect(() => {
      if (!activeDossierId) {
         setDueDiligenceChecklist({ auditoria: false, acordoSocios: false });
         setParecerIATexto(null);
         setLeituraProjecaoCurto(null);
         setLeituraProjecaoMedio(null);
         setLeituraProjecaoLongo(null);
         setOportunidadesTexto(null);
         setCarregandoParecer(true);
         return;
      }
      ultimoDocPareceRef.current = null;
      setCarregandoParecer(true);
      const unsubscribe = onSnapshot(doc(db, 'cenarios', String(activeDossierId)), (snap) => {
         if (snap.exists()) {
            const dados = snap.data();
            setDueDiligenceChecklist(dados.dueDiligenceChecklist || { auditoria: false, acordoSocios: false });
            setParecerIATexto(dados.parecerIA || null);
            setLeituraProjecaoCurto(dados.leituraProjecaoCurto || null);
            setLeituraProjecaoMedio(dados.leituraProjecaoMedio || null);
            setLeituraProjecaoLongo(dados.leituraProjecaoLongo || null);
            setOportunidadesTexto(dados.oportunidadesIA || null);
            // Assim que o parecer chega (qualquer valor não-vazio), a
            // geração terminou — desliga o loading de geração.
            if (dados.parecerIA && geracoesIAEmAndamentoRef.current === 0) setIaGerando(false);

            // 🔥 UPGRADE AUTOMÁTICO: se o plano atual libera um prazo cujo
            // bloco ainda não existe no Firestore (ex: acabou de virar
            // Plus e o bloco "médio" nunca foi gerado), completa só esse
            // bloco sozinho — sem regerar parecer nem os blocos que já
            // existem. Só faz isso se já existe um parecer gerado (evita
            // completar um dossiê que nunca rodou a IA de verdade ainda).
            // Guarda o último estado do documento. O preenchimento de blocos faltantes (upgrade de plano) só
            // roda com a pessoa VENDO o relatório — antes disparava a IA a cada gravação do salvamento
            // automático, inclusive enquanto ela ainda digitava no formulário.
            ultimoDocPareceRef.current = dados;
            if (viewAtualRef.current === 'report') completarSeFaltarBloco(dados);
         }
         setCarregandoParecer(false);
      }, (e) => {
         console.error('[PHOENIX] Erro ao escutar checklist de Due Diligence:', e);
         setCarregandoParecer(false);
      });
      return () => unsubscribe();
   }, [activeDossierId, gestorPlan, consultorPlan]);

   const toggleChecklistItem = async (chave) => {
      if (!user) return;
      const atualizado = { ...dueDiligenceChecklist, [chave]: !dueDiligenceChecklist[chave] };
      setDueDiligenceChecklist(atualizado); // atualiza a tela na hora (otimista)

      try {
         let idAlvo = activeDossierId;

         // 🔥 CORREÇÃO: antes, se o diagnóstico ainda não tivesse sido salvo
         // (usuário abriu a aba Boardroom direto, sem clicar em "Salvar
         // Cenário Preditivo"), o clique no checklist não fazia nada — ficava
         // esperando um ID de dossiê que não existia. Agora, se não existir
         // ainda, salvamos o cenário nesse momento (mesmo formato que o botão
         // de salvar usa) só pra garantir que exista um documento no
         // Firestore capaz de guardar essa marcação.
         if (!idAlvo) {
            idAlvo = gerarIdUnico();
            await setDoc(doc(db, "cenarios", idAlvo), sanitizeForFirestore({
               ...formData,
               userType: formData.userType || 'Gestor',
               userId: user.uid,
               userEmail: user.email,
               dataCriacao: new Date().toISOString(),
               status: 'processado',
               plan: activePlan === 'entry' ? 'ENTRY' : String(formData.userType === 'Consultor' ? (consultorPlan || activePlan) : (gestorPlan || activePlan) || '').toUpperCase()
            }), { merge: true });
            setActiveDossierId(idAlvo);
         }

         await setDoc(doc(db, 'cenarios', String(idAlvo)), { dueDiligenceChecklist: atualizado }, { merge: true });
      } catch (e) {
         console.error('[PHOENIX] Erro ao salvar checklist de Due Diligence:', e);
      }
   };

   const saveScenario = async () => {
      if (!user) {
         console.error('[PHOENIX] Salvar cenário falhou: usuário não autenticado.');
         if (typeof onLogout === 'function') onLogout();
         return;
      }
      try {
         setSaveBtnText('Salvando na Nuvem...');
         // 🔥 ID FIXO: mesmo princípio do onSyncDashboard, evita duplicar documento
         const dossieId = activeDossierId ? String(activeDossierId) : gerarIdUnico();

         // 🔥 CORREÇÃO CRÍTICA: essa função espalha "...formData" no documento,
         // o que inclui o campo userType de dentro do formData. Se por
         // qualquer motivo (troca rápida de perfil, corrida entre efeitos)
         // activeDossierId ainda apontasse pro dossiê ANTIGO enquanto
         // formData.userType já tivesse virado outro perfil, essa gravação
         // sobrescrevia o tipo do dossiê errado — exatamente o bug relatado
         // de dossiê Gestor "virando" Consultor sozinho. Agora, ao EDITAR
         // um dossiê que já existe, o userType gravado é sempre o que já
         // estava salvo nele (histórico local), nunca o que estiver no
         // formData no instante do autosave — só um dossiê NOVO (sem
         // activeDossierId ainda) usa o userType do formData.
         const itemExistente = activeDossierId ? historyList.find(i => i.id === activeDossierId) : null;
         const userTypeSeguro = itemExistente?.snapshot?.userType || formData.userType || 'Gestor';

         // 🔒 'plan' e 'statusPagamento' só quem escreve é o servidor (webhook/Cloud Function): as regras do
         // Firestore NEGAM qualquer update que mude esses campos. Se o formData carregasse algum deles de um
         // dossiê anterior, o salvamento inteiro falhava com "permission-denied". Nunca enviamos daqui.
         const { plan: _planIgnorado, statusPagamento: _statusIgnorado, ...formDataSeguro } = formData;
         await setDoc(doc(db, "cenarios", dossieId), sanitizeForFirestore({
            ...formDataSeguro,
            userType: userTypeSeguro,
            userId: user.uid,
            userEmail: user.email,
            dataCriacao: new Date().toISOString(),
            status: 'processado'
         }), { merge: true });
         if (!activeDossierId) setActiveDossierId(dossieId);
         setSaveBtnText('Cenário Salvo com Sucesso!');
         setTimeout(() => setSaveBtnText('Salvar Cenário Preditivo'), 2000);

         // 🤖 A IA NÃO é chamada daqui. O salvamento (automático a cada pausa, ou manual) só grava os
         // dados. A geração do parecer acontece quando a pessoa INICIA o diagnóstico (durante o scanning)
         // e é confirmada ao clicar em "Visualizar/Atualizar Dossiê" — ver dispararGeracaoIA.
         // (Plano Entry de R$ 49,90: só depois do pagamento confirmado, em outro fluxo.)
      } catch (error) {
         console.error("[PHOENIX] Erro ao salvar no Firebase:", error?.code || error, '| dossiê:', activeDossierId, '| conta:', user?.uid);
         setSaveBtnText('Erro ao salvar!');
         setTimeout(() => setSaveBtnText('Salvar Cenário Preditivo'), 2000);
      }
   };

   // 🤖 Monta o CONTEXTO COMPLETO (financeEngine.js) e pede pra IA escrever a
   // narrativa. Roda em segundo plano, sem travar o salvamento.
   //
   // 🔒 TRAVA DE REPETIÇÃO: antes de chamar a IA (que é pago por uso), monta
   // uma "impressão digital" dos dados financeiros relevantes e compara com
   // a última que gerou parecer (salva junto no Firestore). Se for igual,
   // não chama a IA de novo — evita pagar por parecer repetido quando o
   // usuário clica em "Salvar" várias vezes sem mudar nada de fato.
   //
   // 🔥 REUTILIZÁVEL: recebe formDataAlvo como parâmetro (em vez de sempre
   // usar o estado atual da tela) porque é chamada em dois fluxos diferentes:
   // 1) saveScenario (assinaturas Tactical/Plus/Total) — usa o formData atual
   // 2) fluxo Entry avulso (pagamento único) — usa o snapshot já congelado
   //    no momento da compra, que pode não ser mais o que está na tela
   // 🔥 Protege contra chamadas SOBREPOSTAS: se o autosave disparar de novo
   // (ex: usuário mexendo rápido nos campos) antes da chamada anterior
   // terminar de ler/comparar o retrato salvo, cada uma lia um retrato
   // desatualizado e as duas achavam que precisavam chamar a IA — gerando
   // parecer em dobro/triplo à toa. Agora só uma chamada roda por vez por
   // dossiê; a próxima espera essa terminar antes de decidir se precisa.
   const gerandoParecerPorDossieRef = useRef({});

   const gerarParecerComIA = async (dossieId, tipoUsuario, formDataAlvo = formData) => {
      if (gerandoParecerPorDossieRef.current[dossieId]) {
         return;
      }
      gerandoParecerPorDossieRef.current[dossieId] = true;
      try {
         await _gerarParecerComIA(dossieId, tipoUsuario, formDataAlvo);
      } finally {
         gerandoParecerPorDossieRef.current[dossieId] = false;
      }
   };

   const _gerarParecerComIA = async (dossieId, tipoUsuario, formDataAlvo = formData) => {
      const rev = parseCurrencyToNumber(formDataAlvo.revenue);
      const cos = parseCurrencyToNumber(formDataAlvo.costs);
      const deb = parseCurrencyToNumber(formDataAlvo.debt);
      const core = calculateCoreMetrics(rev, cos, deb, formDataAlvo.segment, {
         custoNaoRecorrente: parseCurrencyToNumber(formDataAlvo.custoNaoRecorrente),
         custoDivida: formDataAlvo.custoDivida
      });
      const health = calculateHealthStatus({ ebitda: core.ebitdaAjustado, alavancagem: core.alavancagem, deb, rev, sector: formDataAlvo.segment });
      const extended = calculateExtendedMetrics({
 sector: formDataAlvo.segment,
         rev, cos, ebitda: core.ebitdaAjustado, alavancagem: core.alavancagem, currentMargin: core.currentMargin,
         prazoRecebimento: formDataAlvo.prazoRecebimento, prazoPagamento: formDataAlvo.prazoPagamento,
         dividaCurtoPrazo: parseCurrencyToNumber(formDataAlvo.dividaCurtoPrazo), proLabore: parseCurrencyToNumber(formDataAlvo.proLabore)
      });
      const projecao = calculateStochasticProjection(
         { ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado, currentMargin: core.currentMargin },
         rev
      );

      // 🔥 Só manda pra IA os horizontes que ESSE plano já libera — evita
      // vazar conteúdo de 12/24/36 meses no texto corrido pra quem só
      // pagou por 3/6 (os cards numéricos já eram bloqueados certinho,
      // mas o texto da IA ignorava isso e comentava tudo mesmo assim).
      const planoAtualParaHorizonte = tipoUsuario === 'Consultor' ? consultorPlan : gestorPlan;
      const horizontesLiberados = getHorizontesLiberados(tipoUsuario, planoAtualParaHorizonte);
      const projecaoLiberada = projecao.filter(p => horizontesLiberados.includes(p.months));

      // 🔥 NOVO: a IA agora escreve em BLOCOS separados por prazo — Curto
      // (3-6m), Médio (12m), Longo (24-36m) — em vez de um texto único
      // cobrindo tudo. Cada bloco só é pedido pra IA se o plano libera
      // aquele horizonte; ela literalmente nunca escreve sobre um prazo
      // que a pessoa não pagou pra ver.
      const blocosProjecao = [];
      if (horizontesLiberados.includes(3) || horizontesLiberados.includes(6)) blocosProjecao.push('curto');
      if (horizontesLiberados.includes(12)) blocosProjecao.push('medio');
      if (horizontesLiberados.includes(24) || horizontesLiberados.includes(36)) blocosProjecao.push('longo');

      // Oportunidades Estratégicas continua exclusivo do topo de cada
      // perfil (Total pro Gestor, Enterprise Ultra pro Consultor).
      const incluirOportunidades = tipoUsuario === 'Consultor'
         ? planoAtualParaHorizonte === 'enterprise'
         : planoAtualParaHorizonte === 'total';

      // Contexto completo — tudo que é relevante pra IA entender a saúde
      // real do negócio, não só 3 números soltos.
      const metricas = {
         receita: rev, custos: cos, divida: deb,
         ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado,
         currentMargin: core.currentMargin, alavancagem: core.alavancagem,
         coberturaJuros: core.coberturaJuros, ruptureRisk: core.ruptureRisk,
         statusSaude: health.label,
         ncg: extended.ncg, reservaEmergencia: extended.emergencyReserve,
         dividendosSeguros: extended.safeDividends, scoreMA: extended.maScore,
         coberturaCurtoPrazo: extended.coberturaCurtoPrazo, caixaMinimo: extended.caixaMinimo
      };

      // "Impressão digital" simples dos dados de entrada (não das métricas
      // calculadas — dos INPUTS, que são a causa raiz da mudança).
      // 🔥 CORREÇÃO: todos os campos numéricos precisam estar no MESMO
      // formato (Number) aqui e no Dashboard (GestorDashboardInterface.jsx),
      // senão a "impressão digital" nunca bate entre os dois lugares — e a
      // IA é chamada de novo à toa mesmo quando nada financeiro mudou de
      // verdade (cobrando por edições irrelevantes, como só o nome).
      // 🔥 Também inclui os horizontes liberados: assim, um UPGRADE de
      // plano (que libera mais meses) dispara uma nova chamada de IA de
      // propósito — não é economia "errada", é conteúdo novo de verdade
      // que a pessoa passou a ter direito de ver.
      const retratoAtual = JSON.stringify({
         rev, cos, deb, segment: formDataAlvo.segment,
         custoNaoRecorrente: parseCurrencyToNumber(formDataAlvo.custoNaoRecorrente),
         custoDivida: parseCurrencyToNumber(formDataAlvo.custoDivida),
         prazoRecebimento: Number(formDataAlvo.prazoRecebimento) || 0,
         prazoPagamento: Number(formDataAlvo.prazoPagamento) || 0,
         dividaCurtoPrazo: parseCurrencyToNumber(formDataAlvo.dividaCurtoPrazo),
         proLabore: parseCurrencyToNumber(formDataAlvo.proLabore),
         horizontesLiberados,
         metodo: METODO_PARECER // muda quando a metodologia dos números muda: refaz o texto uma vez
      });

      const docAtual = await getDoc(doc(db, 'cenarios', String(dossieId)));
      const retratoSalvo = docAtual.exists() ? docAtual.data().parecerIAInputSnapshot : null;

      if (retratoSalvo === retratoAtual) {
         setIaGerando(false); // 🔥 nada mudou: desliga o loading (se por acaso tivesse ligado)
         return;
      }

      // 🔍 DIAGNÓSTICO: se a IA disparar de novo sem mudança financeira real,
      // este log mostra os dois retratos lado a lado — compare campo por
      // campo pra achar exatamente qual difere (formato, tipo, etc).

      // 🔥 CORREÇÃO: só liga o "carregando" AQUI — depois de confirmar que
      // a IA vai ser chamada de verdade. Antes isso era ligado do lado de
      // fora, incondicionalmente, então até quando a trava de economia
      // acima evitava a chamada, a tela ainda ficava presa em loading até
      // o tempo limite de segurança (30s) por nada.
      setIaGerando(true);
      geracoesIAEmAndamentoRef.current += 1;
      const tempoLimiteId = setTimeout(() => { geracoesIAEmAndamentoRef.current = 0; setIaGerando(false); }, 90000);

      try {
         await gerarParecerIA({
            dossieId,
            tipoUsuario,
            nicho: ((formData.niche && formData.niche !== '-') ? `${formData.niche} (setor: ${formData.segment || 'não informado'})` : (formData.segment || 'negócio geral')),
            metricas,
            projecao, // manda a projeção completa — a Cloud Function filtra por bloco internamente
            inputSnapshot: retratoAtual, // a Cloud Function salva isso pra próxima comparação
            blocosProjecao,
            incluirOportunidades
         });
      } finally {
         clearTimeout(tempoLimiteId);
         geracoesIAEmAndamentoRef.current = Math.max(0, geracoesIAEmAndamentoRef.current - 1);
         if (geracoesIAEmAndamentoRef.current === 0) setIaGerando(false);
      }
   };

   // ==========================================
   // 💾 SALVAMENTO AUTOMÁTICO — a cada mudança nos dados da empresa OU nos
   // sliders do Simulador "E Se" (aba Operação), salva sozinho ~2 segundos
   // depois da última alteração, sem precisar clicar em nada. Antes só
   // ativava dentro da tela do Assistente (view === 'wizard') — só que a
   // edição de dados existentes pode acontecer em outras telas também, e
   // nessa restrição o autosave simplesmente não disparava nelas. Agora
   // dispara em QUALQUER tela, sempre que `formData`, `simRevenue` ou
   // `simCosts` mudarem, contanto que já exista um dossiê salvo
   // (activeDossierId) e a pessoa esteja logada. Um diagnóstico novo,
   // ainda em criação (sem activeDossierId ainda), continua exigindo o
   // fluxo normal de finalização — evita gerar registros pela metade no
   // meio da digitação dos primeiros dados.
   // 🔥 INCLUÍDO simRevenue/simCosts na dependência: o botão "Salvar
   // Cenário Preditivo" da aba Operação salva justamente esses 2 valores
   // (os sliders da simulação), e o autosave antigo não os observava —
   // só reagia a mudanças em formData. Alguém mexendo só nos sliders,
   // sem tocar nos dados da empresa, nunca disparava o salvamento
   // automático.
   // ==========================================
   useEffect(() => { viewAtualRef.current = view; }, [view]);

   useEffect(() => {
      if (!activeDossierId || !user) return;
      // 🔥 Camada extra de segurança: se o dossiê ativo já existir no
      // histórico local e o perfil dele não bater com o formData atual
      // (ex: a pessoa trocou de Gestor pra Consultor rápido demais), NÃO
      // dispara o autosave — evita qualquer chance de gravar dado do
      // perfil errado em cima do dossiê errado.
      const itemAtivo = historyList.find(i => i.id === activeDossierId);
      const perfilDivergente = itemAtivo && String(itemAtivo.snapshot?.userType || '').toLowerCase() !== String(formData.userType || 'gestor').toLowerCase();
      if (perfilDivergente) return;

      setStatusAutoSave('salvando');
      const temporizador = setTimeout(async () => {
         await saveScenario();
         setStatusAutoSave('salvo');
         setTimeout(() => setStatusAutoSave('inativo'), 2500);
      }, 2000);
      return () => clearTimeout(temporizador);
   }, [formData, simRevenue, simCosts, activeDossierId, user]);

   // ==========================================
   // CARREGAR DOSSIÊ SALVO (CORREÇÃO DE CONTEXTO ATIVO)
   // ==========================================
   const loadDossier = async (item) => {
      // 🔥 CORRIGIDO — VULNERABILIDADE CRÍTICA (achada em teste real com
      // Asaas de produção, 2026-09-28): dossiês Entry (avulso, R$49,90)
      // ficam salvos no Firestore com status 'aguardando_pagamento' assim
      // que a cobrança é gerada — ANTES de qualquer pagamento — e podem
      // acabar na lista "Meus Dossiês" (ex: sincronização de dossiês
      // "perdidos"). Esta função abria o relatório completo de qualquer
      // item da lista sem nunca checar se o pagamento foi confirmado.
      // Agora, pra dossiês Entry, busca o documento de verdade no
      // Firestore (onde só o webhookAsaas grava 'pago') antes de liberar.
      const planoRotuloBruto = String(item.plan || '').toLowerCase();
      if (planoRotuloBruto === 'entry') {
         try {
            const dossieSnap = await getDoc(doc(db, 'cenarios', String(item.id)));
            const statusReal = dossieSnap.exists() ? dossieSnap.data().statusPagamento : null;
            // 🔥 CORRIGIDO (2026-09-29): antes, se não estivesse pago, esta
            // função parava aqui com um toast e NUNCA abria a tela — a
            // pessoa clicava no dossiê pendente e não via nada acontecer,
            // sem jeito nenhum de saber o que fazer ou de pagar de novo.
            // Agora deixa o carregamento continuar normalmente até
            // 'setView('report')' logo abaixo — quem realmente impede o
            // relatório de aparecer sem pagamento é a trava universal no
            // render ('relatorioEntryBloqueado', mais abaixo no JSX), que
            // mostra uma tela dedicada de "Aguardando Pagamento" com um
            // botão pra retomar o pagamento do MESMO dossiê.
            if (statusReal !== 'pago') {
               setToastMessage('Este dossiê ainda não teve o pagamento confirmado.');
            }
         } catch (e) {
            console.error('[PHOENIX] Erro ao reverificar pagamento do dossiê Entry:', e);
            setToastMessage('Não foi possível confirmar o pagamento agora. Tente novamente.');
            return;
         }
      }

      const snapshot = item.snapshot || {};
      const savedFormData = snapshot.formData || {};

      setFormData({
         expertise: savedFormData.expertise || 'Avançado',
         userType: savedFormData.userType || 'Gestor',
         companyName: savedFormData.companyName || '',
         segment: savedFormData.segment || '',
         niche: savedFormData.niche || '',
         revenue: savedFormData.revenue || '',
         costs: savedFormData.costs || '',
         debt: savedFormData.debt || '',
         custoNaoRecorrente: savedFormData.custoNaoRecorrente || '',
         prazoRecebimento: savedFormData.prazoRecebimento || '',
         prazoPagamento: savedFormData.prazoPagamento || '',
         dividaCurtoPrazo: savedFormData.dividaCurtoPrazo || '',
         custoDivida: savedFormData.custoDivida || '',
         proLabore: savedFormData.proLabore || ''
      });

      setSimRevenue(snapshot.simRevenue || 0);
      setSimCosts(snapshot.simCosts || 0);

      // Resgata o plano original da simulação
      let savedPlan = item.plan?.toLowerCase() || 'entry';
      if (savedPlan === 'avulso_360') savedPlan = 'total'; // Normaliza compatibilidade legada

      const planLevelMap = {
         'entry': 1,
         'tactical': 2,
         'plus': 3,
         'total': 4,
         'boardroom': 4,
         'advisor': 5,
         'boutique': 6,
         'enterprise': 7
      };

      const savedLevel = planLevelMap[savedPlan] || 1;

      // Verifica se o usuário tem plano ativo >= ao plano do dossiê
      const currentGestorLevel = planLevelMap[gestorPlan] || 1;
      const isConsultorDossier = String(item.snapshot?.userType || '').toLowerCase() === 'consultor';

      // 🔒 Gestor — relatório gerado DURANTE um plano (Tactical/Plus/Total):
      //  • SEM plano ativo (cancelou e o período acabou): bloqueado — não é apagado e volta se reassinar;
      //  • COM plano ativo: abre SEMPRE no nível do plano ATUAL (sobe de plano → ganha os recursos novos; desce → vê só o
      //    que o plano atual libera). Antes dependia de um rótulo reescrito na lista, e o comportamento mudava conforme o
      //    aparelho (rótulo do documento x rótulo da lista).
      // Entry pago e Super 360º avulso (AVULSO_360) são comprados à parte e NUNCA são restringidos nem reclassificados.
      if (!isConsultorDossier) {
         const rotuloDossie = String(item.plan || '').toLowerCase();
         const compradoAParte = rotuloDossie === 'entry' || rotuloDossie === 'avulso_360';
         if (!compradoAParte && (planLevelMap[rotuloDossie] || 1) > 1) {
            if (currentGestorLevel <= 1) {
               setToastMessage('Este relatório foi gerado durante um plano que não está ativo. Reative a assinatura para acessá-lo.');
               return;
            }
            savedPlan = String(gestorPlan).toLowerCase();
         }
      }

      if (isConsultorDossier) {
         // Consultor: verifica plano consultor ativo
         const currentConsultorLevel = planLevelMap[consultorPlan] || 0;
         if (currentConsultorLevel === 0) {
            setToastMessage('Assine um plano Consultor para acessar este dossiê.');
            return;
         }
         setConsultorPlan(consultorPlan);
      } else {
         setGestorPlan(gestorPlan);
         setUserLevel(currentGestorLevel);
      }

      setActivePlan(savedPlan);
      setActiveDossierId(item.id);

      // Salva no localStorage para consistência em reloads de página
      if (typeof window !== 'undefined') {
         // Não persistir — o plano já está em memória via activePlan
         localStorage.setItem('phoenix_user_level', savedLevel);
      }

      setReportTab('visao');
      setView('report');

      // 🔥 SINCRONIA COM O DASHBOARD: o formData acima veio do cache local
      // (historyList), que só se atualiza quando o app salva. Se esse
      // dossiê foi editado pelo Dashboard Gestor (dados, ou a Profundidade
      // da Análise), o cache local não sabe disso ainda. Busca a versão
      // mais recente direto no Firestore em segundo plano — a tela já
      // abre na hora com o cache, e se algo tiver mudado, atualiza sozinha
      // um instante depois, sem travar a navegação.
      const formatarCampoMoeda = (v) => {
         if (v === undefined || v === null || v === '') return undefined;
         if (typeof v === 'number') return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
         return v;
      };

      // 🔥 LISTA ÚNICA DE CAMPOS SINCRONIZÁVEIS — antes cada campo era
      // escrito à mão dentro do setFormData (foi assim que o CNPJ e outros
      // "detalhes opcionais" passaram batido: simplesmente esqueci de
      // incluir a linha deles). Agora, pra sincronizar um dado novo entre
      // App e Dashboard, só precisa aparecer NESTA lista, uma vez — nunca
      // mais em vários lugares espalhados. `moeda: true` aplica a máscara
      // de R$ automaticamente; os demais são copiados como estão.
      const CAMPOS_SINCRONIZAVEIS = [
         { campo: 'companyName' }, { campo: 'cnpj' }, { campo: 'segment' }, { campo: 'niche' },
         { campo: 'expertise' },
         { campo: 'revenue', moeda: true }, { campo: 'costs', moeda: true }, { campo: 'debt', moeda: true },
         { campo: 'custoNaoRecorrente', moeda: true }, { campo: 'dividaCurtoPrazo', moeda: true },
         { campo: 'proLabore', moeda: true },
         { campo: 'custoDivida' }, { campo: 'prazoRecebimento' }, { campo: 'prazoPagamento' },
      ];

      const aplicarDadosFrescos = (dadosFrescos) => {
         // 🔥 CORREÇÃO: antes escolhia UM formato inteiro (solto OU
         // aninhado) e usava ele pra tudo. O problema é que existe uma
         // função separada no app (sincronizarDossieUnico) que também
         // grava um formato aninhado (snapshot.formData) toda vez que o
         // app salva algo — e como campos novos tipo Prazo de Recebimento/
         // Pagamento nunca tinham sido gravados nesse formato aninhado em
         // dossiês antigos, a escolha "tudo ou nada" acabava preferindo o
         // aninhado (que existia, mas sem esses campos) e ignorava o
         // formato solto (que o Dashboard tinha acabado de atualizar).
         // Agora a escolha é campo por campo: sempre prioriza o formato
         // solto quando ele tiver o campo preenchido, só cai pro aninhado
         // se o campo realmente não existir lá.
         const aninhado = dadosFrescos.snapshot?.formData || {};
         const pegar = (campo) => (dadosFrescos[campo] !== undefined && dadosFrescos[campo] !== null && dadosFrescos[campo] !== '')
            ? dadosFrescos[campo]
            : aninhado[campo];

         setFormData(prev => {
            const atualizado = { ...prev };
            CAMPOS_SINCRONIZAVEIS.forEach(({ campo, moeda }) => {
               const valorBruto = pegar(campo);
               const valorFinal = moeda ? formatarCampoMoeda(valorBruto) : valorBruto;
               if (valorFinal !== undefined && valorFinal !== null && valorFinal !== '') {
                  atualizado[campo] = valorFinal;
               }
            });
            return atualizado;
         });

         // 🔥 CORREÇÃO: até aqui só atualizava a TELA aberta do relatório.
         // A lista "Meus Dossiês" é um cache separado (historyList) — sem
         // isso, o nome exibido na lista ficava desatualizado mesmo depois
         // do relatório já mostrar o nome novo vindo do Dashboard.
         const nomeFresco = pegar('companyName');
         if (nomeFresco) {
            setHistoryList(prev => prev.map(h => h.id === item.id ? { ...h, name: nomeFresco } : h));
         }
      };

      getDoc(doc(db, 'cenarios', String(item.id))).then(snap => {
         if (snap.exists()) {
            aplicarDadosFrescos(snap.data());
            return;
         }
         // 🔥 CORREÇÃO: em dossiês mais antigos, o ID guardado no cache local
         // (historyList) pode ter ficado dessincronizado do ID de verdade no
         // Firestore (drift de dados legados) — a busca direta por ID falha
         // silenciosamente, e a sincronia nunca acontecia pra esses casos
         // (só funcionava pros dossiês criados recentemente, com ID
         // consistente). Como plano B, procura o documento certo pelo nome
         // da empresa entre os dossiês do usuário.
         if (!user?.uid) return;
         getDocs(query(collection(db, 'cenarios'), where('userId', '==', user.uid))).then(snapTodos => {
            let encontrado = null;
            snapTodos.forEach(docSnap => {
               const dados = docSnap.data();
               const origem = dados.snapshot?.formData || dados;
               const nome = origem.companyName || dados.name;
               if (nome && nome === item.name) encontrado = dados;
            });
            if (encontrado) aplicarDadosFrescos(encontrado);
         }).catch(e => console.error('[PHOENIX] Erro na busca alternativa por nome:', e));
      }).catch(e => console.error('[PHOENIX] Erro ao buscar versão mais recente do dossiê:', e));
   };

   // ==========================================
   // RESET COMPLETO DO ESTADO (LIMPAR DADOS)
   // ==========================================
   const resetAppState = () => {
      // Limpa localStorage
      localStorage.removeItem('phoenix_consultor_plan');
      localStorage.removeItem('phoenix_gestor_plan');
      localStorage.removeItem('phoenix_active_plan');
      localStorage.removeItem('phoenix_user_type');
      localStorage.removeItem('phoenix_user_level');
      localStorage.removeItem('phoenix_user_name');
      localStorage.removeItem('phoenix_user_email');
      localStorage.removeItem('phoenix_history_consultor');
      localStorage.removeItem('phoenix_history_gestor');

      // Reseta estados
      setActivePlan('entry');
      setUserLevel(1);
      setFormData({
         method: '', profile: '', companyName: '', segment: '', niche: '',
         revenue: '', costs: '', debt: '', expertise: '', userType: ''
      });
      setHistoryList([]);
      setActiveDossierId(null);
      setView('landing');
      setStep(1);

      // Recarrega a página para garantir
      window.location.reload();
   };

   const startNewDiagnosis = () => {
      if (gestorPlan && gestorPlan !== 'entry' && !user) {
         setPendingGestorPlan({ id: gestorPlan, name: String(gestorPlan).toUpperCase(), price: '' });
         setShowGestorCreateAccount(true);
         return;
      }
      setFormData({ method: '', profile: '', companyName: '', segment: '', niche: '', revenue: '', costs: '', debt: '', expertise: '', userType: 'Gestor', custoNaoRecorrente: '', prazoRecebimento: '', prazoPagamento: '', dividaCurtoPrazo: '', proLabore: '' });
      setSimRevenue(0);
      setSimCosts(0);
      setAiJustification('');
      setActiveDossierId(null);
      // 🔥 Reseta o activePlan baseado só no plano do GESTOR — nunca herda
      // um valor deixado pelo Consultor (ex: 'enterprise')
      setActivePlan(gestorPlan && gestorPlan !== 'entry' ? gestorPlan : 'entry');
      setStep(1);
      setReportTab('visao');
      setView('wizard');
   };

   const handleCurrencyChange = (e, field) => {
      let value = e.target.value.replace(/\D/g, '');
      if (value === '') {
         setFormData({ ...formData, [field]: '' });
         return;
      }
      const numericValue = (parseInt(value, 10) / 100).toLocaleString('pt-BR', {
         minimumFractionDigits: 2,
         maximumFractionDigits: 2
      });
      setFormData({ ...formData, [field]: numericValue });
   };

   const handleManualSliderChange = (type, val) => {
      setAiJustification('');
      if (type === 'rev') setSimRevenue(val);
      if (type === 'cost') setSimCosts(val);
   }

   // 🔥 CORREÇÃO: antes esse botão só fingia ler o arquivo — esperava 2,5s
   // e preenchia sempre os MESMOS 3 valores fixos, ignorando completamente
   // o que a pessoa tinha enviado. Agora lê a planilha de verdade (.xlsx,
   // .xls ou .csv) e procura por linhas com palavras-chave de Faturamento,
   // Custos e Dívida, pegando o primeiro número que aparecer do lado.
   const [erroUploadPlanilha, setErroUploadPlanilha] = useState('');

   // Converte "R$ 2.500.000,00", "2500000", "2.500.000" etc. num número puro.
   const extrairNumeroDaCelula = (valor) => {
      if (typeof valor === 'number') return valor;
      if (!valor) return null;
      const texto = String(valor).replace(/[^\d,.-]/g, '').trim();
      if (!texto) return null;
      // Formato brasileiro (1.234.567,89) → remove pontos de milhar, troca vírgula por ponto.
      const normalizado = texto.includes(',')
         ? texto.replace(/\./g, '').replace(',', '.')
         : texto;
      const numero = parseFloat(normalizado);
      return isNaN(numero) ? null : numero;
   };

   const normalizarTexto = (s) => String(s || '')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // remove acentos

   const PALAVRAS_CHAVE_PLANILHA = {
      revenue: ['receita', 'faturamento', 'vendas', 'venda bruta', 'receita bruta'],
      costs: ['custo', 'despesa', 'despesas', 'custos totais', 'custos e despesas'],
      debt: ['divida', 'dívida', 'passivo', 'emprestimo', 'empréstimo', 'financiamento'],
      // Campos opcionais — destravam Ponto de Equilíbrio avançado, NCG,
      // Caixa Mínimo e outras métricas técnicas quando preenchidos.
      custoNaoRecorrente: ['custo nao recorrente', 'custo não recorrente', 'despesa nao recorrente', 'evento extraordinario'],
      prazoRecebimento: ['prazo de recebimento', 'prazo medio de recebimento', 'dias para receber'],
      prazoPagamento: ['prazo de pagamento', 'prazo medio de pagamento', 'dias para pagar'],
      dividaCurtoPrazo: ['divida de curto prazo', 'dívida de curto prazo', 'passivo circulante'],
      proLabore: ['pro labore', 'pró-labore', 'pro-labore', 'retirada dos socios', 'retirada dos sócios']
   };

   // Varre todas as linhas de todas as abas procurando as palavras-chave;
   // ao achar uma, pega o primeiro número que aparecer na mesma linha
   // (a maioria das planilhas coloca "Receita | R$ 2.500.000,00" lado a lado).
   // ORDEM IMPORTA: campos específicos (ex: "Custo Não Recorrente") são
   // checados ANTES dos genéricos (ex: "Custo"), senão "Custo Não Recorrente"
   // seria lido errado como se fosse só "Custos" (a palavra "custo" está
   // contida nas duas). Uma linha só pode alimentar UM campo por vez.
   const ORDEM_PRIORIDADE_CAMPOS = [
      'custoNaoRecorrente', 'prazoRecebimento', 'prazoPagamento', 'dividaCurtoPrazo', 'proLabore',
      'revenue', 'costs', 'debt'
   ];

   const extrairDadosDaPlanilha = (workbook) => {
      const encontrados = { revenue: null, costs: null, debt: null, custoNaoRecorrente: null, prazoRecebimento: null, prazoPagamento: null, dividaCurtoPrazo: null, proLabore: null };

      workbook.SheetNames.forEach((nomeAba) => {
         const aba = workbook.Sheets[nomeAba];
         const linhas = XLSX.utils.sheet_to_json(aba, { header: 1, defval: '' });

         linhas.forEach((linha) => {
            const celulasTexto = linha.map(c => normalizarTexto(c));
            const numerosDaLinha = linha.map(extrairNumeroDaCelula).filter(n => n !== null);
            if (numerosDaLinha.length === 0) return;

            for (const campo of ORDEM_PRIORIDADE_CAMPOS) {
               if (encontrados[campo] !== null) continue; // já achou, não sobrescreve
               const bateu = celulasTexto.some(txt =>
                  PALAVRAS_CHAVE_PLANILHA[campo].some(palavra => txt.includes(palavra))
               );
               if (bateu) {
                  encontrados[campo] = numerosDaLinha[0];
                  break; // essa linha já foi usada, não deixa outro campo genérico reaproveitar
               }
            }
         });
      });

      return encontrados;
   };

   // 📥 MODELO DE PLANILHA — baixa um .xlsx já com os rótulos exatos que
   // nosso leitor reconhece, em Reais "cheios" (não em milhares), evitando
   // a ambiguidade de formato que cada empresa costuma usar diferente.
   const baixarModeloPlanilha = () => {
      const linhas = [
         ['Campo', 'Valor mensal (em Reais — não em milhares)'],
         ['Faturamento', ''],
         ['Custos', ''],
         ['Dívida', ''],
         ['Custo Não Recorrente', ''],
         ['Prazo de Recebimento (dias)', ''],
         ['Prazo de Pagamento (dias)', ''],
         ['Dívida de Curto Prazo', ''],
         ['Pró-Labore', ''],
      ];
      const planilha = XLSX.utils.aoa_to_sheet(linhas);
      planilha['!cols'] = [{ wch: 32 }, { wch: 34 }];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, planilha, 'Dados da Empresa');
      XLSX.writeFile(workbook, 'Phoenix_Modelo_Planilha.xlsx');
   };

   const handleFileUpload = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setErroUploadPlanilha('');
      setIsUploading(true);

      const reader = new FileReader();
      reader.onload = (evento) => {
         try {
            const dados = new Uint8Array(evento.target.result);
            const workbook = XLSX.read(dados, { type: 'array' });
            const { revenue, costs, debt, custoNaoRecorrente, prazoRecebimento, prazoPagamento, dividaCurtoPrazo, proLabore } = extrairDadosDaPlanilha(workbook);

            if (revenue === null && costs === null && debt === null) {
               // Não achou nada reconhecível — melhor avisar e deixar a
               // pessoa preencher na mão do que inventar número.
               setErroUploadPlanilha('Não encontramos Faturamento, Custos ou Dívida na planilha. Preencha manualmente abaixo, ou baixe nosso modelo pronto.');
               setIsUploading(false);
               setStep(4);
               return;
            }

            setFormData(prev => ({
               ...prev,
               revenue: revenue !== null ? formatCurrency(revenue) : prev.revenue,
               costs: costs !== null ? formatCurrency(costs) : prev.costs,
               debt: debt !== null ? formatCurrency(debt) : (prev.debt || ''),
               custoNaoRecorrente: custoNaoRecorrente !== null ? formatCurrency(custoNaoRecorrente) : (prev.custoNaoRecorrente || ''),
               prazoRecebimento: prazoRecebimento !== null ? String(Math.round(prazoRecebimento)) : (prev.prazoRecebimento || ''),
               prazoPagamento: prazoPagamento !== null ? String(Math.round(prazoPagamento)) : (prev.prazoPagamento || ''),
               dividaCurtoPrazo: dividaCurtoPrazo !== null ? formatCurrency(dividaCurtoPrazo) : (prev.dividaCurtoPrazo || ''),
               proLabore: proLabore !== null ? formatCurrency(proLabore) : (prev.proLabore || '')
            }));

            if (revenue === null || costs === null) {
               setErroUploadPlanilha(`Encontramos alguns dados, mas ${revenue === null ? 'Faturamento' : 'Custos'} não apareceu claramente na planilha — confira e complete abaixo.`);
               setIsUploading(false);
               setStep(4);
               return;
            }

            setIsUploading(false);
            setStep(5);
            handleStartScanning();
         } catch (erro) {
            console.error('[PHOENIX] Erro ao ler planilha:', erro);
            setErroUploadPlanilha('Não conseguimos ler esse arquivo. Confira se é um .xlsx, .xls ou .csv válido, ou preencha manualmente.');
            setIsUploading(false);
            setStep(4);
         }
      };
      reader.onerror = () => {
         setErroUploadPlanilha('Erro ao abrir o arquivo. Tente novamente ou preencha manualmente.');
         setIsUploading(false);
         setStep(4);
      };
      reader.readAsArrayBuffer(file);
   };

   const isStepValid = () => {
      if (step === 1) return formData.expertise !== '' && formData.userType !== '';
      if (step === 2) return formData.companyName.trim() !== '' && formData.segment !== '' && formData.niche.trim() !== '';
      if (step === 3) return true;
      if (step === 4) return formData.revenue !== '' && formData.costs !== '';
      if (step === 5) return formData.debt !== '';
      return true;
   };

   // 🤖 Dispara a geração do parecer em segundo plano. NUNCA para o plano Entry (R$ 49,90): lá a IA só roda
   // depois do pagamento confirmado, pra não gerar custo à toa. A trava de impressão digital dentro de
   // gerarParecerComIA evita gerar duas vezes quando nada mudou.
   const dispararGeracaoIA = (dossieId, tipo, planoDoDossie) => {
      if (!dossieId) return;
      if (String(dossieId) === String(activeDossierId) && entryPendenteRef.current) return; // Entry não pago: sem IA
      const plano = String(planoDoDossie || '').toLowerCase();
      if (plano === 'entry' || plano === 'free' || plano === '') return;
      gerarParecerComIA(String(dossieId), tipo || 'Gestor').catch(e => {
         console.error('[PHOENIX] Erro ao gerar parecer com IA (usando fallback fixo):', e);
         setIaGerando(false);
      });
   };

   // 🔥 OPÇÃO B — quem tem assinatura de Gestor ativa e abre um Entry NÃO PAGO pode gerar o relatório pelo próprio
   // plano (diagnósticos ilimitados já estão incluídos), sem pagar o Entry. Cria um dossiê NOVO com os mesmos dados
   // (o dossiê Entry pendente não é alterado: o servidor/regras não deixam mudar plano/pagamento dele) e gera o parecer
   // pelo plano. O plano é conferido no servidor — nunca na memória do app.
   const [gerandoPeloPlano, setGerandoPeloPlano] = useState(false);
   const gerarRelatorioPeloPlano = async () => {
      if (!user || user.isAnonymous || gerandoPeloPlano) return;
      setGerandoPeloPlano(true);
      try {
         const perfilSnap = await getDoc(doc(db, 'usuarios', user.uid));
         const dadosPerfil = perfilSnap.exists() ? perfilSnap.data() : {};
         const planoReal = String(dadosPerfil.plan || dadosPerfil.gestorPlan || '').toLowerCase();
         if (!['tactical', 'plus', 'total'].includes(planoReal)) {
            setToastMessage('Seu plano de Gestor não está ativo. Assine um plano ou pague o Entry para liberar este relatório.');
            return;
         }
         const novoId = gerarIdUnico();
         const novoItem = {
            id: novoId,
            name: formData.companyName || 'Nova Análise',
            date: new Date().toLocaleDateString('pt-BR'),
            plan: planoReal.toUpperCase(),
            snapshot: { formData: { ...formData, userType: 'Gestor' }, simRevenue, simCosts, userType: 'Gestor' }
         };
         setHistoryList(prev => [novoItem, ...prev]);
         await sincronizarDossieUnico(novoItem);
         setGestorPlan(planoReal);
         setActivePlan(planoReal);
         setActiveDossierId(novoId);
         setReportTab('visao');
         setView('report');
         setToastMessage('Gerando seu relatório pelo plano ' + planoReal.toUpperCase() + '…');
         dispararGeracaoIA(novoId, 'Gestor', planoReal);
      } catch (e) {
         console.error('[PHOENIX] Erro ao gerar relatório pelo plano:', e);
         setToastMessage('Não foi possível gerar o relatório agora. Tente novamente.');
      } finally {
         setGerandoPeloPlano(false);
      }
   };

   const handleStartScanning = () => {
      setProgress(0);
      setView('scanning');
      // A IA NÃO começa aqui: o scanning é só a animação. A geração começa quando a pessoa clica em
      // "Visualizar/Atualizar Dossiê" (tela "Análise Pronta"), pra manter a "mágica" do scanning.
   };

   const planLevels = { 'entry': 1, 'tactical': 2, 'plus': 3, 'total': 4 };
   const checkAcquired = (planName) => {
      const currentGestorLevel = planLevels[gestorPlan] || planLevels[activePlan] || 0;
      return currentGestorLevel >= planLevels[planName];
   };
   // ==========================================
   // FUNÇÃO MESTRA DE EXPORTAÇÃO PDF CORPORATIVO
   // ==========================================
   const handleExportPDF = (forceSuper = false) => {
      // 🔒 Não exporta com o texto da IA desatualizado: espera a geração terminar.
      if (iaGerando || geracoesIAEmAndamentoRef.current > 0) {
         setToastMessage('Atualizando o parecer com os novos dados… aguarde alguns segundos e exporte de novo.');
         return;
      }
      try { setShowDownloadConfirm(false); } catch (e) { }

      // CORREÇÃO: Se forceSuper for true (compra de R$ 1490), ele baixa o Super PDF mesmo estando na aba Visão
      const isSuper = forceSuper === true || reportTab === 'ma' || reportTab === 'whitelabel';
      const element = isSuper ? superPdfRef.current : basicPdfRef.current;

      if (!element) {
         console.error("Erro: Container do PDF não encontrado.");
         return;
      }

      const nomeArquivo = nomeArquivoRelatorio(isSuper ? '360' : 'master', formData.companyName);
      const opt = {
         margin: 0,
         filename: nomeArquivo,
         image: { type: 'jpeg', quality: 1 },
         html2canvas: { scale: 2, useCORS: true, logging: false, letterRendering: true, scrollX: 0, scrollY: 0 },
         jsPDF: { unit: 'px', format: [800, 1131], orientation: 'portrait' }
      };

      // 🔥 CORREÇÃO: dentro do app nativo (Capacitor), o método antigo
      // (.save()) usa um link de download que só funciona em navegador —
      // ele "finge" que deu certo (a Promise resolve normalmente) mas não
      // salva nada em lugar nenhum acessível no celular. Por isso, dentro
      // do app, geramos o PDF em base64 e abrimos a caixa de compartilhar
      // nativa do Android, onde a pessoa escolhe salvar em Arquivos/Drive/
      // Downloads — dando uma prova real e visível de que funcionou.
      if (Capacitor.isNativePlatform()) {
         html2pdf().set(opt).from(element).toPdf().output('datauristring').then(async (pdfDataUri) => {
            const base64Data = pdfDataUri.split(',')[1];
            const resultado = await Filesystem.writeFile({
               path: nomeArquivo,
               data: base64Data,
               directory: Directory.Cache,
            });
            await Share.share({
               title: nomeArquivo,
               url: resultado.uri,
               dialogTitle: 'Salvar ou compartilhar relatório',
            });
            setToastMessage('Relatório gerado! Escolha onde salvar na tela que abriu.');
         }).catch((erro) => {
            console.error('[PHOENIX] Erro ao gerar/compartilhar PDF nativo:', erro);
            setToastMessage('Não foi possível gerar o PDF agora. Tente novamente.');
         });
      } else {
         html2pdf().set(opt).from(element).save().then(() => {
            setToastMessage('Relatório baixado com sucesso! Verifique a pasta de Downloads do seu dispositivo.');
         }).catch(() => {
            setToastMessage('Não foi possível gerar o PDF agora. Tente novamente.');
         });
      }
   };

   const calculateReport = () => {
      const rev = parseCurrencyToNumber(formData.revenue);
      const cos = parseCurrencyToNumber(formData.costs);
      const deb = parseCurrencyToNumber(formData.debt);

      // 🔥 Matemática vem do financeEngine.js — mesma fonte usada pelos Dashboards
      const core = calculateCoreMetrics(rev, cos, deb, formData.segment, {
         custoNaoRecorrente: parseCurrencyToNumber(formData.custoNaoRecorrente),
         custoDivida: formData.custoDivida
      });
      const ebitda = core.ebitda;
      // Alavancagem agora vem pronta do financeEngine.js, já usando o EBITDA Ajustado
      const alavancagem = core.alavancagem === "10.00+" ? "10.00+" : Number(core.alavancagem).toFixed(2);
      const currentMargin = rev > 0 ? ((ebitda / rev) * 100).toFixed(1) : "0.0";

      const health = calculateHealthStatus({ ebitda: core.ebitdaAjustado, alavancagem, deb, rev, sector: formData.segment });
      const statusStyles = {
         'Falência Iminente': { color: 'text-red-600', border: 'border-red-600/50', bg: 'bg-red-600/10', icon: <XOctagon size={40} className="text-red-600 animate-pulse" /> },
         'Crítico': { color: 'text-red-500', border: 'border-red-500/30', bg: 'bg-red-500/10', icon: <ShieldAlert size={40} className="text-red-500" /> },
         'Atenção': { color: 'text-orange-500', border: 'border-orange-500/30', bg: 'bg-orange-500/10', icon: <AlertTriangle size={40} className="text-orange-500" /> },
         'Saudável': { color: 'text-lime-400', border: 'border-lime-400/30', bg: 'bg-lime-400/10', icon: <ShieldCheck size={40} className="text-lime-400" /> },
      };
      // 🔥 NOVO: sinaliza os dois status realmente graves (a empresa pode
      // estar perto de quebrar) pra tela tratar visualmente diferente do
      // resto — ver o card "Status da Operação" mais abaixo. Decisão de
      // produto: alerta grave não deve ter o mesmo brilho neon decorativo
      // do resto do app, porque isso comunica "vitrine bonita" quando a
      // pessoa precisa sentir "isso é sério, preste atenção".
      const grave = health.label === 'Crítico' || health.label === 'Falência Iminente';
      const status = { label: health.label, grave, ...statusStyles[health.label] };

      const sRev = Number(simRevenue) || 0;
      const sCos = Number(simCosts) || 0;
      const proj = calculateProjection(rev, cos, ebitda, sRev, sCos);
      const { gains, projectedMargin } = proj;
      // O EBITDA projetado parte do EBITDA AJUSTADO (mesma base do painel, do valuation e do PDF): soma só o efeito
      // da simulação (gains) ao ajustado. Antes mostrava o EBITDA sem ajuste, chamado de "ajustado" no relatório.
      const projectedEbitda = core.ebitdaAjustado + gains;

      let simStatus = "Análise Pendente";
      let simColor = "text-slate-400";
      let simDescription = "Ajuste as alavancas de receita e custo acima para visualizar a projeção e receber o parecer estratégico da IA.";

      if (sCos > 15) {
         simStatus = "Alerta: Corte Agressivo";
         simColor = "text-orange-400";
         simDescription = `A simulação de redução de ${sCos}% nos custos gera alívio imediato no caixa, mas exige renegociação severa com fornecedores e possíveis demissões. Execute com extrema cautela para não paralisar a operação.`;
      } else if (projectedEbitda <= 0) {
         simStatus = "Alerta: Sangramento de Caixa";
         simColor = "text-red-500";
         simDescription = "As projeções indicam que a operação continuará operando no vermelho, consumindo capital. É vital um choque de gestão profundo, reestruturação de passivos ou injeção urgente de capital de giro.";
      } else if (status.label === 'Falência Iminente') {
         simStatus = "Risco de Insolvência Estrutural";
         simColor = "text-red-600";
         simDescription = `Sua margem não suporta a estrutura de capital atual. Todo o caixa será consumido pelo peso da dívida. Apenas cortar custos não é suficiente; foco total deve ser direcionado para Recuperação Judicial/Extrajudicial e Liability Management.`;
      } else if (Number(alavancagem) > 2.5) {
         simStatus = "Atenção: Dívida Consumindo Caixa";
         simColor = "text-orange-500";
         simDescription = `A operação é viável, mas o nível de dívida de ${String(alavancagem).replace('.', ',')}x exige que todo o ganho projetado de R$ ${formatCurrency(gains)} seja blindado para amortização, sob o risco de asfixiar o fluxo de caixa futuramente.`;
      } else if (projectedMargin > 20) {
         simStatus = "Cenário de Alta Eficiência";
         simColor = "text-lime-400";
         simDescription = `Excelente! Ao atingir ${String(projectedMargin).replace('.', ',')}% de margem com passivos controlados, sua empresa entra no quadrante de alto desempenho. Este nível garante resiliência e permite financiar expansões com o próprio caixa.`;
      } else if (sRev > 0 || sCos > 0) {
         simStatus = "Otimização Saudável";
         simColor = "text-lime-400";
         simDescription = `Os ajustes projetam um ganho de R$ ${formatCurrency(gains)} no lucro operacional. O cenário é realista e melhora a liquidez da empresa sem aplicar estresse excessivo na equipe.`;
      }

      return { ebitda, ebitdaAjustado: core.ebitdaAjustado, custoServicoDivida: core.custoServicoDivida, coberturaJuros: core.coberturaJuros, alavancagem, status, currentMargin, projectedEbitda, gains, projectedMargin, simStatus, simColor, simDescription };
   };

   const report = calculateReport();

   // 🔥 CORREÇÃO: calculoEmprestimoApp precisa de `report.ebitda`, então só
   // pode ser calculado DEPOIS que `report` existir — antes estava lá em
   // cima junto dos outros estados (declarados bem antes de `report`),
   // causando "Cannot access 'report' before initialization".
   const calculoEmprestimoApp = (() => {
      const principal = parseCurrencyToNumber(emprestimoValorApp);
      const taxaMensal = Math.pow(1 + (Number(emprestimoJurosApp) || 0) / 100, 1 / 12) - 1;
      const n = Number(emprestimoPrazoApp) || 1;
      if (principal <= 0) return { valido: false };
      const parcela = taxaMensal === 0 ? principal / n : (principal * taxaMensal) / (1 - Math.pow(1 + taxaMensal, -n));
      const totalPago = parcela * n;
      const lucroMes = report.ebitda;
      const comprometimento = lucroMes > 0 ? (parcela / lucroMes) * 100 : null;
      // 🔥 CORREÇÃO: limites revisados — 24% do lucro numa parcela só sendo
      // chamado de "cabe com folga" era otimista demais pra fluxo de caixa
      // de empresa (mais instável que salário de pessoa física). Regra
      // mais prudente: até 15% é confortável, 15-35% pede atenção, acima
      // de 35% é crítico.
      let nivel = 'seguro';
      let veredito = 'A parcela cabe com folga dentro do lucro mensal atual da empresa.';
      if (comprometimento === null) {
         nivel = 'critico';
         veredito = 'A empresa está com lucro mensal zerado ou negativo — assumir uma parcela fixa nesse momento aumenta bastante o risco financeiro.';
      } else if (comprometimento > 35) {
         nivel = 'critico';
         veredito = 'A parcela compromete uma fatia grande do lucro mensal — risco alto de aperto no caixa em qualquer imprevisto.';
      } else if (comprometimento > 15) {
         nivel = 'atencao';
         veredito = 'A parcela tem peso relevante no lucro mensal. Cabe, mas com pouca margem de segurança — vale considerar um prazo mais longo ou valor menor.';
      }
      return { valido: true, parcela, totalPago, totalJuros: totalPago - principal, comprometimento, nivel, veredito };
   })();

   const handleAutoOptimize = () => {
      const rev = parseCurrencyToNumber(formData.revenue);
      const cos = parseCurrencyToNumber(formData.costs);
      const deb = parseCurrencyToNumber(formData.debt);
      const core = calculateCoreMetrics(rev, cos, deb, formData.segment);
      // Dívida / EBITDA anualizado (EBITDA é mensal) — mesma definição do financeEngine.
      const alavancagem = deb > 0 && core.ebitda > 0 ? (deb / (core.ebitda * 12)) : (deb > 0 ? 10 : 0);
      const margin = rev > 0 ? (core.ebitda / rev) * 100 : 0;

      if (alavancagem > 4.5 || core.ebitda <= 0) {
         setSimRevenue(5);
         setSimCosts(25);
         setAiJustification("A operação encontra-se em risco grave. Para evitar o colapso do caixa, o Phoenix OS® definiu um corte de sobrevivência de 25% nas despesas (downsize) alinhado a um esforço comercial conservador (5%). A prioridade absoluta é estancar o sangramento e preservar a liquidez diária.");
      } else if (margin < 15 || alavancagem > 2.5) {
         setSimRevenue(15);
         setSimCosts(12);
         // O motivo vem dos números da própria empresa (antes o texto era o mesmo para qualquer caso, até para empresa saudável).
         const margemTxt = margin.toFixed(1).replace('.', ',');
         const alavTxt = alavancagem.toFixed(1).replace('.', ',');
         const motivo = (margin < 15 && alavancagem > 2.5)
            ? `A margem está em ${margemTxt}% (abaixo de 15%) e a dívida equivale a ${alavTxt} anos de EBITDA`
            : (margin < 15 ? `A margem está em ${margemTxt}%, abaixo de 15%, o que deixa pouco espaço para imprevistos` : `A dívida equivale a ${alavTxt} anos de EBITDA, um peso relevante para o caixa`);
         setAiJustification(`${motivo}. O Phoenix OS® calibrou uma meta híbrida: aumento viável de vendas (15%) com revisão rigorosa de contratos e desperdícios (12%), para melhorar o lucro sem romper a capacidade produtiva da equipe.`);
      } else {
         setSimRevenue(20);
         setSimCosts(5);
         setAiJustification("Sua fundação é robusta e a empresa está saudável. O foco não deve ser demitir ou cortar na carne (apenas 5% de ajuste fino), mas sim tracionar a máquina de vendas em 20%. Este cenário de crescimento foca em maximizar a geração de patrimônio e dividendos para os sócios.");
      }
   };

   const getFinancialMetrics = () => {
      const rev = parseCurrencyToNumber(formData.revenue);
      const cos = parseCurrencyToNumber(formData.costs);
      const debt = parseCurrencyToNumber(formData.debt);

      // 🔥 Matemática vem do financeEngine.js — mesma fonte usada pelos Dashboards
      const core = calculateCoreMetrics(rev, cos, debt, formData.segment, {
         custoNaoRecorrente: parseCurrencyToNumber(formData.custoNaoRecorrente),
         custoDivida: formData.custoDivida
      });
      const extended = calculateExtendedMetrics({
 sector: formData.segment,
         rev, cos, ebitda: core.ebitdaAjustado, alavancagem: report.alavancagem, currentMargin: Number(report.currentMargin),
         prazoRecebimento: formData.prazoRecebimento, prazoPagamento: formData.prazoPagamento,
         dividaCurtoPrazo: parseCurrencyToNumber(formData.dividaCurtoPrazo)
      });

      return {
         multiple: core.multiple,
         enterpriseValue: core.enterpriseValue,
         equityValue: core.equityValue,
         maScore: extended.maScore,
         safeDividends: extended.safeDividends,
         ncg: extended.ncg,
         emergencyReserve: extended.emergencyReserve,
         ruptureRisk: core.ruptureRisk,
         coberturaCurtoPrazo: extended.coberturaCurtoPrazo
      };
   };

   // ==========================================
   // FINANCE E APEX STATES
   // ==========================================
   const finance = getFinancialMetrics();

   // 🔥 ESTADOS DO APEX (DEVEM VIR DEPOIS DO FINANCE)
   const [apexMultiple, setApexMultiple] = useState(finance?.multiple || 5.5);
   const [apexWacc, setApexWacc] = useState(12.5);

   // Atualiza apexMultiple quando finance mudar (ex: mudança de segmento)
   useEffect(() => {
      if (finance?.multiple) {
         setApexMultiple(finance.multiple);
      }
   }, [finance?.multiple]);
   const usaTomTecnico = formData?.userType === 'Consultor' || formData?.expertise === 'Avançado';

   // 🔥 CORREÇÃO: o PDF do app estava usando SEMPRE o parecer estático
   // (gerarInsights), mesmo quando o parecer real da IA já existia e
   // estava sendo mostrado certinho na tela (parecerIATexto). Os dois
   // Dashboards já tinham sido corrigidos numa sessão anterior — esse
   // era o único lugar que ainda faltava, por isso o PDF gerado pelo
   // app mostrava um texto genérico completamente diferente do que
   // aparecia na tela pro mesmo cliente.
   // Converte o texto real da IA (com "## títulos") no mesmo formato
   // {t, d} que gerarInsights sempre produziu — assim entra no PDF
   // usando o layout já pronto, sem precisar redesenhar nada. Idêntico
   // ao parser usado nos dois Dashboards, de propósito.
   const parecerIAParaBlocosPdf = (texto) => {
      if (!texto) return null;
      const linhas = texto.split('\n').map(l => l.trim()).filter(l => l && l !== '---');
      const blocos = [];
      let atual = null;
      linhas.forEach((l) => {
         if (l.startsWith('## ')) {
            if (atual && atual.d) blocos.push(atual);
            atual = { t: l.replace(/^##\s*/, '').replace(/\*\*/g, ''), d: '' };
         } else if (l.startsWith('# ')) {
            // ignorado — o layout do PDF já tem seus próprios títulos de página.
         } else if (atual) {
            const textoLimpo = l.replace(/\*\*/g, '');
            atual.d = atual.d ? `${atual.d} ${textoLimpo}` : textoLimpo;
         }
      });
      if (atual && atual.d) blocos.push(atual);
      return blocos.length > 0 ? blocos : null;
   };

   const getDynamicInsights = () => {
      return parecerIAParaBlocosPdf(parecerIATexto) || gerarInsights(report, formData, usaTomTecnico);
   };

   const dynamicInsights = getDynamicInsights();

   const landingProtocols = [
      { id: 'tactical', name: 'Protocolo Tactical', price: '189,00', color: 'lime', textSize: 'text-5xl', features: ['IA Estocástica — Projeção de 3 e 6 Meses', 'Diagnósticos Ilimitados', 'Radar de Fluxo de Caixa D+0', 'Simulador de Turnaround', 'Metas Ideais de IA'] },
      { id: 'plus', name: 'Protocolo Plus', price: '499,00', color: 'lime', textSize: 'text-5xl', features: ['Tudo do Tactical + IA Estocástica 12 Meses', 'CFO Virtual Preditivo', 'Risco de Ruptura de Caixa', 'Cálculo de Saque Seguro (Sócios)', 'Reserva de Emergência Ideal'] },
      { id: 'total', name: 'Protocolo Total', price: '799,00', color: 'purple', textSize: 'text-4xl', features: ['Tudo do Plano Plus + IA Estocástica 24 e 36 Meses', 'Guia de Aumento de Valor', 'Super Relatório 360º Grátis', 'Checklist Governança Corporativa'] }
   ];

   const faqData = [
      {
         q: "O que é o Phoenix OS® e para quem é indicado?",
         a: "O Phoenix OS® é um sistema de inteligência executiva que atende tanto Sócios/Gestores (para diagnóstico da própria empresa) quanto Consultores/M&A (para gestão de múltiplos clientes). Ele entrega análises financeiras profundas, simulações de turnaround e valuation em tempo real."
      },
      {
         q: "Qual a diferença entre o perfil Gestor e o perfil Consultor?",
         a: "O perfil Gestor foca na sua própria empresa, com abas de Visão, Operação, CFO Virtual e M&A para um único CNPJ. O perfil Consultor é multi-tenant: gerencia até 5, 15 ou ilimitados CNPJs, com módulo APEX de valuation, white-label e IA preditiva."
      },
      {
         q: "Quais são os planos disponíveis?",
         a: "Para Gestor: Entry (R$ 49,90 diagnóstico único), Tactical (R$ 189/mês), Plus (R$ 499/mês) e Total (R$ 799/mês). Para Consultor: Advisor PRO (R$ 499/mês — Carteira de até 5 CNPJs + APEX ao vivo), Boutique Elite (R$ 1.997/mês — até 15 CNPJs + Matriz de Sensibilidade) e Enterprise Ultra (R$ 3.997/mês — CNPJs ilimitados + IA Preditiva + White-Label)."
      },
      {
         q: "Como funciona o módulo APEX (DCF Engine)?",
         a: "O APEX é o motor de valuation do Phoenix OS®. Ele calcula o Enterprise Value e Equity Value baseado no EBITDA e múltiplos setoriais. No plano Advisor PRO+, você pode ajustar múltiplo e risco (WACC) ao vivo para simular cenários de M&A com o cliente."
      },
      {
         q: "O que é o White-Label e como funciona?",
         a: "Exclusivo do plano Enterprise Ultra (R$ 3.997/mês), o White-Label permite personalizar os relatórios com o nome, logo e cores da sua consultoria, entregando laudos com sua própria marca diretamente para os clientes."
      },
      {
         q: "Meus dados ficam salvos na nuvem?",
         a: "Sim! Planos a partir de R$ 189/mês (Tactical para Gestor ou Advisor PRO para Consultor) incluem conta permanente na nuvem. Você pode acessar seus relatórios de qualquer dispositivo e ter histórico completo. O plano Entry (R$ 49,90) é apenas diagnóstico único sem login."
      },
      {
         q: "O que acontece com meus relatórios se eu cancelar o plano?",
         a: "Você mantém o acesso até o fim do período que já pagou. Depois disso: os relatórios que você comprou pelo Entry (R$ 49,90) continuam disponíveis para sempre; os relatórios gerados durante o plano (Tactical, Plus, Total, Advisor PRO, Boutique Elite ou Enterprise Ultra) ficam bloqueados, mas nada é apagado — eles voltam a abrir assim que você assinar de novo. O painel no computador também deixa de abrir enquanto não houver plano ativo."
      },
      {
         q: "O Phoenix OS® substitui meu contador ou auditor?",
         a: "Não. O Phoenix OS® é uma ferramenta de apoio à decisão executiva. Suas projeções e pareceres gerados por IA servem como bússola estratégica para proteger seu patrimônio e preparar sua empresa para M&A, mas não substituem pareceres de auditorias ou contábeis oficiais."
      }
   ];

   const getBadgeStyle = (plan) => {
      if (plan === 'TOTAL') return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      if (plan === 'PLUS' || plan === 'TACTICAL') return 'bg-lime-400/20 text-lime-400 border-lime-400/30';
      return 'bg-slate-800 text-slate-300 border-white/10';
   };

   const companyNameText = formData.companyName || 'Empresa Não Informada';
   const nameSizeClass = companyNameText.length > 20 ? 'text-2xl' : companyNameText.length > 12 ? 'text-3xl' : 'text-4xl';

   const getDynamicTextSize = (textStr) => {
      const len = textStr?.toString().length || 0;
      if (len > 26) return 'text-base';
      if (len > 22) return 'text-lg';
      if (len > 18) return 'text-xl';
      if (len > 14) return 'text-2xl';
      if (len > 10) return 'text-3xl';
      return 'text-4xl';
   };

   const ebitdaStrFormat = `R$ ${formatCurrency(report.ebitda)}`;
   const projEbitdaStrFormat = `R$ ${formatCurrency(report.projectedEbitda)}`;
   const projAnualStrFormat = `+ R$ ${formatCurrency(report.gains * 12)}`;

   const equityStrFormat = `R$ ${formatCurrency(finance.equityValue)}`;
   const evStrFormat = `R$ ${formatCurrency(finance.enterpriseValue)}`;
   const debtStrFormat = `R$ ${formatCurrency(parseCurrencyToNumber(formData.debt))}`;
   const divStrFormat = `R$ ${formatCurrency(finance.safeDividends)}`;
   const reserveStrFormat = `R$ ${formatCurrency(finance.emergencyReserve)}`;
   const ebitdaAjustadoStrFormat = `R$ ${formatCurrency(report.ebitdaAjustado)}`;
   // 🔥 CAIXA MÍNIMO — usa o pró-labore, informado no cadastro mas nunca
   // usado em nenhum cálculo. Mesma tela do CFO Virtual, ao lado da
   // Reserva de Emergência.
   const caixaMinimoValor = parseCurrencyToNumber(formData.costs) + parseCurrencyToNumber(formData.proLabore);
   const caixaMinimoStrFormat = `R$ ${formatCurrency(caixaMinimoValor)}`;
   const ncgStrFormat = `R$ ${formatCurrency(Math.abs(finance.ncg))}`;
   const custoServicoDividaStrFormat = `R$ ${formatCurrency(report.custoServicoDivida)}`;

   const TAB_LEVELS = { 'visao': 1, 'operacao': 2, 'cfo': 3, 'ma': 4 };
   const gestorAccessLevel = gestorPlan && gestorPlan !== 'entry'
      ? ({ 'tactical': 2, 'plus': 3, 'total': 4, 'boardroom': 4 }[gestorPlan] || 1)
      : 1;

   // 🔥 CORREÇÃO: o card de upsell ("Assine o Plus") usava só o plano salvo
   // DENTRO do dossiê (activePlan) — se uma empresa foi criada antes da
   // conta virar Total, ela pode ter ficado com um plano antigo "congelado"
   // nela, mesmo a conta já sendo Total de verdade. planoEfetivoGestor pega
   // sempre o MAIOR entre o plano da conta (gestorPlan) e o do dossiê
   // (activePlan), pra nunca mostrar upsell indevido pra quem já pagou.
   const ORDEM_PLANO_GESTOR = { entry: 1, tactical: 2, plus: 3, total: 4 };
   const planoEfetivoGestor = (ORDEM_PLANO_GESTOR[gestorPlan] || 1) >= (ORDEM_PLANO_GESTOR[activePlan] || 1)
      ? (gestorPlan || 'entry')
      : activePlan;

   // ==========================================
   // FUNÇÃO PARA MAPEAR O PLANO DO USUÁRIO
   // ==========================================
   const getUserPlanLevel = () => {
      if (activePlan === 'advisor') return 'advisor';
      if (activePlan === 'boutique') return 'boutique';
      if (activePlan === 'enterprise') return 'enterprise';
      if (activePlan === 'free' || activePlan === 'entry') return 'free';
      if (activePlan === 'tactical') return 'tactical';
      if (activePlan === 'plus') return 'plus';
      if (activePlan === 'total') return 'total';
      return 'free';
   };

   // Verifica se o usuário já tem um plano ativo (que não seja entry/free)
   const hasActiveConsultorPlan = () => {
      const plan = activePlan || localStorage.getItem('phoenix_consultor_plan');
      return plan && plan !== 'entry' && plan !== 'free';
   };
   // ==========================================
   // RENDERIZADOR SEGURO DE PAYWALL (B2C) - VITRINE PREMIUM
   // ==========================================
   const renderPaywall = (tabName) => {
      // 1. Dicionário exato e blindado de planos por aba (Fim do pulo de abas)
      let plan = {};

      // 🔥 CORRIGIDO (2026-09-29) — mesmo ajuste de copy da aba Visão: em vez
      // de 3 nomes técnicos em CAIXA ALTA, mostra TODOS os benefícios de
      // cada plano como "benefício em linguagem simples (termo técnico)" —
      // o público-alvo (pequeno gestor) precisa entender o valor, não só o
      // nome do recurso.
      // 🔥 CORRIGIDO (2026-09-29): os horizontes de projeção da IA
      // Estocástica são cumulativos e travados por plano (ver o bloco de
      // Projeção mais abaixo, 'planoNecessario' de cada prazo): Tactical
      // libera só Curto Prazo (3 e 6 meses); Plus ADICIONA o Médio Prazo
      // (12 meses); Total ADICIONA o Longo Prazo (24 e 36 meses). A copy
      // tinha ficado incorreta (dizia "12 meses" já no Tactical) — corrigido
      // pra não prometer um horizonte que aquele plano não libera.
      if (tabName === 'operacao') {
         plan = {
            id: 'tactical', name: 'Protocolo Tactical', price: '189,00', level: 2,
            color: 'text-lime-400', bg: 'bg-lime-400', btnText: 'text-black', shadow: 'shadow-[0_0_25px_rgba(190,242,100,0.2)]',
            features: [
               'Diagnósticos sem limite (Ilimitados)',
               'Acesso ao Dashboard pelo computador',
               'Projeção de 3 e 6 meses (IA Estocástica)',
               'Se o caixa vai apertar (Radar D+0)',
               'Simule uma virada de jogo (Turnaround)',
               'Metas calculadas pela IA (Metas Ideais)',
               'Histórico salvo na nuvem'
            ]
         };
      } else if (tabName === 'cfo') {
         plan = {
            id: 'plus', name: 'Protocolo Plus', price: '499,00', level: 3,
            color: 'text-lime-400', bg: 'bg-lime-400', btnText: 'text-black', shadow: 'shadow-[0_0_25px_rgba(190,242,100,0.2)]',
            previousPlan: 'Tactical',
            features: [
               'Um CFO disponível 24h (CFO Virtual)',
               'Se vai faltar caixa (Risco de Ruptura)',
               'Quanto sacar sem sufocar (Saque Seguro)',
               'Reserva ideal (Reserva de Emergência)',
               'Projeção +12 meses (IA Estocástica)'
            ]
         };
      } else {
         // Fallback para Boardroom / M&A
         plan = {
            id: 'total', name: 'Protocolo Total', price: '799,00', level: 4,
            color: 'text-purple-400', bg: 'bg-purple-600', btnText: 'text-white', shadow: 'shadow-[0_0_25px_rgba(168,85,247,0.25)]',
            previousPlan: 'Tactical e Plus',
            features: [
               'Projeção +24 e 36 meses (IA Estocástica)',
               'Relatório premium de graça, todo mês (Super 360º)',
               'Passo a passo pra valer mais (Guia de Valor)',
               'Quanto vale sua empresa hoje (Valuation Real-Time)',
               'Pronta pra vender ou receber investimento? (M&A Score)',
               'Organize a governança antes de negociar (Checklist)'
            ]
         };
      }

      // 2. Interface Premium para Instigar a Assinatura (CRO Otimizado)
      return (
         <div className="flex flex-col items-center justify-center py-6 px-2 animate-fade-up">
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/5 p-8 rounded-[3rem] w-full max-w-sm space-y-8 text-center shadow-2xl relative overflow-hidden group">

               {/* Fundo Glow Volumétrico */}
               <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full blur-[60px] opacity-20 pointer-events-none ${plan.bg}`}></div>

               <div className="flex flex-col items-center relative z-10">
                  <div className={`w-16 h-16 bg-[#020617] rounded-3xl flex items-center justify-center mb-5 shadow-inner border border-white/5`}>
                     <Lock size={28} className={plan.color} />
                  </div>

                  <span className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest mb-4 border border-white/10 ${plan.color} bg-white/5`}>
                     Módulo Restrito • Nível {plan.level}
                  </span>

                  <h3 className="text-3xl font-black text-white italic uppercase tracking-tighter leading-none mb-2">
                     {plan.name}
                  </h3>
                  <p className="text-[11px] text-slate-400 tracking-normal leading-relaxed mb-6 px-4">
                     Desbloqueie esta aba para acessar a inteligência executiva.
                  </p>

                  <div className={`${plan.bg} ${plan.btnText} px-6 py-5 rounded-[2rem] ${plan.shadow} flex flex-col items-center w-full mb-6 transform transition-transform hover:scale-105`}>
                     <div className="flex items-baseline gap-1">
                        <span className="font-black text-xl italic uppercase">R$</span>
                        <span className="font-black text-5xl tracking-tighter italic leading-none">{plan.price}</span>
                     </div>
                     <span className="text-[11px] font-black uppercase opacity-70 italic mt-2 tracking-widest">Assinatura Mensal</span>
                  </div>
               </div>

               {plan.previousPlan && (
                  <p className={`text-[11px] font-black uppercase tracking-widest ${plan.color} text-left w-full border-t border-white/5 pt-6 -mb-3.5`}>Tudo do {plan.previousPlan}, mais:</p>
               )}
               <ul className={`space-y-2.5 w-full text-left relative z-10 ${plan.previousPlan ? 'pt-5' : 'border-t border-white/5 pt-6'}`}>
                  {plan.features.map((feature, i) => {
                     // Separa "texto simples (Termo Técnico)" pra destacar
                     // o termo técnico em cinza, igual aos cards da aba Visão.
                     const match = feature.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
                     return (
                        <li key={i} className="flex items-center gap-2 text-[11px] font-bold text-slate-300 whitespace-nowrap overflow-hidden text-ellipsis">
                           <span className={plan.color}>✓</span>
                           {match ? (
                              <span className="overflow-hidden text-ellipsis">{match[1]} <span className="text-slate-500 normal-case">({match[2]})</span></span>
                           ) : (
                              <span className="overflow-hidden text-ellipsis">{feature}</span>
                           )}
                        </li>
                     );
                  })}
               </ul>

               <button
                  onClick={() => handleProtocolSelection(plan.id, plan.name, plan.price, 'B2C')}
                  className={`w-full ${plan.id === 'total' ? 'bg-purple-600 text-white' : 'bg-white text-black'} py-6 rounded-full font-black uppercase text-[11px] shadow-xl active:scale-95 transition-all relative z-10 mt-2 flex items-center justify-center gap-2`}
               >
                  <Lock size={14} /> Liberar Acesso Imediato
               </button>
            </div>
         </div>
      );
   };
   // ==========================================
   // RECUPERAÇÃO DE DOSSIÊS PERDIDOS
   // Reconstrói a "lista-índice" (Meus Dossiês) a partir dos dados brutos que
   // sobreviveram na coleção 'cenarios' — para os casos em que um dossiê
   // sumiu da lista (ex: corrompido pelo bug antigo do safeStringify) mas o
   // dado financeiro em si continua salvo no Firestore.
   // ==========================================
   const recuperarDossiesPerdidos = async (silencioso = false) => {
      if (!user?.uid) return;
      setRecuperando(true);
      try {
         const q = query(collection(db, 'cenarios'), where('userId', '==', user.uid));
         const snap = await getDocs(q);

         const normalizar = (str) => String(str || '').trim().toLowerCase();
         // A chave inclui se o dossiê é Entry ou de plano: o relatório Entry pago e a versão gerada pelo plano (mesma
         // empresa) são dossiês DIFERENTES e precisam aparecer os dois. Sem isso, num aparelho novo só a versão mais
         // recente aparecia e o Entry pago ficava escondido (e inacessível depois de cancelar o plano).
         const chaveDe = (nome, tipo, plano) => `${normalizar(nome)}|${normalizar(tipo || 'gestor')}|${normalizar(plano) === 'entry' ? 'entry' : 'plano'}`;

         // 🔥 CORREÇÃO: antes só verificava "mesma empresa + mesmo perfil" pelo
         // NOME — mas aí um dossiê renomeado pelo Dashboard deixava de bater
         // com a chave antiga (nome mudou), essa função achava que era um
         // dossiê "novo" e reintroduzia uma cópia com o ID igual só que com o
         // nome ANTIGO (o que sobrescrevia a correção que acabou de chegar
         // pelo listener ao vivo). Agora verifica por ID primeiro (nunca
         // muda, é sempre confiável) — o nome só entra como critério extra
         // pra pegar duplicatas de sessões diferentes (IDs diferentes).
         // Cura rótulos errados: dossiê Entry (ou 360 avulso) que ficou como "TACTICAL" na lista local/backup (efeito antigo
         // que alinhava tudo ao plano atual) volta ao rótulo real gravado no documento.
         const rotuloRealPorId = new Map();
         snap.forEach(d => { const pl = String(d.data().plan || '').toUpperCase(); if (pl === 'ENTRY' || pl === 'AVULSO_360') rotuloRealPorId.set(d.id, pl); });
         if (rotuloRealPorId.size > 0) {
            setHistoryList(prev => prev.map(i => (rotuloRealPorId.has(i.id) && i.plan !== rotuloRealPorId.get(i.id)) ? { ...i, plan: rotuloRealPorId.get(i.id) } : i));
         }

         const idsJaNaLista = new Set(historyList.map(i => i.id));
         const chavesJaNaLista = new Set(
            historyList.map(i => chaveDe(i.name, i.snapshot?.userType, i.plan))
         );

         // Candidatos encontrados no Firestore que ainda não estão na lista local
         const candidatos = [];
         snap.forEach(docSnap => {
            const dados = docSnap.data();
            const origem = dados.snapshot?.formData || dados; // formato "solto" (Gestor) ou aninhado (Consultor)
            const userType = String(dados.userType || dados.snapshot?.userType || origem.userType || 'Gestor');
            const nome = origem.companyName || dados.name || 'Empresa Recuperada';
            const chave = chaveDe(nome, userType, dados.plan);
            if (idsJaNaLista.has(docSnap.id) || chavesJaNaLista.has(chave)) return; // já existe na lista local, ignora

            const isConsultor = userType.toLowerCase() === 'consultor';
            // 🔥 CORRIGIDO — VULNERABILIDADE CRÍTICA (2026-09-29, 4ª causa raiz
            // encontrada): pra Gestor, esta função IGNORAVA o campo real
            // `dados.plan` (gravado como 'ENTRY' no dossiê avulso desde o
            // momento em que a cobrança é gerada) e "adivinhava" o rótulo só
            // pelo `expertise` — um dossiê Entry sem pagamento confirmado
            // virava 'TACTICAL'/'TOTAL' na lista "Meus Dossiês". Como
            // `loadDossier` só reverifica o pagamento quando o rótulo é
            // exatamente 'entry', esse dossiê "recuperado" abria o relatório
            // completo direto, sem nunca checar `statusPagamento`. Esta
            // função roda sozinha, em segundo plano, toda vez que a pessoa
            // loga — inclusive logo depois de criar conta a partir de uma
            // sessão anônima que já tinha gerado uma cobrança Entry (mesmo
            // uid, via linkWithCredential), que é exatamente o caminho
            // usado pra burlar o pagamento. Agora usa o rótulo real salvo no
            // Firestore sempre que existir; o palpite por `expertise` só
            // entra pra dossiês antigos, de antes deste campo existir.
            const planoRotulo = isConsultor
               ? (dados.plan || 'enterprise').toUpperCase()
               : String(dados.plan || (origem.expertise === 'Avançado' ? 'TOTAL' : 'TACTICAL')).toUpperCase();
            const dataOrdenacao = dados.dataCriacao || dados.timestamp || '';

            candidatos.push({
               id: docSnap.id,
               name: nome,
               date: dados.dataCriacao ? new Date(dados.dataCriacao).toLocaleDateString('pt-BR') : (dados.date || '-'),
               plan: planoRotulo,
               // 🔥 NOVO (2026-09-29): recupera o e-mail digitado no checkout
               // (salvo no dossiê desde a criação da cobrança avulsa) — é o
               // que o convite pra criar conta sugere, mesmo depois do app
               // reiniciar e perder qualquer estado em memória.
               emailComprador: dados.emailComprador || null,
               snapshot: {
                  formData: { ...origem, userType },
                  userType
               },
               _chave: chave,
               _dataOrdenacao: dataOrdenacao
            });
         });

         // 🔥 Entre os candidatos, se houver mais de uma "versão" da mesma
         // empresa (vários salvamentos antigos no Firestore), traz só a mais
         // recente — evita reimportar várias cópias da mesma coisa de uma vez.
         const maisRecentePorChave = new Map();
         candidatos.forEach(item => {
            const existente = maisRecentePorChave.get(item._chave);
            if (!existente || item._dataOrdenacao > existente._dataOrdenacao) {
               maisRecentePorChave.set(item._chave, item);
            }
         });
         const recuperados = Array.from(maisRecentePorChave.values()).map(({ _chave, _dataOrdenacao, ...resto }) => resto);

         if (recuperados.length > 0) {
            setHistoryList(prev => dedupeById([...prev, ...recuperados]));
         }
         // 🔥 CORREÇÃO: a sincronização agora roda sozinha, em segundo plano,
         // várias vezes (login, troca de tela, etc.) — mostrar o toast "tudo
         // certo, já está sincronizado" toda vez ficaria repetitivo e
         // incômodo. Só avisa quando encontra algo de verdade pra trazer, ou
         // quando é um clique manual de verdade (silencioso=false).
         if (recuperados.length > 0) {
            setToastMessage(`${recuperados.length} dossiê(s) sincronizado(s) com sucesso!`);
         } else if (!silencioso) {
            setToastMessage('Tudo certo — sua lista já está sincronizada com a nuvem.');
         }
         setUltimaSincronizacao(new Date());
      } catch (e) {
         console.error('[PHOENIX] Erro ao recuperar dossiês:', e);
         if (!silencioso) setToastMessage('Não foi possível sincronizar agora. Tenta de novo em instantes.');
      } finally {
         setRecuperando(false);
      }
   };

   // ==========================================
   // 🔄 SINCRONIA AUTOMÁTICA — antes precisava clicar em "Sincronizar com a
   // Nuvem" manualmente; a maioria das pessoas nunca lembraria de fazer
   // isso, e ficaria sem ver dossiês que só existem no Firestore (ex:
   // criados pelo Dashboard). Agora roda sozinha: uma vez ao entrar
   // (login/sessão restaurada) e de novo sempre que a pessoa abre a tela
   // de Histórico — silenciosa, sem toast de "tudo certo" repetitivo.
   // ==========================================
   useEffect(() => {
      if (!user?.uid) return;
      recuperarDossiesPerdidos(true);
      // eslint-disable-next-line
   }, [user?.uid]);

   useEffect(() => {
      if (view === 'history' && user?.uid) {
         recuperarDossiesPerdidos(true);
      }
      // eslint-disable-next-line
   }, [view]);

   // TELA DE SPLASH — cobre a tela inteira, sem cabeçalho nem menu, só a
   // marca. Sai sozinha depois de ~1,8s (efeito acima).
   if (view === 'splash') {
      return (
         <div className="w-full h-screen bg-[#010208] flex justify-center overflow-hidden relative z-10">
            <div className="w-full max-w-md h-screen bg-[#020617] relative flex flex-col items-center justify-center border-x border-white/5 shadow-[0_0_150px_rgba(0,0,0,1)]">
               <GlobalStyles />
               <CyberBackground isMobileView={true} />
               <div className="relative z-10 flex flex-col items-center">
                  {/* O ESCUDO É O PRÓPRIO CARREGAMENTO — em vez de uma barra
                      de progresso separada, o ícone da marca "respira"
                      (pulsa) enquanto a splash está na tela. Comunica
                      "carregando" sem precisar de mais nenhum elemento. */}
                  <div className="animate-splash-shield mb-6">
                     <IconShieldBig size={88} />
                  </div>
                  <span className="text-white font-black italic tracking-tighter text-6xl uppercase animate-splash-logo">
                     PHOENIX<span className="text-lime-400">OS</span><sup className="text-sm ml-1">®</sup>
                  </span>
                  <p className="text-[11px] text-slate-500 font-black uppercase tracking-[0.35em] mt-3 text-center max-w-[240px]">
                     A Inteligência Por Trás da Sua Virada
                  </p>
               </div>
            </div>
         </div>
      );
   }

   return (
      <div className="w-full h-screen bg-[#010208] flex justify-center overflow-hidden text-left relative z-10">

         <div className="w-full max-w-md h-screen bg-[#020617] relative flex flex-col border-x border-white/5 shadow-[0_0_150px_rgba(0,0,0,1)]">
            {/* MODAL CRIAÇÃO DE CONTA GESTOR */}
            {/* AVISO: acesso ao Painel Desktop liberado com o mesmo login.
                Fica visível até a pessoa fechar de propósito — diferente
                do toast rápido, essa é uma informação importante que não
                pode passar batido. */}
            {/* MODAL DE META FINANCEIRA — mesmo campo do Firestore que o
                Dashboard usa, sincronizado automaticamente. */}
            {editandoMetaApp && (
               <div className="absolute inset-0 z-[9999] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm">
                  <div className="bg-[#0f172a] border border-lime-400/30 rounded-3xl p-7 w-full max-w-xs shadow-2xl">
                     <div className="flex items-center justify-between mb-5">
                        <h3 className="text-base font-black text-white uppercase italic tracking-tight">Definir Meta</h3>
                        <button onClick={() => setEditandoMetaApp(false)} className="text-slate-500 hover:text-white transition-colors"><X size={18} /></button>
                     </div>
                     <div className="grid grid-cols-2 gap-2 mb-4">
                        <button onClick={() => setMetaTipoFormApp('faturamento')} className={`py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${metaTipoFormApp === 'faturamento' ? 'bg-lime-400 text-black' : 'bg-white/5 text-slate-400 border border-white/10'}`}>Faturamento</button>
                        <button onClick={() => setMetaTipoFormApp('lucro')} className={`py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${metaTipoFormApp === 'lucro' ? 'bg-lime-400 text-black' : 'bg-white/5 text-slate-400 border border-white/10'}`}>Lucro</button>
                     </div>
                     <input type="text" inputMode="numeric" value={metaValorFormApp}
                        onChange={(e) => {
                           const apenasNumeros = e.target.value.replace(/\D/g, '');
                           if (apenasNumeros === '') { setMetaValorFormApp(''); return; }
                           setMetaValorFormApp((parseInt(apenasNumeros, 10) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                        }}
                        placeholder="Valor alvo (R$/mês)"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 mb-5" />
                     <button onClick={salvarMetaApp} disabled={!metaValorFormApp} className="w-full bg-lime-400 text-black py-3.5 rounded-2xl font-black uppercase text-xs tracking-widest disabled:opacity-50">Salvar Meta</button>
                  </div>
               </div>
            )}

            {showDashboardUnlockedNotice && (
               <div className="absolute inset-0 z-[9999] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm">
                  <div className="bg-[#0f172a] border border-lime-400/30 rounded-3xl p-7 w-full max-w-sm shadow-2xl animate-fade-up text-center">
                     {/* Indicador de etapa — fecha a sequência do checkout. */}
                     <div className="flex items-center justify-center gap-2 mb-5">
                        <span className="text-[11px] font-black uppercase tracking-widest text-lime-400">Etapa 3 de 3</span>
                        <div className="flex gap-1.5">
                           <div className="w-6 h-1 rounded-full bg-lime-400"></div>
                           <div className="w-6 h-1 rounded-full bg-lime-400"></div>
                           <div className="w-6 h-1 rounded-full bg-lime-400"></div>
                        </div>
                     </div>
                     <div className="w-14 h-14 bg-lime-400/10 rounded-2xl flex items-center justify-center border border-lime-400/20 mx-auto mb-4">
                        <Monitor size={24} className="text-lime-400" />
                     </div>
                     <h2 className="text-lg font-black text-white uppercase italic tracking-tight">
                        Seu <span className="text-lime-400">Painel Desktop</span> já está liberado
                     </h2>
                     <p className="text-xs text-slate-400 mt-3 leading-relaxed">
                        {nomeParaAvisoDashboard ? `${nomeParaAvisoDashboard}, agora` : 'Agora'} você também pode acompanhar sua empresa pelo computador — é só entrar com o <strong className="text-white">mesmo e-mail e senha</strong> que você acabou de usar aqui no app.
                     </p>
                     <div className="flex flex-col gap-3 mt-6">
                        <button
                           onClick={() => {
                              setShowDashboardUnlockedNotice(false);
                              if (typeof onOpenDashboardGestor === 'function') onOpenDashboardGestor();
                           }}
                           className="w-full bg-lime-400 text-black py-3.5 rounded-2xl font-black uppercase text-xs tracking-widest hover:scale-[1.02] transition-all"
                        >
                           Abrir Painel Desktop
                        </button>
                        <button
                           onClick={() => setShowDashboardUnlockedNotice(false)}
                           className="w-full text-slate-500 hover:text-white text-xs font-bold uppercase tracking-widest transition-colors"
                        >
                           Continuar no App
                        </button>
                     </div>
                  </div>
               </div>
            )}

            {showGestorCreateAccount && (
               <div className="absolute inset-0 z-[9999] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm">
                  <div className="bg-[#0f172a] border border-lime-400/30 rounded-3xl p-7 w-full max-w-sm shadow-2xl animate-fade-up">

                     {/* Indicador de etapa — mesma sequência do checkout. */}
                     <div className="flex items-center justify-center gap-2 mb-5">
                        <span className="text-[11px] font-black uppercase tracking-widest text-lime-400">Etapa 2 de 3</span>
                        <div className="flex gap-1.5">
                           <div className="w-6 h-1 rounded-full bg-lime-400"></div>
                           <div className="w-6 h-1 rounded-full bg-lime-400"></div>
                           <div className="w-6 h-1 rounded-full bg-white/10"></div>
                        </div>
                     </div>

                     <div className="w-14 h-14 bg-lime-400/10 rounded-2xl flex items-center justify-center border border-lime-400/20 mx-auto mb-4">
                        <Shield size={24} className="text-lime-400" />
                     </div>
                     <h2 className="text-xl font-black text-white uppercase italic tracking-tight text-center">
                        {gestorAccountMode === 'create' ? (
                           <>Crie sua <span className="text-lime-400">Conta</span></>
                        ) : (
                           <>Fazer <span className="text-lime-400">Login</span></>
                        )}
                     </h2>
                     {/* 🔥 CORRIGIDO: dizia "Plano X ativado" mesmo pra quem tava só
                         criando conta nova (sem nenhum pagamento verificado) — texto
                         enganoso, removido junto com a vulnerabilidade que ele descrevia. */}
                     <p className="text-[11px] text-slate-500 uppercase tracking-widest mt-1 text-center mb-6">
                        {gestorAccountMode === 'create'
                           ? 'Já pagou pelo plano antes? Faça login em vez de criar conta nova.'
                           : `Entre na sua conta pra continuar com o plano ${pendingGestorPlan?.name || ''}`}
                     </p>

                     {/* AVISO: 1 empresa por Gestor */}
                     <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-3 mb-5">
                        <p className="text-[11px] text-amber-400 font-bold leading-relaxed">
                           ⚠️ O perfil Gestor gerencia <strong>1 empresa</strong>. Para gerenciar filiais ou múltiplos CNPJs, use o perfil <strong>Consultor</strong>.
                        </p>
                     </div>

                     {gestorAccountMode === 'create' ? (
                        <form onSubmit={async (e) => {
                           e.preventDefault();
                           const nome = e.target.nome.value.trim();
                           const email = e.target.email.value.trim();
                           const senha = e.target.senha.value;
                           if (!nome || !email || senha.length < 6) {
                              setToastMessage('Preencha todos os campos. Senha mínima: 6 caracteres.');
                              return;
                           }
                           if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                              setToastMessage('Digite um e-mail válido (ex: nome@empresa.com).');
                              return;
                           }
                           try {
                              // 🔥 CORRIGIDO — VULNERABILIDADE CRÍTICA (achada em teste real com
                              // Asaas de produção, 2026-09-28): este código criava a conta e
                              // gravava `plan: pendingGestorPlan?.id` DIRETO do navegador, sem
                              // NENHUMA verificação de pagamento real. `pendingGestorPlan` vem de
                              // `gestorPlan`, que por sua vez pode vir só do localStorage — ou
                              // seja, bastava fechar a cobrança sem pagar (ou só editar o
                              // localStorage no DevTools) e criar conta que o plano pago era
                              // liberado de graça. Viola o princípio central do projeto: só a
                              // Cloud Function `webhookAsaas` (servidor, depois que o Asaas
                              // confirma o dinheiro de verdade) pode escrever o campo `plan`.
                              // Agora a conta sempre nasce SEM plano — quem realmente pagou
                              // antes já tem uma conta de verdade com o plano correto gravado
                              // pelo webhook, e deve usar "Fazer Login", não "Criar conta".
                              skipNextLoadRef.current = true;   // 🔥 liga a trava ANTES da chamada ao Firebase
                              const cred = await createUserWithEmailAndPassword(auth, email, senha);
                              registrarAceite('criar_conta', null);
                              await setDoc(doc(db, 'usuarios', cred.user.uid), {
                                 nome,
                                 email,
                                 role: 'gestor',
                                 criadoEm: new Date().toISOString()
                              });

                              localStorage.setItem('phoenix_user_name', nome);
                              localStorage.setItem('phoenix_user_type', 'gestor');
                              localStorage.removeItem('phoenix_gestor_plan');

                              // 🔥 Conta nova começa sem plano — o listener em tempo real do
                              // Firestore (useEffect mais abaixo) atualiza `gestorPlan` sozinho
                              // se/quando o webhook confirmar um pagamento de verdade pra esse uid.
                              setGestorPlan(null);
                              setActivePlan('entry');
                              setUserLevel(1);

                              setShowGestorCreateAccount(false);

                              // 🔥 Sem promoção automática de dossiês Entry pra plano pago aqui —
                              // essa "promoção" só pode acontecer de verdade via pagamento
                              // confirmado pelo webhook, nunca client-side.
                              const historyListPromovida = historyList;

                              localStorage.setItem(`phoenix_history_${cred.user.uid}`, safeStringify(historyListPromovida));
                              skipNextLoadRef.current = true;

                              if (typeof onLoginSuccess === 'function') onLoginSuccess(cred.user);

                              const temRelatorioGestor = historyList.some(item =>
                                 String(item.snapshot?.userType || item.snapshot?.formData?.userType || '').toLowerCase() !== 'consultor'
                              );
                              if (temRelatorioGestor) {
                                 // 🔥 Mesma correção: sem toast duplicado por cima da tela "Etapa 3 de 3".
                                 setNomeParaAvisoDashboard(nome);
                                 setShowDashboardUnlockedNotice(true);
                                 const dossieAtual = historyList.find(item =>
                                    String(item.snapshot?.userType || item.snapshot?.formData?.userType || '').toLowerCase() !== 'consultor'
                                 );
                                 const fd = dossieAtual?.snapshot?.formData || {};
                                 const aindaFalta = [fd.custoNaoRecorrente, fd.prazoRecebimento, fd.prazoPagamento, fd.dividaCurtoPrazo, fd.proLabore, fd.custoDivida].some(v => !v || v === '');
                                 if (aindaFalta) {
                                    setPendingCompleteDossierId(dossieAtual?.id || null);
                                    setShowCompleteDataPrompt(true);
                                 }
                                 setView('report');
                                 setReportTab('operacao');
                              } else {
                                 setToastMessage(`Bem-vindo, ${nome}! Insira os dados da sua empresa.`);
                                 setFormData({
                                    method: '', profile: '', companyName: '', segment: '', niche: '',
                                    revenue: '', costs: '', debt: '', expertise: '', userType: 'Gestor'
                                 });
                                 setSimRevenue(0);
                                 setSimCosts(0);
                                 setActiveDossierId(null);
                                 setStep(2);
                                 setView('wizard');
                              }
                           } catch (error) {
                              console.error('[PHOENIX] Erro ao criar conta:', error.code);
                              if (error.code === 'auth/email-already-in-use') {
                                 setToastMessage('Este email já possui uma conta. Faça login em vez de criar uma nova.');
                                 setGestorAccountMode('login');
                              } else {
                                 setToastMessage(mensagemErroCriarConta(error.code));
                              }
                           }
                        }} className="space-y-3">

                           <div>
                              <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1 block">Nome completo</label>
                              <input name="nome" type="text" placeholder="Seu nome"
                                 className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors" />
                           </div>

                           <div>
                              <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1 block">E-mail</label>
                              <input name="email" type="email" placeholder="voce@empresa.com"
                                 className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors" />
                           </div>

                           <div>
                              <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1 block">Senha (mín. 6 caracteres)</label>
                              <input name="senha" type="password" autoComplete="new-password" placeholder="••••••••"
                                 className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors" />
                           </div>

                           <AceiteAtivo name="aceite" required />

                           <button type="submit"
                              className="w-full bg-lime-400 text-black py-4 rounded-2xl font-black uppercase text-xs tracking-widest mt-2 active:scale-95 transition-all">
                              Criar conta e acessar
                           </button>

                           <button type="button" onClick={() => { setGestorAccountMode('login'); setGestorLoginError(''); }}
                              className="w-full text-slate-400 text-[11px] uppercase tracking-widest py-2 font-bold hover:text-lime-400 transition-colors">
                              Já tenho conta — Fazer login
                           </button>
                        </form>
                     ) : (
                        <div className="space-y-3">
                           <div>
                              <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1 block">E-mail</label>
                              <input
                                 type="email"
                                 value={gestorLoginEmail}
                                 onChange={(e) => { setGestorLoginEmail(e.target.value); setGestorLoginError(''); }}
                                 placeholder="voce@empresa.com"
                                 className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors"
                              />
                           </div>
                           <div>
                              <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1 block">Senha</label>
                              <input
                                 type="password" autoComplete="current-password"
                                 value={gestorLoginPassword}
                                 onChange={(e) => { setGestorLoginPassword(e.target.value); setGestorLoginError(''); }}
                                 placeholder="••••••••"
                                 className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors"
                              />
                           </div>
                           {gestorLoginError && (
                              <p className="text-red-400 text-[11px] font-medium text-center">{gestorLoginError}</p>
                           )}
                           <button
                              onClick={async () => {
                                 if (!gestorLoginEmail || !gestorLoginPassword) {
                                    setGestorLoginError('Preencha email e senha.');
                                    return;
                                 }
                                 setGestorLoginLoading(true);
                                 setGestorLoginError('');
                                 try {
                                    const cred = await signInWithEmailAndPassword(auth, gestorLoginEmail, gestorLoginPassword);

                                    // Busca plano real no Firestore (mesma lógica dos outros logins)
                                    try {
                                       const userDoc = await getDoc(doc(db, 'usuarios', cred.user.uid));
                                       if (userDoc.exists()) {
                                          const dados = userDoc.data();
                                          const planLevelMap = { entry: 1, tactical: 2, plus: 3, total: 4, boardroom: 4, advisor: 5, boutique: 6, enterprise: 7 };
                                          if (dados.nome) localStorage.setItem('phoenix_user_name', dados.nome);
                                          if (dados.plan) {
                                             localStorage.setItem('phoenix_gestor_plan', dados.plan);
                                             setGestorPlan(dados.plan);
                                             setActivePlan(dados.plan);
                                             setUserLevel(planLevelMap[dados.plan] || 1);
                                          }
                                          if (dados.consultorPlan) {
                                             localStorage.setItem('phoenix_consultor_plan', dados.consultorPlan);
                                             setConsultorPlan(dados.consultorPlan);
                                          }
                                       }
                                    } catch (fetchError) {
                                       console.error('[PHOENIX] Erro ao buscar dados do perfil:', fetchError);
                                    }

                                    if (typeof onLoginSuccess === 'function') onLoginSuccess(cred.user);
                                    setShowGestorCreateAccount(false);

                                    // 🔥 REMOVIDO (2026-09-30): aqui o app RENOMEAVA no cliente
                                    // todos os dossiês ENTRY da lista para o plano "pendente"
                                    // (que podia ser um Total/Tactical velho vindo do
                                    // localStorage) e avisava "Plano X ativado" — sem nenhum
                                    // pagamento. O rótulo real do dossiê vem do Firestore e o
                                    // plano da conta só é liberado pelo webhookAsaas.

                                    // "Iniciar Diagnóstico" = SEMPRE novo diagnóstico. Nunca abre relatório antigo.
                                    setToastMessage('Login realizado!');
                                    setFormData({ method: '', profile: '', companyName: '', segment: '', niche: '', revenue: '', costs: '', debt: '', expertise: '', userType: 'Gestor', custoNaoRecorrente: '', prazoRecebimento: '', prazoPagamento: '', dividaCurtoPrazo: '', proLabore: '' });
                                    setSimRevenue(0);
                                    setSimCosts(0);
                                    setActiveDossierId(null);
                                    setStep(2);
                                    setView('wizard');
                                 } catch (error) {
                                    console.error('[PHOENIX] Erro no login:', error.code);
                                    setGestorLoginError('Email ou senha inválidos.');
                                 } finally {
                                    setGestorLoginLoading(false);
                                 }
                              }}
                              disabled={gestorLoginLoading}
                              className="w-full bg-lime-400 text-black py-4 rounded-2xl font-black uppercase text-xs tracking-widest mt-2 active:scale-95 transition-all disabled:opacity-50"
                           >
                              {gestorLoginLoading ? 'Entrando...' : 'Entrar'}
                           </button>
                           <button type="button" onClick={() => { setGestorAccountMode('create'); setGestorLoginError(''); }}
                              className="w-full text-slate-400 text-[11px] uppercase tracking-widest py-2 font-bold hover:text-lime-400 transition-colors">
                              Ainda não tenho conta — Criar conta
                           </button>
                        </div>
                     )}

                     <button type="button" onClick={() => {
                        setShowGestorCreateAccount(false);
                        setGestorPlan('entry');
                        localStorage.setItem('phoenix_gestor_plan', 'entry');
                        setPendingGestorPlan(null);
                        setGestorAccountMode('create');
                        setToastMessage('Assinatura cancelada. Complete a criação de conta para ativar o plano.');
                     }}
                        className="w-full text-slate-600 text-[11px] uppercase tracking-widest py-2 font-bold mt-2">
                        Cancelar
                     </button>
                  </div>
               </div>
            )}
            {toastMessage && (
               <div className="fixed bottom-24 left-0 right-0 z-[9999] flex justify-center px-6 pointer-events-none">
                  <div className="bg-[#0f172a] border border-lime-400/30 rounded-2xl px-5 py-3.5 max-w-xs w-full shadow-2xl flex items-center gap-3 animate-fade-up pointer-events-auto">
                     <div className="w-7 h-7 shrink-0 bg-lime-400/10 rounded-full flex items-center justify-center border border-lime-400/20">
                        <span className="text-sm">✓</span>
                     </div>
                     <p className="text-white text-[11px] font-bold leading-snug break-words flex-1">{toastMessage}</p>
                  </div>
               </div>
            )}

            {/* CONFIRMAÇÃO DE PAGAMENTO — diferente do toast comum, essa
                NUNCA some sozinha. É a prova de que o dinheiro foi
                processado direito, então exige um clique consciente pra
                fechar (mesmo padrão de app de banco). */}
            {confirmacaoPagamento && (
               <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm">
                  <div className="bg-[#0f172a] border border-lime-400/40 rounded-3xl p-6 w-full max-w-xs text-center shadow-2xl animate-fade-up">
                     <div className="w-12 h-12 bg-lime-400/10 rounded-full flex items-center justify-center border border-lime-400/20 mx-auto mb-4">
                        <span className="text-2xl">✓</span>
                     </div>
                     <p className="text-white text-sm font-black uppercase tracking-wide mb-5 break-words leading-snug">{confirmacaoPagamento}</p>
                     {/* 🔥 CORRIGIDO: aqui embaixo tinha um texto FIXO dizendo
                         "Pagamento processado com sucesso" — sempre, não
                         importa qual fosse a mensagem de cima. Isso incluía
                         o momento em que só a COBRANÇA tinha sido gerada
                         (pagamento ainda nem confirmado), dando a falsa
                         impressão de que já tinha pago. Removido — a
                         mensagem de cima já é clara sozinha. */}
                     <button
                        onClick={() => {
                           setConfirmacaoPagamento('');
                           // Leva direto pro lugar certo (normalmente "Meus
                           // Dossiês"), em vez de largar a pessoa na tela de
                           // venda com a cobrança ainda aberta embaixo.
                           if (destinoAposConfirmacao) {
                              setView(destinoAposConfirmacao);
                              setDestinoAposConfirmacao(null);
                           }
                        }}
                        className="w-full py-3 rounded-2xl bg-lime-400 text-black text-xs font-black uppercase tracking-widest active:scale-95 transition-all">
                        {destinoAposConfirmacao === 'history' ? 'Ver Meus Dossiês' : 'Entendi'}
                     </button>
                  </div>
               </div>
            )}

            {itemToDelete && (
               <div className="absolute inset-0 z-[400] bg-[#010208]/90 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-200">
                  <div className="bg-[#020617] border border-white/10 p-8 rounded-[3rem] shadow-2xl w-full max-w-sm flex flex-col items-center text-center">
                     <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20 mb-6">
                        <AlertTriangle size={32} className="text-red-500" />
                     </div>
                     <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter mb-2">Excluir Dossiê?</h3>
                     <p className="text-[11px] text-slate-400 tracking-normal leading-relaxed mb-8">Esta ação é irreversível e o relatório será removido permanentemente.</p>
                     <div className="flex gap-4 w-full">
                        <button onClick={() => setItemToDelete(null)} className="flex-1 bg-white/5 text-white py-4 rounded-full font-black uppercase text-xs border border-white/10 hover:bg-white/10 transition-all">Cancelar</button>
                        <button onClick={confirmDelete} className="flex-1 bg-red-500 text-white py-4 rounded-full font-black uppercase text-xs shadow-[0_0_20px_rgba(239,68,68,0.4)] active:scale-95 transition-all">Excluir</button>
                     </div>
                  </div>
               </div>
            )}

            {/* Aviso global: a IA está atualizando o parecer (mesmo aviso do Painel). Some quando termina. */}
            {iaGerando && view !== 'splash' && view !== 'login' && view !== 'landing' && view !== 'scanning' && view !== 'ready' && (
               <div className="absolute bottom-[96px] left-1/2 -translate-x-1/2 z-[350] pointer-events-none flex items-center gap-2 bg-[#020617]/95 border border-lime-400/30 rounded-full px-4 py-2 shadow-[0_0_24px_rgba(163,230,53,0.15)]">
                  <div className="w-3 h-3 border-2 border-lime-400 border-t-transparent rounded-full animate-spin shrink-0" />
                  <span className="text-[11px] font-black text-lime-400 uppercase tracking-widest whitespace-nowrap">Atualizando parecer…</span>
               </div>
            )}

            {showDownloadConfirm && (
               <div className="absolute inset-0 z-[400] bg-[#010208]/90 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-200">
                  <div className="bg-[#020617] border border-white/10 p-8 rounded-[3rem] shadow-2xl w-full max-w-sm flex flex-col items-center text-center">
                     <div className="w-16 h-16 bg-lime-400/10 rounded-full flex items-center justify-center border border-lime-400/20 mb-6">
                        <Download size={32} className="text-lime-400" />
                     </div>
                     <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter mb-2">Exportar Relatório?</h3>
                     <p className="text-[11px] text-slate-400 tracking-normal leading-relaxed mb-8">O documento completo será compilado e baixado no seu dispositivo.</p>
                     <div className="flex gap-4 w-full">
                        <button onClick={() => setShowDownloadConfirm(false)} className="flex-1 bg-white/5 text-white py-4 rounded-full font-black uppercase text-xs border border-white/10 hover:bg-white/10 transition-all">Cancelar</button>
                        <button onClick={() => handleExportPDF(downloadConfirmEhSuper)} disabled={iaGerando} className="flex-1 disabled:opacity-50 bg-lime-400 text-black py-4 rounded-full font-black uppercase text-xs shadow-[0_0_20px_rgba(190,242,100,0.4)] active:scale-95 transition-all">Confirmar</button>
                     </div>
                  </div>
               </div>
            )}

            <GlobalStyles />
            <CyberBackground isMobileView={true} />

            <header className="shrink-0 w-full z-[100] bg-slate-950/40 backdrop-blur-xl border-b border-white/5 px-6 py-4 flex justify-between items-center min-h-20 relative" style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}>
               <span onClick={() => { setView('landing'); setStep(1); }} className="text-white font-black italic tracking-tighter text-2xl cursor-pointer uppercase">PHOENIX<span className="text-lime-400">OS</span><sup className="text-[11px] ml-0.5">®</sup></span>
               <div className="flex items-center gap-6 text-white/20 relative">
                  {/* INDICADOR DE SINCRONIZAÇÃO AUTOMÁTICA — 🔥 CORREÇÃO: as
                      duas tentativas anteriores (flutuando por cima do
                      conteúdo, depois uma segunda linha no cabeçalho)
                      tinham problema: uma sobrepunha texto, a outra fazia o
                      cabeçalho crescer e "pular" o conteúdo. Agora é só um
                      ícone pequeno, sem texto, ao lado do ícone do
                      Dashboard (computador) — cabe no espaço que já existe,
                      sem esticar nada nem sobrepor nada. */}
                  {statusAutoSave !== 'inativo' && (
                     <span title={statusAutoSave === 'salvando' ? 'Sincronizando...' : 'Dados sincronizados'}>
                        {statusAutoSave === 'salvando' ? (
                           <Loader2 size={18} className="text-slate-400 animate-spin" />
                        ) : (
                           <CheckCircle2 size={18} className="text-lime-400" />
                        )}
                     </span>
                  )}
                  {/* 🔥 CORRIGIDO: esse ícone abria um menu com "Dashboard Gestor"
                      e "Dashboard Consultor" que não iam a lugar nenhum — as
                      props onOpenDashboardGestor/onOpenDashboardConsultor nunca
                      foram passadas pelo App.js. Agora ele chama direto o
                      pareamento por QR Code (o mesmo que já existe e funciona
                      em Configurações → "Conectar Painel Desktop"), que é o
                      jeito real de abrir o Dashboard sem senha. Só aparece pra
                      quem já tem conta de verdade (sessão anônima não pareia). */}
                  {user && !user.isAnonymous && (
                     <button onClick={abrirPareamentoQRCode} className="hover:text-blue-400 transition-colors" title="Conectar Painel Desktop">
                        <Monitor size={22} />
                     </button>
                  )}
                  <button onClick={() => setView('history')} className={`hover:text-lime-400 transition-colors ${view === 'history' ? 'text-lime-400' : ''}`}><History size={22} /></button>
                  <button onClick={() => setView('profile')} className={`hover:text-lime-400 transition-colors ${view === 'profile' ? 'text-lime-400' : ''}`}><User size={22} /></button>
               </div>
            </header>

            {/* 🔥 NOVO: aviso de pagamento atrasado — fica visível toda vez
                que a pessoa abre o app durante o prazo de 48h, funcionando
                como o "lembrete diário" pedido, sem precisar de e-mail/push
                (infraestrutura que ainda não existe no projeto). Depois das
                48h, a Cloud Function suspende sozinha e esse aviso vira a
                mensagem de "acesso suspenso", já sem prazo pra contar. */}
            {/* 🔥 CORRIGIDO: quem tem os DOIS perfis ativos (Gestor + Consultor)
                não deve ver "(Gestor)"/"(Consultor)" no aviso — só quem tem
                UM perfil só precisa dessa especificação, pra saber qual dos
                dois está em risco. */}
            {pagamentoAtrasado && (() => {
               const temGestorAtivo = ['tactical', 'plus', 'total'].includes(gestorPlan);
               const temConsultorAtivo = ['advisor', 'boutique', 'enterprise'].includes(consultorPlan);
               const temAmbosPerfis = temGestorAtivo && temConsultorAtivo;
               return (
                  <div
                     style={{
                        backgroundColor: 'rgba(239, 68, 68, 0.15)',
                        borderBottom: '1px solid rgba(239, 68, 68, 0.5)',
                        padding: '12px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        position: 'relative',
                        zIndex: 100,
                     }}
                  >
                     <span style={{ color: '#f87171', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        ⚠️ Não conseguimos identificar seu pagamento. Verifique sua forma de pagamento nas próximas{' '}
                        {Math.max(0, Math.ceil(48 - (Date.now() - new Date(pagamentoAtrasado.desde).getTime()) / 3600000))}h para continuar usando o Phoenix OS
                        {!temAmbosPerfis && (pagamentoAtrasado.tipo === 'consultor' ? ' (Consultor)' : ' (Gestor)')}.
                     </span>
                  </div>
               );
            })()}

            {/* 🔥 NOVO: acesso já foi suspenso (passou das 48h). Tom brando,
                igual outras plataformas fazem — sem palavras como "suspensa
                por falta de pagamento", só um convite claro pra reativar. */}
            {acessoSuspensoPorAtraso && (() => {
               const temGestorAtivo = ['tactical', 'plus', 'total'].includes(gestorPlan);
               const temConsultorAtivo = ['advisor', 'boutique', 'enterprise'].includes(consultorPlan);
               const temAmbosPerfis = temGestorAtivo && temConsultorAtivo;
               return (
                  <div
                     style={{
                        backgroundColor: 'rgba(239, 68, 68, 0.15)',
                        borderBottom: '1px solid rgba(239, 68, 68, 0.5)',
                        padding: '12px 24px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        textAlign: 'center',
                        position: 'relative',
                        zIndex: 100,
                     }}
                  >
                     <span style={{ color: '#f87171', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        ⚠️ Não conseguimos identificar seu pagamento{!temAmbosPerfis && (acessoSuspensoPorAtraso.tipo === 'consultor' ? ' do Consultor' : ' do Gestor')}. Atualize sua forma de pagamento pra continuar usando {temAmbosPerfis ? 'o Phoenix OS' : 'esse perfil'}.
                     </span>
                     <button
                        onClick={() => { setView('landing'); setStep(1); }}
                        style={{
                           backgroundColor: '#f87171',
                           color: '#000',
                           fontSize: '10px',
                           fontWeight: 900,
                           textTransform: 'uppercase',
                           letterSpacing: '0.05em',
                           padding: '6px 16px',
                           borderRadius: '9999px',
                           border: 'none',
                           cursor: 'pointer',
                           whiteSpace: 'nowrap',
                        }}
                     >
                        Atualizar pagamento
                     </button>
                  </div>
               );
            })()}

            {isUpgrading && (
               <div className="absolute inset-0 z-[300] bg-[#020617]/95 backdrop-blur-md flex flex-col items-center justify-center">
                  <Loader2 size={60} className="text-lime-400 animate-spin mb-6" />
                  <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">Processando Licença</h2>
                  <p className="text-[11px] text-lime-400 font-black uppercase tracking-widest mt-2">{upgradeTarget}</p>
                  <div className="w-48 h-1 bg-white/10 rounded-full mt-6 overflow-hidden"><div className="h-full bg-lime-400 transition-all duration-300" style={{ width: `${progress}%` }}></div></div>
               </div>
            )}

            {showCompleteDataPrompt && (
               <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
                  <div className="bg-slate-900 border border-lime-400/20 rounded-[2rem] p-8 max-w-sm w-full text-center space-y-4 shadow-2xl">
                     <div className="w-14 h-14 bg-lime-400/10 rounded-full flex items-center justify-center mx-auto">
                        <FileText size={24} className="text-lime-400" />
                     </div>
                     <h3 className="text-white font-black text-xl uppercase italic tracking-tighter">Você já tem um relatório</h3>
                     <p className="text-slate-400 text-sm leading-relaxed">
                        Quer completá-lo com os novos dados avançados que seu plano liberou? Isso deixa o diagnóstico mais preciso.
                     </p>
                     <div className="flex flex-col gap-3 pt-2">
                        <button
                           onClick={() => {
                              const dossie = historyList.find(item => item.id === pendingCompleteDossierId);
                              if (dossie) {
                                 setFormData({ ...dossie.snapshot.formData, userType: 'Gestor' });
                                 setActiveDossierId(dossie.id);
                                 setStep(5);
                                 setView('wizard');
                              }
                              setShowCompleteDataPrompt(false);
                           }}
                           className="bg-lime-400 text-black py-4 rounded-2xl font-black uppercase text-xs active:scale-95 transition-all"
                        >
                           Completar Agora
                        </button>
                        <button
                           onClick={() => setShowCompleteDataPrompt(false)}
                           className="text-slate-500 py-2 font-bold uppercase text-xs"
                        >
                           Depois
                        </button>
                     </div>
                  </div>
               </div>
            )}

            <div ref={mainScrollRef} className="flex-1 overflow-y-auto no-scrollbar relative z-10 w-full">
               {/* Faixa fixa com o nome da empresa (e o selo de saúde) enquanto se rola o relatório. Altura 0: não desloca o conteúdo. */}
               <div className="sticky top-0 z-30 h-0 overflow-visible pointer-events-none">
                  {view === 'report' && !relatorioEntryBloqueado && rolouAlemDoTitulo && formData.companyName && !(formData.userType === 'Consultor' && reportTab === 'carteira') && (
                     <div className="pointer-events-auto flex items-center justify-between gap-3 px-6 py-2.5 bg-[#010208]/90 backdrop-blur-md border-b border-white/10 animate-fade-in">
                        <span className="text-[11px] font-black text-white uppercase tracking-wide truncate">{companyNameText}</span>
                        {report?.status?.label && (
                           <span className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-widest border border-white/10 bg-white/5 ${report.status.color}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                              {report.status.label}
                           </span>
                        )}
                     </div>
                  )}
               </div>
               <div className={`w-full px-6 ${view === 'report' ? 'pb-32' : 'pb-10'}`}>

                  {view === 'landing' && (
                     <div className="flex flex-col space-y-12 py-12 animate-fade-up text-left">
                        <div className="flex flex-col items-center pt-4">
                           <IconShieldBig className="scale-110 mb-2" />
                           <h1 className="text-6xl font-black italic uppercase tracking-tighter mt-8 text-white leading-none text-center">Phoenix<span className="text-lime-400">OS</span><sup className="text-2xl ml-1">®</sup></h1>
                        </div>

                        <div className="bg-slate-900/30 backdrop-blur-2xl p-10 rounded-[3.5rem] border border-white/5 text-center shadow-2xl relative">
                           <Zap className="mx-auto text-lime-400 mb-6 drop-shadow-[0_0_10px_rgba(190,242,100,0.5)]" size={36} />
                           <h2 className="text-white font-black text-2xl uppercase tracking-tight mb-4 italic leading-tight text-center">Inteligência Executiva ao Seu Alcance</h2>
                           <p className="text-[11px] text-slate-400 leading-relaxed font-bold tracking-[0.2em] italic text-center">Diagnóstico de risco e proteção de caixa em tempo real.</p>
                        </div>

                        {gestorPlan && gestorPlan !== 'entry' ? (
                           /* USUÁRIO JÁ TEM PLANO PAGO — mostra botão direto de novo diagnóstico */
                           <div className="bg-gradient-to-br from-slate-900/80 to-black/90 border-2 border-lime-400/30 rounded-[4rem] p-10 space-y-6 shadow-2xl text-center">
                              <div>
                                 <h3 className="font-black uppercase text-3xl text-white italic tracking-tighter leading-none">Novo Diagnóstico</h3>
                                 <p className="text-[11px] text-lime-400/80 font-black uppercase italic mt-3 tracking-widest">Plano {String(gestorPlan).toUpperCase()} ativo · Diagnósticos ilimitados</p>
                              </div>
                              <p className="text-[11px] text-slate-500 uppercase tracking-widest leading-relaxed">
                                 ⚠️ O Phoenix OS analisa <strong className="text-slate-300">uma empresa por vez</strong>. Para gerenciar múltiplos CNPJs simultaneamente, use o perfil Consultor.
                              </p>
                              <button onClick={startNewDiagnosis} className="w-full bg-lime-400 text-black py-6 rounded-full font-black uppercase text-[15px] whitespace-nowrap shadow-xl active:scale-95 transition-all">
                                 + Iniciar Novo Diagnóstico
                              </button>
                           </div>
                        ) : (
                           /* SEM PLANO PAGO — mostra o card Entry com R$ 49,90. É o primeiro
                              contato de quem nunca usou o Phoenix OS, então precisa chamar mais
                              atenção que os cards de upgrade abaixo dele: título e ícone maiores,
                              selo de destaque, borda com brilho mais forte. */
                           <div className="bg-gradient-to-br from-slate-900/90 to-black/90 border-2 border-lime-400/40 rounded-[3rem] p-8 space-y-7 shadow-[0_0_50px_rgba(190,242,100,0.12)] text-left">
                              <div className="text-left">
                                 <span className="inline-block bg-lime-400 text-black text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4">Comece por aqui</span>
                                 <div className="flex flex-col items-center text-center gap-2 mb-1">
                                    <div className="w-12 h-12 shrink-0 rounded-full bg-lime-400/10 border border-lime-400/30 flex items-center justify-center shadow-[0_0_20px_rgba(190,242,100,0.25)]">
                                       <Zap size={22} className="text-lime-400" />
                                    </div>
                                    <h3 className="font-black uppercase text-4xl text-white italic tracking-tighter leading-none text-center">Phoenix Entry</h3>
                                 </div>
                                 <p className="text-xs text-lime-400/80 font-black uppercase italic mt-3 tracking-widest leading-none text-center">Análise de Solvência Master</p>
                                 <p className="text-[11px] text-slate-500 uppercase tracking-widest mt-2 leading-relaxed">
                                    ⚠️ Uma empresa por análise · Pay-per-use
                                 </p>
                              </div>
                              <div className="space-y-3.5 pt-1 text-left">                                 {['Diagnóstico Rápido (Pay-per-use)', 'EBITDA Real Time', 'Alavancagem IFRS', 'Parecer IA Profissional', 'Dossiê em PDF'].map((b, i) => (
                                 <div key={i} className="flex items-center gap-3 text-left">
                                    <div className="w-6 h-6 shrink-0 rounded-full bg-lime-400/10 flex items-center justify-center border border-lime-400/20"><ShieldCheck size={13} className="text-lime-400" /></div>
                                    <span className="text-xs font-bold text-slate-300 uppercase tracking-tight italic">{b}</span>
                                 </div>
                              ))}
                              </div>
                              <div className="flex flex-col items-center pt-6 border-t border-white/5 space-y-5">
                                 <div className="bg-lime-400 text-black px-6 py-4 rounded-[1.75rem] shadow-[0_0_35px_rgba(190,242,100,0.45)] flex flex-col items-center w-full max-w-[280px]">
                                    <div className="flex items-baseline gap-1">
                                       <span className="font-black text-2xl italic uppercase">R$</span>
                                       <span className="font-black text-6xl tracking-tighter italic leading-none">49,90</span>
                                    </div>
                                    <span className="text-[11px] font-black uppercase opacity-70 italic mt-1.5 tracking-widest">Pagamento Único</span>
                                 </div>
                                 <button onClick={startNewDiagnosis} className="w-full bg-white text-black py-6 rounded-full font-black uppercase text-base shadow-xl active:scale-95 transition-all">Iniciar Diagnóstico Master</button>
                              </div>
                           </div>
                        )}

                        <div className="space-y-6 pt-4 pb-4">
                           <h4 className="text-[11px] font-black uppercase text-slate-400 tracking-[0.6em] text-center italic leading-none mb-6">Upgrade Estratégico</h4>

                           {landingProtocols.map(prot => {
                              const isAcquired = checkAcquired(prot.id);
                              const isPurple = prot.color === 'purple';
                              const themeText = isPurple ? 'text-purple-400' : 'text-lime-400';
                              const themeBgPill = isPurple ? 'bg-purple-500' : 'bg-lime-400';
                              const themeTextPill = isPurple ? 'text-white' : 'text-black';
                              const themeShadow = isPurple ? 'shadow-[0_0_30px_rgba(168,85,247,0.4)]' : 'shadow-[0_0_30px_rgba(190,242,100,0.4)]';

                              return (
                                 <div key={prot.id} className="bg-slate-900/60 p-8 rounded-[3rem] border border-white/5 shadow-2xl relative overflow-hidden">
                                    {isPurple && <div className="absolute top-0 right-0 p-6 opacity-10"><Binary size={80} className="text-purple-400" /></div>}
                                    {!isPurple && prot.id === 'tactical' && <div className="absolute top-0 right-0 p-6 opacity-10"><Target size={80} className="text-lime-400" /></div>}
                                    {!isPurple && prot.id === 'plus' && <div className="absolute top-0 right-0 p-6 opacity-10"><Coins size={80} className="text-lime-400" /></div>}

                                    <div className="flex justify-between items-center mb-8 relative z-10 gap-3">
                                       <h5 className={`font-black text-white text-xl uppercase italic whitespace-nowrap`}>{prot.name}</h5>
                                       {isAcquired ? <CheckCircle2 size={20} className={`${themeText} shrink-0`} /> : <Lock size={16} className="text-white/20 shrink-0" />}
                                    </div>

                                    <ul className="space-y-4 mb-10 relative z-10">
                                       {prot.features.map((f, i) => (
                                          <li key={i} className="flex items-center gap-3 text-[11px] font-bold text-slate-300 uppercase tracking-widest">
                                             <div className={`w-1.5 h-1.5 rounded-full ${themeBgPill}`}></div>{f}
                                          </li>
                                       ))}
                                    </ul>

                                    <p className="text-[11px] text-slate-600 tracking-normal leading-relaxed mt-[-24px] mb-4 relative z-10">
                                       ⚠️ Uma empresa por vez · Para múltiplos CNPJs use o perfil Consultor
                                    </p>

                                    <div className="flex flex-col space-y-6 items-center border-t border-white/5 pt-8 relative z-10">
                                       <div className={`${themeBgPill} ${themeTextPill} px-8 py-4 rounded-[2rem] ${themeShadow} flex flex-col items-center w-full max-w-[280px]`}>
                                          <div className="flex items-baseline gap-1">
                                             <span className="font-black text-xl italic uppercase">R$</span>
                                             <span className={`font-black ${prot.textSize} tracking-tighter italic leading-none`}>{prot.price}</span>
                                          </div>
                                          <span className="text-[11px] font-black uppercase opacity-70 italic mt-2 tracking-widest">Assinatura Mensal</span>
                                       </div>

                                       {isAcquired ? (
                                          <div className="w-full bg-white/5 text-slate-400 py-6 rounded-full font-black uppercase text-sm border border-white/10 flex items-center justify-center gap-2 mt-2">
                                             <CheckCircle2 size={16} /> Módulo Já Adquirido
                                          </div>
                                       ) : (
                                          <button onClick={() => handleProtocolSelection(prot.id, prot.name, prot.price, 'upgrade')} className={`w-full ${isPurple ? 'bg-purple-600 text-white' : 'bg-white text-black'} py-6 rounded-full font-black uppercase text-sm shadow-xl active:scale-95 transition-all mt-2`}>
                                             {isPurple ? 'Ativar Protocolo Total' : `Ativar ${prot.name}`}
                                          </button>
                                       )}
                                    </div>
                                 </div>
                              );
                           })}
                        </div>

                        {/* ========================================== */}
                        {/* PRÉ-VISUALIZAÇÃO PARA CONSULTORES (B2B) */}
                        {/* ========================================== */}
                        <div className="space-y-6 pt-8 border-t border-white/5">
                           <div className="text-center">
                              <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 px-4 py-2 rounded-full mb-4">
                                 <Monitor size={14} className="text-blue-400" />
                                 <span className="text-[11px] font-black text-blue-400 uppercase tracking-widest">Console Partner B2B</span>
                              </div>
                              <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter">
                                 Painel do <span className="text-blue-400">Consultor</span>
                              </h3>
                              <p className="text-[12px] text-slate-500 mt-2 max-w-xs mx-auto">
                                 Gerencie múltiplos clientes, emita laudos white-label e simule valuation em tempo real
                              </p>
                           </div>

                           <div className="grid grid-cols-2 gap-3">
                              <div className="bg-slate-900/40 border border-white/5 rounded-2xl p-4 text-center hover:border-blue-500/30 hover:bg-blue-500/5 transition-all duration-300 group">
                                 <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                                    <Briefcase size={24} className="text-blue-400" />
                                 </div>
                                 <h4 className="text-[12px] font-black text-white uppercase">Carteira</h4>
                                 <p className="text-[11px] text-slate-500 mt-1">Gestão de múltiplos CNPJs</p>
                              </div>

                              <div className="bg-slate-900/40 border border-white/5 rounded-2xl p-4 text-center hover:border-blue-500/30 hover:bg-blue-500/5 transition-all duration-300 group">
                                 <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                                    <TrendingUp size={24} className="text-blue-400" />
                                 </div>
                                 <h4 className="text-[12px] font-black text-white uppercase">APEX DCF</h4>
                                 <p className="text-[11px] text-slate-500 mt-1">Valuation em tempo real</p>
                              </div>

                              <div className="bg-slate-900/40 border border-white/5 rounded-2xl p-4 text-center hover:border-purple-500/30 hover:bg-purple-500/5 transition-all duration-300 group">
                                 <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                                    <Palette size={24} className="text-purple-400" />
                                 </div>
                                 <h4 className="text-[12px] font-black text-white uppercase">White-Label</h4>
                                 <p className="text-[11px] text-slate-500 mt-1">Sua marca nos relatórios</p>
                              </div>

                              <div className="bg-slate-900/40 border border-white/5 rounded-2xl p-4 text-center hover:border-amber-500/30 hover:bg-amber-500/5 transition-all duration-300 group">
                                 <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                                    <Zap size={24} className="text-amber-400" />
                                 </div>
                                 <h4 className="text-[12px] font-black text-white uppercase">IA Preditiva</h4>
                                 <p className="text-[11px] text-slate-500 mt-1">Simulação de cenários</p>
                              </div>
                           </div>

                           <button
                              onClick={() => {
                                 if (consultorPlan && consultorPlan !== 'free') {
                                    // 🔥 Já tem plano ativo — vai direto pra Carteira,
                                    // não faz sentido mostrar a vitrine de planos de novo.
                                    setFormData({ ...formData, userType: 'Consultor' });
                                    setView('report');
                                    setReportTab('carteira');
                                    return;
                                 }
                                 setFormData({
                                    ...formData,
                                    userType: 'Consultor',
                                    expertise: 'Avançado'  // Define automaticamente como Avançado
                                 });

                                 // 🔥 Vai direto para a vitrine de planos, pulando o wizard
                                 setView('consultor_showcase');
                              }}
                              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-5 rounded-full font-black uppercase text-sm shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2"
                           >
                              <Zap size={16} /> {consultorPlan && consultorPlan !== 'free' ? 'Acessar Minha Carteira' : 'Começar como Consultor'}
                           </button>
                           {consultorPlan && consultorPlan !== 'free' && (
                              <button
                                 onClick={() => {
                                    setFormData({ ...formData, userType: 'Consultor', expertise: 'Avançado' });
                                    setView('consultor_showcase');
                                 }}
                                 className="w-full text-blue-400 py-3 font-black uppercase text-[11px] tracking-widest hover:text-blue-300 transition-all"
                              >
                                 Ver Planos
                              </button>
                           )}
                           <div className="flex items-center gap-3 pt-4">
                              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                              <span className="text-[11px] text-slate-500 uppercase tracking-widest font-black">Ou para empresas</span>
                              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                           </div>
                        </div>

                        <div className="pt-8 pb-4">
                           <h4 className="text-[11px] font-black uppercase text-slate-400 tracking-[0.6em] text-center italic leading-none mb-8">Dúvidas Frequentes</h4>
                           <div className="space-y-4">
                              {faqData.map((faq, index) => (
                                 <div key={index} className="bg-slate-900/40 border border-white/5 rounded-3xl overflow-hidden transition-all duration-300">
                                    <button onClick={() => setExpandedFaq(expandedFaq === index ? null : index)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none">
                                       <span className="text-xs font-black text-white uppercase tracking-widest leading-relaxed pr-4">{faq.q}</span>
                                       {expandedFaq === index ? <ChevronUp size={20} className="text-lime-400 shrink-0" /> : <ChevronDown size={20} className="text-slate-500 shrink-0" />}
                                    </button>
                                    {expandedFaq === index && (
                                       <div className="px-6 pb-6 text-[11px] text-slate-400 font-medium leading-relaxed tracking-wide border-t border-white/5 pt-4 animate-in slide-in-from-top-2 duration-300">
                                          {faq.a}
                                       </div>
                                    )}
                                 </div>
                              ))}
                           </div>
                        </div>

                        <div className="pt-12 space-y-6 text-left bg-black/5 backdrop-blur-3xl pb-10 border-t border-white/5">
                           <div className="text-center pb-2">
                              <div className="w-14 h-14 bg-lime-400/10 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-lime-400/20">
                                 <ShieldCheck size={26} className="text-lime-400" strokeWidth={1.5} />
                              </div>
                              <p className="text-[11px] text-slate-500 uppercase tracking-[0.2em] font-semibold">Acesso Seguro</p>
                           </div>
                           <div className="grid grid-cols-3 gap-2">
                              <div className="bg-slate-900/20 rounded-2xl border border-white/5 py-4 px-2 text-center">
                                 <Lock size={18} className="text-lime-400 mx-auto" strokeWidth={1.5} />
                                 <p className="text-[11px] text-slate-500 font-semibold mt-1.5 leading-tight whitespace-nowrap">AES-256</p>
                              </div>
                              <div className="bg-slate-900/20 rounded-2xl border border-white/5 py-4 px-2 text-center">
                                 <History size={18} className="text-lime-400 mx-auto" strokeWidth={1.5} />
                                 <p className="text-[11px] text-slate-500 font-semibold mt-1.5 leading-tight">Histórico Salvo</p>
                              </div>
                              <div className="bg-slate-900/20 rounded-2xl border border-white/5 py-4 px-2 text-center">
                                 <Monitor size={18} className="text-lime-400 mx-auto" strokeWidth={1.5} />
                                 <p className="text-[11px] text-slate-500 font-semibold mt-1.5 leading-tight">App + Web</p>
                              </div>
                           </div>
                           {/* 🔥 CORREÇÃO: antes checava só "user" — como agora sempre
                               existe um usuário (mesmo anônimo, criado sozinho pra
                               tirar fricção da compra avulsa), essa tela nunca mais
                               mostrava o login pra quem JÁ tinha conta de verdade.
                               Sessão anônima agora conta como "sem login" aqui. */}
                           {user && !user.isAnonymous ? (
                              <button
                                 onClick={() => setView('history')}
                                 className="w-full bg-white/5 text-white py-7 rounded-full font-black uppercase text-sm border border-white/10 flex items-center justify-center gap-3 hover:bg-white hover:text-black transition-all active:scale-95"
                              >
                                 <History size={16} className="text-lime-400" /><span>Ver Meus Relatórios</span>
                              </button>
                           ) : (
                              <div className="bg-slate-900/20 p-7 rounded-[2.5rem] border border-white/5 text-left relative">
                                 {!showForgotPassword ? (
                                    <>
                                       <p className="text-[15px] font-bold text-white tracking-wide text-center mb-5">Acesso à conta</p>
                                       <div className="space-y-3">
                                          <input
                                             type="email"
                                             value={landingEmail}
                                             onChange={(e) => { setLandingEmail(e.target.value); setLandingLoginError(''); }}
                                             placeholder="seu@email.com"
                                             className="w-full bg-black/30 border border-white/5 px-5 py-4 rounded-2xl text-sm text-white outline-none focus:border-lime-400/50 transition-all placeholder:text-slate-600"
                                          />
                                          <input
                                             type="password" autoComplete="current-password"
                                             value={landingPassword}
                                             onChange={(e) => { setLandingPassword(e.target.value); setLandingLoginError(''); }}
                                             placeholder="Senha"
                                             className="w-full bg-black/30 border border-white/5 px-5 py-4 rounded-2xl text-sm text-white outline-none focus:border-lime-400/50 transition-all placeholder:text-slate-600"
                                          />
                                       </div>
                                       {landingLoginError && (
                                          <p className="text-red-400 text-[11px] font-medium text-center mt-3">{landingLoginError}</p>
                                       )}
                                       <button
                                          onClick={async () => {
                                             if (!landingEmail || !landingPassword) {
                                                setLandingLoginError('Preencha email e senha.');
                                                return;
                                             }
                                             setLandingLoginLoading(true);
                                             setLandingLoginError('');
                                             try {
                                                const cred = await signInWithEmailAndPassword(auth, landingEmail, landingPassword);
                                                // Busca o nome e plano reais salvos no Firestore (perdidos do
                                                // localStorage após logout) para restaurar "Meu Perfil" corretamente.
                                                try {
                                                   const userDoc = await getDoc(doc(db, 'usuarios', cred.user.uid));
                                                   if (userDoc.exists()) {
                                                      const dados = userDoc.data();
                                                      if (dados.nome) localStorage.setItem('phoenix_user_name', dados.nome);
                                                      if (dados.role) localStorage.setItem('phoenix_user_type', dados.role);

                                                      const planLevelMap = { entry: 1, tactical: 2, plus: 3, total: 4, boardroom: 4, advisor: 5, boutique: 6, enterprise: 7 };

                                                      // 🔥 Campos SEMPRE separados: "plan" é sempre Gestor, "consultorPlan" é sempre Consultor.
                                                      // Não depende mais do "role" da conta — uma conta pode ter os dois ao mesmo tempo.
                                                      if (dados.plan) {
                                                         localStorage.setItem('phoenix_gestor_plan', dados.plan);
                                                         setGestorPlan(dados.plan);
                                                         setActivePlan(dados.plan);
                                                         setUserLevel(planLevelMap[dados.plan] || 1);
                                                      }
                                                      if (dados.consultorPlan) {
                                                         localStorage.setItem('phoenix_consultor_plan', dados.consultorPlan);
                                                         setConsultorPlan(dados.consultorPlan);
                                                      }
                                                   }
                                                } catch (fetchError) {
                                                   console.error('[PHOENIX] Erro ao buscar dados do perfil:', fetchError);
                                                }
                                                if (typeof onLoginSuccess === 'function') onLoginSuccess(cred.user);
                                             } catch (error) {
                                                console.error('[PHOENIX] Erro no login:', error.code);
                                                setLandingLoginError('Email ou senha inválidos.');
                                             } finally {
                                                setLandingLoginLoading(false);
                                             }
                                          }}
                                          disabled={landingLoginLoading}
                                          className="w-full bg-white/5 hover:bg-white hover:text-black text-white font-bold text-sm py-4 rounded-2xl border border-white/10 flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 mt-4"
                                       >
                                          <Lock size={15} className="text-lime-400" /><span>{landingLoginLoading ? 'Entrando...' : 'Entrar'}</span>
                                       </button>
                                       <button
                                          onClick={() => { setShowForgotPassword(true); setForgotPasswordMessage(''); }}
                                          className="w-full text-center text-[13px] text-slate-400 hover:text-lime-400 font-semibold transition-colors mt-3"
                                       >
                                          Esqueci minha senha
                                       </button>
                                    </>
                                 ) : (
                                    <>
                                       <p className="text-[15px] font-bold text-white tracking-wide text-center mb-3">Recuperar senha</p>
                                       <p className="text-[12px] text-slate-500 text-center leading-relaxed mb-4">
                                          Digite o email da sua conta e enviaremos um link para redefinir sua senha.
                                       </p>
                                       <input
                                          type="email"
                                          value={landingEmail}
                                          onChange={(e) => setLandingEmail(e.target.value)}
                                          placeholder="seu@email.com"
                                          className="w-full bg-black/30 border border-white/5 px-5 py-4 rounded-2xl text-sm text-white outline-none focus:border-lime-400/50 transition-all placeholder:text-slate-600"
                                       />
                                       {forgotPasswordMessage && (
                                          <p className="text-lime-400 text-[11px] font-medium text-center mt-3">{forgotPasswordMessage}</p>
                                       )}
                                       <button
                                          onClick={async () => {
                                             if (!landingEmail) {
                                                setForgotPasswordMessage('Digite seu email acima.');
                                                return;
                                             }
                                             try {
                                                await sendPasswordResetEmail(auth, landingEmail, { url: 'https://app.phoenixos.com.br' });
                                                setForgotPasswordMessage('Enviamos um link de redefinição para o seu email.');
                                             } catch (error) {
                                                console.error('[PHOENIX] Erro ao enviar redefinição:', error.code);
                                                setForgotPasswordMessage('Não foi possível enviar o email. Verifique o endereço.');
                                             }
                                          }}
                                          className="w-full bg-white/5 hover:bg-white hover:text-black text-white font-bold text-sm py-4 rounded-2xl border border-white/10 transition-all active:scale-95 mt-4"
                                       >
                                          Enviar link de redefinição
                                       </button>
                                       <button
                                          onClick={() => setShowForgotPassword(false)}
                                          className="w-full text-center text-[13px] text-slate-400 hover:text-white font-semibold transition-colors mt-3"
                                       >
                                          ← Voltar ao login
                                       </button>
                                    </>
                                 )}
                              </div>
                           )}
                           {!user && (
                              <p className="text-[11px] text-slate-600 text-center leading-relaxed px-4">
                                 O acesso é liberado após a assinatura de um plano. Ainda não tem conta? Escolha um protocolo para começar.
                              </p>
                           )}
                        </div>

                        <div className="pt-8 text-center opacity-40 px-0 pb-20 border-t border-white/5 text-left">
                           <p className="text-[11px] text-slate-400 font-bold uppercase tracking-[0.2em] leading-relaxed text-justify text-justify-last-center">
                              {DISCLAIMER_FULL}
                           </p>
                        </div>
                     </div>
                  )}

                  {view === 'wizard' && (gestorPlan === 'entry' || user) && (
                     <div className="flex flex-col space-y-10 py-10 animate-fade-up text-left">
                        <div className="flex justify-between items-center px-2">
                           <div className="bg-lime-400/10 text-lime-400 px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest border border-lime-400/20 italic">Etapa {step}/5</div>
                           <button onClick={() => step > 1 ? setStep(step - 1) : setView('landing')} className="bg-white/5 text-slate-400 px-5 py-1.5 rounded-full text-[11px] font-black uppercase italic border border-white/10 flex items-center gap-2 hover:text-white transition-all"><ArrowLeft size={12} /> Voltar</button>
                        </div>

                        {/* ETAPA 1: COMPLEXIDADE E PERFIL */}
                        {step === 1 && (
                           <div className="space-y-10 animate-fade-up">
                              {/* 1. NÍVEL DE PROFUNDIDADE */}
                              <div className="space-y-4">
                                 <div className="space-y-2 text-center">
                                    <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none">Profundidade da <span className="text-lime-400">Análise</span></h2>
                                    <p className="text-[11px] text-slate-500 font-black uppercase tracking-[0.3em] leading-relaxed italic">Defina o nível de complexidade do relatório.</p>
                                 </div>
                                 <div className="grid grid-cols-2 gap-3">
                                    {[
                                       { id: 'Executivo', title: 'Visão Executiva', desc: 'Clara e Direta', icon: '📊' },
                                       { id: 'Avançado', title: 'Visão Avançada', desc: 'Técnica / Profunda', icon: '🏛️' }
                                    ].map(lvl => (
                                       <button key={lvl.id} type="button" onClick={() => setFormData({ ...formData, expertise: lvl.id })}
                                          className={`w-full p-6 rounded-[2rem] border-2 transition-all flex flex-col items-center justify-center gap-3 text-center ${formData.expertise === lvl.id ? 'bg-lime-400/10 border-lime-400 shadow-[0_0_15px_rgba(190,242,100,0.15)] scale-[1.02]' : 'bg-slate-900/40 border-white/5 hover:border-white/20'}`}>
                                          <span className="text-4xl drop-shadow-md mb-1">{lvl.icon}</span>
                                          <div className="flex flex-col items-center">
                                             <span className={`block font-black uppercase tracking-tight text-sm ${formData.expertise === lvl.id ? 'text-lime-400' : 'text-white'}`}>{lvl.title}</span>
                                             <span className="text-[11px] text-slate-500 font-bold tracking-widest uppercase mt-1 block leading-tight">{lvl.desc}</span>
                                          </div>
                                       </button>
                                    ))}
                                 </div>
                              </div>

                              {/* 2. GATEWAY DE PERFIS */}
                              <div className="space-y-4">
                                 <div className="space-y-2 text-center">
                                    <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none">Porta de <span className="text-lime-400">Entrada</span></h2>
                                    <p className="text-[11px] text-slate-500 font-black uppercase tracking-[0.3em] leading-relaxed italic">Escolha a sua trilha no ecossistema.</p>
                                 </div>
                                 <div className="space-y-3">
                                    <button
                                       type="button"
                                       onClick={() => setFormData({ ...formData, userType: 'Gestor' })}
                                       className={`w-full p-6 rounded-[2rem] border-2 transition-all flex flex-col items-center justify-center gap-2 text-center ${formData.userType === 'Gestor' ? 'bg-lime-400/10 border-lime-400 shadow-[0_0_15px_rgba(190,242,100,0.15)] scale-[1.02]' : 'bg-slate-900/40 border-white/5 hover:border-white/20'}`}
                                    >
                                       <span className={`font-black uppercase tracking-tight text-xl ${formData.userType === 'Gestor' ? 'text-lime-400' : 'text-white'}`}>Sócio / Gestor</span>
                                       <span className="text-[11px] text-slate-500 font-bold uppercase tracking-widest leading-relaxed">Diagnóstico da minha própria empresa.</span>
                                    </button>

                                    <button
                                       type="button"
                                       onClick={() => setFormData({ ...formData, userType: 'Consultor' })}
                                       className={`w-full p-6 rounded-[2rem] border-2 transition-all flex flex-col items-center justify-center gap-2 text-center ${formData.userType === 'Consultor' ? 'bg-blue-500/10 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.15)] scale-[1.02]' : 'bg-slate-900/40 border-white/5 hover:border-white/20'}`}
                                    >
                                       <div className="flex flex-col items-center justify-center gap-2 w-full">
                                          <span className="bg-blue-500/20 border border-blue-500/30 text-blue-400 text-[11px] font-black uppercase px-3 py-1 rounded-full animate-pulse tracking-widest">Portal Partner B2B</span>
                                          <span className={`font-black uppercase tracking-tight text-xl ${formData.userType === 'Consultor' ? 'text-blue-400' : 'text-white'}`}>Consultor / M&A</span>
                                       </div>
                                       <span className="text-[11px] text-slate-500 font-bold uppercase tracking-widest leading-relaxed mt-1">Acessar vitrine de planos e login B2B.</span>
                                    </button>
                                 </div>
                              </div>
                           </div>
                        )}

                        {/* ETAPA 2: DADOS DA EMPRESA */}
                        {step === 2 && (
                           <div className="space-y-8 animate-fade-up text-left">
                              <div className="space-y-2 text-left">
                                 <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none">Dados da <span className="text-lime-400">Empresa</span></h2>
                                 <p className="text-[11px] text-slate-500 font-black uppercase tracking-[0.3em] leading-relaxed italic">Como a IA deve referenciar o seu negócio.</p>
                              </div>

                              <div className="space-y-3">
                                 <label className="text-[11px] font-black text-slate-600 uppercase tracking-widest ml-4 italic">Razão Social / Nome Fantasia</label>
                                 <input
                                    type="text"
                                    value={formData.companyName || ''}
                                    onChange={e => setFormData({ ...formData, companyName: e.target.value })}
                                    className="w-full bg-slate-900/40 border border-white/5 p-6 rounded-[2rem] text-white font-bold outline-none focus:border-lime-400/50 transition-all shadow-inner placeholder:text-slate-700"
                                    placeholder="Ex: Phoenix OS"
                                 />
                              </div>

                              <div className="space-y-3">
                                 <label className="text-[11px] font-black text-slate-600 uppercase tracking-widest ml-4 italic">CNPJ <span className="text-slate-700 normal-case">(opcional)</span></label>
                                 <input
                                    type="text"
                                    value={formData.cnpj || ''}
                                    onChange={e => setFormData({ ...formData, cnpj: e.target.value })}
                                    className="w-full bg-slate-900/40 border border-white/5 p-6 rounded-[2rem] text-white font-bold outline-none focus:border-lime-400/50 transition-all shadow-inner placeholder:text-slate-700"
                                    placeholder="00.000.000/0000-00"
                                 />
                              </div>

                              <div className="space-y-3">
                                 <label className="text-[11px] font-black text-slate-600 uppercase tracking-widest ml-4 italic">Segmento de Atuação</label>
                                 <div className="grid grid-cols-2 gap-3">
                                    {[
                                       'Varejo',
                                       'Alimentação',
                                       'Indústria',
                                       'Serviços',
                                       'Tech/SaaS',
                                       'Holding / Grupo',
                                       'Atacado/Distrib.',
                                       'Saúde/Clínicas',
                                       'Agronegócio',
                                       'Construção/Eng.',
                                       'Logística/Frota',
                                       'Beleza/Estética'
                                    ].map(seg => (
                                       <button key={seg} onClick={() => setFormData({ ...formData, segment: seg })}
                                          className={`p-4 rounded-[1.5rem] border-2 font-black uppercase text-[11px] transition-all tracking-tighter leading-tight min-h-[60px] flex items-center justify-center text-center ${formData.segment === seg ? 'bg-lime-400/10 text-lime-400 border-lime-400 shadow-[0_0_15px_rgba(190,242,100,0.15)] scale-[1.02]' : 'bg-slate-900/40 border-white/5 text-slate-400 hover:border-white/20'}`}>
                                          {seg}
                                       </button>
                                    ))}
                                 </div>
                              </div>

                              <div className="space-y-3">
                                 <label className="text-[11px] font-black text-slate-600 uppercase tracking-widest ml-4 italic">Atividade Específica (Obrigatório)</label>
                                 <input
                                    type="text"
                                    value={formData.niche || ''}
                                    onChange={e => setFormData({ ...formData, niche: e.target.value })}
                                    className="w-full bg-slate-900/40 border border-white/5 p-6 rounded-[2rem] text-white font-bold outline-none focus:border-lime-400/50 transition-all shadow-inner placeholder:text-slate-700"
                                    placeholder="Ex: Padaria, Clínica, Agência..."
                                 />
                              </div>
                           </div>
                        )}

                        {/* ETAPA 3: ORIGEM DOS DADOS */}
                        {step === 3 && (
                           <div className="space-y-8 animate-fade-up text-left">
                              <div className="space-y-2 text-left">
                                 <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none">Origem dos <span className="text-lime-400">Dados</span></h2>
                                 <p className="text-[11px] text-slate-500 font-black uppercase tracking-[0.3em] leading-relaxed italic">Como deseja inserir os números da operação?</p>
                              </div>

                              <div className="relative w-full bg-lime-400/5 border-2 border-dashed border-lime-400/50 rounded-[2rem] p-10 flex flex-col items-center justify-center transition-all hover:bg-lime-400/10 hover:border-lime-400 group overflow-hidden">
                                 <input
                                    type="file"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleFileUpload}
                                    disabled={isUploading}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                                 />

                                 {isUploading ? (
                                    <div className="flex flex-col items-center space-y-4 animate-fade-in relative z-10">
                                       <div className="w-12 h-12 border-4 border-lime-400 border-t-transparent rounded-full animate-spin"></div>
                                       <span className="text-lime-400 font-black uppercase tracking-widest text-[11px] animate-pulse">Lendo Planilha...</span>
                                    </div>
                                 ) : (
                                    <div className="flex flex-col items-center space-y-4 relative z-10">
                                       <div className="p-4 bg-lime-400/10 rounded-full group-hover:scale-110 transition-transform">
                                          <UploadCloud size={32} className="text-lime-400 drop-shadow-[0_0_10px_rgba(190,242,100,0.5)]" />
                                       </div>
                                       <div className="text-center">
                                          <h3 className="text-white font-black text-xl uppercase italic tracking-tighter">Planilha Inteligente</h3>
                                          <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-2">Toque para buscar ou Arraste o arquivo</p>
                                          <p className="text-[11px] text-lime-400/60 uppercase tracking-widest mt-1">Formatos: .XLSX, .XLS, .CSV</p>
                                       </div>
                                    </div>
                                 )}
                              </div>

                              <button
                                 onClick={baixarModeloPlanilha}
                                 className="w-full flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-lime-400 transition-colors py-2"
                              >
                                 <Download size={12} /> Baixar modelo de planilha (.xlsx)
                              </button>

                              <p className="text-[11px] text-slate-600 text-center leading-relaxed px-4">
                                 O Phoenix OS lê planilhas simples, com os dados básicos da empresa. Balanços Patrimoniais e DREs completos e detalhados (como os publicados por grandes corporações) não são o foco do sistema — nesses casos, use o preenchimento manual com os números já consolidados.
                              </p>

                              {erroUploadPlanilha && (
                                 <div className="bg-orange-500/10 border border-orange-500/30 rounded-2xl p-4 flex items-start gap-3">
                                    <AlertTriangle size={16} className="text-orange-400 shrink-0 mt-0.5" />
                                    <p className="text-xs text-orange-300 leading-relaxed">{erroUploadPlanilha}</p>
                                 </div>
                              )}

                              <div className="flex items-center justify-center gap-4 opacity-50">
                                 <div className="h-[1px] flex-1 bg-white/20"></div>
                                 <span className="text-[11px] font-black uppercase tracking-widest text-white">OU INSERÇÃO MANUAL</span>
                                 <div className="h-[1px] flex-1 bg-white/20"></div>
                              </div>

                              <button onClick={() => setStep(4)}
                                 className="w-full p-6 rounded-[2rem] border-2 bg-slate-900/40 border-white/5 transition-all flex items-center gap-4 hover:border-white/20">
                                 <div className="p-3 bg-white/5 rounded-full"><Keyboard size={20} className="text-white" /></div>
                                 <div className="text-left">
                                    <span className="block font-black uppercase tracking-tight text-[13px] text-white">Raio-X Expresso</span>
                                    <span className="text-[11px] text-slate-500 font-bold tracking-widest uppercase mt-1 block leading-tight">Digitar 3 métricas principais na tela</span>
                                 </div>
                              </button>
                           </div>
                        )}

                        {/* ETAPA 4: FINANCEIRO (MANUAL) */}
                        {step === 4 && (() => {
                           const rotulos = getFinancialFieldLabels(formData.segment);
                           return (
                           <div className="space-y-8 animate-fade-up text-left">
                              <div className="space-y-2 text-left">
                                 <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none">Visão <span className="text-lime-400">Financeira</span></h2>
                                 <p className="text-[11px] text-slate-500 font-black tracking-[0.3em] leading-relaxed italic">Métricas mensais — o motor anualiza pro valuation.</p>
                              </div>

                              <div className="space-y-3">
                                 <label className="text-[11px] font-black text-slate-600 uppercase tracking-widest ml-4">{rotulos.revenueLabel}</label>
                                 {rotulos.revenueHint && <p className="text-[11px] text-slate-500 ml-4 leading-relaxed">{rotulos.revenueHint}</p>}
                                 <div className="bg-slate-900/40 border border-white/5 p-8 rounded-[2rem] shadow-inner focus-within:border-lime-400/50 transition-all">
                                    <input type="tel" value={formData.revenue || ''} onChange={e => handleCurrencyChange(e, 'revenue')}
                                       className={`w-full bg-transparent text-white font-black outline-none transition-all placeholder:text-slate-700 ${(formData.revenue || '').length > 15 ? 'text-lg' : (formData.revenue || '').length > 11 ? 'text-xl' : (formData.revenue || '').length > 8 ? 'text-2xl' : 'text-3xl'}`}
                                       placeholder="R$ 0,00"
                                    />
                                 </div>
                              </div>

                              <div className="space-y-3">
                                 <label className="text-[11px] font-black text-slate-600 uppercase tracking-widest ml-4">{rotulos.costsLabel}</label>
                                 {rotulos.costsHint && <p className="text-[11px] text-slate-500 ml-4 leading-relaxed">{rotulos.costsHint}</p>}
                                 <div className="bg-slate-900/40 border border-white/5 p-8 rounded-[2rem] shadow-inner focus-within:border-lime-400/50 transition-all">
                                    <input type="tel" value={formData.costs || ''} onChange={e => handleCurrencyChange(e, 'costs')}
                                       className={`w-full bg-transparent text-white font-black outline-none transition-all placeholder:text-slate-700 ${(formData.costs || '').length > 15 ? 'text-lg' : (formData.costs || '').length > 11 ? 'text-xl' : (formData.costs || '').length > 8 ? 'text-2xl' : 'text-3xl'}`}
                                       placeholder="R$ 0,00"
                                    />
                                 </div>
                              </div>
                           </div>
                           );
                        })()}

                        {/* ETAPA 5: PASSIVO (MANUAL) */}
                        {step === 5 && (() => {
                           const rotulosPassivo = getFinancialFieldLabels(formData.segment);
                           return (
                           <div className="space-y-8 animate-fade-up text-left">
                              <div className="space-y-2 text-left">
                                 <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none">Estrutura de <span className="text-lime-400">Capital</span></h2>
                                 <p className="text-[11px] text-slate-500 font-black uppercase tracking-[0.3em] leading-relaxed italic">Mapeamento de passivos e alavancagem.</p>
                              </div>

                              <div className="space-y-3">
                                 <label className="text-[11px] font-black text-slate-600 uppercase tracking-widest ml-4">Total de Dívidas (Empréstimos/Financiamentos)</label>
                                 {rotulosPassivo.debtHint && <p className="text-[11px] text-slate-500 ml-4 leading-relaxed">{rotulosPassivo.debtHint}</p>}
                                 <div className="bg-slate-900/40 border border-white/5 p-8 rounded-[2rem] shadow-inner focus-within:border-lime-400/50 transition-all">
                                    <input type="tel" value={formData.debt || ''} onChange={e => handleCurrencyChange(e, 'debt')}
                                       className={`w-full bg-transparent text-white font-black outline-none transition-all placeholder:text-slate-700 ${(formData.debt || '').length > 15 ? 'text-lg' : (formData.debt || '').length > 11 ? 'text-xl' : (formData.debt || '').length > 8 ? 'text-2xl' : 'text-3xl'}`}
                                       placeholder="R$ 0,00"
                                    />
                                 </div>
                              </div>

                              {/* 🔥 CAMPOS AVANÇADOS — só Gestor com plano pago (Tactical+) */}
                              {gestorPlan && gestorPlan !== 'entry' && formData.userType !== 'Consultor' && (
                                 <div className="space-y-6 pt-4 border-t border-white/5">
                                    <div>
                                       <p className="text-[11px] text-lime-400 font-black uppercase tracking-widest mb-3">Detalhes Opcionais</p>
                                       {(() => {
                                          const preenchidos = [formData.custoNaoRecorrente, formData.prazoRecebimento, formData.prazoPagamento, formData.dividaCurtoPrazo, formData.proLabore, formData.custoDivida].filter(v => v && v !== '').length;
                                          const precisao = 60 + Math.round((preenchidos / 6) * 40);
                                          return (
                                             <div className="bg-slate-900/60 border border-white/5 rounded-2xl p-5">
                                                <div className="flex items-baseline justify-between mb-2">
                                                   <span className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">Precisão do diagnóstico</span>
                                                   <span className="text-2xl text-lime-400 font-black italic">{precisao}%</span>
                                                </div>
                                                <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden">
                                                   <div
                                                      className="h-full bg-gradient-to-r from-lime-500 to-lime-400 rounded-full transition-all duration-500 shadow-[0_0_10px_rgba(190,242,100,0.5)]"
                                                      style={{ width: `${precisao}%` }}
                                                   />
                                                </div>
                                             </div>
                                          );
                                       })()}
                                    </div>
                                    <p className="text-[11px] text-slate-500 leading-relaxed">
                                       Esses campos não são obrigatórios, mas quanto mais você preencher, mais preciso fica o seu diagnóstico.
                                    </p>

                                    <div className="space-y-3">
                                       <label className="text-[11px] font-bold text-slate-400 normal-case tracking-normal leading-snug italic">Algum custo foi algo que não se repete todo mês? (ex: uma reforma, um processo)</label>
                                       <div className="bg-slate-900/40 border border-white/5 p-6 rounded-[2rem] shadow-inner focus-within:border-lime-400/50 transition-all">
                                          <input type="tel" value={formData.custoNaoRecorrente || ''} onChange={e => handleCurrencyChange(e, 'custoNaoRecorrente')}
                                             className="w-full bg-transparent text-white font-black text-2xl outline-none placeholder:text-slate-700"
                                             placeholder="R$ 0,00 (opcional)" />
                                       </div>
                                       <AvisoCustoNaoRecorrente custos={formData.costs} naoRecorrente={formData.custoNaoRecorrente} />
                                    </div>

                                    <div className="space-y-3">
                                       <label className="text-[11px] font-bold text-slate-400 normal-case tracking-normal leading-snug italic">Em quantos dias, em média, seus clientes pagam?</label>
                                       <input type="number" value={formData.prazoRecebimento || ''} onChange={e => setFormData({ ...formData, prazoRecebimento: e.target.value })}
                                          className="w-full bg-slate-900/40 border border-white/5 p-6 rounded-[2rem] text-white font-bold outline-none focus:border-lime-400/50 transition-all shadow-inner placeholder:text-slate-700"
                                          placeholder="Ex: 30 (opcional)" />
                                    </div>

                                    <div className="space-y-3">
                                       <label className="text-[11px] font-bold text-slate-400 normal-case tracking-normal leading-snug italic">Em quantos dias, em média, você paga seus fornecedores?</label>
                                       <input type="number" value={formData.prazoPagamento || ''} onChange={e => setFormData({ ...formData, prazoPagamento: e.target.value })}
                                          className="w-full bg-slate-900/40 border border-white/5 p-6 rounded-[2rem] text-white font-bold outline-none focus:border-lime-400/50 transition-all shadow-inner placeholder:text-slate-700"
                                          placeholder="Ex: 45 (opcional)" />
                                    </div>

                                    <div className="space-y-3">
                                       <label className="text-[11px] font-bold text-slate-400 normal-case tracking-normal leading-snug italic">Dessa dívida total, quanto vence até o fim deste ano?</label>
                                       <div className="bg-slate-900/40 border border-white/5 p-6 rounded-[2rem] shadow-inner focus-within:border-lime-400/50 transition-all">
                                          <input type="tel" value={formData.dividaCurtoPrazo || ''} onChange={e => handleCurrencyChange(e, 'dividaCurtoPrazo')}
                                             className="w-full bg-transparent text-white font-black text-2xl outline-none placeholder:text-slate-700"
                                             placeholder="R$ 0,00 (opcional)" />
                                       </div>
                                    </div>

                                    <div className="space-y-3">
                                       <label className="text-[11px] font-bold text-slate-400 normal-case tracking-normal leading-snug italic">Qual a taxa de juros média dessa dívida? (ao ano)</label>
                                       <div className="bg-slate-900/40 border border-white/5 p-6 rounded-[2rem] shadow-inner focus-within:border-lime-400/50 transition-all flex items-center">
                                          <input type="number" value={formData.custoDivida || ''} onChange={e => setFormData({ ...formData, custoDivida: e.target.value })}
                                             className="w-full bg-transparent text-white font-black text-2xl outline-none placeholder:text-slate-700"
                                             placeholder="Ex: 18 (opcional)" />
                                          <span className="text-slate-500 font-bold text-xl ml-2">% a.a.</span>
                                       </div>
                                    </div>

                                    <div className="space-y-3">
                                       <label className="text-[11px] font-bold text-slate-400 normal-case tracking-normal leading-snug italic">Quanto você tira de salário/retirada por mês?</label>
                                       <div className="bg-slate-900/40 border border-white/5 p-6 rounded-[2rem] shadow-inner focus-within:border-lime-400/50 transition-all">
                                          <input type="tel" value={formData.proLabore || ''} onChange={e => handleCurrencyChange(e, 'proLabore')}
                                             className="w-full bg-transparent text-white font-black text-2xl outline-none placeholder:text-slate-700"
                                             placeholder="R$ 0,00 (opcional)" />
                                       </div>
                                    </div>
                                 </div>
                              )}
                           </div>
                           );
                        })()}

                        {/* BOTÃO GLOBAL DE NAVEGAÇÃO UNIFICADO (DENTRO DO WIZARD) */}
                        {step !== 3 && (
                           <div className="pt-6">
                              <button
                                 onClick={() => {
                                    if (step === 5) return handleStartScanning();
                                    if (step === 1 && formData.userType === 'Consultor') {
                                       setView('consultor_showcase');
                                       return;
                                    }
                                    setStep(s => s + 1);
                                 }}
                                 disabled={step === 1 ? (!formData.expertise || !formData.userType) : !isStepValid()}
                                 className={`w-full py-6 rounded-[2rem] font-black uppercase tracking-widest text-sm transition-all shadow-xl flex items-center justify-center gap-2 
                                 ${(step === 1 ? (!formData.expertise || !formData.userType) : !isStepValid())
                                       ? 'bg-slate-800 text-slate-600 border border-white/5 cursor-not-allowed'
                                       : (step === 1 && formData.userType === 'Consultor')
                                          ? 'bg-blue-600 text-white shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:bg-blue-500'
                                          : 'bg-lime-400 text-slate-900 hover:bg-lime-500 shadow-[0_0_20px_rgba(190,242,100,0.2)] active:scale-95'}`}
                              >
                                 {step === 5 ? 'Iniciar Diagnóstico IA' : (step === 1 && formData.userType === 'Consultor' ? 'Prosseguir para planos corporativos' : 'Próxima Etapa')}
                                 {step < 5 && (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={(step === 1 && formData.userType === 'Consultor') ? 'text-white' : 'text-slate-900'}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                                 )}
                              </button>
                           </div>
                        )}
                     </div>
                  )}

                  {/* =================================================================== */}
                  {/* TELA: VITRINE DE ASSINATURA DOS 3 PLANOS DO CONSULTOR (GLASS ELITE) */}
                  {/* =================================================================== */}
                  {view === 'consultor_showcase' && (
                     <div className="w-full min-h-screen bg-transparent text-white py-12 px-3 relative z-50 overflow-y-auto block text-left animate-fade-up">

                        {/* CABEÇALHO INTEGRADO - ALINHAMENTO COMPACTO EM LINHA */}
                        <div className="w-full max-w-xl mx-auto mb-10 block">
                           <div className="flex flex-row items-center justify-between gap-4 border-b border-white/10 pb-5 w-full">
                              <div className="block">
                                 <span className="px-3.5 py-1 rounded-full text-[11px] font-black uppercase tracking-widest text-blue-400 bg-blue-500/10 border border-blue-500/20">
                                    Console Partner B2B
                                 </span>
                                 <h2 className="text-xl font-black text-white uppercase italic tracking-tighter mt-2.5 leading-none">
                                    Mesa de Operações <span className="text-blue-500">Corporate</span>
                                 </h2>
                              </div>

                              {/* 💊 BOTÃO VOLTAR MINIMALISTA (PILULA ENXUTA) */}
                              <div className="shrink-0">
                                 <button
                                    onClick={() => setView('wizard')}
                                    className="rounded-full py-1.5 px-4 bg-white/5 border border-white/10 text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all flex items-center gap-1.5"
                                 >
                                    ← Voltar
                                 </button>
                              </div>
                           </div>
                        </div>

                        {/* 💎 VITRINE EM GLASSMORPHISM REAL - SEM FUNDO PRETO INTERNO */}
                        <div className="w-full max-w-xl mx-auto block mb-16">
                           <div className="flex flex-col gap-6 w-full">
                              {[
                                 {
                                    id: 'advisor',
                                    name: 'Advisor PRO',
                                    price: '499,00',
                                    limit: 'Até 5 CNPJs Simultâneos',
                                    tag: 'Mesa de Operações Standard',
                                    icon: <Briefcase size={20} className="text-blue-400" />,
                                    theme: {
                                       glow: 'from-blue-500/10 to-transparent',
                                       border: 'border-white/10 hover:border-blue-500/30',
                                       badgeBg: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
                                       textMain: 'text-blue-400',
                                       buttonBg: 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_20px_rgba(37,99,235,0.2)]',
                                    },
                                    // 🔥 CORRIGIDO (2026-09-29): horizonte de projeção real é 3 e 6
                                    // meses (Curto Prazo) — não 12, que só entra no Boutique Elite
                                    // (mesma regra de negócio do lado Gestor: Tactical=curto,
                                    // +médio no nível seguinte, +longo no topo).
                                    benefits: ['IA Estocástica — Projeção de 3 e 6 Meses', 'Carteira de até 5 CNPJs simultâneos', 'Mesa de Operações Integrada', 'CRM de Carteira de Clientes', 'Simulações e Diagnósticos Ilimitados por IA', 'Módulo APEX de Valuation ao vivo (múltiplo e WACC ajustáveis)', 'Dossiês por cliente salvos na nuvem, acesso de qualquer aparelho']
                                 },
                                 {
                                    id: 'boutique',
                                    name: 'Boutique Elite',
                                    price: '1.997,00',
                                    limit: 'Até 15 CNPJs + Matriz de Sensibilidade',
                                    tag: 'Partner B2B Elite',
                                    badge: 'MAIS VENDIDO',
                                    icon: <Target size={20} className="text-purple-400" />,
                                    theme: {
                                       glow: 'from-purple-500/10 to-transparent',
                                       border: 'border-white/10 shadow-[0_0_30px_rgba(168,85,247,0.05)] hover:border-purple-500/30',
                                       borderStrong: 'border-purple-400/60',
                                       badgeBg: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
                                       textMain: 'text-purple-400',
                                       buttonBg: 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg',
                                    },
                                    // 🔥 CORREÇÃO: prometia "Dossiê 100% White-Label" aqui, mas essa
                                    // marca própria é exclusiva do Enterprise Ultra — deixado assim
                                    // induzia o cliente Boutique a pagar achando que já tinha isso.
                                    previousPlan: 'Advisor PRO',
                                    benefits: ['IA Estocástica — Projeção +12 Meses', 'Carteira de até 15 CNPJs simultâneos', 'Matriz de Sensibilidade APEX — 9 Cenários', 'Super Relatório 360º em PDF Ilimitado', 'Prioridade de suporte sobre o Advisor PRO', 'Selo Partner B2B Elite pra apresentar ao cliente']
                                 },
                                 {
                                    id: 'enterprise',
                                    name: 'Enterprise Ultra',
                                    price: '3.997,00',
                                    limit: 'CNPJs e Carteiras Ilimitadas',
                                    tag: 'Corporate Enterprise',
                                    icon: <Crown size={20} className="text-lime-400" />,
                                    theme: {
                                       glow: 'from-white/5 to-transparent',
                                       border: 'border-white/10 hover:border-lime-500/30',
                                       badgeBg: 'bg-lime-400/10 text-lime-400 border-lime-500/20',
                                       textMain: 'text-lime-400',
                                       buttonBg: 'bg-lime-400 hover:bg-lime-300 text-black font-black shadow-md',
                                    },
                                    previousPlan: 'Boutique Elite',
                                    benefits: ['IA Estocástica — Projeção +24 e 36 Meses', 'Carteiras Multi-Tenant Ilimitadas (CNPJs sem limite)', 'Dossiê 100% White-Label com sua marca, logo e cores', 'IA Preditiva avançada', 'Suporte Dedicado com SLA de 99,9%']
                                 }
                              ].map((plano) => {
                                 // 🔥 MAPEAMENTO DE NÍVEIS DOS PLANOS DO CONSULTOR
                                 const consultorPlanLevel = {
                                    'advisor': 5,
                                    'boutique': 6,
                                    'enterprise': 7
                                 };

                                 // Verifica se o plano já foi adquirido
                                 const isAcquired = consultorPlanLevel[consultorPlan] >= consultorPlanLevel[plano.id] && consultorPlan !== 'free';
                                 return (
                                    <div
                                       key={plano.id}
                                       className={`w-full bg-gradient-to-b from-white/[0.08] to-transparent backdrop-blur-2xl rounded-[2rem] flex flex-col justify-between relative overflow-hidden transition-all duration-300 hover:bg-white/[0.12] ${plano.badge ? `border-2 ${plano.theme.borderStrong || 'border-lime-400/50'} ${plano.theme.featuredGlow || 'shadow-[0_0_45px_rgba(190,242,100,0.18)]'} p-6` : 'border border-white/10 p-6'}`}
                                    >
                                       {/* Fita "Mais Vendido" — dentro do próprio card (não flutua por
                                       cima da borda), pra nunca ser cortada pela barra fixa do topo do
                                       app quando a tela rola. */}
                                       {plano.badge && (
                                          <div className="flex justify-center -mt-6 -mx-6 mb-5">
                                             <span className={`inline-flex items-center gap-1.5 ${plano.theme.badgeBg} text-[11px] font-black uppercase px-4 py-2 tracking-[0.15em] border-b border-x rounded-b-2xl ${plano.theme.featuredGlow || 'shadow-[0_0_20px_rgba(190,242,100,0.35)]'} whitespace-nowrap`}>
                                                ★ {plano.badge}
                                             </span>
                                          </div>
                                       )}
                                       {/* Selo "Adquirido" isolado no canto — desce quando tem a fita
                                       "Mais Vendido" acima (senão ficam um em cima do outro), e o texto
                                       da tag logo abaixo ganha respiro à direita pra nunca ficar
                                       embaixo dele. */}
                                       {isAcquired && (
                                          <span className={`absolute ${plano.badge ? 'top-12' : 'top-4'} right-4 z-20 bg-lime-400/20 text-lime-400 text-[11px] font-black uppercase px-2 py-1 rounded-full tracking-wider border border-lime-400/30 whitespace-nowrap`}>
                                             ✓ Adquirido
                                          </span>
                                       )}

                                       {/* Esfera Cromática de Brilho de Fundo — mais presente, dá apelo visual a todos os cards */}
                                       <div className={`absolute -top-24 -right-24 w-40 h-40 rounded-full blur-[50px] pointer-events-none opacity-[0.14] bg-gradient-to-br ${plano.theme.glow}`}></div>
                                       <div className="relative z-10 block text-left w-full">
                                          <span className={`block text-[11px] font-black uppercase tracking-widest text-slate-500 mb-5 ${isAcquired ? 'pr-24' : ''}`}>
                                             {plano.tag}
                                          </span>

                                          {/* 💎 PADRÃO VISUAL GESTOR: Alinhamento horizontal limpo e preço direto no vidro */}
                                          <div className="w-full flex items-center justify-between gap-4 mb-5 border-b border-white/5 pb-5">
                                             <div className="flex flex-col text-left">
                                                <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-1 whitespace-nowrap">
                                                   {plano.name}
                                                </h3>
                                                <div className="flex flex-row items-baseline gap-0.5 whitespace-nowrap flex-nowrap select-none">
                                                   <span className="text-[11px] font-black text-slate-400 italic">R$</span>
                                                   <span className="text-3xl font-black text-white tracking-tighter italic leading-none">{plano.price}</span>
                                                   <span className="text-[11px] text-slate-500 font-bold uppercase tracking-wider ml-1">/mês</span>
                                                </div>
                                             </div>

                                             {/* Ícone flutuando na direita de forma orgânica e luxuosa */}
                                             <div className="w-11 h-11 bg-white/[0.04] rounded-2xl flex items-center justify-center border border-white/10 shrink-0 shadow-inner">
                                                {plano.icon}
                                             </div>
                                          </div>

                                          <div className="text-[11px] font-black uppercase tracking-widest text-slate-400 bg-white/5 border border-white/5 px-2.5 py-1 rounded-lg w-max mb-5">
                                             {plano.limit}
                                          </div>

                                          {/* Grade de Recursos */}
                                          <div className="w-full mb-2">
                                             {/* 🔥 NOVO (2026-09-29): mesma estrutura dos cards do Gestor —
                                                 título "Tudo do X, mais:" acima da lista, em vez de
                                                 enterrar isso como só mais um item da lista. */}
                                             {plano.previousPlan && (
                                                <p className={`text-[11px] font-black uppercase tracking-widest ${plano.theme.textMain} mb-3`}>Tudo do {plano.previousPlan}, mais:</p>
                                             )}
                                             <ul className="space-y-3.5 w-full">
                                                {plano.benefits.map((benefit, idx) => (
                                                   <li key={idx} className="flex items-start gap-3 text-[11px] font-bold text-slate-400 tracking-wide uppercase leading-tight w-full">
                                                      <div className={`w-1.5 h-1.5 rounded-full mt-1 shrink-0 shadow-[0_0_6px_currentColor] ${plano.theme.textMain}`}></div>
                                                      <span className="flex-1">{benefit}</span>
                                                   </li>
                                                ))}
                                             </ul>
                                          </div>
                                       </div>

                                       {isAcquired ? (
                                          <button
                                             onClick={() => {
                                                setView('report');
                                                setReportTab('carteira');
                                             }}
                                             className="w-full py-4 px-3 rounded-full uppercase text-[11px] font-black tracking-widest transition-all duration-200 active:scale-95 flex items-center justify-center gap-2 relative z-10 mt-4 bg-lime-400/20 text-lime-400 border border-lime-400/30 hover:bg-lime-400/30 text-center"
                                          >
                                             <CheckCircle2 size={16} className="shrink-0" /> <span className="text-center leading-snug">Já Adquirido — Acessar Carteira</span>
                                          </button>
                                       ) : (
                                          <button
                                             onClick={() => {
                                                // 🔥 CORREÇÃO: mesma checagem central de dupla
                                                // assinatura, em vez de abrir o checkout direto.
                                                handleProtocolSelection(plano.id, plano.name, plano.price, 'CONSULTOR');
                                             }}
                                             className={`w-full py-4 rounded-full uppercase text-[11px] font-black tracking-widest transition-all duration-200 active:scale-95 flex items-center justify-center gap-2 relative z-10 mt-6 ${plano.theme.buttonBg}`}
                                          >
                                             Ativar Infraestrutura <ArrowRight size={14} />
                                          </button>
                                       )}
                                    </div>
                                 );
                              })}
                           </div>
                           {/* 🔥 CORRIGIDO — DISCLAIMERS DIVERGENTES: existiam 4 cópias
                               manuais deste texto em arquivos diferentes (2 no app, 1 no
                               Dashboard Gestor, 1 no PDF), cada uma redigida ligeiramente
                               diferente e sujeita a divergir ainda mais com o tempo. Agora
                               todas usam a mesma constante DISCLAIMER_FULL (constants.js) —
                               fonte única, nunca mais diverge. */}
                           <p className="text-[11px] text-slate-600 text-center uppercase tracking-widest leading-relaxed mt-2 px-4">
                              {DISCLAIMER_FULL}
                           </p>
                        </div>

                     </div>
                  )}

                  {/* =================================================================== */}
                  {/* TELA: CRIAÇÃO DE CONTA DO CONSULTOR (PÓS-ASSINATURA) */}
                  {/* =================================================================== */}
                  {view === 'consultor_create_account' && (
                     <div className="absolute inset-0 z-[60] bg-[#010208] flex flex-col items-center justify-center p-6 animate-in fade-in duration-500">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-600/10 rounded-full blur-[100px] pointer-events-none"></div>

                        <div className="w-full max-w-md bg-slate-900/60 backdrop-blur-2xl border border-white/10 p-8 md:p-10 rounded-[2.5rem] shadow-[0_12px_40px_0_rgba(0,0,0,0.5)] relative z-10">
                           <div className="flex justify-center mb-6">
                              <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center border border-blue-500/20 shadow-inner">
                                 <User size={32} className="text-blue-400" />
                              </div>
                           </div>

                           <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter text-center mb-2">
                              {user && !user.isAnonymous ? (<>Ativar Perfil <span className="text-blue-400">Consultor</span></>) : (<>Crie sua <span className="text-blue-400">Conta</span></>)}
                           </h2>
                           <p className="text-[11px] text-slate-400 text-center mb-8 font-bold uppercase tracking-widest leading-relaxed">
                              {user && !user.isAnonymous
                                 ? `Sua conta (${user.email}) vai ganhar acesso à Mesa de Operações do Consultor.`
                                 : 'Defina suas credenciais para acessar a sua Mesa de Operações.'}
                           </p>

                           {user && !user.isAnonymous ? (
                              // 🔥 Já logado (ex: Gestor querendo também virar Consultor): não pede
                              // senha de novo, só adiciona o perfil Consultor à conta já existente.
                              <button
                                 onClick={() => {
                                    // 🔥 CORRIGIDO — VULNERABILIDADE CRÍTICA (achada em teste real
                                    // com Asaas de produção, 2026-09-28): este botão chamava
                                    // `ativarPerfilConsultorDeVerdade()`, que gravava
                                    // `consultorPlan` direto no Firestore SEM NENHUM pagamento —
                                    // bastava estar logado e clicar aqui pra ganhar o perfil
                                    // Consultor de graça. Agora sempre manda pro checkout de
                                    // verdade (mesmo fluxo do card de decisão de dupla
                                    // assinatura) — o plano só é liberado depois que o Asaas
                                    // confirma o pagamento e o webhook grava no servidor.
                                    if (gestorPlan && gestorPlan !== 'entry') {
                                       setMostrarDecisaoDuplaAssinatura(true);
                                       return;
                                    }
                                    setView('consultor_showcase');
                                 }}
                                 className="w-full bg-blue-500 text-white py-4 rounded-2xl font-black uppercase text-xs tracking-widest active:scale-95 transition-all"
                              >
                                 Ver Planos do Consultor
                              </button>
                           ) : (
                              <form
                                 onSubmit={async (e) => {
                                    e.preventDefault();

                                    const nome = e.target.nome.value;
                                    const email = e.target.email.value;
                                    const senha = e.target.senha.value;

                                    if (!nome || !email || !senha || senha.length < 6) {
                                       setToastMessage("Preencha todos os campos e use uma senha com pelo menos 6 caracteres.");
                                       return;
                                    }

                                    try {
                                       // 🔥 Liga a trava ANTES de chamar o Firebase — ele dispara o onAuthStateChanged
                                       // por conta própria, de forma independente do resto do código, então a trava
                                       // precisa estar ligada antes da chamada, não depois.
                                       skipNextLoadRef.current = true;

                                       // 🔥 CORRIGIDO — MESMA VULNERABILIDADE CRÍTICA DO LADO GESTOR (ver
                                       // comentário na criação de conta Gestor): gravava
                                       // `consultorPlan: activePlan || 'advisor'` direto do navegador,
                                       // sem checar se algum pagamento foi confirmado de verdade pelo
                                       // webhook. Removido — conta nasce sem plano de Consultor; o
                                       // listener do Firestore assume o valor certo se/quando o
                                       // webhook confirmar um pagamento real pra esse uid.
                                       const cred = await createUserWithEmailAndPassword(auth, email, senha);
                                       registrarAceite('criar_conta', null);
                                       await setDoc(doc(db, 'usuarios', cred.user.uid), {
                                          nome, email, role: 'consultor', criadoEm: new Date().toISOString()
                                       });

                                       // 🔥 Migra os dossiês avulsos pra nova conta
                                       localStorage.setItem(`phoenix_history_${cred.user.uid}`, safeStringify(historyList));

                                       if (typeof onLoginSuccess === 'function') onLoginSuccess(cred.user);

                                       setToastMessage(`Bem-vindo, ${nome}! Sua conta foi criada com sucesso.`);

                                       localStorage.setItem('phoenix_user_name', nome);
                                       localStorage.setItem('phoenix_user_type', 'consultor');
                                       localStorage.removeItem('phoenix_consultor_plan');

                                       setFormData({
                                          method: '', profile: '', companyName: '', segment: '', niche: '',
                                          revenue: '', costs: '', debt: '', expertise: 'Avançado', userType: 'Consultor'
                                       });
                                       setSimRevenue(0);
                                       setSimCosts(0);
                                       setAiJustification('');
                                       setActiveDossierId(null);
                                       if (typeof setStep === 'function') setStep(1);
                                       if (typeof setReportTab === 'function') setReportTab('carteira');

                                       setView('consultor_data_entry');
                                    } catch (error) {
                                       console.error('[PHOENIX] Erro ao criar conta:', error.code);
                                       if (error.code === 'auth/email-already-in-use') {
                                          setToastMessage('Este email já possui uma conta. Faça login em vez de criar uma nova.');
                                       } else {
                                          setToastMessage(mensagemErroCriarConta(error.code));
                                       }
                                    }
                                 }}
                                 className="space-y-5"
                              >
                                 <div>
                                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Nome do Auditor</label>
                                    <input
                                       type="text"
                                       name="nome"
                                       required
                                       className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                       placeholder="Seu nome"
                                    />
                                 </div>

                                 <div>
                                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">E-mail Corporativo</label>
                                    <input
                                       type="email"
                                       name="email"
                                       required
                                       className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                       placeholder="seu@email.com"
                                    />
                                 </div>

                                 <div>
                                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Definir Senha de Acesso</label>
                                    <input
                                       type="password" autoComplete="new-password"
                                       name="senha"
                                       required
                                       className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                       placeholder="••••••••"
                                    />
                                 </div>

                                 <AceiteAtivo name="aceite" required />

                                 <button
                                    type="submit"
                                    className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-black uppercase tracking-widest shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all active:scale-95 mt-4"
                                 >
                                    Registrar e Acessar Portal
                                 </button>
                              </form>
                           )}
                        </div>
                     </div>
                  )}

                  {/* =================================================================== */}
                  {/* TELA: ENTRADA DE DADOS DO CONSULTOR (PÓS-ASSINATURA) */}
                  {/* =================================================================== */}
                  {view === 'consultor_data_entry' && (
                     <div className="w-full min-h-screen bg-transparent text-white flex flex-col items-center justify-center p-2 relative z-50 animate-in fade-in duration-500">

                        <div className="w-full max-w-md bg-slate-900/40 backdrop-blur-2xl border border-white/10 p-5 md:p-10 rounded-[2.5rem] shadow-[0_12px_40px_0_rgba(0,0,0,0.5)] relative z-10">

                           {/* 🔥 BOTÃO VOLTAR - POSICIONADO NO CANTO SUPERIOR ESQUERDO */}
                           <button
                              onClick={() => {
                                 const ultimoDossierConsultor = historyList.find(i =>
                                    String(i.snapshot?.userType || i.snapshot?.formData?.userType || '').toLowerCase() === 'consultor'
                                 );
                                 if (ultimoDossierConsultor) {
                                    loadDossier(ultimoDossierConsultor);
                                 }
                                 setReportTab('carteira');
                                 setView('report');
                              }}
                              className="absolute top-4 left-4 text-slate-400 hover:text-white transition-colors text-[11px] font-black uppercase tracking-widest flex items-center gap-1 z-20"
                           >
                              <ArrowLeft size={14} /> Voltar
                           </button>

                           {/* ÍCONE - CENTRALIZADO */}
                           <div className="flex justify-center mb-6 pt-6">
                              <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center border border-blue-500/20 shadow-inner">
                                 <Briefcase size={32} className="text-blue-400" />
                              </div>
                           </div>

                           <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter text-center mb-2">
                              Dados do <span className="text-blue-400">Cliente</span>
                           </h2>
                           <p className="text-[11px] text-slate-400 text-center mb-8 font-bold tracking-normal leading-relaxed">
                              Insira as informações da empresa que você vai analisar.
                           </p>

                           <form
                              onSubmit={async (e) => {
                                 e.preventDefault();

                                 if (!formData.companyName || !formData.segment || !formData.niche || !formData.revenue || !formData.costs || !formData.debt || !formData.custoNaoRecorrente || !formData.prazoRecebimento || !formData.prazoPagamento || !formData.dividaCurtoPrazo || !formData.custoDivida || !formData.proLabore) {
                                    setToastMessage("Preencha todos os campos obrigatórios.");
                                    return;
                                 }

                                 // 🔥 CORREÇÃO: usava activePlan — uma variável genérica que guarda
                                 // o plano do ÚLTIMO dossiê aberto, seja Gestor ou Consultor. Se a
                                 // pessoa vinha de um dossiê Gestor "Total" e ativava o Consultor,
                                 // essa variável continuava "total" (nem existe pro Consultor),
                                 // bagunçando o nível de acesso. Agora usa consultorPlan de verdade.
                                 const planId = consultorPlan || 'advisor';
                                 const planLevelMap = {
                                    'entry': 1,
                                    'tactical': 2,
                                    'plus': 3,
                                    'total': 4,
                                    'boardroom': 4,
                                    'advisor': 5,
                                    'boutique': 6,
                                    'enterprise': 7
                                 };

                                 const targetLevel = planLevelMap[planId] || 5;
                                 setUserLevel(targetLevel);

                                 // 🔥 BLINDAGEM: só reaproveita activeDossierId se ele já pertencer
                                 // a um dossiê CONSULTOR de verdade (edição de um cliente existente).
                                 // Sem essa checagem, se activeDossierId "vazasse" de alguma forma
                                 // de uma tela anterior (ex: um dossiê Gestor recém-criado), o
                                 // formData/merge:true poderia sobrescrever o userType daquele
                                 // dossiê Gestor pra 'Consultor' por engano — exatamente o bug
                                 // relatado de dossiê Gestor "vazando" pra dentro da carteira do
                                 // Consultor. Sempre que não tiver certeza, gera um ID novo.
                                 const itemExistente = activeDossierId ? historyList.find(i => i.id === activeDossierId) : null;
                                 const pertenceAoConsultor = itemExistente
                                    ? String(itemExistente.snapshot?.userType || '').toLowerCase() === 'consultor'
                                    : false;
                                 const idParaSalvar = pertenceAoConsultor ? activeDossierId : gerarIdUnico();
                                 const novoItemConsultor = {
                                    id: idParaSalvar,
                                    name: formData.companyName || 'Nova Análise',
                                    date: new Date().toLocaleDateString('pt-BR'),
                                    plan: String(consultorPlan || planId || 'advisor').toUpperCase(),
                                    snapshot: { formData, simRevenue, simCosts, userType: 'Consultor' }
                                 };
                                 setHistoryList(prev => {
                                    const jaExiste = prev.some(item => item.id === idParaSalvar);
                                    return jaExiste
                                       ? prev.map(item => item.id === idParaSalvar ? novoItemConsultor : item)
                                       : [novoItemConsultor, ...prev];
                                 });

                                 // 🔥 CORREÇÃO: antes o app ligava a escuta em tempo real (via
                                 // setActiveDossierId) ANTES do documento existir de verdade no
                                 // Firestore — sincronizarDossieUnico rodava em paralelo, sem
                                 // esperar. Se a escuta chegasse primeiro, o Firestore negava
                                 // ("documento não existe ainda") e NUNCA tentava de novo sozinho,
                                 // mesmo depois do documento ser criado — Tarefas e Anotações
                                 // ficavam mudas pro resto da sessão. Agora espera o documento
                                 // ser criado ANTES de ligar a escuta.
                                 await sincronizarDossieUnico(novoItemConsultor);
                                 setActiveDossierId(idParaSalvar);

                                 if (typeof window !== 'undefined') {
                                    localStorage.setItem('phoenix_active_plan', planId);
                                    localStorage.setItem('phoenix_user_level', targetLevel);
                                 }

                                 // 🔥 VAI PARA A TELA DE SCANNING
                                 setProgress(0);
                                 setView('scanning');
                              }}
                              className="space-y-5"
                           >

                              <div>
                                 <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Razão Social / Nome</label>
                                 <input
                                    type="text"
                                    value={formData.companyName || ''}
                                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                    placeholder="Ex: Cliente S.A."
                                    required
                                 />
                              </div>

                              <div>
                                 <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">CNPJ <span className="text-slate-600 normal-case">(opcional)</span></label>
                                 <input
                                    type="text"
                                    value={formData.cnpj || ''}
                                    onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                    placeholder="00.000.000/0000-00"
                                 />
                              </div>

                              <div>
                                 <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Segmento</label>
                                 <select
                                    value={formData.segment || ''}
                                    onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                    required
                                 >
                                    <option value="">Selecione...</option>
                                    <option value="Varejo">Varejo</option>
                                    <option value="Alimentação">Alimentação</option>
                                    <option value="Indústria">Indústria</option>
                                    <option value="Serviços">Serviços</option>
                                    <option value="Tech/SaaS">Tech/SaaS</option>
                                    <option value="Holding / Grupo">Holding / Grupo</option>
                                    <option value="Atacado/Distrib.">Atacado/Distrib.</option>
                                    <option value="Saúde/Clínicas">Saúde/Clínicas</option>
                                    <option value="Agronegócio">Agronegócio</option>
                                    <option value="Construção/Eng.">Construção/Eng.</option>
                                    <option value="Logística/Frota">Logística/Frota</option>
                                 </select>
                              </div>

                              <div>
                                 <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Nicho Específico <span className="text-slate-600 normal-case">(quanto mais específico, melhor a análise)</span></label>
                                 <input
                                    type="text"
                                    value={formData.niche || ''}
                                    onChange={(e) => setFormData({ ...formData, niche: e.target.value })}
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                    placeholder="Ex: Restaurante vegano (não só 'Alimentação')"
                                    required
                                 />
                              </div>

                              {/* Faturamento - linha única */}
                              <div>
                                 <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">{getFinancialFieldLabels(formData.segment).revenueLabel}</label>
                                 {getFinancialFieldLabels(formData.segment).revenueHint && <p className="text-[11px] text-slate-500 mb-2 ml-1 leading-relaxed normal-case tracking-normal">{getFinancialFieldLabels(formData.segment).revenueHint}</p>}
                                 <input
                                    type="tel"
                                    value={formData.revenue || ''}
                                    onChange={(e) => handleCurrencyChange(e, 'revenue')}
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                    placeholder="R$ 0,00"
                                    required
                                 />
                              </div>

                              {/* Custos - linha única */}
                              <div>
                                 <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">{getFinancialFieldLabels(formData.segment).costsLabel}</label>
                                 {getFinancialFieldLabels(formData.segment).costsHint && <p className="text-[11px] text-slate-500 mb-2 ml-1 leading-relaxed normal-case tracking-normal">{getFinancialFieldLabels(formData.segment).costsHint}</p>}
                                 <input
                                    type="tel"
                                    value={formData.costs || ''}
                                    onChange={(e) => handleCurrencyChange(e, 'costs')}
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                    placeholder="R$ 0,00"
                                    required
                                 />
                              </div>

                              {/* Dívidas - linha única */}
                              <div>
                                 <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Total de Dívidas (Empréstimos/Financiamentos)</label>
                                 {getFinancialFieldLabels(formData.segment).debtHint && <p className="text-[11px] text-slate-500 mb-2 ml-1 leading-relaxed normal-case tracking-normal">{getFinancialFieldLabels(formData.segment).debtHint}</p>}
                                 <input
                                    type="tel"
                                    value={formData.debt || ''}
                                    onChange={(e) => handleCurrencyChange(e, 'debt')}
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                    placeholder="R$ 0,00"
                                    required
                                 />
                              </div>
                              {/* Dados Técnicos Avançados — sempre visíveis pro Consultor */}
                              <div className="space-y-5 pt-5 border-t border-white/10">
                                 <div>
                                    <p className="text-[11px] text-blue-400 font-black uppercase tracking-widest mb-1">Dados Técnicos Avançados</p>
                                    <p className="text-[11px] text-slate-500 leading-relaxed">
                                       Necessários para o cálculo técnico completo. Você poderá editar esses dados a qualquer momento depois.
                                    </p>
                                 </div>

                                 {(() => {
                                    const preenchidos = [formData.custoNaoRecorrente, formData.prazoRecebimento, formData.prazoPagamento, formData.dividaCurtoPrazo, formData.custoDivida, formData.proLabore].filter(v => v && v !== '').length;
                                    const precisao = Math.round((preenchidos / 6) * 100);
                                    return (
                                       <div className="bg-slate-900/60 border border-white/5 rounded-2xl p-5">
                                          <div className="flex items-baseline justify-between mb-2">
                                             <span className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">Progresso do cadastro</span>
                                             <span className="text-2xl text-blue-400 font-black italic">{precisao}%</span>
                                          </div>
                                          <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden">
                                             <div
                                                className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all duration-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                                                style={{ width: `${precisao}%` }}
                                             />
                                          </div>
                                       </div>
                                    );
                                 })()}

                                 <div>
                                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Custo Não Recorrente (evento pontual)</label>
                                    <input
                                       type="tel"
                                       value={formData.custoNaoRecorrente || ''}
                                       onChange={(e) => handleCurrencyChange(e, 'custoNaoRecorrente')}
                                       className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                       placeholder="R$ 0,00"
                                       required
                                    />
                                    <AvisoCustoNaoRecorrente className="mt-2" custos={formData.costs} naoRecorrente={formData.custoNaoRecorrente} />
                                 </div>

                                 <div className="grid grid-cols-2 gap-3">
                                    <div>
                                       <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Prazo de Recebimento (dias)</label>
                                       <input
                                          type="number"
                                          value={formData.prazoRecebimento || ''}
                                          onChange={(e) => setFormData({ ...formData, prazoRecebimento: e.target.value })}
                                          className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                          placeholder="Ex: 30"
                                          required
                                       />
                                    </div>
                                    <div>
                                       <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Prazo de Pagamento (dias)</label>
                                       <input
                                          type="number"
                                          value={formData.prazoPagamento || ''}
                                          onChange={(e) => setFormData({ ...formData, prazoPagamento: e.target.value })}
                                          className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                          placeholder="Ex: 45"
                                          required
                                       />
                                    </div>
                                 </div>

                                 <div>
                                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Dívida de Curto Prazo (vence este ano)</label>
                                    <input
                                       type="tel"
                                       value={formData.dividaCurtoPrazo || ''}
                                       onChange={(e) => handleCurrencyChange(e, 'dividaCurtoPrazo')}
                                       className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                       placeholder="R$ 0,00"
                                       required
                                    />
                                 </div>

                                 <div className="grid grid-cols-2 gap-3">
                                    <div>
                                       <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Custo da Dívida (% a.a.)</label>
                                       <input
                                          type="number"
                                          value={formData.custoDivida || ''}
                                          onChange={(e) => setFormData({ ...formData, custoDivida: e.target.value })}
                                          className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                          placeholder="Ex: 18"
                                          required
                                       />
                                    </div>
                                    <div>
                                       <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Pró-labore (mensal)</label>
                                       <input
                                          type="tel"
                                          value={formData.proLabore || ''}
                                          onChange={(e) => handleCurrencyChange(e, 'proLabore')}
                                          className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors shadow-inner"
                                          placeholder="R$ 0,00"
                                          required
                                       />
                                    </div>
                                 </div>
                              </div>

                              <button
                                 type="submit"
                                 className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-[11px] font-black uppercase tracking-widest shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all active:scale-95 mt-4"
                              >
                                 Gerar Relatório do Cliente
                              </button>
                           </form>

                           <div className="mt-6 flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-500">
                              <ShieldCheck size={12} className="text-blue-500" /> Dados 100% confidenciais
                           </div>
                        </div>
                     </div>
                  )}

                  {/* 🔥 AJUSTADO: essa tela era alta demais pra caber numa tela de
                      celular real sem rolar (ícone gigante + espaçamentos largos +
                      5 itens de lista). Reduzido padding/tamanhos em todo o card pra
                      caber inteira numa tela só, sem sobra embaixo nem precisar
                      rolar. `justify-center` no container garante que fica
                      centralizada verticalmente independente da altura do aparelho. */}
                  {view === 'scanning' && (
                     <div className="flex flex-col items-center justify-center animate-fade-up w-full px-2 min-h-[80vh]">
                        <div className="w-full bg-slate-900/40 backdrop-blur-2xl border border-white/10 rounded-[3rem] p-7 flex flex-col items-center shadow-[0_0_50px_rgba(0,0,0,0.5)] relative overflow-hidden">

                           <div className="absolute -top-20 -left-20 w-64 h-64 bg-lime-400/5 blur-[80px] rounded-full"></div>
                           <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-blue-500/5 blur-[80px] rounded-full"></div>

                           <div className="relative w-44 h-44 flex items-center justify-center mb-6">
                              <div className="absolute inset-0 bg-lime-400/5 rounded-full blur-2xl animate-pulse"></div>
                              <svg className="w-full h-full transform -rotate-90 drop-shadow-[0_0_20px_rgba(190,242,100,0.3)]" viewBox="0 0 120 120">
                                 <circle cx="60" cy="60" r="54" fill="none" stroke="#1e293b" strokeWidth="4" />
                                 <circle cx="60" cy="60" r="54" fill="none" stroke="#bef264" strokeWidth="4"
                                    strokeDasharray={2 * Math.PI * 54}
                                    strokeDashoffset={2 * Math.PI * 54 - (progress / 100) * (2 * Math.PI * 54)}
                                    strokeLinecap="round" className="transition-all duration-300 ease-out" />
                              </svg>
                              <div className="absolute flex flex-col items-center justify-center">
                                 <span className="text-4xl font-black text-white italic tracking-tighter">{progress}%</span>
                                 <span className="text-[11px] text-lime-400 font-black uppercase tracking-[0.3em] mt-1 animate-pulse">Analysing</span>
                              </div>
                           </div>

                           <div className="text-center space-y-2 mb-6 relative z-10">
                              <h3 className="text-white font-black text-xl uppercase italic tracking-tighter leading-none">Phoenix OS® <span className="text-lime-400">Core</span></h3>
                              <p className="text-slate-500 text-[11px] font-black uppercase tracking-[0.4em]">Arquitetura de Dados Neural</p>
                           </div>

                           <div className="w-full space-y-2.5 relative z-10">
                              {(formData.expertise === 'Avançado'
                                 ? ["Matrix Engine v4.2", "Solvency Analytics", "Risk Mapping AI", "Fiscal Compliance Core", "EBITDA Predictor"]
                                 : ["Saúde do Caixa", "Peso das Dívidas", "Margens de Lucro", "Metas de Melhoria", "Parecer Executivo"]
                              ).map((eng, idx) => {
                                 const isActive = progress >= (idx * 20) && progress < ((idx + 1) * 20);
                                 const isDone = progress >= ((idx + 1) * 20);

                                 return (
                                    <div key={idx} className={`p-2.5 rounded-2xl border transition-all duration-500 flex items-center gap-3 ${isDone ? 'bg-lime-400/5 border-lime-400/20' : isActive ? 'bg-white/5 border-white/20 scale-[1.02]' : 'bg-transparent border-transparent opacity-20'}`}>
                                       <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                          {isDone ? (
                                             <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-lime-400 drop-shadow-[0_0_8px_#bef264]"><path d="M20 6 9 17l-5-5" /></svg>
                                          ) : isActive ? (
                                             <div className="w-3.5 h-3.5 border-2 border-lime-400 border-t-transparent rounded-full animate-spin"></div>
                                          ) : (
                                             <div className="w-1.5 h-1.5 rounded-full bg-slate-600"></div>
                                          )}
                                       </div>
                                       <span className={`text-[11px] font-black uppercase tracking-widest ${isDone ? 'text-lime-400' : isActive ? 'text-white' : 'text-slate-500'}`}>
                                          {eng}
                                       </span>
                                    </div>
                                 );
                              })}
                           </div>
                        </div>
                     </div>
                  )}

                  {view === 'ready' && (
                     <div className="flex flex-col justify-center space-y-4 py-3 animate-fade-up text-left min-h-[88vh]">
                        <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none text-left">Análise <span className="text-lime-400">Pronta</span></h2>
                        <div className="bg-slate-900/40 backdrop-blur-3xl border border-white/10 rounded-[2.5rem] p-7 space-y-6 shadow-2xl relative overflow-hidden text-left">
                           <div className="space-y-3.5 text-left"><p className="text-[11px] font-black text-white uppercase tracking-[0.3em] border-b border-white/5 pb-3.5 italic leading-none text-left">Dossiê Master Disponível:</p><ul className="space-y-3.5">{['EBITDA Real Time', 'Mapeamento de Ponto de Equilíbrio', 'Índices de Solvência', 'Parecer IA Profissional', 'Dossiê em PDF'].map(t => (<li key={t} className="flex items-center gap-3 text-slate-300 text-[11px] font-black uppercase tracking-tight leading-none text-left"><ShieldCheck className="text-lime-400" size={17} /> {t}</li>))}</ul></div>
                           <div className="pt-5 border-t border-white/10 flex flex-col items-center space-y-4">

                              {/* 🔥 BUG CORRIGIDO: essa condição só checava
                              'gestorPlan', nunca 'consultorPlan'. Resultado:
                              um Consultor com Enterprise Ultra ativo (R$
                              3.997/mês) via o paywall de R$49,90 toda vez
                              que cadastrava um cliente novo — porque o
                              gestorPlan dele (que nem é o perfil em uso)
                              estava vazio. Agora checa o plano certo
                              conforme o perfil do diagnóstico atual. */}
                              {activePlan === 'entry' && !(
                                 formData.userType === 'Consultor'
                                    ? (consultorPlan && consultorPlan !== 'free')
                                    : (gestorPlan && gestorPlan !== 'entry')
                              ) ? (
                                 // Mostra o paywall de R$ 49,90
                                 <>
                                    <div className="flex flex-col items-center">
                                       <span className="text-lime-400 font-black text-[11px] uppercase italic tracking-[0.5em] mb-2 leading-none">Apenas</span>
                                       <div className="bg-[#020617]/80 border border-lime-400/50 px-8 py-4 rounded-[2rem] shadow-[0_0_20px_rgba(190,242,100,0.15)] flex flex-col items-center w-full">
                                          <div className="flex items-baseline gap-1 text-lime-400">
                                             <span className="font-black text-xl italic uppercase">R$</span>
                                             <span className="font-black text-6xl tracking-tighter italic leading-none drop-shadow-[0_0_15px_rgba(190,242,100,0.6)]">49,90</span>
                                          </div>
                                          <span className="text-[11px] font-black uppercase opacity-80 italic mt-2 tracking-widest">Pagamento Único</span>
                                       </div>
                                    </div>
                                    <button
                                       onClick={() => handleProtocolSelection('entry', 'Phoenix Entry', '49,90', 'PAGAMENTO ÚNICO')}
                                       className="w-full bg-white text-black py-7 rounded-full font-black uppercase text-base shadow-lg hover:scale-105 active:scale-95 transition-all"
                                    >
                                       Desbloquear Dossiê
                                    </button>
                                    <button onClick={() => { setView('landing'); setStep(1); }} className="text-slate-500 font-bold py-3 mt-4 text-[11px] uppercase tracking-widest hover:text-white transition-colors">
                                       Cancelar e Voltar ao Início
                                    </button>
                                 </>
                              ) : (
                                 // Mostra "Acesso Liberado"
                                 <>
                                    <div className="bg-lime-400/10 border border-lime-400/30 p-6 rounded-3xl w-full text-center">
                                       <CheckCircle2 size={32} className="text-lime-400 mx-auto mb-3 drop-shadow-[0_0_10px_rgba(190,242,100,0.5)]" />
                                       <p className="text-lime-400 font-black uppercase tracking-widest text-xs italic">Acesso Liberado via Protocolo</p>
                                    </div>
                                    <button onClick={unlockDossier} className="w-full bg-lime-400 text-black py-7 rounded-full font-black uppercase text-base shadow-[0_0_20px_rgba(190,242,100,0.3)] hover:scale-105 active:scale-95 transition-all flex justify-center gap-2">
                                       {activePlan !== 'entry' && activeDossierId ? 'Atualizar Dossiê' : 'Visualizar Dossiê'}
                                    </button>
                                 </>
                              )}
                           </div>
                        </div>
                     </div>
                  )}           {/* DOSSIÊ FINAL E ABAS NAVEGÁVEIS */}
                  {/* 🔒 Dossiê Entry sem pagamento confirmado: mostra uma tela de
                      espera em vez do relatório, não importa como 'view' chegou
                      a 'report' (ver 'relatorioEntryBloqueado' acima). */}
                  {view === 'report' && relatorioEntryBloqueado && !entryStatusCarregado && (
                     <div className="flex items-center justify-center min-h-[70vh]">
                        <Loader2 size={28} className="animate-spin text-lime-400" />
                     </div>
                  )}
                  {view === 'report' && relatorioEntryBloqueado && entryStatusCarregado && (
                     <div className="flex flex-col items-center justify-center text-center py-20 px-6 space-y-6 animate-fade-up min-h-[70vh]">
                        <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                           <Lock size={28} className="text-amber-400" />
                        </div>
                        <div className="space-y-2">
                           <h2 className="text-xl font-black text-white uppercase tracking-tighter">Aguardando Pagamento</h2>
                           <p className="text-sm text-slate-400 max-w-xs mx-auto leading-relaxed">
                              Este relatório é liberado automaticamente assim que o pagamento do Phoenix Entry for confirmado. Se você já pagou, aguarde alguns instantes — a tela atualiza sozinha.
                           </p>
                        </div>
                        {/* 🔥 NOVO (2026-09-29): quem fecha a página do Asaas
                            sem querer (ou o PIX expira, o cartão é recusado
                            etc.) precisa de um jeito de pagar de novo sem
                            começar o diagnóstico do zero. Reabre o checkout
                            do Entry reaproveitando o MESMO dossiê
                            (activeDossierId já está setado nesta tela) — o
                            Asaas só recebe uma cobrança nova pra pagar. */}
                        {user && !user.isAnonymous && entryDocPlano === 'entry' && formData.userType !== 'Consultor'
                           && gestorPlan && gestorPlan !== 'entry' && (
                           <div className="w-full max-w-xs bg-lime-400/10 border border-lime-400/30 rounded-2xl p-4 space-y-3">
                              <p className="text-[11px] text-lime-200 leading-relaxed">
                                 Você tem o plano <strong className="text-white">{String(gestorPlan).toUpperCase()}</strong> ativo, que já inclui diagnósticos ilimitados. Gere este relatório pelo seu plano, sem pagar o Entry.
                              </p>
                              <button
                                 onClick={gerarRelatorioPeloPlano}
                                 disabled={gerandoPeloPlano}
                                 className="w-full bg-lime-400 text-black py-3.5 rounded-full font-black uppercase text-xs shadow-lg disabled:opacity-60 active:scale-95 transition-all"
                              >
                                 {gerandoPeloPlano ? 'Gerando…' : 'Gerar pelo meu plano'}
                              </button>
                           </div>
                        )}
                        <button
                           onClick={() => handleProtocolSelection('entry', 'Phoenix Entry', '49,90', 'PAGAMENTO ÚNICO')}
                           className="w-full max-w-xs bg-white text-black py-4 rounded-full font-black uppercase text-sm shadow-lg hover:scale-105 active:scale-95 transition-all"
                        >
                           Pagar Agora
                        </button>
                        <button
                           onClick={() => { setView('history'); }}
                           className="text-slate-500 font-bold text-[11px] uppercase tracking-widest hover:text-white transition-colors"
                        >
                           Voltar para Meus Dossiês
                        </button>
                     </div>
                  )}

                  {view === 'report' && !relatorioEntryBloqueado && (
                     <div className={`flex flex-col ${reportTab === 'agentes-ia' ? 'space-y-5 py-6' : 'space-y-12 py-10'} animate-fade-up text-left relative z-10 pb-32`}>

                        {/* CABEÇALHO */}
                        <div className={`space-y-2 border-b border-white/5 ${reportTab === 'agentes-ia' ? 'pb-4' : 'pb-8'} text-left`}>
                           <div className="flex justify-between items-center text-[11px] font-black text-slate-500 uppercase tracking-widest italic leading-none">
                              <span>{new Date().toLocaleDateString('pt-BR')}</span>
                              <span className="text-lime-400">PROTOCOLO: {formData.userType === 'Consultor' && consultorPlan && consultorPlan !== 'free' ? ({ advisor: 'ADVISOR PRO', boutique: 'BOUTIQUE ELITE', enterprise: 'ENTERPRISE ULTRA' }[String(consultorPlan).toLowerCase()] || String(consultorPlan).toUpperCase()) : String(activePlan || '').toUpperCase()}</span>
                           </div>
                           <h2 data-titulo-relatorio className={`${nameSizeClass} font-black text-white uppercase tracking-tighter leading-tight ${reportTab === 'agentes-ia' ? 'mt-3' : 'mt-6'} text-left line-clamp-2 break-words drop-shadow-[0_0_10px_currentColor]`}>
                              {/* 🔒 Título "Minha Carteira" só quando é REALMENTE Consultor
                                  na aba carteira — antes dependia só de reportTab, então um
                                  Gestor que caísse nessa aba (ex: alternando perfis) via o
                                  título errado. Amarrar ao userType fecha esse buraco. */}
                              {(reportTab === 'carteira' && formData.userType === 'Consultor') ? (
                                 <>Minha <span className="text-lime-400">Carteira</span></>
                              ) : companyNameText}
                           </h2>
                           <p className="text-[11px] text-slate-600 font-black uppercase tracking-[0.4em] mt-2 italic text-left">
                              {(reportTab === 'carteira' && formData.userType === 'Consultor') ? 'PARECER EXECUTIVO' : `${formData.segment || 'VAREJO'} • PARECER EXECUTIVO`}
                           </p>
                           {/* Selo de saúde em TODAS as abas do perfil Consultor com empresa aberta (igual ao Painel) */}
                           {formData.userType === 'Consultor' && reportTab !== 'carteira' && report?.status?.label && (
                              <span className={`inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest border border-white/10 bg-white/5 ${report.status.color}`}>
                                 <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                                 Saúde: {report.status.label}
                              </span>
                           )}
                        </div>

                        {/* Entry JÁ PAGO + plano de Gestor ativo: oferece a versão completa pelo plano (novo dossiê com os mesmos
                            dados). O Entry pago continua intacto e acessível mesmo se a assinatura for cancelada depois. */}
                        {reportTab === 'visao' && user && !user.isAnonymous && entryDocPlano === 'entry' && entryStatusPagamento === 'pago'
                           && formData.userType !== 'Consultor' && gestorPlan && gestorPlan !== 'entry' && (
                           <div className="bg-lime-400/10 border border-lime-400/30 rounded-2xl p-4 space-y-3 -mt-6">
                              <p className="text-[11px] text-lime-200 leading-relaxed">
                                 Este é o seu relatório <strong className="text-white">Entry</strong>. Como você tem o plano <strong className="text-white">{String(gestorPlan).toUpperCase()}</strong>, pode gerar a versão completa dele pelo plano (com as projeções e análises que o plano libera). <strong className="text-white">O Entry continua salvo e acessível para sempre, mesmo se você cancelar o plano.</strong> Já a versão gerada pelo plano fica disponível enquanto o plano estiver ativo.
                              </p>
                              <button
                                 onClick={gerarRelatorioPeloPlano}
                                 disabled={gerandoPeloPlano}
                                 className="w-full bg-lime-400 text-black py-3.5 rounded-full font-black uppercase text-xs shadow-lg disabled:opacity-60 active:scale-95 transition-all"
                              >
                                 {gerandoPeloPlano ? 'Gerando…' : ('Gerar versão ' + String(gestorPlan).toUpperCase())}
                              </button>
                           </div>
                        )}


                        {/* ========================================== */}
                        {/* ABAS - RENDERIZAÇÃO CONDICIONAL POR PERFIL */}
                        {/* ========================================== */}
                        {formData.userType === 'Consultor' ? (
                           <ConsultorReportTabs
                              reportTab={reportTab}
                              setReportTab={setReportTab}
                              formData={formData}
                              report={report}
                              finance={finance}
                              dynamicInsights={dynamicInsights}
                              parecerIATexto={parecerIATexto}
                              leituraProjecaoCurto={leituraProjecaoCurto}
                              leituraProjecaoMedio={leituraProjecaoMedio}
                              leituraProjecaoLongo={leituraProjecaoLongo}
                              oportunidadesTexto={oportunidadesTexto}
                              carregandoParecer={carregandoParecer}
                              iaGerando={iaGerando}
                              formatarParecerIA={formatarParecerIA}
                              formatCurrency={formatCurrency}
                              apexMultiple={apexMultiple}
                              setApexMultiple={setApexMultiple}
                              apexWacc={apexWacc}
                              setApexWacc={setApexWacc}
                              onGenerateSuperReport={() => handleExportPDF(true)}
                              setItemToDelete={setItemToDelete}

                              /* 🔥 GATILHO MESTRE DO RELATÓRIO 360 (Igual ao do Gestor) */
                              setShowDownloadConfirm={(v) => { if (v) setDownloadConfirmEhSuper(false); setShowDownloadConfirm(v); }}

                              /* Persistência Absoluta do Plano */
                              userPlan={String(consultorPlan || 'free').toLowerCase()}
                              historyList={historyList}
                              loadDossier={loadDossier}

                              /* 🔥 MOTOR DE CHECKOUT DINÂMICO DA ESCADA B2B: */
                              onUpgradeRequest={(planId) => {
                                 const productTiers = {
                                    'advisor': { id: 'advisor', name: 'Advisor PRO', price: '499,00' },
                                    'boutique': { id: 'boutique', name: 'Mesa Boutique Elite', price: '1.997,00' },
                                    'enterprise': { id: 'enterprise', name: 'Mesa Enterprise Ultra', price: '3.997,00' }
                                 };
                                 const selectedProduct = productTiers[planId] || productTiers['advisor'];

                                 // 🔥 CORREÇÃO: antes abria o checkout direto, pulando a
                                 // checagem de dupla assinatura (handleProtocolSelection é
                                 // quem faz essa checagem — esses botões estavam sendo o
                                 // único caminho que não passava por ela). Agora usa a
                                 // mesma função central que qualquer outro botão de plano.
                                 handleProtocolSelection(selectedProduct.id, selectedProduct.name, selectedProduct.price, 'ASSINATURA MENSAL');
                              }}

                              onNewClient={() => {
                                 setFormData({ method: '', profile: '', companyName: '', segment: '', niche: '', revenue: '', costs: '', debt: '', expertise: 'Avançado', userType: 'Consultor', custoNaoRecorrente: '', prazoRecebimento: '', prazoPagamento: '', dividaCurtoPrazo: '', proLabore: '' });
                                 setSimRevenue(0);
                                 setSimCosts(0);
                                 setAiJustification('');
                                 setActiveDossierId(null);
                                 setStep(1);
                                 setReportTab('carteira');
                                 setView('consultor_data_entry');
                              }}

                              onEditClient={() => {
                                 setView('consultor_data_entry');
                              }}

                              wlName={wlName} setWlName={setWlName}
                              wlColor={wlColor} setWlColor={setWlColor}
                              wlLogo={wlLogo} setWlLogo={setWlLogo}

                              // FUNÇÃO DE SINCRONIZAÇÃO (FIREBASE MANTIDA INTACTA)
                              onSyncDashboard={async () => {
                                 try {
                                    if (!user) {
                                       console.error('[PHOENIX] Sincronização falhou: usuário não autenticado.');
                                       if (typeof onLogout === 'function') onLogout();
                                       return false;
                                    }

                                    // 🔥 ID FIXO: usa o id do dossiê local (se já existe) ou cria um novo.
                                    // Isso garante que sincronizar de novo ATUALIZA o mesmo documento,
                                    // em vez de criar um documento novo toda vez.
                                    const dossieId = activeDossierId ? String(activeDossierId) : gerarIdUnico();

                                    await setDoc(doc(db, "cenarios", dossieId), sanitizeForFirestore({
                                       name: formData.companyName || "Empresa sem Nome",
                                       date: new Date().toLocaleDateString('pt-BR'),
                                       plan: activePlan === 'entry' ? 'free' : activePlan,
                                       userId: user.uid,
                                       userEmail: user.email,
                                       ebitda: report.ebitda,
                                       currentMargin: report.currentMargin,
                                       alavancagem: report.alavancagem,
                                       equityValue: finance.equityValue,
                                       snapshot: {
                                          formData,
                                          report,
                                          finance,
                                          dynamicInsights,
                                          userType: formData.userType
                                       },
                                       timestamp: new Date().toISOString()
                                    }), { merge: true });

                                    if (!activeDossierId) setActiveDossierId(dossieId);

                                    // 🔥 Atualiza o item existente no histórico local em vez de duplicar
                                    setHistoryList(prev => {
                                       const jaExiste = prev.some(item => item.id === dossieId);
                                       const itemAtualizado = {
                                          id: dossieId,
                                          name: formData.companyName || "Empresa sem Nome",
                                          date: new Date().toLocaleDateString('pt-BR'),
                                          plan: activePlan === 'entry' ? 'free' : activePlan,
                                          snapshot: { formData, report, finance, dynamicInsights }
                                       };
                                       return jaExiste
                                          ? prev.map(item => item.id === dossieId ? itemAtualizado : item)
                                          : [itemAtualizado, ...prev];
                                    });

                                    return true;
                                 } catch (error) {
                                    console.error("[PHOENIX] Erro ao sincronizar com o Firebase:", error);
                                    // 🔥 EXIBE MENSAGEM MAIS AMIGÁVEL
                                    if (error.code === 'permission-denied') {
                                       alert('Permissão negada. Verifique se você está logado corretamente.');
                                    } else if (error.code === 'unavailable') {
                                       alert('Serviço indisponível. Tente novamente em alguns instantes.');
                                    } else {
                                       alert('Erro ao sincronizar: ' + (error.message || 'Tente novamente.'));
                                    }
                                    return false;
                                 }
                              }}
                           />
                        ) : (
                           <>
                              {/* ABA 1: VISÃO DO GESTOR */}
                              {reportTab === 'visao' && (
                                 <div className="space-y-12 animate-fade-up text-left">
                                    {/* ECOSSISTEMA PHOENIX OS — mesmo princípio do banner que já
                                        existe pro Consultor (ConsultorReportTabs.jsx), explicando o
                                        que fazer no Celular vs no Computador. Só aparece pra quem já
                                        assinou algum plano pago (Tactical+) — é justamente quando o
                                        Painel Desktop é liberado, então faz sentido avisar aqui. */}
                                    {gestorAccessLevel >= TAB_LEVELS['operacao'] && (
                                       <div className="bg-slate-900/30 border border-white/5 rounded-2xl p-4 flex flex-col gap-3 text-left">
                                          <div className="flex gap-3 items-start">
                                             <Monitor size={16} className="text-lime-400 shrink-0 mt-0.5" />
                                             <p className="text-xs text-slate-400 leading-relaxed">
                                                <strong className="text-lime-400 block mb-0.5 uppercase tracking-wide">Ecossistema Phoenix OS:</strong>
                                                <span className="text-white font-bold">No Celular:</span> gere o diagnóstico, simule cenários e acompanhe o dia a dia da empresa de onde estiver.
                                             </p>
                                          </div>
                                          <div className="flex gap-3 items-start border-t border-white/5 pt-3">
                                             <Monitor size={16} className="text-lime-400 shrink-0 mt-0.5" />
                                             <p className="text-xs text-slate-400 leading-relaxed">
                                                {/* 🔥 CORRIGIDO: o texto antigo dizia que Tarefas, Anotações
                                                    e exportação de PDF só existiam no Painel Desktop — mas
                                                    isso já funciona aqui no app também (ver handleExportPDF
                                                    e a seção de Tarefas/Anotações mais abaixo). O real
                                                    diferencial do Desktop é a tela maior pra visualizar
                                                    gráficos — não subestimar o que o app já entrega. */}
                                                <span className="text-white font-bold">No Computador:</span> acesse o Painel Desktop com o mesmo login pra ver os mesmos dados numa tela maior — tudo (diagnóstico, Tarefas, Anotações e PDFs) sincronizado automaticamente com o app.
                                             </p>
                                          </div>
                                          <div className="flex gap-3 items-start border-t border-white/5 pt-3">
                                             <Zap size={16} className="text-purple-400 shrink-0 mt-0.5" />
                                             <p className="text-xs text-slate-400 leading-relaxed">
                                                <span className="text-white font-bold">IA Estocástica:</span> na aba Operação, veja pra onde sua empresa está caminhando — projeção de até <span className="text-purple-300 font-bold">36 meses à frente</span>, não só o retrato de hoje.
                                             </p>
                                          </div>
                                       </div>
                                    )}

                                    {/* 🔥 AJUSTADO: status grave (Crítico / Falência Iminente) usa
                                        tratamento visual mais sóbrio — sem o glow neon decorativo
                                        (drop-shadow) nem itálico no rótulo, fundo mais sólido e uma
                                        barra de destaque à esquerda. O resto do app pode ser
                                        estilizado/chamativo à vontade, mas o aviso mais grave que a
                                        pessoa pode receber não deveria competir visualmente com
                                        elementos decorativos — precisa parecer auditoria séria, não
                                        vitrine de produto. */}
                                    <div className={`${report.status.bg} ${report.status.border} p-8 rounded-[3rem] border flex items-center gap-6 shadow-2xl text-left ${report.status.grave ? 'border-l-[6px]' : ''}`}>
                                       <div className={`p-4 rounded-full bg-black/20 ${report.status.grave ? '' : 'drop-shadow-[0_0_10px_currentColor]'}`}>{report.status.icon}</div>
                                       <div className="text-left">
                                          {/* 🔥 AJUSTADO: rótulo e valor quase coladas quando o texto do
                                              status quebra em 2 linhas (ex: "Falência Iminente" em telas
                                              mais largas) — ficava com a etiqueta "Status da Operação"
                                              espremida contra o valor. mb-1→mb-3 no rótulo e leading-none→
                                              leading-[1.05] no valor dão respiro sem perder o impacto visual. */}
                                          <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-3 leading-none italic text-left">Status da Operação</p>
                                          <p className={`${report.status.color} text-4xl font-black uppercase tracking-tighter leading-[1.05] text-left`}>{report.status.label}</p>
                                       </div>
                                    </div>

                                    {/* LUCRO DO MÊS + PONTO DE EQUILÍBRIO — trazido do Dashboard
                                        Gestor pra manter paridade. Ponto de Equilíbrio é benefício
                                        do plano Plus (mesma regra do Dashboard); mesma aproximação
                                        documentada lá: sem separar custo fixo/variável ainda, usa o
                                        custo total como referência de equilíbrio. */}
                                    {formData.userType !== 'Consultor' && (
                                       <div className="grid grid-cols-1 gap-4 text-left">
                                          <div className="bg-slate-900/40 p-6 rounded-[2.5rem] border border-white/5">
                                             <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-1">Lucro do Mês</p>
                                             <p className="text-[11px] text-slate-500 mb-3">O que sobra depois de pagar todos os custos da empresa.</p>
                                             <p className={`text-2xl font-black ${report.ebitda >= 0 ? 'text-lime-400' : 'text-red-400'}`}>R$ {formatCurrency(report.ebitda)}</p>
                                          </div>
                                          <div className="bg-slate-900/40 p-6 rounded-[2.5rem] border border-white/5">
                                             <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-1">Ponto de Equilíbrio</p>
                                             <p className="text-[11px] text-slate-500 mb-3">Quanto sua empresa precisa faturar por mês para não ter prejuízo.</p>
                                             {gestorAccessLevel >= TAB_LEVELS['cfo'] ? (() => {
                                                const revNum = parseCurrencyToNumber(formData.revenue);
                                                const custNum = parseCurrencyToNumber(formData.costs);
                                                const favoravel = revNum >= custNum;
                                                return (
                                                   <>
                                                      <p className="text-2xl font-black text-white">R$ {formatCurrency(custNum)}</p>
                                                      <p className="text-[11px] text-slate-500 mt-1">
                                                         {favoravel ? `Faturamento atual R$ ${formatCurrency(Math.abs(revNum - custNum))} acima desse patamar.` : `Faturamento atual R$ ${formatCurrency(Math.abs(revNum - custNum))} abaixo desse patamar.`}
                                                      </p>
                                                   </>
                                                );
                                             })() : (
                                                <div className="flex items-center justify-between">
                                                   <p className="text-xs text-slate-600 font-bold">Disponível no Plus</p>
                                                   <span className="text-[11px] font-black uppercase tracking-widest text-lime-400 flex items-center gap-1"><Lock size={11} /> Liberar</span>
                                                </div>
                                             )}
                                          </div>

                                          {/* CAIXA MÍNIMO MENSAL — 🔥 NOVO: usa o pró-labore, campo
                                              coletado no cadastro mas que não entrava em nenhum
                                              cálculo. Mesma trava de plano do Ponto de Equilíbrio. */}
                                          <div className="bg-slate-900/40 p-6 rounded-[2.5rem] border border-white/5">
                                             <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-1">Caixa Mínimo Mensal</p>
                                             <p className="text-[11px] text-slate-500 mb-3">Custos fixos somados ao pró-labore dos sócios — o piso de caixa que a operação precisa ter todo mês.</p>
                                             {gestorAccessLevel >= TAB_LEVELS['cfo'] ? (() => {
                                                const custNum = parseCurrencyToNumber(formData.costs);
                                                const proLaboreNum = parseCurrencyToNumber(formData.proLabore);
                                                return (
                                                   <>
                                                      <p className="text-2xl font-black text-white">R$ {formatCurrency(custNum + proLaboreNum)}</p>
                                                      {proLaboreNum > 0 && (
                                                         <p className="text-[11px] text-slate-500 mt-1">Inclui R$ {formatCurrency(proLaboreNum)} de pró-labore mensal informado.</p>
                                                      )}
                                                   </>
                                                );
                                             })() : (
                                                <div className="flex items-center justify-between">
                                                   <p className="text-xs text-slate-600 font-bold">Disponível no Plus</p>
                                                   <span className="text-[11px] font-black uppercase tracking-widest text-lime-400 flex items-center gap-1"><Lock size={11} /> Liberar</span>
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    )}

                                    {/* Meta Financeira agora vive na aba Operação — Tarefas e
                                        Anotações ficam logo abaixo do Parecer Analítico,
                                        já que fazem mais sentido como acompanhamento do
                                        diagnóstico do que como um bloco solto antes dele. */}

                                    <div className="flex flex-col gap-4 text-left pt-2">
                                       <div className="bg-slate-900/40 p-8 rounded-[3rem] border border-white/5 space-y-2 text-left shadow-lg w-full overflow-hidden relative group">
                                          <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity"><Zap size={60} className="text-lime-400" /></div>
                                          <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest leading-none text-left mb-2 relative z-10">EBITDA Real Time</p>
                                          <p className={`${getDynamicTextSize(ebitdaStrFormat)} font-black text-white italic tracking-tighter leading-none text-left drop-shadow-[0_0_15px_#bef26433] whitespace-nowrap relative z-10 w-full`}>
                                             {ebitdaStrFormat}
                                          </p>
                                       </div>
                                       <div className="grid grid-cols-2 gap-4">
                                          <div className="bg-slate-900/40 p-6 rounded-[2.5rem] border border-white/5 space-y-2 text-left shadow-lg overflow-hidden">
                                             <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest leading-none text-left mb-1">Alavancagem</p>
                                             <p className="text-2xl font-black text-white italic tracking-tighter leading-none text-left drop-shadow-[0_0_10px_#bef26433] truncate w-full">{String(report.alavancagem).replace('.', ',')}x</p>
                                          </div>
                                          <div className="bg-slate-900/40 p-6 rounded-[2.5rem] border border-white/5 space-y-2 text-left shadow-lg overflow-hidden">
                                             <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest leading-none text-left mb-1">Margem Atual</p>
                                             <p className="text-2xl font-black text-white italic tracking-tighter leading-none text-left drop-shadow-[0_0_10px_#bef26433] truncate w-full">{String(report.currentMargin).replace('.', ',')}%</p>
                                          </div>
                                       </div>
                                    </div>
                                    <div className="space-y-8 bg-slate-950/40 px-5 py-7 sm:px-8 sm:py-8 rounded-[2.5rem] sm:rounded-[4rem] border border-white/5 text-left shadow-inner">
                                       <div className="border-b border-white/5 pb-4 space-y-3">
                                          <div className="flex items-center gap-3 min-w-0"><Activity className="text-lime-400 shrink-0" size={20} /><h3 className="text-[11px] font-black text-white uppercase tracking-[0.15em] italic leading-tight text-left min-w-0">Parecer Analítico Phoenix</h3></div>
                                          <span className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest border flex items-center gap-1.5 w-fit ${report.status.bg} ${report.status.color} ${report.status.border}`}>
                                             <span className={`w-1.5 h-1.5 rounded-full ${report.status.color.replace('text-', 'bg-')}`}></span>
                                             {report.status.label}
                                          </span>
                                       </div>
                                       <div className="space-y-10 text-left">
                                          {(carregandoParecer || iaGerando) ? (
                                             <div className="space-y-3 animate-pulse">
                                                <div className="h-3 bg-white/5 rounded-full w-3/4" />
                                                <div className="h-3 bg-white/5 rounded-full w-full" />
                                                <div className="h-3 bg-white/5 rounded-full w-5/6" />
                                             </div>
                                          ) : parecerIATexto ? (
                                             <div className="space-y-1 text-left">
                                                {formatarParecerIA(parecerIATexto)}
                                                <p className="text-[11px] text-slate-600 uppercase tracking-widest font-bold mt-4 flex items-center gap-1.5">
                                                   <span className="w-1 h-1 rounded-full bg-lime-400" /> Gerado por IA
                                                </p>
                                             </div>
                                          ) : (
                                             <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 border-2 border-lime-400 border-t-transparent rounded-full animate-spin shrink-0" />
                                                <p className="text-[11px] text-slate-500 italic">Gerando o parecer com IA... salve o diagnóstico se ainda não salvou.</p>
                                             </div>
                                          )}
                                       </div>
                                    </div>

                                    {/* TAREFAS + ANOTAÇÕES — agora abaixo do Parecer, como
                                        acompanhamento do diagnóstico, não antes dele. Liberado
                                        pros dois perfis (antes só o Gestor tinha essa seção). */}
                                    {activeDossierId && (
                                       <div className="grid grid-cols-1 gap-4 text-left">
                                          <div className="bg-slate-900/40 p-6 rounded-[2.5rem] border border-white/5">
                                             <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-3">Tarefas</p>
                                             <div className="flex gap-2 mb-4">
                                                <input type="text" value={novaTarefaTextoApp} onChange={(e) => setNovaTarefaTextoApp(e.target.value)}
                                                   placeholder="Nova tarefa..."
                                                   className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-lime-400/50" />
                                                <button onClick={adicionarTarefaApp} disabled={!novaTarefaTextoApp.trim()} className="w-10 shrink-0 bg-lime-400 text-black rounded-xl disabled:opacity-40 flex items-center justify-center">
                                                   <Plus size={16} />
                                                </button>
                                             </div>
                                             {tarefasGestor.length === 0 ? (
                                                <p className="text-[11px] text-slate-600 text-center py-4">Nenhuma tarefa ainda.</p>
                                             ) : (
                                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                                   {tarefasGestor.map(t => {
                                                      const atrasada = !t.concluida && t.prazo && new Date(t.prazo) < new Date(new Date().toDateString());
                                                      return (
                                                         <div key={t.id} className={`flex items-start gap-2 p-3 rounded-xl border ${atrasada ? 'bg-red-500/5 border-red-500/20' : 'bg-white/5 border-white/5'}`}>
                                                            <button onClick={() => alternarTarefaApp(t.id)} className="shrink-0 mt-0.5">
                                                               {t.concluida ? <CheckCircle2 size={16} className="text-lime-400" /> : <Circle size={16} className="text-slate-600" />}
                                                            </button>
                                                            <p className={`flex-1 text-xs font-bold ${t.concluida ? 'text-slate-600 line-through' : 'text-white'}`}>{t.texto}</p>
                                                            <button onClick={() => excluirTarefaApp(t.id)} className="text-slate-600 hover:text-red-400 transition-colors shrink-0">
                                                               <Trash2 size={13} />
                                                            </button>
                                                         </div>
                                                      );
                                                   })}
                                                </div>
                                             )}
                                          </div>

                                          <div className="bg-slate-900/40 p-6 rounded-[2.5rem] border border-white/5">
                                             <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-3">Anotações</p>
                                             <div className="flex gap-2 mb-4">
                                                <textarea value={novaNotaApp} onChange={(e) => setNovaNotaApp(e.target.value)} rows={2}
                                                   placeholder="Escreva uma anotação..."
                                                   className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-lime-400/50 resize-none" />
                                                <button onClick={adicionarNotaApp} disabled={!novaNotaApp.trim()} className="w-10 shrink-0 bg-lime-400 text-black rounded-xl disabled:opacity-40 flex items-center justify-center">
                                                   <Plus size={16} />
                                                </button>
                                             </div>
                                             {notasGestor.length === 0 ? (
                                                <p className="text-[11px] text-slate-600 text-center py-4">Nenhuma anotação ainda.</p>
                                             ) : (
                                                <div className="space-y-3 max-h-64 overflow-y-auto">
                                                   {notasGestor.map((n, i) => (
                                                      <div key={i} className="border-l-2 border-lime-400/20 pl-3 flex items-start justify-between gap-2">
                                                         <div>
                                                            <p className="text-xs text-slate-300">{n.texto}</p>
                                                            <p className="text-[11px] text-slate-600 mt-1">{new Date(n.data).toLocaleString('pt-BR')}</p>
                                                         </div>
                                                         <button onClick={() => excluirNotaApp(i)} className="text-slate-600 hover:text-red-400 transition-colors shrink-0">
                                                            <Trash2 size={12} />
                                                         </button>
                                                      </div>
                                                   ))}
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    )}
                                    <div className="pt-6 space-y-4 text-left pb-6">
                                       <button onClick={() => { setDownloadConfirmEhSuper(false); setShowDownloadConfirm(true); }} className="w-full bg-white text-black py-7 rounded-full font-black uppercase text-sm shadow-2xl flex items-center justify-center gap-3 active:scale-95 transition-all text-left">
                                          <Download size={20} /><span>Exportar Dossiê PDF Master</span>
                                       </button>
                                       <button
                                          onClick={() => {
                                             // 🔥 NOVO (2026-09-30): no Entry (avulso, R$ 49,90) cada
                                             // versão do relatório é uma compra. Antes a pessoa só
                                             // descobria isso depois de editar tudo e dar de cara com
                                             // o checkout. Agora avisa ANTES, e oferece a assinatura
                                             // (relatórios ilimitados) como alternativa.
                                             if (planoEfetivoGestor === 'entry' && formData.userType !== 'Consultor') {
                                                setShowEditEntryWarning(true);
                                                return;
                                             }
                                             // Volta ao wizard com os dados atuais preenchidos (edição).
                                             // Mantém activeDossierId intacto — ao finalizar, o dossiê
                                             // existente é ATUALIZADO, não duplicado (ver unlockDossier).
                                             setStep(2);
                                             setView('wizard');
                                          }}
                                          className="w-full bg-slate-900/40 border border-white/10 text-slate-300 py-6 rounded-full font-black uppercase text-xs tracking-widest flex items-center justify-center gap-3 active:scale-95 transition-all hover:bg-slate-900/70"
                                       >
                                          <Settings size={16} className="text-lime-400" /><span>Editar Dados do Relatório</span>
                                       </button>
                                       <div className="bg-gradient-to-r from-slate-900/60 to-black/60 p-6 rounded-[2rem] border border-white/5 flex items-center gap-4 shadow-lg mt-6">
                                          <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center shrink-0 border border-white/10"><ShieldCheck size={20} className="text-slate-300" /></div>
                                          <div>
                                             <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Diretriz Estratégica</p>
                                             <p className="text-[11px] text-slate-400 leading-relaxed">Os dados refletem a posição atual. Recomenda-se a revisão periódica para garantir a precisão da estratégia.</p>
                                          </div>
                                       </div>
                                    </div>

                                    {/* ESCADA DE COMPRA - GESTOR */}
                                    {planoEfetivoGestor === 'total' ? (
                                       <div className="mt-8 pt-6 border-t border-white/10 text-center">
                                          <div className="bg-lime-400/5 border border-lime-400/20 rounded-2xl py-4 px-5 flex items-center justify-center gap-2">
                                             <span className="text-lg">🏆</span>
                                             <p className="text-[11px] font-black text-lime-400 uppercase tracking-widest">Você atingiu o nível máximo do Gestor</p>
                                          </div>
                                       </div>
                                    ) : (
                                       <div className="mt-8 pt-6 border-t border-white/10">
                                          {/* AVISO: a pessoa no Entry precisa entender que R$49,90
                                              comprou um diagnóstico pontual e simples — não a
                                              análise completa que os planos por assinatura
                                              oferecem (histórico, simulações, IA Estocástica,
                                              CFO Virtual, M&A). Sem isso, ela pode achar que já
                                              viu "tudo que o Phoenix faz". */}
                                          {planoEfetivoGestor === 'entry' && (
                                             <div className="bg-amber-500/[0.07] border-2 border-amber-500/30 rounded-3xl p-6 mb-6 text-left shadow-[0_0_25px_rgba(245,158,11,0.08)]">
                                                <div className="flex items-center gap-2 mb-3">
                                                   <span className="text-2xl">⚠️</span>
                                                   <p className="text-sm text-amber-400 font-black uppercase tracking-wide">Isso é só a ponta do iceberg</p>
                                                </div>
                                                <p className="text-xs text-slate-300 leading-relaxed">
                                                   Este diagnóstico usa só os números de <strong className="text-white">hoje</strong>. Ele não te avisa se o caixa vai apertar mês que vem, não simula o que acontece se as vendas caírem, e não mostra pra onde sua empresa está indo nos próximos 12, 24 ou 36 meses. Quem assina um plano abaixo passa a enxergar o <strong className="text-white">futuro do negócio antes dele acontecer</strong> — não só o retrato de agora.
                                                </p>
                                             </div>
                                          )}
                                          <div className="bg-gradient-to-br from-slate-900/80 to-[#020617] rounded-3xl p-6 border border-lime-400/30 shadow-[0_0_30px_rgba(190,242,100,0.1)] text-center">
                                             <div className="flex items-center justify-center gap-2 mb-2">
                                                <Zap size={20} className="text-lime-400 animate-pulse" />
                                                <p className="text-[11px] font-black text-lime-400 uppercase tracking-widest">O que você destrava no próximo nível</p>
                                             </div>
                                             <p className="text-[11px] text-slate-500 mb-4 px-2">Analise a fundo, antecipe o que vem por aí e descubra oportunidades que hoje passam despercebidas.</p>
                                             {planoEfetivoGestor === 'entry' && (
                                                <>
                                                   <h3 className="text-2xl font-black text-white uppercase mb-2">Protocolo Tactical</h3>
                                                   <div className="flex justify-center items-baseline gap-1 mb-2">
                                                      <span className="text-5xl font-black text-white">R$ 189</span>
                                                      <span className="text-sm text-slate-400">/mês</span>
                                                   </div>
                                                   <p className="text-[11px] text-lime-400 font-black uppercase tracking-widest mb-5">Tudo que você destrava:</p>
                                                   <ul className="space-y-2 mb-6 text-left w-full">
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Diagnósticos sem limite <span className="text-slate-500 shrink-0">(Ilimitados)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Projeção de 3 e 6 meses <span className="text-slate-500 shrink-0">(IA Estocástica)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Saiba se o caixa vai apertar <span className="text-slate-500 shrink-0">(Radar D+0)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Teste uma virada de jogo <span className="text-slate-500 shrink-0">(Simulador de Turnaround)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Metas calculadas pela IA <span className="text-slate-500 shrink-0">(Metas Ideais)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Acesso ao Dashboard pelo computador</li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Histórico salvo na nuvem</li>
                                                   </ul>
                                                   <button onClick={() => handleProtocolSelection('tactical', 'Protocolo Tactical', '189,00', 'ASSINATURA')} className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black py-4 rounded-xl font-black uppercase text-sm shadow-[0_0_20px_rgba(190,242,100,0.3)] hover:scale-[1.02] transition-all">
                                                      Fazer Upgrade — R$ 189/mês
                                                   </button>
                                                </>
                                             )}
                                             {planoEfetivoGestor === 'tactical' && (
                                                <>
                                                   <h3 className="text-2xl font-black text-white uppercase mb-2">Protocolo Plus</h3>
                                                   <div className="flex justify-center items-baseline gap-1 mb-2">
                                                      <span className="text-5xl font-black text-white">R$ 499</span>
                                                      <span className="text-sm text-slate-400">/mês</span>
                                                   </div>
                                                   <p className="text-[11px] text-lime-400 font-black uppercase tracking-widest mb-5">Tudo do Tactical, mais:</p>
                                                   <ul className="space-y-2 mb-6 text-left w-full">
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Um CFO disponível 24h <span className="text-slate-500 shrink-0">(CFO Virtual)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Descubra ANTES se vai faltar caixa <span className="text-slate-500 shrink-0">(Risco de Ruptura)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Quanto dá pra sacar sem sufocar a empresa <span className="text-slate-500 shrink-0">(Saque Seguro)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Reserva ideal pra dormir tranquilo <span className="text-slate-500 shrink-0">(Reserva de Emergência)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-lime-400 shrink-0">✓</span> Projeção +12 meses <span className="text-slate-500 shrink-0">(IA Estocástica)</span></li>
                                                   </ul>
                                                   <button onClick={() => handleProtocolSelection('plus', 'Protocolo Plus', '499,00', 'ASSINATURA')} className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black py-4 rounded-xl font-black uppercase text-sm shadow-[0_0_20px_rgba(190,242,100,0.3)] hover:scale-[1.02] transition-all">
                                                      Fazer Upgrade — R$ 499/mês
                                                   </button>
                                                </>
                                             )}
                                             {planoEfetivoGestor === 'plus' && (
                                                <>
                                                   <div className="flex justify-center mb-4">
                                                      <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-[11px] font-black uppercase border border-purple-500/30">⚡ PLANO RECOMENDADO</span>
                                                   </div>
                                                   <h3 className="text-2xl font-black text-white uppercase mb-2">Protocolo Total</h3>
                                                   <div className="flex justify-center items-baseline gap-1 mb-2">
                                                      <span className="text-5xl font-black text-white">R$ 799</span>
                                                      <span className="text-sm text-slate-400">/mês</span>
                                                   </div>
                                                   <p className="text-[11px] text-purple-400 font-black uppercase tracking-widest mb-5">Tudo do Plus, mais:</p>
                                                   <ul className="space-y-2 mb-6 text-left w-full">
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-purple-400 shrink-0">✓</span> Projeção +24 e 36 meses <span className="text-slate-500 shrink-0">(IA Estocástica)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-purple-400 shrink-0">✓</span> Relatório premium de graça, todo mês <span className="text-slate-500 shrink-0">(Super 360º)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-purple-400 shrink-0">✓</span> Passo a passo pra valer mais <span className="text-slate-500 shrink-0">(Guia de Valor)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-purple-400 shrink-0">✓</span> Quanto vale sua empresa hoje <span className="text-slate-500 shrink-0">(Valuation Real-Time)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-purple-400 shrink-0">✓</span> Pronta pra vender ou receber investimento? <span className="text-slate-500 shrink-0">(M&A Score)</span></li>
                                                      <li className="text-[11px] text-slate-300 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis"><span className="text-purple-400 shrink-0">✓</span> Organize a governança antes de negociar <span className="text-slate-500 shrink-0">(Checklist)</span></li>
                                                   </ul>
                                                   <button onClick={() => handleProtocolSelection('total', 'Protocolo Total', '799,00', 'ASSINATURA')} className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-4 rounded-xl font-black uppercase text-sm shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:scale-[1.02] transition-all">
                                                      Fazer Upgrade — R$ 799/mês
                                                   </button>
                                                </>
                                             )}
                                          </div>
                                       </div>
                                    )}
                                    {planoEfetivoGestor === 'total' && (
                                       <div className="mt-8 pt-6 border-t border-white/10">
                                          <div className="bg-gradient-to-br from-slate-900/80 to-[#020617] rounded-3xl p-6 border border-lime-400/30 shadow-[0_0_30px_rgba(190,242,100,0.1)] text-center">
                                             <div className="flex items-center justify-center gap-2 mb-4">
                                                <Crown size={24} className="text-lime-400" />
                                                <p className="text-[11px] font-black text-lime-400 uppercase tracking-widest">🏆 PLANO MÁXIMO</p>
                                             </div>
                                             <h3 className="text-2xl font-black text-white uppercase mb-2">Protocolo Total</h3>
                                             <p className="text-sm text-slate-400 mb-4">Você já está no plano máximo</p>
                                             <div className="w-full h-px bg-gradient-to-r from-transparent via-lime-400/50 to-transparent my-4"></div>
                                             <p className="text-xs text-slate-500">Acesso total a todas as funcionalidades do Phoenix OS</p>
                                          </div>
                                       </div>
                                    )}
                                 </div>
                              )}

                              {/* ABA 2: OPERAÇÃO DO GESTOR */}
                              {reportTab === 'operacao' && (
                                 gestorAccessLevel >= TAB_LEVELS['operacao'] ? (
                                    <div className="animate-fade-up">
                                       <div className="space-y-8 pb-10 pt-2">
                                          <div className="space-y-4">
                                             <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none">Cockpit <span className="text-lime-400">Tático</span></h2>
                                             <p className="text-[11px] text-slate-500 font-black tracking-[0.3em] leading-relaxed italic">Alavancas operacionais para otimização de margem e caixa.</p>
                                          </div>
                                          <div className="grid grid-cols-2 gap-4">
                                             <div className="bg-slate-900/60 border border-white/5 p-6 rounded-[2rem] shadow-lg">
                                                <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2">Margem Atual</p>
                                                <div className="flex items-baseline gap-1">
                                                   <span className="text-2xl font-black text-white italic tracking-tighter">{String(report.currentMargin).replace('.', ',')}</span>
                                                   <span className="text-sm font-black text-slate-400">%</span>
                                                </div>
                                             </div>
                                             <div className="bg-slate-900/60 border border-white/5 p-6 rounded-[2rem] shadow-lg relative overflow-hidden">
                                                <div className="absolute top-0 right-0 p-4 opacity-5"><Gauge size={40} className={report.simColor} /></div>
                                                <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2">Projetada</p>
                                                <div className="flex items-baseline gap-1">
                                                   <span className={`text-2xl font-black italic tracking-tighter drop-shadow-[0_0_10px_currentColor] ${report.simColor}`}>{String(report.projectedMargin).replace('.', ',')}</span>
                                                   <span className={`text-sm font-black ${report.simColor}`}>%</span>
                                                </div>
                                             </div>
                                          </div>

                                          {/* META FINANCEIRA — movida da aba Visão pra cá, já que é
                                              uma alavanca de acompanhamento tático, mais alinhada
                                              com o resto do que vive nessa aba Operação. */}
                                          {formData.userType !== 'Consultor' && activeDossierId && (
                                             <div className="bg-slate-900/40 p-6 rounded-[2.5rem] border border-white/5 text-left">
                                                <div className="flex items-center justify-between mb-1">
                                                   <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold">Meta Financeira</p>
                                                   <button onClick={() => { setMetaTipoFormApp(metaFinanceira?.tipo || 'faturamento'); setMetaValorFormApp(metaFinanceira?.valor ? formatCurrency(metaFinanceira.valor) : ''); setEditandoMetaApp(true); }} className="text-slate-500 hover:text-lime-400 transition-colors">
                                                      <FileSignature size={13} />
                                                   </button>
                                                </div>
                                                {metaFinanceira ? (() => {
                                                   const atual = metaFinanceira.tipo === 'lucro' ? report.ebitda : parseCurrencyToNumber(formData.revenue);
                                                   const percentual = metaFinanceira.valor > 0 ? Math.max(0, Math.min(100, (atual / metaFinanceira.valor) * 100)) : 0;
                                                   return (
                                                      <div>
                                                         <div className="flex items-baseline justify-between mb-2">
                                                            <p className="text-[11px] text-slate-500">{metaFinanceira.tipo === 'lucro' ? 'Meta de Lucro Mensal' : 'Meta de Faturamento Mensal'}</p>
                                                            <p className="text-xs font-black text-white">R$ {formatCurrency(atual)} <span className="text-slate-600 font-normal">/ R$ {formatCurrency(metaFinanceira.valor)}</span></p>
                                                         </div>
                                                         <div className="w-full bg-black/40 rounded-full h-2 overflow-hidden">
                                                            <div className="h-2 rounded-full bg-lime-400" style={{ width: `${percentual}%` }}></div>
                                                         </div>
                                                         <p className="text-[11px] text-slate-500 mt-2">{percentual >= 100 ? 'Meta atingida! 🎉' : `${percentual.toFixed(0)}% da meta alcançada.`}</p>
                                                      </div>
                                                   );
                                                })() : (
                                                   <p className="text-xs text-slate-600">Nenhuma meta definida ainda. Toque no lápis pra criar uma.</p>
                                                )}
                                             </div>
                                          )}

                                          <div className="bg-slate-900/80 p-8 rounded-[3rem] border border-white/10 shadow-3xl flex flex-col items-center text-center space-y-8 relative overflow-hidden group">
                                             <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-lime-400/50 to-transparent opacity-50"></div>
                                             <div className="space-y-2 w-full text-center relative z-10 pt-2">
                                                <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest italic leading-none">EBITDA Otimizado</p>
                                                <p className={`${getDynamicTextSize(projEbitdaStrFormat)} font-black italic tracking-tighter drop-shadow-[0_0_15px_currentColor] py-2 whitespace-nowrap w-full ${report.simColor}`}>
                                                   {projEbitdaStrFormat}
                                                </p>
                                                <div className={`flex items-center justify-center gap-2 px-4 py-2 rounded-full w-fit mx-auto mt-2 border ${report.gains > 0 ? 'bg-lime-400/10 text-lime-400 border-lime-400/20' : 'bg-slate-800 text-slate-400 border-white/5'}`}>
                                                   <TrendingUp size={12} />
                                                   <span className="text-[11px] font-black uppercase truncate max-w-[180px]">Variação: R$ {formatCurrency(report.gains)}</span>
                                                </div>
                                             </div>
                                             <div className="w-full space-y-8 pt-6 border-t border-white/5 relative z-10">
                                                <button onClick={handleAutoOptimize} className="flex items-center justify-center gap-2 w-full bg-lime-400/10 text-lime-400 border border-lime-400/30 py-3 rounded-full font-black uppercase text-[11px] tracking-widest hover:bg-lime-400/20 transition-all active:scale-95 shadow-[0_0_15px_rgba(190,242,100,0.1)]">
                                                   <Wand size={14} /> Sugestão IA: Calcular Meta Ideal
                                                </button>
                                                {aiJustification && (
                                                   <div className="bg-[#020617] border border-lime-400/20 p-4 rounded-2xl animate-in slide-in-from-top-2 duration-300">
                                                      <p className="text-[11px] font-black text-lime-400 uppercase tracking-widest mb-1">Racional da IA</p>
                                                      <p className="text-[11px] text-slate-400 leading-relaxed text-justify">{aiJustification}</p>
                                                   </div>
                                                )}
                                                <div className="space-y-5 text-left">
                                                   <div className="flex justify-between items-center text-[11px] font-black uppercase italic">
                                                      <span className="text-slate-400">Expansão de Receita</span>
                                                      <div className="bg-[#020617] px-3 py-1 rounded-lg border border-white/5 shadow-inner">
                                                         <span className="text-lime-400 font-bold drop-shadow-[0_0_5px_currentColor]">+{simRevenue}%</span>
                                                      </div>
                                                   </div>
                                                   <input type="range" min="0" max="50" value={simRevenue} onChange={(e) => handleManualSliderChange('rev', e.target.value)} className="w-full accent-lime-400 h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer" />
                                                </div>
                                                <div className="space-y-5 text-left">
                                                   <div className="flex justify-between items-center text-[11px] font-black uppercase italic">
                                                      <span className="text-slate-400">Corte de OPEX</span>
                                                      <div className="bg-[#020617] px-3 py-1 rounded-lg border border-white/5 shadow-inner">
                                                         <span className="text-lime-400 font-bold drop-shadow-[0_0_5px_currentColor]">-{simCosts}%</span>
                                                      </div>
                                                   </div>
                                                   <input type="range" min="0" max="30" value={simCosts} onChange={(e) => handleManualSliderChange('cost', e.target.value)} className="w-full accent-lime-400 h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer" />
                                                </div>
                                             </div>
                                          </div>
                                          <div className={`p-8 rounded-[2.5rem] border flex flex-col gap-4 shadow-lg ${report.simColor === 'text-lime-400' ? 'bg-lime-400/5 border-lime-400/20' : report.simColor === 'text-orange-400' ? 'bg-orange-500/5 border-orange-500/20' : report.simColor === 'text-red-500' || report.simColor === 'text-red-600' ? 'bg-red-500/5 border-red-500/20' : 'bg-slate-900/40 border-white/5'}`}>
                                             <div className="flex items-center gap-4 border-b border-white/5 pb-4">
                                                <Zap size={24} className={report.simColor} />
                                                <div>
                                                   <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-0.5 italic">Parecer Preditivo IA</p>
                                                   <p className={`text-sm font-black uppercase italic ${report.simColor}`}>{report.simStatus}</p>
                                                </div>
                                             </div>
                                             <p className="text-[11px] text-slate-400 font-medium leading-relaxed text-justify">
                                                {report.simDescription}
                                             </p>
                                          </div>
                                          <button onClick={saveScenario} className="w-full bg-white/5 text-white py-7 rounded-full font-black uppercase text-sm border border-white/10 hover:bg-white hover:text-black hover:scale-105 active:scale-95 transition-all">
                                             {saveBtnText}
                                          </button>
                                          <div className="bg-lime-400/5 border border-lime-400/20 p-8 rounded-[2.5rem] flex flex-col items-center text-center shadow-lg mt-8">
                                             <p className="text-[11px] font-black text-lime-500 uppercase tracking-widest mb-3">Projeção Anualizada (12 Meses)</p>
                                             <p className={`${getDynamicTextSize(projAnualStrFormat)} font-black text-lime-400 italic tracking-tighter drop-shadow-[0_0_15px_rgba(190,242,100,0.4)] py-2 whitespace-nowrap w-full`}>
                                                {projAnualStrFormat}
                                             </p>
                                             <p className="text-[11px] text-slate-400 mt-4 max-w-[280px] leading-relaxed">Geração de caixa adicional estimada caso o cenário tático acima seja mantido rigorosamente ao longo de um ano fiscal completo.</p>
                                          </div>

                                          {/* CALCULADORA DE PRECIFICAÇÃO — trazida do Dashboard Gestor */}
                                          {formData.userType !== 'Consultor' && (
                                             <div className="bg-slate-900/40 p-8 rounded-[2.5rem] border border-white/5 mt-8">
                                                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-1">Calculadora de Precificação</p>
                                                <p className="text-[11px] text-slate-500 mb-5">Descubra quanto cobrar por um produto ou serviço pra ter a margem de lucro que você quer.</p>
                                                <div className="space-y-4 mb-5">
                                                   <input type="text" inputMode="numeric" value={precoCustoApp}
                                                      onChange={(e) => setPrecoCustoApp(mascaraMoedaApp(e.target.value))}
                                                      placeholder="Custo do produto/serviço (R$)"
                                                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50" />
                                                   <div>
                                                      <div className="flex justify-between mb-1"><span className="text-[11px] text-slate-400 font-bold uppercase">Impostos e Taxas</span><span className="text-lime-400 font-black text-xs">{precoDespesasApp}%</span></div>
                                                      <input type="range" min="0" max="40" value={precoDespesasApp} onChange={(e) => setPrecoDespesasApp(Number(e.target.value))} className="w-full accent-lime-400" />
                                                   </div>
                                                   <div>
                                                      <div className="flex justify-between mb-1"><span className="text-[11px] text-slate-400 font-bold uppercase">Margem de Lucro</span><span className="text-lime-400 font-black text-xs">{precoMargemApp}%</span></div>
                                                      <input type="range" min="0" max="60" value={precoMargemApp} onChange={(e) => setPrecoMargemApp(Number(e.target.value))} className="w-full accent-lime-400" />
                                                   </div>
                                                </div>
                                                {calculoPrecoApp.valido ? (
                                                   <div className="grid grid-cols-3 gap-2">
                                                      <div className="bg-black/30 rounded-xl p-3 text-center"><p className="text-[11px] text-slate-500 uppercase font-bold">Preço</p><p className="text-sm font-black text-lime-400 mt-1">R$ {formatCurrency(calculoPrecoApp.precoSugerido)}</p></div>
                                                      <div className="bg-black/30 rounded-xl p-3 text-center"><p className="text-[11px] text-slate-500 uppercase font-bold">Lucro</p><p className="text-sm font-black text-white mt-1">R$ {formatCurrency(calculoPrecoApp.lucroReais)}</p></div>
                                                      <div className="bg-black/30 rounded-xl p-3 text-center"><p className="text-[11px] text-slate-500 uppercase font-bold">Impostos</p><p className="text-sm font-black text-white mt-1">R$ {formatCurrency(calculoPrecoApp.despesasReais)}</p></div>
                                                   </div>
                                                ) : (
                                                   <p className="text-[11px] text-slate-500 text-center py-4 bg-black/20 rounded-xl">Informe o custo pra calcular o preço sugerido.</p>
                                                )}
                                             </div>
                                          )}

                                          {/* 🔮 PROJEÇÃO POR IA ESTOCÁSTICA — visual real, usando o motor
                                              de cálculo (financeEngine.js). Gating por plano confirmado:
                                              Tactical → 3 e 6 meses. Plus → +12 meses. Total → +24 e 36.
                                              🔥 NOVO: dividido em 3 BLOCOS (Curto/Médio/Longo), cada um
                                              com seus números E seu próprio texto de IA — nunca mais um
                                              texto único que comenta prazos que o plano não libera. */}
                                          {(() => {
                                             const horizontesLiberados = getHorizontesLiberados('Gestor', gestorPlan);
                                             const infoPlano = {
                                                tactical: { nome: 'Protocolo Tactical', preco: '189,00' },
                                                plus: { nome: 'Protocolo Plus', preco: '499,00' },
                                                total: { nome: 'Protocolo Total', preco: '799,00' }
                                             };
                                             const revNum = parseCurrencyToNumber(formData.revenue);
                                             const projecaoCompleta = calculateStochasticProjection(
                                                { ebitda: report.ebitda, ebitdaAjustado: report.ebitdaAjustado, currentMargin: Number(report.currentMargin) },
                                                revNum
                                             );

                                             const grupos = [
                                                { chave: 'curto', titulo: 'Curto Prazo', meses: [3, 6], planoNecessario: 'tactical', texto: leituraProjecaoCurto },
                                                { chave: 'medio', titulo: 'Médio Prazo', meses: [12], planoNecessario: 'plus', texto: leituraProjecaoMedio },
                                                { chave: 'longo', titulo: 'Longo Prazo', meses: [24, 36], planoNecessario: 'total', texto: leituraProjecaoLongo },
                                             ];

                                             return (
                                                <div className="bg-gradient-to-br from-purple-900/20 to-transparent border border-purple-500/20 px-5 py-8 rounded-[2.5rem] mt-4 relative overflow-hidden">
                                                   <div className="absolute top-0 right-0 p-4 opacity-10"><Zap size={70} className="text-purple-400" /></div>
                                                   <div className="flex items-center justify-center gap-2 mb-1 relative z-10">
                                                      <Wand size={16} className="text-purple-400" />
                                                      <p className="text-[11px] font-black text-purple-400 uppercase tracking-widest italic">Projeção por IA Estocástica</p>
                                                   </div>
                                                   <p className="text-xs font-black text-purple-300 italic text-center mb-1 relative z-10">Pra onde esse negócio está indo?</p>
                                                   <p className="text-[11px] text-slate-500 mb-5 text-center relative z-10">Não é só um retrato de hoje — é pra onde sua empresa está indo, em 3 cenários</p>

                                                   <div className="space-y-4 relative z-10">
                                                      {grupos.map((grupo) => {
                                                         const liberado = horizontesLiberados.includes(grupo.meses[0]);
                                                         const info = infoPlano[grupo.planoNecessario];

                                                         if (!liberado) {
                                                            return (
                                                               <button
                                                                  key={grupo.chave}
                                                                  onClick={() => handleProtocolSelection(grupo.planoNecessario, info.nome, info.preco, 'ASSINATURA')}
                                                                  className="w-full flex items-center justify-between bg-black/20 border border-white/5 rounded-2xl px-4 py-3.5 hover:border-purple-500/30 transition-all active:scale-[0.99]"
                                                               >
                                                                  <p className="text-[11px] font-black text-slate-600 uppercase tracking-widest flex items-center gap-1.5">
                                                                     <Lock size={10} /> {grupo.titulo} ({grupo.meses.join(' e ')} meses)
                                                                  </p>
                                                                  <p className="text-[11px] font-black text-purple-300 uppercase tracking-widest">
                                                                     Desbloquear no {info.nome.replace('Protocolo ', '')}
                                                                  </p>
                                                               </button>
                                                            );
                                                         }

                                                         return (
                                                            <div key={grupo.chave} className="bg-purple-500/5 border border-purple-500/20 rounded-2xl p-4">
                                                               <p className="text-[11px] font-black text-purple-300 uppercase tracking-widest mb-3">{grupo.titulo} ({grupo.meses.join(' e ')} meses)</p>

                                                               <div className="space-y-2">
                                                                  {grupo.meses.map((h) => {
                                                                     const dado = projecaoCompleta.find(p => p.months === h);
                                                                     if (!dado) return null;
                                                                     return (
                                                                        <div key={h} className="bg-black/20 rounded-xl p-3">
                                                                           <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2.5 text-center">{h} meses</p>
                                                                           {/* 🔥 CORRIGIDO: era um grid de 3 colunas — com valores na
                                                                               casa dos bilhões (ex: R$ 1.030.301.000,00) os números
                                                                               não cabiam e se sobrepunham. Empilhado em linhas,
                                                                               cada valor tem a largura toda pra si, então funciona
                                                                               com qualquer tamanho de número. */}
                                                                           <div className="space-y-1.5">
                                                                              <div className="flex items-center justify-between gap-3">
                                                                                 <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide shrink-0">Pessimista</p>
                                                                                 <p className="text-[11px] font-black text-red-300 text-right">R$ {formatCurrency(dado.pessimista)}</p>
                                                                              </div>
                                                                              <div className="flex items-center justify-between gap-3 border-t border-white/5 pt-1.5">
                                                                                 <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide shrink-0">Provável</p>
                                                                                 <p className="text-[11px] font-black text-white text-right">R$ {formatCurrency(dado.provavel)}</p>
                                                                              </div>
                                                                              <div className="flex items-center justify-between gap-3 border-t border-white/5 pt-1.5">
                                                                                 <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide shrink-0">Otimista</p>
                                                                                 <p className="text-[11px] font-black text-lime-300 text-right">R$ {formatCurrency(dado.otimista)}</p>
                                                                              </div>
                                                                           </div>
                                                                        </div>
                                                                     );
                                                                  })}
                                                               </div>

                                                               {/* Texto de IA DESSE bloco específico — nunca comenta
                                                                   outro prazo. Enquanto não chegou, mostra um estado
                                                                   de carregando de verdade (nunca mais um texto
                                                                   genérico fixo tipo "boa previsibilidade..." — isso
                                                                   é regra interna, não é análise real da IA). */}
                                                               {grupo.texto ? (
                                                                  <div className="mt-3 pt-3 border-t border-white/10">
                                                                     {formatarParecerIA(grupo.texto)}
                                                                  </div>
                                                               ) : (
                                                                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
                                                                     <div className="w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin shrink-0" />
                                                                     <p className="text-[11px] text-slate-500 italic">Gerando a análise desse prazo com IA...</p>
                                                                  </div>
                                                               )}
                                                            </div>
                                                         );
                                                      })}
                                                   </div>

                                                   <p className="text-[11px] text-slate-600 uppercase tracking-widest font-bold mt-4 flex items-center gap-1.5 relative z-10">
                                                      <span className="w-1 h-1 rounded-full bg-purple-400" /> Previsão gerada por IA
                                                   </p>
                                                </div>
                                             );
                                          })()}

                                          {/* 🏆 OPORTUNIDADES ESTRATÉGICAS — card totalmente separado,
                                              exclusivo do plano Total. É o que justifica visualmente o
                                              salto de preço: vai além de diagnóstico/projeção, entrega
                                              sugestões concretas de crescimento pro nicho específico. */}
                                          {gestorPlan === 'total' && oportunidadesTexto && (
                                             <div className="bg-gradient-to-br from-amber-900/20 via-amber-950/10 to-transparent border border-amber-500/30 p-8 rounded-[2.5rem] mt-5 relative overflow-hidden">
                                                <div className="absolute top-0 right-0 p-4 opacity-10"><Crown size={70} className="text-amber-400" /></div>
                                                <div className="flex items-center gap-2 mb-1 relative z-10">
                                                   <Crown size={16} className="text-amber-400" />
                                                   <p className="text-sm font-black text-amber-300 uppercase tracking-widest italic">Oportunidades Estratégicas</p>
                                                </div>
                                                <p className="text-[11px] text-amber-500/70 uppercase tracking-widest font-black mb-5 relative z-10">Exclusivo Protocolo Total</p>
                                                <div className="relative z-10">
                                                   {formatarParecerIA(oportunidadesTexto)}
                                                </div>
                                             </div>
                                          )}
                                       </div>
                                    </div>
                                 ) : renderPaywall('operacao')
                              )}

                              {/* ABA 3: CFO DO GESTOR */}
                              {reportTab === 'cfo' && (
                                 gestorAccessLevel >= TAB_LEVELS['cfo'] ? (
                                    <div className="animate-fade-up">
                                       <div className="space-y-8 pb-10 pt-2">
                                          <div className="space-y-4">
                                             <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none">CFO <span className="text-lime-400">Virtual</span></h2>
                                             <p className="text-[11px] text-slate-500 font-black tracking-[0.3em] leading-relaxed italic">Tesouraria preditiva, proteção contra ruptura e distribuição de lucros.</p>
                                          </div>
                                          <div className="bg-gradient-to-br from-slate-900/80 to-[#020617] p-8 rounded-[3rem] border border-lime-400/30 shadow-[0_0_30px_rgba(190,242,100,0.1)] relative overflow-hidden text-center">
                                             <div className="absolute top-0 left-0 w-full h-1 bg-lime-400/50"></div>
                                             <div className="flex items-center justify-center gap-2 mb-2 relative z-10">
                                                <Coins size={14} className="text-lime-400" />
                                                <p className="text-[11px] font-black text-lime-400 uppercase tracking-widest italic">Saque Seguro (Dividendos Ano)</p>
                                             </div>
                                             <h3 className={`${getDynamicTextSize(divStrFormat)} font-black text-white italic tracking-tighter leading-tight mb-6 relative z-10 break-all w-full`}>
                                                {divStrFormat}
                                             </h3>
                                             <p className="text-[11px] text-slate-400 mt-2 max-w-[280px] mx-auto relative z-10 text-justify text-justify-last-center">
                                                Limite matemático calculado pela IA de quanto os sócios podem retirar da empresa no ano sem comprometer o fluxo de capital de giro e o pagamento de dívidas.
                                             </p>
                                          </div>
                                          <div className="grid grid-cols-1 gap-4">
                                             <div className="bg-slate-900/60 border border-white/5 p-6 rounded-[2rem] shadow-lg relative overflow-hidden">
                                                <div className="absolute top-0 right-0 p-4 opacity-5"><Shield size={40} className="text-blue-400" /></div>
                                                <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Reserva de Emergência Ideal</p>
                                                <p className={`${getDynamicTextSize(reserveStrFormat)} font-black text-white italic tracking-tighter drop-shadow-[0_0_10px_rgba(255,255,255,0.1)] break-all leading-tight w-full`}>
                                                   {reserveStrFormat}
                                                </p>
                                                <p className="text-[11px] text-slate-500 font-bold uppercase mt-2">Caixa alvo para suportar 6 meses sem receita.</p>
                                             </div>

                                             {/* CAIXA MÍNIMO MENSAL — 🔥 NOVO, mesma tela do CFO Virtual,
                                                 já que é justamente um indicador de tesouraria. */}
                                             <div className="bg-slate-900/60 border border-white/5 p-6 rounded-[2rem] shadow-lg relative overflow-hidden">
                                                <div className="absolute top-0 right-0 p-4 opacity-5"><Wallet size={40} className="text-lime-400" /></div>
                                                <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Caixa Mínimo Mensal</p>
                                                <p className={`${getDynamicTextSize(caixaMinimoStrFormat)} font-black text-white italic tracking-tighter drop-shadow-[0_0_10px_rgba(255,255,255,0.1)] break-all leading-tight w-full`}>
                                                   {caixaMinimoStrFormat}
                                                </p>
                                                <p className="text-[11px] text-slate-500 font-bold mt-2">Custos fixos + pró-labore dos sócios — o piso de caixa mensal.</p>
                                             </div>
                                          </div>

                                          {(report.ebitdaAjustado !== report.ebitda || finance.ncg) && (
                                             <div className="space-y-4">
                                                <p className="text-[11px] text-blue-400 font-black uppercase tracking-widest italic px-2">Análise Técnica Avançada</p>

                                                {report.ebitdaAjustado !== report.ebitda && (
                                                   <div className="bg-slate-900/60 border border-blue-500/20 p-6 rounded-[2rem] shadow-lg relative overflow-hidden">
                                                      <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">EBITDA Ajustado (Normalizado)</p>
                                                      <p className={`${getDynamicTextSize(ebitdaAjustadoStrFormat)} font-black text-blue-400 italic tracking-tighter break-all leading-tight w-full`}>
                                                         {ebitdaAjustadoStrFormat}
                                                      </p>
                                                      <p className="text-[11px] text-slate-500 font-bold uppercase mt-2">EBITDA real ({ebitdaStrFormat}) somado ao custo não recorrente informado — reflete a capacidade real de geração de caixa da operação.</p>
                                                   </div>
                                                )}

                                                <div className="grid grid-cols-1 gap-4">
                                                   <div className="bg-slate-900/60 border border-white/5 p-6 rounded-[2rem] shadow-lg">
                                                      <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Necessidade de Capital de Giro</p>
                                                      <p className={`${getDynamicTextSize(ncgStrFormat)} font-black italic tracking-tighter break-all leading-tight w-full ${finance.ncg < 0 ? 'text-lime-400' : 'text-white'}`}>
                                                         {finance.ncg < 0 ? '- ' : ''}{ncgStrFormat}
                                                      </p>
                                                      <p className="text-[11px] text-slate-500 font-bold uppercase mt-2">
                                                         {finance.ncg < 0
                                                            ? 'Ciclo favorável: seus fornecedores financiam parte da operação.'
                                                            : 'Caixa necessário para cobrir o intervalo entre pagar fornecedores e receber dos clientes.'}
                                                      </p>

                                                      {formData.prazoRecebimento && formData.prazoPagamento && (() => {
                                                         const cicloCliente = Number(formData.prazoRecebimento) - Number(formData.prazoPagamento);
                                                         const cicloSetor = getSectorCycleBenchmark(formData.segment);
                                                         const diferenca = cicloCliente - cicloSetor;
                                                         const maxEscala = Math.max(Math.abs(cicloCliente), cicloSetor, 1) * 1.2;
                                                         return (
                                                            <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
                                                               <p className="text-[11px] text-blue-400 font-black uppercase tracking-widest">Comparado ao setor ({formData.segment || 'geral'})</p>
                                                               <div>
                                                                  <div className="flex justify-between text-[11px] font-bold uppercase mb-1">
                                                                     <span className="text-slate-400">Seu ciclo</span>
                                                                     <span className="text-white">{cicloCliente} dias</span>
                                                                  </div>
                                                                  <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                                                                     <div className="h-full bg-blue-400 rounded-full" style={{ width: `${Math.min(100, Math.max(2, (Math.abs(cicloCliente) / maxEscala) * 100))}%` }} />
                                                                  </div>
                                                               </div>
                                                               <div>
                                                                  <div className="flex justify-between text-[11px] font-bold uppercase mb-1">
                                                                     <span className="text-slate-400">Média do setor</span>
                                                                     <span className="text-slate-400">{cicloSetor} dias</span>
                                                                  </div>
                                                                  <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                                                                     <div className="h-full bg-white/25 rounded-full" style={{ width: `${Math.min(100, Math.max(2, (cicloSetor / maxEscala) * 100))}%` }} />
                                                                  </div>
                                                               </div>
                                                               <p className="text-[11px] text-slate-500 font-bold uppercase leading-relaxed">
                                                                  {diferenca > 5
                                                                     ? `Seu ciclo é ${diferenca} dias mais longo que a média do setor — exige mais capital de giro que os concorrentes.`
                                                                     : diferenca < -5
                                                                        ? `Seu ciclo é ${Math.abs(diferenca)} dias mais curto que a média do setor — vantagem competitiva de caixa.`
                                                                        : 'Seu ciclo está alinhado com a média do setor.'}
                                                               </p>
                                                            </div>
                                                         );
                                                      })()}
                                                   </div>

                                                   {report.custoServicoDivida > 0 && (
                                                      <div className="bg-slate-900/60 border border-white/5 p-6 rounded-[2rem] shadow-lg">
                                                         <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Custo Anual da Dívida (Juros)</p>
                                                         <p className={`${getDynamicTextSize(custoServicoDividaStrFormat)} font-black text-orange-400 italic tracking-tighter break-all leading-tight w-full`}>
                                                            {custoServicoDividaStrFormat}
                                                         </p>
                                                         <p className="text-[11px] text-slate-500 font-bold uppercase mt-2">
                                                            {report.coberturaJuros
                                                               ? (report.coberturaJuros > 0
                                                                  ? `Seu EBITDA Ajustado cobre esse custo ${report.coberturaJuros}x — quanto maior, mais folga.`
                                                                  : 'Alerta: a operação está com EBITDA negativo e não gera caixa suficiente nem pra cobrir os juros da dívida.')
                                                               : 'Quanto a empresa paga de juros por ano sobre a dívida total.'}
                                                         </p>
                                                      </div>
                                                   )}

                                                   {finance.coberturaCurtoPrazo !== null && finance.coberturaCurtoPrazo !== undefined && (
                                                      <div className="bg-slate-900/60 border border-white/5 p-6 rounded-[2rem] shadow-lg">
                                                         <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Cobertura da Dívida de Curto Prazo</p>
                                                         <p className={`text-3xl font-black italic tracking-tighter ${finance.coberturaCurtoPrazo < 1 ? 'text-red-500' : 'text-lime-400'}`}>
                                                            {finance.coberturaCurtoPrazo}x
                                                         </p>
                                                         <p className="text-[11px] text-slate-500 font-bold uppercase mt-2">
                                                            {finance.coberturaCurtoPrazo < 1
                                                               ? 'Alerta: o EBITDA não cobre a dívida que vence este ano.'
                                                               : 'Quantas vezes o EBITDA cobre a dívida que vence este ano.'}
                                                         </p>
                                                      </div>
                                                   )}
                                                </div>
                                             </div>
                                          )}

                                          <div className="bg-slate-950/60 border border-red-500/20 p-8 rounded-[2.5rem] text-center shadow-lg">
                                             <div className="flex justify-center mb-3">
                                                <AlertTriangle size={28} className={finance.ruptureRisk > 50 ? 'text-red-500 animate-pulse' : 'text-orange-500'} />
                                             </div>
                                             <p className="text-[11px] text-slate-500 font-black uppercase tracking-widest mb-4">Risco de Ruptura de Caixa (12 Meses)</p>
                                             <div className="flex justify-center items-baseline gap-1 mb-4">
                                                <span className={`text-6xl font-black italic tracking-tighter ${finance.ruptureRisk > 50 ? 'text-red-500 drop-shadow-[0_0_15px_rgba(239,68,68,0.4)]' : 'text-orange-400'}`}>
                                                   {finance.ruptureRisk.toFixed(0)}
                                                </span>
                                                <span className={`text-xl font-black ${finance.ruptureRisk > 50 ? 'text-red-500' : 'text-orange-400'}`}>%</span>
                                             </div>
                                             <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden mb-4">
                                                <div className={`h-full transition-all duration-1000 ${finance.ruptureRisk > 70 ? 'bg-red-600' : finance.ruptureRisk > 40 ? 'bg-orange-500' : 'bg-lime-400'}`} style={{ width: `${finance.ruptureRisk}%` }}></div>
                                             </div>
                                             <p className="text-[11px] text-slate-400 leading-relaxed text-justify text-justify-last-center">
                                                Probabilidade preditiva da empresa ficar inadimplente com base no estrangulamento da dívida sobre a margem atual.
                                                {finance.ruptureRisk > 50 && " Risco Crítico de insolvência. Reduza passivos imediatamente."}
                                             </p>
                                          </div>

                                          {/* SIMULADOR DE EMPRÉSTIMO — trazido do Dashboard Gestor */}
                                          {formData.userType !== 'Consultor' && (
                                             <div className="bg-slate-900/40 p-8 rounded-[2.5rem] border border-white/5">
                                                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-1">Simulador de Empréstimo</p>
                                                <p className="text-[11px] text-slate-500 mb-5">Simule se um empréstimo cabe no orçamento antes de assumir o compromisso.</p>
                                                <div className="space-y-4 mb-5">
                                                   <input type="text" inputMode="numeric" value={emprestimoValorApp}
                                                      onChange={(e) => setEmprestimoValorApp(mascaraMoedaApp(e.target.value))}
                                                      placeholder="Valor do empréstimo (R$)"
                                                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50" />
                                                   <div>
                                                      <div className="flex justify-between mb-1"><span className="text-[11px] text-slate-400 font-bold uppercase">Juros ao Ano</span><span className="text-lime-400 font-black text-xs">{emprestimoJurosApp}%</span></div>
                                                      <input type="range" min="0" max="80" value={emprestimoJurosApp} onChange={(e) => setEmprestimoJurosApp(Number(e.target.value))} className="w-full accent-lime-400" />
                                                   </div>
                                                   <div>
                                                      <div className="flex justify-between mb-1"><span className="text-[11px] text-slate-400 font-bold uppercase">Prazo (meses)</span><span className="text-lime-400 font-black text-xs">{emprestimoPrazoApp}</span></div>
                                                      <input type="range" min="1" max="60" value={emprestimoPrazoApp} onChange={(e) => setEmprestimoPrazoApp(Number(e.target.value))} className="w-full accent-lime-400" />
                                                   </div>
                                                </div>
                                                {calculoEmprestimoApp.valido ? (
                                                   <>
                                                      <div className="grid grid-cols-2 gap-2 mb-4">
                                                         <div className="bg-black/30 rounded-xl p-3 text-center"><p className="text-[11px] text-slate-500 uppercase font-bold">Parcela Mensal</p><p className="text-sm font-black text-white mt-1">R$ {formatCurrency(calculoEmprestimoApp.parcela)}</p></div>
                                                         <div className="bg-black/30 rounded-xl p-3 text-center"><p className="text-[11px] text-slate-500 uppercase font-bold">Total de Juros</p><p className="text-sm font-black text-white mt-1">R$ {formatCurrency(calculoEmprestimoApp.totalJuros)}</p></div>
                                                      </div>
                                                      <div className={`rounded-xl p-4 border text-center ${calculoEmprestimoApp.nivel === 'critico' ? 'bg-red-500/5 border-red-500/20' : calculoEmprestimoApp.nivel === 'atencao' ? 'bg-orange-500/5 border-orange-500/20' : 'bg-lime-400/5 border-lime-400/20'}`}>
                                                         {calculoEmprestimoApp.comprometimento !== null && (
                                                            <p className={`text-xl font-black mb-1 ${calculoEmprestimoApp.nivel === 'critico' ? 'text-red-400' : calculoEmprestimoApp.nivel === 'atencao' ? 'text-orange-400' : 'text-lime-400'}`}>{calculoEmprestimoApp.comprometimento.toFixed(0)}% <span className="text-[11px] text-slate-500 font-bold uppercase">do lucro mensal</span></p>
                                                         )}
                                                         <p className="text-[11px] text-slate-400">{calculoEmprestimoApp.veredito}</p>
                                                      </div>
                                                   </>
                                                ) : (
                                                   <p className="text-[11px] text-slate-500 text-center py-4 bg-black/20 rounded-xl">Informe o valor pra simular a parcela.</p>
                                                )}
                                             </div>
                                          )}
                                       </div>
                                    </div>
                                 ) : renderPaywall('cfo')
                              )}

                              {/* ABA 4: M&A DO GESTOR */}
                              {reportTab === 'ma' && (
                                 gestorAccessLevel >= TAB_LEVELS['ma'] ? (
                                    <div className="animate-fade-up">
                                       <div className="space-y-8 pb-10 pt-2">
                                          <div className="space-y-4">
                                             <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none">Board<span className="text-purple-400">room</span></h2>
                                             <p className="text-[11px] text-slate-500 font-black tracking-[0.3em] leading-relaxed italic">Valuation, Governança e Prontidão para M&A ou Sucessão.</p>
                                          </div>
                                          <div className="bg-gradient-to-br from-slate-900/80 to-[#020617] p-8 rounded-[3rem] border border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.15)] relative overflow-hidden text-center">
                                             <div className="absolute top-0 left-0 w-full h-1 bg-purple-500/50"></div>
                                             <p className="text-[11px] font-black text-purple-400 uppercase tracking-widest italic mb-2 relative z-10">Patrimônio Líquido (Equity Value)</p>
                                             <h3 className={`${getDynamicTextSize(equityStrFormat)} font-black text-white italic tracking-tighter leading-tight mb-6 relative z-10 whitespace-nowrap w-full`}>
                                                {equityStrFormat}
                                             </h3>
                                             <div className="grid grid-cols-[35%_65%] gap-0 border-t border-white/5 pt-6 relative z-10 w-full">
                                                <div className="text-left pr-3">
                                                   <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold mb-1">Múltiplo Base</p>
                                                   <p className="text-xl text-white font-black italic">{finance.multiple}x</p>
                                                </div>
                                                <div className="text-left pl-3 border-l border-white/5 w-full min-w-0">
                                                   <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold mb-1">Dívida Deduzida</p>
                                                   <p className="text-base text-red-400 font-black italic whitespace-nowrap leading-tight w-full">{debtStrFormat}</p>
                                                </div>
                                             </div>
                                          </div>
                                          <div className="space-y-4 bg-slate-900/40 p-8 rounded-[2.5rem] border border-white/5 text-left shadow-lg">
                                             <div className="flex items-center gap-3 border-b border-white/5 pb-4 mb-4">
                                                <PieChart className="text-purple-400" size={20} />
                                                <h4 className="text-[11px] font-black text-white uppercase tracking-widest italic">Enterprise Value (EV)</h4>
                                             </div>
                                             <p className="text-[11px] text-slate-400 font-medium leading-relaxed">
                                                O EV representa o valor total da operação (EBITDA Projetado x Múltiplo do Setor), desconsiderando a forma como ela é financiada.
                                             </p>
                                             <p className={`${getDynamicTextSize(evStrFormat)} font-black text-white italic tracking-tighter pt-2 break-all leading-tight w-full`}>
                                                {evStrFormat}
                                             </p>
                                          </div>

                                          {/* 🔥 NOVO: Gráfico de Sensibilidade — mostra o impacto de mudar a margem no Valuation */}
                                          {(() => {
                                             const rev = parseCurrencyToNumber(formData.revenue);
                                             const cos = parseCurrencyToNumber(formData.costs);
                                             const deb = parseCurrencyToNumber(formData.debt);
                                             if (!rev) return null;

                                             const advanced = {
                                                custoNaoRecorrente: parseCurrencyToNumber(formData.custoNaoRecorrente),
                                                custoDivida: formData.custoDivida
                                             };
                                             const passo = rev * 0.05;
                                             const cenarios = [
                                                { label: '-5% margem', cos: cos + passo },
                                                { label: 'Atual', cos: cos },
                                                { label: '+5% margem', cos: Math.max(0, cos - passo) }
                                             ].map(c => {
                                                const core = calculateCoreMetrics(rev, c.cos, deb, formData.segment, advanced);
                                                return { ...c, equity: Math.max(0, core.equityValue) };
                                             });
                                             const maxEquity = Math.max(...cenarios.map(c => c.equity), 1);

                                             return (
                                                <div className="space-y-6 bg-slate-900/40 p-8 rounded-[2.5rem] border border-white/5 text-left shadow-lg">
                                                   <div className="flex items-center gap-3 border-b border-white/5 pb-4">
                                                      <Activity className="text-purple-400" size={20} />
                                                      <h4 className="text-[11px] font-black text-white uppercase tracking-widest italic">Sensibilidade do Valuation</h4>
                                                   </div>
                                                   <p className="text-[11px] text-slate-400 font-medium leading-relaxed">
                                                      O quanto o Patrimônio Líquido (Equity Value) muda se a margem operacional variar 5 pontos percentuais pra cima ou pra baixo.
                                                   </p>
                                                   <div className="space-y-4 pt-2">
                                                      {cenarios.map((c, i) => (
                                                         <div key={i}>
                                                            <div className="flex justify-between items-baseline mb-1.5">
                                                               <span className={`text-[11px] font-black uppercase tracking-widest ${c.label === 'Atual' ? 'text-purple-400' : 'text-slate-500'}`}>{c.label}</span>
                                                               <span className={`text-[11px] font-black ${c.label === 'Atual' ? 'text-white' : 'text-slate-400'}`}>R$ {formatCurrency(c.equity)}</span>
                                                            </div>
                                                            <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden">
                                                               <div
                                                                  className={`h-full rounded-full transition-all duration-700 ${c.label === 'Atual' ? 'bg-gradient-to-r from-purple-600 to-purple-400' : 'bg-white/20'}`}
                                                                  style={{ width: `${Math.max(3, (c.equity / maxEquity) * 100)}%` }}
                                                               />
                                                            </div>
                                                         </div>
                                                      ))}
                                                   </div>
                                                </div>
                                             );
                                          })()}

                                          <div className="bg-slate-900/60 border border-purple-500/20 p-8 rounded-[3rem] text-center relative overflow-hidden">
                                             <div className="absolute top-0 right-0 p-4 opacity-5"><Binary size={80} className="text-purple-400" /></div>
                                             <p className="text-[11px] text-purple-400 font-black uppercase tracking-widest mb-4 relative z-10">M&A Readiness Score</p>
                                             <div className="flex justify-center items-baseline gap-1 relative z-10">
                                                <span className="text-6xl font-black text-white italic tracking-tighter drop-shadow-[0_0_15px_#a855f7]">{finance.maScore}</span>
                                                <span className="text-xl text-purple-400 font-black">/100</span>
                                             </div>
                                             <p className="text-[11px] text-slate-400 mt-6 max-w-[250px] mx-auto relative z-10">
                                                {finance.maScore > 70 ? "Sua empresa apresenta governança atrativa para Fundos ou transição geracional segura." : "Métricas sob risco. Risco alto de colapso numa sucessão familiar ou diligência de fundos."}
                                             </p>
                                          </div>

                                          <div className="space-y-6 bg-slate-900/40 p-8 rounded-[2.5rem] border border-white/5 text-left shadow-lg">
                                             <div className="flex items-center gap-3 border-b border-white/5 pb-4">
                                                <Briefcase className="text-purple-400" size={20} />
                                                <h4 className="text-[11px] font-black text-white uppercase tracking-widest italic">Checklist Due Diligence</h4>
                                                <span className="ml-auto text-[11px] text-slate-500 font-bold">{Object.values(dueDiligenceChecklist).filter(Boolean).length}/2</span>
                                             </div>
                                             <div className="space-y-5">
                                                <button onClick={() => toggleChecklistItem('auditoria')} className="w-full flex items-start gap-4 text-left">
                                                   <div className={`w-6 h-6 rounded border flex items-center justify-center shrink-0 mt-0.5 transition-all ${dueDiligenceChecklist.auditoria ? 'bg-lime-400 border-lime-400' : 'bg-slate-800 border-white/10'}`}>
                                                      {dueDiligenceChecklist.auditoria && <Check size={14} className="text-black" strokeWidth={3} />}
                                                   </div>
                                                   <div>
                                                      <p className={`text-[11px] font-black uppercase tracking-widest mb-1 ${dueDiligenceChecklist.auditoria ? 'text-slate-500 line-through' : 'text-white'}`}>Auditoria Contábil Independente</p>
                                                      <p className="text-[11px] text-slate-500 leading-relaxed">Balanços dos últimos 3 anos livres de confusão patrimonial (separação Pessoa Física/Jurídica).</p>
                                                   </div>
                                                </button>
                                                <button onClick={() => toggleChecklistItem('acordoSocios')} className="w-full flex items-start gap-4 text-left">
                                                   <div className={`w-6 h-6 rounded border flex items-center justify-center shrink-0 mt-0.5 transition-all ${dueDiligenceChecklist.acordoSocios ? 'bg-lime-400 border-lime-400' : 'bg-slate-800 border-white/10'}`}>
                                                      {dueDiligenceChecklist.acordoSocios && <Check size={14} className="text-black" strokeWidth={3} />}
                                                   </div>
                                                   <div>
                                                      <p className={`text-[11px] font-black uppercase tracking-widest mb-1 ${dueDiligenceChecklist.acordoSocios ? 'text-slate-500 line-through' : 'text-white'}`}>Acordo de Sócios CVM</p>
                                                      <p className="text-[11px] text-slate-500 leading-relaxed">Cláusulas de sucessão e regras claras de saída de sócios revisadas juridicamente.</p>
                                                   </div>
                                                </button>
                                             </div>
                                          </div>
                                          <button onClick={() => { setDownloadConfirmEhSuper(true); setShowDownloadConfirm(true); }} className="w-full bg-purple-600 text-white py-7 rounded-full font-black uppercase text-sm shadow-[0_0_20px_rgba(168,85,247,0.4)] active:scale-95 transition-all flex flex-col items-center justify-center gap-1 mt-8 hover:bg-purple-500">
                                             <div className="flex items-center gap-2">
                                                <FileSignature size={20} />
                                                <span>Baixar Relatório 360º</span>
                                             </div>
                                             <span className="text-[11px] uppercase font-bold tracking-widest opacity-80">Download Ilimitado no Plano Total</span>
                                          </button>
                                       </div>
                                    </div>
                                 ) : (
                                    <div className="flex flex-col items-center justify-center pt-3 space-y-6 shadow-2xl animate-fade-up">
                                       <div className="relative scale-100 mb-2">
                                          <div className="absolute inset-0 bg-purple-500/20 blur-[40px] rounded-full animate-pulse"></div>
                                          <Binary size={40} className="text-white opacity-5 relative z-10" />
                                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"><Lock size={20} className="text-purple-400 drop-shadow-[0_0_10px_currentColor]" /></div>
                                       </div>
                                       <div className="space-y-3 max-w-[280px] text-center mb-4">
                                          <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter leading-none text-center">Boardroom & M&A</h2>
                                          <p className="text-[11px] text-slate-400 font-black tracking-[0.2em] leading-relaxed italic text-center">Escolha como deseja acessar o motor avançado de Valuation e Parecer Executivo de alto nível.</p>
                                       </div>
                                       <div className="bg-slate-900/60 p-8 rounded-[3rem] border border-purple-500/30 w-full space-y-5 text-center shadow-[0_0_30px_rgba(168,85,247,0.15)] relative overflow-hidden group">
                                          <div className="absolute top-0 right-0 p-6 opacity-10"><BarChart3 size={80} className="text-purple-400" /></div>
                                          <div className="flex flex-col items-center relative z-10">
                                             <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest mb-4 border border-purple-500/30">Assinatura Premium Recomendada</span>
                                             {/* 🔥 CORRIGIDO (2026-09-29): faltava o nome do plano — a
                                                 pessoa via preço e badge, mas nunca "Protocolo Total"
                                                 escrito, diferente de todos os outros cards de upgrade
                                                 do app. */}
                                             <h3 className="text-2xl font-black text-white uppercase mb-3">Protocolo Total</h3>
                                             <div className="bg-purple-500 text-white px-6 py-4 rounded-[1.5rem] shadow-[0_0_30px_rgba(168,85,247,0.4)] flex flex-col items-center w-full">
                                                <div className="flex items-baseline gap-1"><span className="font-black text-lg italic uppercase">R$</span><span className="font-black text-4xl tracking-tighter italic leading-none">799,00</span></div>
                                                <span className="text-[11px] font-black uppercase opacity-70 italic mt-1 tracking-widest">Acesso Mensal a Todos os Módulos</span>
                                             </div>
                                          </div>
                                          {/* 🔥 CORRIGIDO (2026-09-29): usava landingProtocols[2] (só 5
                                              itens genéricos, os mesmos da landing page). Este é o
                                              plano mais caro do Gestor — listar TODOS os benefícios
                                              acumulados (Tactical + Plus + Total) é o que "faz valer"
                                              o preço aos olhos de quem está decidindo. Aqui, diferente
                                              dos cards de Operação/CFO, não precisa caber numa linha —
                                              é a vitrine completa. */}
                                          <p className="text-[11px] font-black uppercase tracking-widest text-purple-400 text-left w-full mt-6 mb-2">Tudo do Tactical e Plus, mais:</p>
                                          <ul className="space-y-2.5 w-full text-left relative z-10">
                                             {[
                                                'Diagnósticos sem limite (Ilimitados)',
                                                'Acesso ao Dashboard pelo computador',
                                                'Projeção completa: 3, 6, 12, 24 e 36 meses (IA Estocástica)',
                                                'Saiba se o caixa vai apertar (Radar D+0)',
                                                'Teste uma virada de jogo (Simulador de Turnaround)',
                                                'Metas calculadas pela IA (Metas Ideais)',
                                                'Um CFO disponível 24h (CFO Virtual Preditivo)',
                                                'Descubra ANTES se vai faltar caixa (Risco de Ruptura)',
                                                'Quanto dá pra sacar sem sufocar a empresa (Saque Seguro)',
                                                'Reserva ideal pra dormir tranquilo (Reserva de Emergência)',
                                                'Relatório premium de graça, todo mês (Super 360º)',
                                                'Passo a passo pra sua empresa valer mais (Guia de Valor)',
                                                'Quanto sua empresa vale hoje (Valuation Real-Time)',
                                                'Pronta pra vender ou receber investimento? (M&A Score)',
                                                'Organize a governança antes de negociar (Checklist)'
                                             ].map((t, i) => {
                                                const match = t.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
                                                return (
                                                   <li key={i} className="flex items-start gap-2.5 text-[11px] font-bold text-slate-300 leading-snug">
                                                      <span className="text-purple-400 shrink-0 mt-0.5">✓</span>
                                                      {match ? (<span>{match[1]} <span className="text-slate-500 normal-case">({match[2]})</span></span>) : <span>{t}</span>}
                                                   </li>
                                                );
                                             })}
                                          </ul>
                                          <button onClick={() => handleProtocolSelection('total', 'Protocolo Total', '799,00', 'ASSINATURA')} className="w-full bg-white text-black py-6 rounded-full font-black uppercase text-sm shadow-xl shadow-purple-600/20 active:scale-95 transition-all relative z-10 mt-2">Ativar Protocolo Total</button>
                                       </div>
                                       <div className="flex items-center justify-center w-full gap-4 opacity-50 px-8 py-2">
                                          <div className="h-[1px] flex-1 bg-white/20"></div>
                                          <span className="text-[11px] font-black uppercase tracking-widest text-white">OU APENAS O ARQUIVO</span>
                                          <div className="h-[1px] flex-1 bg-white/20"></div>
                                       </div>
                                       <div className="bg-[#020617] border border-white/5 p-6 rounded-[2.5rem] w-full text-left relative overflow-hidden">
                                          <div className="flex items-center gap-3 mb-3">
                                             <FileSignature size={20} className="text-slate-400" />
                                             <p className="text-[11px] font-black text-white uppercase tracking-widest">Documento Avulso</p>
                                          </div>
                                          <p className="text-[11px] text-slate-400 leading-relaxed mb-6 font-medium">Obtenha apenas a compilação final em PDF com o Valuation, Dossiê de 4 páginas e Parecer de Risco. O arquivo ficará salvo eternamente na sua Galeria, mas sem liberar a assinatura mensal das abas táticas.</p>
                                          {(() => {
                                             const gestorLevelAtual = { 'entry': 1, 'tactical': 2, 'plus': 3, 'total': 4 }[gestorPlan] || 1;
                                             const podeComprarAvulso = gestorLevelAtual >= 3; // Plus (R$499) ou superior
                                             if (!podeComprarAvulso) {
                                                return (
                                                   <div className="w-full bg-slate-900/60 border border-white/10 text-slate-500 py-5 px-6 rounded-[2rem] flex flex-col items-center justify-center gap-2 text-center cursor-not-allowed">
                                                      <span className="flex items-center gap-2 font-black uppercase text-[11px] tracking-widest"><Lock size={12} /> Disponível a partir do Plano Plus</span>
                                                      <span className="opacity-60 normal-case font-medium text-[11px] leading-relaxed">Requer dados de Visão, Operação e CFO — assine Plus (R$ 499,00) ou superior.</span>
                                                   </div>
                                                );
                                             }
                                             return (
                                                <button onClick={() => handleProtocolSelection('report_avulso', 'Super Relatório 360º Avulso', '1.490,00', 'PAGAMENTO ÚNICO')} className="w-full bg-transparent border border-white/20 text-white py-5 rounded-full font-black uppercase text-[11px] active:scale-95 transition-all flex flex-col items-center justify-center gap-1 hover:bg-white/5">
                                                   <span>Comprar Apenas o PDF</span>
                                                   <span className="opacity-70">R$ 1.490,00 (Pagamento Único)</span>
                                                </button>
                                             );
                                          })()}
                                       </div>
                                    </div>
                                 )
                              )}
                           </>
                        )}

                        {/* 🤖 TEASER "AGENTES DE IA" — Em Breve. Sem
                        funcionalidade real por trás, só antecipa a feature
                        e mede interesse. Mesmo bloco pros dois perfis
                        (Gestor e Consultor), por isso fica fora da
                        distinção acima. */}
                        {reportTab === 'agentes-ia' && (
                           <div className="animate-fade-up">
                              <div className="relative bg-gradient-to-br from-amber-950/50 via-[#020617] to-[#020617] px-7 pt-7 pb-8 rounded-[3rem] border border-amber-500/25 overflow-hidden text-center shadow-[0_0_60px_rgba(245,158,11,0.15)]">
                                 {/* Textura de fundo — só decorativa */}
                                 <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #f59e0b 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
                                 <div className="absolute -top-14 -right-14 w-52 h-52 bg-amber-500/15 rounded-full blur-3xl" />
                                 <div className="absolute -bottom-14 -left-14 w-52 h-52 bg-amber-500/10 rounded-full blur-3xl" />

                                 <div className="relative z-10">
                                    <div className="w-20 h-20 mx-auto mb-4 rounded-[1.6rem] bg-gradient-to-br from-amber-400/25 to-amber-600/5 border border-amber-400/40 flex items-center justify-center shadow-[0_0_45px_rgba(245,158,11,0.35)]">
                                       <Bot size={42} className="text-amber-400" />
                                    </div>

                                    <span className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-5 py-2 rounded-full text-[11px] font-black uppercase tracking-[0.25em] border border-amber-500/40 mb-6 shadow-[0_0_20px_rgba(245,158,11,0.15)]">
                                       <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" /> Em Breve
                                    </span>

                                    <h2 className="text-[2.5rem] font-black text-white uppercase italic tracking-tighter leading-[0.9] mb-4">
                                       Agentes de <span className="text-amber-400">IA</span>
                                    </h2>
                                    <p className="text-[13px] text-slate-300 leading-relaxed max-w-xs mx-auto mb-8 font-medium">
                                       {formData.userType === 'Consultor'
                                          ? 'Sua carteira sendo monitorada sozinha: alertas automáticos de risco, tarefas geradas pra você e due diligence assistida — sem precisar abrir cliente por cliente.'
                                          : 'Sua empresa sendo monitorada sozinha: alertas automáticos quando algo mudar, tarefas geradas pra você e leitura de dados bagunçados — sem precisar digitar nada.'}
                                    </p>

                                    <div className="space-y-3 mb-8">
                                       {[
                                          { titulo: formData.userType === 'Consultor' ? 'Monitoramento 24/7' : 'Monitoramento 24/7', desc: formData.userType === 'Consultor' ? 'Toda a carteira acompanhada sozinha' : 'Sua operação acompanhada sozinha' },
                                          { titulo: 'Tarefas Automáticas', desc: 'Alertas e ações sugeridas sem você pedir' },
                                          { titulo: formData.userType === 'Consultor' ? 'Due Diligence Assistida' : 'Leitura Inteligente', desc: formData.userType === 'Consultor' ? 'IA acompanha o processo de M&A com você' : 'Entende planilhas e extratos bagunçados' },
                                       ].map((item, i) => (
                                          <div key={i} className="bg-black/30 border border-amber-500/15 rounded-2xl p-4 text-left flex items-center gap-4">
                                             <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
                                                <Check size={16} className="text-amber-400" />
                                             </div>
                                             <div>
                                                <p className="text-[11px] font-black text-white uppercase tracking-widest">{item.titulo}</p>
                                                <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{item.desc}</p>
                                             </div>
                                          </div>
                                       ))}
                                    </div>

                                    {jaRegistrouInteresseAgentes ? (
                                       <div className="bg-amber-500/15 border border-amber-500/40 rounded-full py-4 px-6 flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(245,158,11,0.15)]">
                                          <CheckCircle2 size={18} className="text-amber-400" />
                                          <p className="text-xs font-black text-amber-400 uppercase tracking-widest">Interesse registrado!</p>
                                       </div>
                                    ) : (
                                       <button
                                          onClick={registrarInteresseAgentesIA}
                                          disabled={registrandoInteresseAgentes}
                                          className="w-full bg-amber-400 hover:bg-amber-300 text-black py-[1.15rem] rounded-full font-black uppercase text-sm tracking-widest active:scale-95 transition-all disabled:opacity-60 shadow-[0_0_35px_rgba(245,158,11,0.3)]"
                                       >
                                          {registrandoInteresseAgentes ? 'Registrando...' : 'Tenho Interesse'}
                                       </button>
                                    )}

                                    {contadorInteresseAgentes && (() => {
                                       const campo = formData.userType === 'Consultor' ? 'totalConsultor' : 'totalGestor';
                                       const total = contadorInteresseAgentes[campo] || 0;
                                       if (total <= 0) return null;
                                       return (
                                          <p className="text-[11px] text-slate-400 mt-5 font-bold">
                                             <span className="text-amber-400 font-black">{total}</span> {total === 1 ? 'pessoa já demonstrou' : 'pessoas já demonstraram'} interesse
                                          </p>
                                       );
                                    })()}
                                 </div>
                              </div>
                           </div>
                        )}

                        {/* DISCLAIMER */}
                        <div className="pt-12 text-center opacity-40 px-2 border-t border-white/5 text-left mt-8">
                           <p className="text-[11px] text-slate-400 font-bold uppercase tracking-[0.2em] leading-relaxed text-justify text-justify-last-center">
                              {DISCLAIMER_FULL}
                           </p>
                        </div>

                     </div>
                  )}

                  {/* HISTÓRICO DE DOSSIÊS (A GALERIA) */}
                  {view === 'history' && (
                     <div className="flex flex-col space-y-8 pt-8 pb-10 animate-fade-up text-left">
                        <div className="flex items-center gap-3 border-b border-white/5 pb-6">
                           <History className="text-lime-400 shrink-0" size={24} />
                           <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter leading-none whitespace-nowrap">Meus <span className="text-lime-400">Dossiês</span></h2>
                        </div>
                        <p className="text-[11px] text-slate-500 font-black uppercase tracking-widest italic leading-relaxed">
                           {user && !user.isAnonymous ? 'Central de relatórios e diagnósticos processados pela sua conta.' : 'Relatórios avulsos gerados neste dispositivo. Faça login para ver todo o histórico da sua conta.'}
                        </p>

                        {/* 🔥 NOVO (2026-09-29) — VULNERABILIDADE DE NEGÓCIO
                            achada em teste real: o convite pra criar conta só
                            disparava dentro do listener em memória que reage
                            ao pagamento confirmar (onSnapshot). Se o processo
                            do app morrer (Android matando por pouca memória,
                            bem comum no meio do fluxo "saiu pra pagar no
                            Asaas e voltou") ANTES desse momento, esse listener
                            específico morre junto e NUNCA MAIS existe uma
                            chance de convidar essa pessoa a criar conta —
                            mesmo com o dossiê pago e acessível (a sessão
                            anônima persiste desde a correção da
                            persistência, mas o convite, não). Este banner é
                            independente de qualquer listener: aparece sempre
                            que a sessão for anônima e a pessoa já tiver pelo
                            menos um dossiê, então sempre existe um caminho
                            pra criar a conta e não depender de pegar o
                            momento exato da confirmação do pagamento.
                        */}
                        {user?.isAnonymous && historyList.length > 0 && anonimoTemDossiePago && (
                           <button
                              onClick={() => {
                                 // 🔥 CORRIGIDO (2026-09-29): sugere o MESMO e-mail
                                 // usado no checkout (salvo no dossiê desde a
                                 // criação da cobrança, sobrevive a reinício do
                                 // app), nunca um e-mail em branco ou de outro
                                 // dossiê — pra pessoa não arriscar criar a conta
                                 // com um e-mail diferente do que o Asaas já
                                 // conhece.
                                 const dossierComEmail = historyList.find(i => i.emailComprador);
                                 setEmailConviteConta(dossierComEmail?.emailComprador || emailPagamento || '');
                                 setMostrarConviteConta(true);
                              }}
                              className="w-full bg-lime-400/10 border border-lime-400/30 rounded-2xl p-4 flex items-center gap-3 text-left hover:bg-lime-400/15 transition-colors"
                           >
                              <ShieldCheck className="text-lime-400 shrink-0" size={22} />
                              <div className="flex-1">
                                 <p className="text-white font-black text-xs uppercase tracking-wide">Crie sua conta pra não perder o acesso</p>
                                 <p className="text-slate-400 text-[11px] mt-0.5">Seus dossiês ficam salvos só neste aparelho até você criar uma senha.</p>
                              </div>
                           </button>
                        )}

                        <div className="space-y-6 pt-4">
                           {(() => {
                              // Sem login: só mostra dossiês Entry (avulsos, sem conta).
                              // Logado: mostra todos os dossiês (Entry + planos pagos).
                              const listaVisivel = user ? historyList : historyList.filter(i => i.plan === 'ENTRY');
                              return listaVisivel.length === 0 && (
                                 <p className="text-sm text-slate-500 italic text-center py-10">Nenhum dossiê gerado ainda.</p>
                              );
                           })()}

                           {/* DOSSIÊS DO GESTOR */}
                           {(user ? historyList : historyList.filter(i => i.plan === 'ENTRY')).filter(i => String(i.snapshot?.userType || i.snapshot?.formData?.userType || 'Gestor').toLowerCase() !== 'consultor').length > 0 && (
                              <div>
                                 <div className="flex items-center gap-2 mb-3">
                                    <Shield size={12} className="text-lime-400" />
                                    <p className="text-[11px] font-black text-lime-400 uppercase tracking-widest">Perfil Gestor</p>
                                 </div>
                                 <div className="space-y-3">
                                    {(user ? historyList : historyList.filter(i => i.plan === 'ENTRY')).filter(i => String(i.snapshot?.userType || i.snapshot?.formData?.userType || 'Gestor').toLowerCase() !== 'consultor').map(item => (
                                       <div key={item.id} className="bg-slate-900/40 p-6 rounded-[2rem] border border-white/5 shadow-lg hover:border-lime-400/20 transition-all group">
                                          {/* Nome da empresa numa linha própria, com largura cheia do card — antes
                                              dividia espaço com os botões de ação na mesma linha, o que obrigava a
                                              fonte a encolher até ficar ilegível pra nomes longos. Agora quebra em
                                              até 2 linhas normalmente, sem cortar nem espremer. */}
                                          <p className="text-white font-black uppercase text-base leading-snug cursor-pointer line-clamp-2" onClick={() => loadDossier(item)}>{item.name}</p>
                                          <div className="flex items-center justify-between gap-3 mt-3">
                                             <div className="flex items-center gap-2 min-w-0 cursor-pointer" onClick={() => loadDossier(item)}>
                                                <span className={`px-2 py-0.5 rounded text-[11px] font-black uppercase border shrink-0 ${getBadgeStyle(item.plan === 'ENTRY' || item.plan === 'AVULSO_360' ? item.plan : String(gestorPlan || item.plan).toUpperCase())}`}>{(item.plan === 'ENTRY' || item.plan === 'AVULSO_360' ? item.plan : String(gestorPlan || item.plan).toUpperCase()).replace('_', ' ')}</span>
                                                <span className="text-[11px] text-slate-500 font-bold uppercase tracking-widest shrink-0">{item.date}</span>
                                             </div>
                                             <div className="flex items-center gap-2 shrink-0">
                                                <button onClick={() => setItemToDelete(item.id)} className="w-9 h-9 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors active:scale-95"><Trash2 size={14} /></button>
                                                <button onClick={() => loadDossier(item)} className="w-9 h-9 bg-lime-400 text-black rounded-full flex items-center justify-center shadow-lg active:scale-95 hover:scale-105 transition-transform"><FileText size={14} /></button>
                                             </div>
                                          </div>
                                       </div>
                                    ))}
                                 </div>
                              </div>
                           )}

                           {/* DOSSIÊS DO CONSULTOR */}
                           {historyList.filter(i => String(i.snapshot?.userType || i.snapshot?.formData?.userType || '').toLowerCase() === 'consultor').length > 0 && (
                              <div>
                                 <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                       <Briefcase size={12} className="text-blue-400" />
                                       <p className="text-[11px] font-black text-blue-400 uppercase tracking-widest">Perfil Consultor</p>
                                    </div>
                                    {consultorPlan && consultorPlan !== 'free' && (
                                       <button
                                          onClick={() => {
                                             setFormData({ ...formData, userType: 'Consultor' });
                                             setView('report');
                                             setReportTab('carteira');
                                          }}
                                          className="flex items-center gap-1.5 bg-blue-500 text-white text-[11px] font-black uppercase tracking-widest rounded-full px-4 py-2 shadow-[0_0_12px_rgba(59,130,246,0.4)] hover:bg-blue-400 active:scale-95 transition-all"
                                       >
                                          <Briefcase size={12} /> Carteira
                                       </button>
                                    )}
                                 </div>
                                 <div className="space-y-3">
                                    {(() => {
                                       // 🏢 Reordena pra cada filial aparecer logo abaixo da
                                       // sua matriz (mesma hierarquia do Dashboard).
                                       const consultorItens = historyList.filter(i => String(i.snapshot?.userType || i.snapshot?.formData?.userType || '').toLowerCase() === 'consultor');
                                       const jaIncluidos = new Set();
                                       const ordenados = [];
                                       consultorItens.forEach((it) => {
                                          if (jaIncluidos.has(it.id)) return;
                                          const raiz = extrairRaizCNPJ(it.snapshot?.formData?.cnpj);
                                          if (!raiz) { ordenados.push(it); jaIncluidos.add(it.id); return; }
                                          const grupo = consultorItens
                                             .filter(o => extrairRaizCNPJ(o.snapshot?.formData?.cnpj) === raiz)
                                             .sort((a, b) => String(a.snapshot?.formData?.cnpj).localeCompare(String(b.snapshot?.formData?.cnpj)));
                                          grupo.forEach((u) => {
                                             if (jaIncluidos.has(u.id)) return;
                                             ordenados.push(u);
                                             jaIncluidos.add(u.id);
                                          });
                                       });
                                       return ordenados;
                                    })().map(item => {
                                       const isAvulso = item.plan === 'AVULSO_360';
                                       // 🏢 Detecta se esse dossiê faz parte de um grupo econômico
                                       // (outra unidade com a mesma raiz de CNPJ na carteira).
                                       // Mesma lógica do Dashboard — detectado pelo próprio CNPJ,
                                       // sem precisar cadastrar vínculo nenhum.
                                       const cnpjItem = item.snapshot?.formData?.cnpj;
                                       const carteiraConsultor = historyList.filter(i => String(i.snapshot?.userType || i.snapshot?.formData?.userType || '').toLowerCase() === 'consultor');
                                       const raizItem = extrairRaizCNPJ(cnpjItem);
                                       const irmasItem = raizItem ? carteiraConsultor.filter(outro =>
                                          outro.id !== item.id && extrairRaizCNPJ(outro.snapshot?.formData?.cnpj) === raizItem
                                       ) : [];
                                       const temGrupo = irmasItem.length > 0;
                                       // Se for filial, acha a matriz do grupo pra dizer de quem ela é.
                                       const matrizDoGrupo = temGrupo && !ehMatriz(cnpjItem)
                                          ? irmasItem.find(o => ehMatriz(o.snapshot?.formData?.cnpj))
                                          : null;
                                       return (
                                          <div key={item.id} className={`p-6 rounded-[2rem] border shadow-lg transition-all group ${matrizDoGrupo ? 'bg-blue-900/[0.06] border-blue-500/20 ml-4' : 'bg-blue-900/10 border-blue-500/10'} hover:border-blue-400/20`}>
                                             <p className="text-white font-black uppercase text-base leading-snug cursor-pointer line-clamp-2" onClick={() => loadDossier(item)}>{item.name}</p>
                                             {matrizDoGrupo && (
                                                <p className="text-[11px] text-blue-300/70 font-bold mt-1 truncate max-w-full">
                                                   filial de {matrizDoGrupo.name}
                                                </p>
                                             )}
                                             <div className="flex items-center justify-between gap-3 mt-3">
                                                <div className="flex items-center gap-2 flex-wrap min-w-0 cursor-pointer" onClick={() => loadDossier(item)}>
                                                   <span className={`px-2 py-0.5 rounded text-[11px] font-black uppercase border shrink-0 ${getBadgeStyle(isAvulso ? item.plan : String(consultorPlan || item.plan).toUpperCase())}`}>{(isAvulso ? item.plan : String(consultorPlan || item.plan).toUpperCase()).replace('_', ' ')}</span>
                                                   {temGrupo && (
                                                      <span className={`px-2 py-0.5 rounded text-[11px] font-black uppercase border shrink-0 ${ehMatriz(cnpjItem) ? 'bg-lime-400/15 text-lime-400 border-lime-400/30' : 'bg-blue-500/15 text-blue-300 border-blue-500/30'}`}>
                                                         {rotuloUnidade(cnpjItem)}
                                                      </span>
                                                   )}
                                                   <span className="text-[11px] text-slate-500 font-bold uppercase tracking-widest shrink-0">{item.date}</span>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                   <button onClick={() => setItemToDelete(item.id)} className="w-9 h-9 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors active:scale-95"><Trash2 size={14} /></button>
                                                   {isAvulso ? (
                                                      <button onClick={() => { loadDossier(item); setTimeout(() => handleExportPDF(true), 500); }} className="w-9 h-9 bg-purple-500 text-white rounded-full flex items-center justify-center shadow-lg active:scale-95 hover:scale-105 transition-transform"><Download size={14} /></button>
                                                   ) : (
                                                      <button onClick={() => loadDossier(item)} className="w-9 h-9 bg-blue-500 text-white rounded-full flex items-center justify-center shadow-lg active:scale-95 hover:scale-105 transition-transform"><FileText size={14} /></button>
                                                   )}
                                                </div>
                                             </div>
                                          </div>
                                       );
                                    })}
                                 </div>
                              </div>
                           )}
                        </div>
                        {user && (
                           <div className="w-full bg-blue-500/10 text-blue-400 py-6 px-6 rounded-full font-black uppercase text-sm border border-blue-400/30 mt-4 flex items-center justify-center gap-3 text-center">
                              {recuperando ? (
                                 <><Loader2 size={16} className="animate-spin shrink-0" /> <span>Sincronizando...</span></>
                              ) : (
                                 <><CheckCircle2 size={16} className="shrink-0" /> <span>Sincronizado com o Servidor Phoenix</span></>
                              )}
                           </div>
                        )}
                        <button onClick={() => setView('landing')} className="w-full bg-white/5 text-white py-6 rounded-full font-black uppercase text-sm border border-white/10 mt-2 hover:bg-white hover:text-black transition-colors">Voltar para Início</button>
                     </div>
                  )}

                  {view === 'profile' && (
                     <div className="flex flex-col space-y-6 pt-6 pb-10 animate-fade-up">

                        <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none mb-2">
                           Meu <span className="text-lime-400">Perfil</span>
                        </h2>

                        {/* ========================================== */}
                        {/* BLOCO 1: DADOS DO USUÁRIO                 */}
                        {/* ========================================== */}
                        <div className="bg-gradient-to-br from-slate-900/80 to-black/90 p-6 rounded-3xl border border-white/10">
                           <div className="flex flex-col items-center text-center gap-3">
                              <div className="w-16 h-16 bg-lime-400/10 rounded-full flex items-center justify-center border border-lime-400/20 shrink-0">
                                 <User size={30} className="text-lime-400" />
                              </div>
                              <div className="w-full min-w-0">
                                 <h3 className="text-base font-black text-white uppercase italic tracking-tight leading-tight truncate">
                                    {localStorage.getItem('phoenix_user_name') || (user?.email ? user.email.split('@')[0] : 'Usuário Phoenix')}
                                 </h3>
                                 <p className="text-[11px] text-slate-400 font-bold mt-1 lowercase truncate">
                                    {user?.email || 'Sem e-mail registrado'}
                                 </p>
                              </div>
                           </div>
                        </div>

                        {/* Sessão anônima — mostra caminho claro pra voltar pra
                            conta de verdade, em vez de deixar a pessoa presa. */}
                        {user?.isAnonymous && (
                           <button
                              onClick={() => setMostrarLoginModal(true)}
                              className="w-full bg-lime-400/10 border border-lime-400/20 text-lime-400 py-4 rounded-2xl font-black uppercase text-xs flex items-center justify-center gap-2 hover:bg-lime-400/20 transition-all active:scale-95"
                           >
                              <Lock size={14} /> Já tenho conta — Fazer login
                           </button>
                        )}
                        {/* ========================================== */}
                        {/* BLOCO 2: PLANOS ATIVOS                    */}
                        {/* ========================================== */}
                        <div className="space-y-3">
                           <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest px-1">Planos Ativos</p>

                           {/* PLANO GESTOR */}
                           <div className="bg-gradient-to-br from-slate-900/80 to-black/90 p-6 rounded-3xl border border-white/10 relative overflow-hidden">
                              <div className="flex items-center justify-between">
                                 <div>
                                    <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Perfil Sócio / Gestor</p>
                                    <p className="text-xl font-black text-white uppercase italic tracking-tighter">
                                       {gestorPlan && gestorPlan !== 'entry' ? String(gestorPlan).toUpperCase() : 'Sem Plano'}
                                    </p>
                                    {user?.email && gestorPlan && gestorPlan !== 'entry' && (
                                       <p className="text-[11px] text-slate-500 mt-1 break-all lowercase">
                                          {user.email}
                                       </p>
                                    )}
                                 </div>
                                 <div className="w-10 h-10 bg-lime-400/10 rounded-xl flex items-center justify-center border border-lime-400/20">
                                    <Shield size={18} className="text-lime-400" />
                                 </div>
                              </div>
                              {user && !user.isAnonymous && gestorPlan && gestorPlan !== 'entry' && (
                                 cancelamentoGestor && cancelamentoGestor.acessoAte ? (
                                    <p className="mt-3 text-[11px] text-amber-300/90 leading-relaxed">
                                       Assinatura cancelada. Seu acesso continua até <strong className="text-amber-200">{formatarDataBR(cancelamentoGestor.acessoAte)}</strong>.
                                    </p>
                                 ) : origemGestor === 'google' ? (
                                    <button onClick={() => Browser.open({ url: linkGerenciarAssinaturaGoogle(produtoGoogleGestor) })} className="mt-3 text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-lime-400 transition-colors underline underline-offset-4">
                                       Assinatura pela Google Play · Gerenciar na Play Store
                                    </button>
                                 ) : (
                                    <button onClick={() => setCancelarAssinaturaAlvo('gestor')} className="mt-3 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-red-400 transition-colors">
                                       Cancelar assinatura
                                    </button>
                                 )
                              )}
                              {(cancelamentoGestor && cancelamentoGestor.acessoAte) ? (() => {
                                 // Assinatura cancelada (acesso até a data): o botão passa a ser REATIVAR o mesmo plano, não subir de nível.
                                 const info = { tactical: { id: 'tactical', nome: 'Protocolo Tactical', preco: '189,00' }, plus: { id: 'plus', nome: 'Protocolo Plus', preco: '499,00' }, total: { id: 'total', nome: 'Protocolo Total', preco: '799,00' } }[String(gestorPlan).toLowerCase()];
                                 return info ? (
                                    <div className="mt-3 pt-3 border-t border-white/5">
                                       <button onClick={() => handleProtocolSelection(info.id, info.nome, info.preco, 'ASSINATURA')}
                                          className="w-full bg-lime-400 text-black py-3 rounded-xl font-black uppercase text-[11px] tracking-widest active:scale-95 transition-all">
                                          Reativar {info.nome.replace('Protocolo ', '')} · R$ {info.preco}/mês
                                       </button>
                                    </div>
                                 ) : null;
                              })() : gestorPlan === 'total' ? (
                                 <div className="mt-3 pt-3 border-t border-lime-400/20">
                                    <p className="text-[11px] font-black text-lime-400 uppercase tracking-widest text-center">
                                       🏆 Você atingiu o nível máximo do Gestor
                                    </p>
                                 </div>
                              ) : (() => {
                                 // 🔥 CORREÇÃO: antes o botão de upgrade só aparecia pra quem não
                                 // tinha plano nenhum ainda — Tactical e Plus ficavam sem nenhum
                                 // caminho de upgrade na tela de Perfil, diferente do Consultor
                                 // (que sempre oferece o próximo degrau). Agora funciona igual
                                 // nos dois: sempre mostra o próximo passo, até chegar no Total.
                                 const proximoDegrauGestor = {
                                    'entry': { id: 'tactical', nome: 'Protocolo Tactical', preco: '189,00' },
                                    'tactical': { id: 'plus', nome: 'Protocolo Plus', preco: '499,00' },
                                    'plus': { id: 'total', nome: 'Protocolo Total', preco: '799,00' }
                                 };
                                 const chave = gestorPlan && gestorPlan !== 'entry' ? gestorPlan : 'entry';
                                 const proximo = proximoDegrauGestor[chave];
                                 if (!proximo) return null;
                                 return (
                                    <div className="mt-3 pt-3 border-t border-white/5">
                                       <p className="text-[11px] text-slate-600 uppercase tracking-widest mb-2">Próximo degrau</p>
                                       <button onClick={() => handleProtocolSelection(proximo.id, proximo.nome, proximo.preco, 'ASSINATURA')}
                                          className="w-full bg-lime-400 text-black py-3 rounded-xl font-black uppercase text-[11px] tracking-widest active:scale-95 transition-all">
                                          Upgrade {proximo.nome.replace('Protocolo ', '')} · R$ {proximo.preco}/mês
                                       </button>
                                    </div>
                                 );
                              })()}
                           </div>

                           {/* PLANO CONSULTOR */}
                           <div className="bg-gradient-to-br from-blue-900/20 to-black/90 p-6 rounded-3xl border border-blue-500/20 relative overflow-hidden">
                              <div className="flex items-center justify-between">
                                 <div>
                                    <p className="text-[11px] font-black text-blue-400/60 uppercase tracking-widest mb-1">Perfil Consultor / M&A</p>
                                    <p className="text-xl font-black text-white uppercase italic tracking-tighter">
                                       {consultorPlan && consultorPlan !== 'free' ? String(consultorPlan).toUpperCase() : 'Sem Plano'}
                                    </p>
                                    <p className="text-[11px] text-blue-400/50 mt-1 break-all lowercase">
                                       {consultorPlan && consultorPlan !== 'free' ? (user?.email || '—') : '—'}
                                    </p>
                                 </div>
                                 <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center border border-blue-500/20">
                                    <Briefcase size={18} className="text-blue-400" />
                                 </div>
                              </div>
                              {user && !user.isAnonymous && consultorPlan && consultorPlan !== 'free' && (
                                 cancelamentoConsultor && cancelamentoConsultor.acessoAte ? (
                                    <p className="mt-3 text-[11px] text-amber-300/90 leading-relaxed">
                                       Assinatura cancelada. Seu acesso continua até <strong className="text-amber-200">{formatarDataBR(cancelamentoConsultor.acessoAte)}</strong>.
                                    </p>
                                 ) : origemConsultor === 'google' ? (
                                    <button onClick={() => Browser.open({ url: linkGerenciarAssinaturaGoogle(produtoGoogleConsultor) })} className="mt-3 text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-lime-400 transition-colors underline underline-offset-4">
                                       Assinatura pela Google Play · Gerenciar na Play Store
                                    </button>
                                 ) : (
                                    <button onClick={() => setCancelarAssinaturaAlvo('consultor')} className="mt-3 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-red-400 transition-colors">
                                       Cancelar assinatura
                                    </button>
                                 )
                              )}
                              {(cancelamentoConsultor && cancelamentoConsultor.acessoAte) ? (() => {
                                 const info = { advisor: { id: 'advisor', nome: 'Advisor PRO', preco: '499,00' }, boutique: { id: 'boutique', nome: 'Mesa Boutique Elite', preco: '1.997,00' }, enterprise: { id: 'enterprise', nome: 'Mesa Enterprise Ultra', preco: '3.997,00' } }[String(consultorPlan).toLowerCase()];
                                 return info ? (
                                    <div className="mt-3 pt-3 border-t border-blue-500/10">
                                       <button onClick={() => handleProtocolSelection(info.id, info.nome, info.preco, 'CONSULTOR')}
                                          className="w-full bg-blue-600 text-white py-3 rounded-xl font-black uppercase text-[11px] tracking-widest active:scale-95 transition-all">
                                          Reativar {info.nome} · R$ {info.preco}/mês
                                       </button>
                                    </div>
                                 ) : null;
                              })() : consultorPlan === 'enterprise' ? (
                                 <div className="mt-3 pt-3 border-t border-blue-400/20">
                                    <p className="text-[11px] font-black text-blue-400 uppercase tracking-widest text-center">
                                       🏆 Você atingiu o nível máximo do Consultor
                                    </p>
                                 </div>
                              ) : (() => {
                                 // 🔥 Mesmo padrão do Gestor: mostra nome + preço do
                                 // próximo degrau, em vez de um botão genérico "Ver Planos".
                                 const proximoDegrauConsultor = {
                                    'free': { id: 'advisor', nome: 'Advisor PRO', preco: '499,00' },
                                    'advisor': { id: 'boutique', nome: 'Mesa Boutique Elite', preco: '1.997,00' },
                                    'boutique': { id: 'enterprise', nome: 'Mesa Enterprise Ultra', preco: '3.997,00' }
                                 };
                                 const chave = consultorPlan && consultorPlan !== 'free' ? consultorPlan : 'free';
                                 const proximo = proximoDegrauConsultor[chave];
                                 if (!proximo) return null;
                                 return (
                                    <div className="mt-3 pt-3 border-t border-blue-500/10">
                                       <p className="text-[11px] text-slate-600 uppercase tracking-widest mb-2">Próximo degrau</p>
                                       <button onClick={() => handleProtocolSelection(proximo.id, proximo.nome, proximo.preco, 'CONSULTOR')}
                                          className="w-full bg-blue-600 text-white py-3 rounded-xl font-black uppercase text-[11px] tracking-widest active:scale-95 transition-all">
                                          {(!consultorPlan || consultorPlan === 'free') ? 'Assinar' : 'Upgrade'} {proximo.nome} · R$ {proximo.preco}/mês
                                       </button>
                                    </div>
                                 );
                              })()}
                           </div>
                        </div>

                        {/* ========================================== */}
                        {/* BLOCO 3: CONFIGURAÇÕES GERAIS             */}
                        {/* ========================================== */}
                        <div className="space-y-3">
                           <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest px-1">Configurações</p>

                           <button onClick={() => setShowSettings(true)}
                              className="w-full bg-white/5 text-white py-4 rounded-2xl font-black uppercase text-xs flex items-center justify-center gap-3 border border-white/5 hover:bg-white hover:text-black transition-all active:scale-95">
                              <Settings size={16} /> Configurações da Conta
                           </button>
                           {/* Pareamento sem senha — só faz sentido pra quem já tem
                               conta de verdade (não faz sentido numa sessão anônima
                               ainda não promovida). */}
                           {user && !user.isAnonymous && (
                              <button onClick={abrirPareamentoQRCode}
                                 className="w-full bg-white/5 text-white py-4 rounded-2xl font-black uppercase text-xs flex items-center justify-center gap-3 border border-white/5 hover:bg-white hover:text-black transition-all active:scale-95">
                                 <Monitor size={16} /> Conectar Painel Desktop
                              </button>
                           )}
                           <button onClick={() => setView('sobre')}
                              className="w-full bg-white/5 text-white py-4 rounded-2xl font-black uppercase text-xs flex items-center justify-center gap-3 border border-white/5 hover:bg-white hover:text-black transition-all active:scale-95">
                              <Info size={16} /> Sobre o Phoenix OS
                           </button>
                           <button
                              onClick={() => {
                                 if (typeof onLogout === 'function') {
                                    onLogout();
                                 } else {
                                    // Fallback: limpa localStorage e recarrega a página
                                    localStorage.clear();
                                    window.location.reload();
                                 }
                              }}
                              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-[11px] font-black uppercase tracking-widest hover:bg-red-500/20 transition-all active:scale-95"
                           >
                              <LogOut size={14} /> Encerrar Sessão Secure
                           </button>
                           <button
                              onClick={async () => {
                                 // 🔥 CORREÇÃO: antes só limpava localStorage e chamava
                                 // onLogout — mas o app usa browserSessionPersistence
                                 // (a sessão do Firebase fica na sessionStorage, não na
                                 // localStorage), e o estado em memória do React
                                 // (gestorPlan, consultorPlan, formData etc.) não se
                                 // resetava sozinho. Resultado: resíduo da conta
                                 // anterior podia vazar pra próxima conta que logasse —
                                 // inclusive liberando acesso a um Dashboard que a nova
                                 // conta não tinha pago. Agora limpa TUDO (as duas
                                 // storages) e força um recarregamento completo da
                                 // página, garantindo estado 100% limpo.
                                 localStorage.clear();
                                 sessionStorage.clear();
                                 try {
                                    if (typeof onLogout === 'function') await onLogout();
                                 } catch (e) {
                                    console.error('[PHOENIX] Erro ao encerrar sessão:', e);
                                 } finally {
                                    window.location.href = '/mobile';
                                 }
                              }}
                              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-white/5 border border-white/10 text-slate-400 text-[11px] font-black uppercase tracking-widest hover:bg-white/10 hover:text-white transition-all active:scale-95 mt-2"
                           >
                              <User size={14} /> Entrar com Outra Conta
                           </button>
                        </div>

                     </div>
                  )}

                  {/* ============================================================ */}
                  {/* TELA "SOBRE" — dados institucionais/comerciais do Phoenix OS.
                      Puxa tudo de COMPANY_INFO (constants.js) — fonte única
                      de verdade, também usada nos dois Dashboards. Existe
                      pensando em exigências de loja de app (Apple/Google
                      costumam pedir contato de suporte, política de
                      privacidade etc.) e pra passar credibilidade comercial
                      pro cliente B2B. Hoje com placeholders — atualiza
                      direto no constants.js assim que a empresa formalizar
                      CNPJ/razão social/contato oficial. */}
                  {/* ============================================================ */}
                  {view === 'sobre' && (
                     <div className="flex flex-col space-y-6 pt-6 pb-10 animate-fade-up px-4">
                        <button onClick={() => setView('profile')} className="flex items-center gap-2 text-slate-500 hover:text-white text-[11px] font-black uppercase tracking-widest transition-colors w-fit">
                           <ArrowLeft size={12} /> Voltar
                        </button>

                        <div className="flex flex-col items-center text-center py-4">
                           <IconShieldBig size={56} />
                           <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter mt-4">
                              {COMPANY_INFO.nomeFantasia}
                           </h2>
                           {/* 🔥 NOVO: faltava dizer, dentro do próprio app, quem opera o
                               produto — só aparecia nos Termos de Uso/Política de Privacidade. */}
                           <p className="text-[11px] text-slate-600 font-bold uppercase tracking-widest mt-1">Um produto {COMPANY_INFO.nomeEmpresa}</p>
                           <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest mt-2">Versão {COMPANY_INFO.versaoApp}</p>
                        </div>

                        <div className="bg-slate-900/60 p-6 rounded-3xl border border-white/10">
                           <p className="text-xs text-slate-400 leading-relaxed">{COMPANY_INFO.descricaoCurta}</p>
                        </div>

                        <div className="bg-slate-900/60 p-6 rounded-3xl border border-white/10 space-y-4">
                           <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Dados da Empresa</p>
                           <div>
                              <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Operado por</p>
                              <p className="text-sm text-white font-bold mt-0.5">{COMPANY_INFO.nomeEmpresa}</p>
                           </div>
                           <div>
                              <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Razão Social</p>
                              <p className="text-sm text-white font-bold mt-0.5">{COMPANY_INFO.razaoSocial}</p>
                           </div>
                           <div>
                              <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">CNPJ</p>
                              <p className="text-sm text-white font-bold mt-0.5">{COMPANY_INFO.cnpj}</p>
                           </div>
                           <div>
                              <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Endereço</p>
                              <p className="text-sm text-white font-bold mt-0.5">{COMPANY_INFO.endereco}</p>
                           </div>
                        </div>

                        <div className="bg-slate-900/60 p-6 rounded-3xl border border-white/10 space-y-4">
                           <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Suporte</p>
                           <a href={`mailto:${COMPANY_INFO.emailSuporte}`} className="flex items-center gap-3 text-sm text-lime-400 font-bold hover:underline">
                              <Mail size={14} /> {COMPANY_INFO.emailSuporte}
                           </a>
                        </div>

                        <div className="flex flex-col gap-2">
                           <a href={COMPANY_INFO.linkPoliticaPrivacidade} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-5 py-4 rounded-2xl bg-white/5 border border-white/5 text-xs font-black text-white uppercase tracking-widest hover:bg-white/10 transition-colors">
                              Política de Privacidade <ArrowRight size={14} />
                           </a>
                           <a href={COMPANY_INFO.linkTermosDeUso} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-5 py-4 rounded-2xl bg-white/5 border border-white/5 text-xs font-black text-white uppercase tracking-widest hover:bg-white/10 transition-colors">
                              Termos de Uso <ArrowRight size={14} />
                           </a>
                           <a href="/reembolso" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-5 py-4 rounded-2xl bg-white/5 border border-white/5 text-xs font-black text-white uppercase tracking-widest hover:bg-white/10 transition-colors">
                              Política de Reembolso <ArrowRight size={14} />
                           </a>
                           <a href="/excluir-conta" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-5 py-4 rounded-2xl bg-white/5 border border-white/5 text-xs font-black text-white uppercase tracking-widest hover:bg-white/10 transition-colors">
                              Como excluir minha conta <ArrowRight size={14} />
                           </a>
                        </div>

                        <p className="text-[11px] text-slate-600 font-bold uppercase tracking-widest text-center pt-2">
                           © {COMPANY_INFO.anoFundacao} {COMPANY_INFO.nomeEmpresa} — {COMPANY_INFO.nomeFantasia}. Todos os direitos reservados.
                        </p>
                     </div>
                  )}

                  {/* MOTOR INVISÍVEL DOS PDFs */}
                  <ReportEngine
                     data={{
                        companyName: formData.companyName,
                        segment: formData.niche ? `${formData.segment} (${formData.niche})` : formData.segment,
                        ebitda: report.ebitda,
                        margin: report.currentMargin,
                        alavancagem: report.alavancagem,
                        valuation: formatCurrency(finance.enterpriseValue),
                        equity: formatCurrency(finance.equityValue),
                        score: finance.maScore?.toString() || '0',
                        projectedEbitda: formatCurrency(report.projectedEbitda),
                        simRevenue: simRevenue,
                        simCosts: simCosts,
                        simStatus: report.simStatus,
                        simDescription: report.simDescription,
                        simColor: report.simColor,
                        safeDividends: formatCurrency(finance.safeDividends),
                        emergencyReserve: formatCurrency(finance.emergencyReserve),
                        ruptureRisk: finance.ruptureRisk,
                        // 🔥 Dados Técnicos Avançados (pra página 4 do Gestor no PDF)
                        sector: formData.segment,
                        debt: parseCurrencyToNumber(formData.debt),
                        custoNaoRecorrente: parseCurrencyToNumber(formData.custoNaoRecorrente),
                        custoDivida: formData.custoDivida,
                        dividaCurtoPrazo: parseCurrencyToNumber(formData.dividaCurtoPrazo),
                        ebitdaAjustado: report.ebitdaAjustado,
                        custoServicoDivida: report.custoServicoDivida,
                        coberturaJuros: report.coberturaJuros,
                        ncg: finance.ncg,
                        coberturaCurtoPrazo: finance.coberturaCurtoPrazo,
                        prazoRecebimento: formData.prazoRecebimento,
                        prazoPagamento: formData.prazoPagamento,
                        // 🔥 CORREÇÃO: faltavam aqui — o app nunca mandava esses dois
                        // campos pro ReportEngine, então a Página 4 sempre mostrava
                        // o teaser "Disponível no Plus", mesmo pra quem tinha acesso.
                        pontoEquilibrio: parseCurrencyToNumber(formData.costs) || null,
                        caixaMinimo: (parseCurrencyToNumber(formData.costs) || 0) + (parseCurrencyToNumber(formData.proLabore) || 0)
                     }}
                     insights={dynamicInsights}
                     projecaoEstocastica={calculateStochasticProjection(
                        { ebitda: report.ebitda, ebitdaAjustado: report.ebitdaAjustado, currentMargin: Number(report.currentMargin) },
                        parseCurrencyToNumber(formData.revenue)
                     )}
                     leituraCurto={leituraProjecaoCurto}
                     leituraMedio={leituraProjecaoMedio}
                     leituraLongo={leituraProjecaoLongo}
                     horizontesLiberados={
                        (formData.userType === 'Consultor' ? HORIZONTES_POR_PLANO_CONSULTOR : HORIZONTES_POR_PLANO_GESTOR)[String((formData.userType === 'Consultor' ? (consultorPlan || activePlan) : activePlan) || '').toLowerCase()] || []
                     }
                     oportunidadesIA={oportunidadesTexto}
                     status={report.status}
                     userPlan={String((formData.userType === 'Consultor' ? (consultorPlan || activePlan) : activePlan) || '').toUpperCase()}
                     basicRef={basicPdfRef}
                     superRef={superPdfRef}
                     // APEX é feature exclusiva do Consultor — Gestor não recebe essa página no PDF
                     apexData={formData.userType === 'Consultor' ? {
                        ebitdaBase: (report.ebitdaAjustado ?? report.ebitda),
                        multiple: finance.multiple,
                        wacc: 12.5,
                        debt: parseCurrencyToNumber(formData.debt),
                        multipleCurrent: apexMultiple,
                        waccCurrent: apexWacc,
                     } : null}
                     // INJEÇÃO DA MARCA DO CONSULTOR
                     wlName={wlName}
                     wlColor={wlColor}
                     wlLogo={wlLogo}
                     isConsultor={usaTomTecnico}
                     isRealConsultor={formData.userType === 'Consultor'}
                     auditId={activeDossierId}
                  />

               </div>
            </div>

            {/* ============================================================ */}
            {/* BARRA INFERIOR (BOTTOM NAV) - LARGURA AJUSTADA AO CONTAINER */}
            {/* ============================================================ */}
            {/* BARRA DE NAVEGAÇÃO INFERIOR */}
            {view === 'report' && !relatorioEntryBloqueado && (
               <div className="absolute bottom-0 left-0 right-0 w-full flex justify-center pointer-events-none z-50">
                  <nav className="w-full bg-[#0a0f16]/95 backdrop-blur-md border-t border-white/10 pointer-events-auto shadow-[0_-10px_40px_rgba(0,0,0,0.5)]" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
                     <div className="flex justify-around items-center px-1.5 pt-4 pb-1">

                        {formData.userType === 'Consultor' ? (
                           <>
                              <button onClick={() => setReportTab('carteira')} className={`flex flex-col items-center gap-1 transition-all ${reportTab === 'carteira' ? 'text-blue-400 scale-105' : 'text-slate-500'}`}>
                                 <Briefcase size={22} />
                                 <span className="text-[11px] uppercase font-black">Carteira</span>
                              </button>
                              <button onClick={() => setReportTab('visao')} className={`flex flex-col items-center gap-1 transition-all ${reportTab === 'visao' ? 'text-lime-400 scale-105' : 'text-slate-500'}`}>
                                 <FileText size={22} />
                                 <span className="text-[11px] uppercase font-black">Relatório</span>
                              </button>
                              <button onClick={() => setReportTab('apex')} className={`flex flex-col items-center gap-1 transition-all ${reportTab === 'apex' ? 'text-blue-400 scale-105' : 'text-slate-500'}`}>
                                 <Activity size={22} />
                                 <span className="text-[11px] uppercase font-black">APEX</span>
                              </button>
                              <button onClick={() => setReportTab('whitelabel')} className={`flex flex-col items-center gap-1 transition-all ${reportTab === 'whitelabel' ? 'text-purple-400 scale-105' : 'text-slate-500'}`}>
                                 <Palette size={22} />
                                 <span className="text-[11px] uppercase font-black">Marca</span>
                              </button>
                              {/* 🤖 TEASER — Agentes de IA (Em Breve). Sem
                              funcionalidade real, só antecipa a feature. */}
                              <button onClick={() => setReportTab('agentes-ia')} className={`flex flex-col items-center gap-1 transition-all relative ${reportTab === 'agentes-ia' ? 'text-amber-400 scale-105' : 'text-slate-500'}`}>
                                 <Bot size={22} />
                                 <span className="text-[11px] uppercase font-black">Agentes</span>
                                 <span className="absolute -top-1.5 -right-2 w-1.5 h-1.5 rounded-full bg-amber-400" />
                              </button>
                           </>
                        ) : (
                           // ABAS DO GESTOR
                           <>
                              <button onClick={() => setReportTab('visao')} className={`flex flex-col items-center gap-1 transition-all ${reportTab === 'visao' ? 'text-lime-400 scale-105' : 'text-slate-500'}`}>
                                 <Activity size={22} />
                                 <span className="text-[11px] uppercase font-black">Visão</span>
                              </button>
                              <button onClick={() => setReportTab('operacao')} className={`flex flex-col items-center gap-1 transition-all ${reportTab === 'operacao' ? 'text-lime-400 scale-105' : 'text-slate-500'}`}>
                                 <Target size={22} />
                                 <span className="text-[11px] uppercase font-black">Operação</span>
                              </button>
                              <button onClick={() => setReportTab('cfo')} className={`flex flex-col items-center gap-1 transition-all ${reportTab === 'cfo' ? 'text-lime-400 scale-105' : 'text-slate-500'}`}>
                                 <Landmark size={22} />
                                 <span className="text-[11px] uppercase font-black">{usaTomTecnico ? 'CFO' : 'Caixa'}</span>
                              </button>
                              <button onClick={() => setReportTab('ma')} className={`flex flex-col items-center gap-1 transition-all ${reportTab === 'ma' ? 'text-lime-400 scale-105' : 'text-slate-500'}`}>
                                 <Briefcase size={22} />
                                 <span className="text-[11px] uppercase font-black">{usaTomTecnico ? 'M&A' : 'Crescimento'}</span>
                              </button>
                              {/* 🤖 TEASER — Agentes de IA (Em Breve). Sem
                              funcionalidade real, só antecipa a feature. */}
                              <button onClick={() => setReportTab('agentes-ia')} className={`flex flex-col items-center gap-1 transition-all relative ${reportTab === 'agentes-ia' ? 'text-amber-400 scale-105' : 'text-slate-500'}`}>
                                 <Bot size={22} />
                                 <span className="text-[11px] uppercase font-black">Agentes</span>
                                 <span className="absolute -top-1.5 -right-2 w-1.5 h-1.5 rounded-full bg-amber-400" />
                              </button>
                           </>
                        )}

                     </div>
                  </nav>
               </div>
            )}

            {/* ============================================================ */}
            {/* MODAL DE CONFIGURAÇÕES DA CONTA (AGORA COM AJUSTES DE APP)   */}
            {/* ============================================================ */}
            {showSettings && (
               <div className="absolute inset-0 z-[500] bg-[#010208]/95 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in zoom-in duration-200">
                  <div className="bg-[#020617] border border-white/10 p-6 rounded-[3rem] shadow-[0_0_50px_rgba(0,0,0,0.8)] w-full max-w-sm relative h-fit max-h-[85vh] overflow-y-auto no-scrollbar">
                     <button onClick={() => setShowSettings(false)} className="absolute top-8 right-8 text-slate-500 hover:text-lime-400 transition-colors"><X size={24} /></button>
                     <h3 className="text-2xl font-black text-white uppercase italic mb-8">Configurações</h3>

                     <div className="space-y-8">
                        {/* Seção Conta — dados reais, não mais placeholder fixo */}
                        <div className="space-y-3">
                           <h4 className="text-[11px] text-slate-500 font-black uppercase tracking-widest px-2">Sua Conta</h4>
                           <div className="p-5 bg-white/5 rounded-2xl border border-white/5 flex justify-between items-center gap-3">
                              <span className="text-[11px] text-slate-400 uppercase font-bold tracking-widest shrink-0">E-mail</span>
                              <span className="text-xs text-white font-black italic truncate">{auth.currentUser?.email || '—'}</span>
                           </div>
                           {gestorPlan && (
                              <div className="p-5 bg-white/5 rounded-2xl border border-white/5 flex justify-between items-center">
                                 <span className="text-[11px] text-slate-400 uppercase font-bold tracking-widest">Plano Gestor</span>
                                 <span className="text-xs text-lime-400 font-black italic uppercase">{gestorPlan}</span>
                              </div>
                           )}
                           {consultorPlan && (
                              <div className="p-5 bg-white/5 rounded-2xl border border-white/5 flex justify-between items-center">
                                 <span className="text-[11px] text-slate-400 uppercase font-bold tracking-widest">Plano Consultor</span>
                                 <span className="text-xs text-blue-400 font-black italic uppercase">{consultorPlan}</span>
                              </div>
                           )}
                        </div>

                        <div className="pt-4 border-t border-white/5">
                           <button
                              onClick={() => setConfirmandoExclusaoConta(true)}
                              className="w-full text-red-500 font-bold py-5 text-[11px] uppercase tracking-widest hover:text-red-400 transition-colors mt-2"
                           >
                              Excluir Minha Conta
                           </button>
                        </div>
                     </div>
                  </div>
               </div>
            )}

            {/* CONFIRMAÇÃO DE EXCLUSÃO DE CONTA — passo extra obrigatório
                pra uma ação irreversível e séria como essa. */}
            {confirmandoExclusaoConta && (
               <div className="fixed inset-0 z-[600] bg-[#010208]/95 backdrop-blur-xl flex items-center justify-center p-6 animate-in fade-in duration-200">
                  <div className="bg-[#020617] border border-red-500/20 p-8 rounded-[3rem] shadow-2xl w-full max-w-sm text-center">
                     <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20 mb-6 mx-auto">
                        <Trash2 size={28} className="text-red-500" />
                     </div>
                     <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-3">Excluir sua conta?</h3>
                     <p className="text-xs text-slate-400 leading-relaxed mb-8">
                        Todos os seus dossiês, planos e histórico serão apagados permanentemente, no app e no Dashboard. <strong className="text-white">Sua assinatura ativa (se houver) será cancelada automaticamente no Asaas</strong> — você não continuará sendo cobrado. Essa ação não pode ser desfeita.
                     </p>
                     {(origemGestor === 'google' || origemConsultor === 'google') && (
                        <div className="bg-amber-950/30 border border-amber-400/20 rounded-2xl p-4 mb-6 text-left">
                           <p className="text-[11px] text-amber-200 leading-relaxed mb-3">
                              <strong className="text-white">Atenção:</strong> sua assinatura foi contratada pela Google Play. Excluir a conta não cancela a cobrança da loja. Cancele antes na Play Store.
                           </p>
                           <button
                              onClick={() => Browser.open({ url: linkGerenciarAssinaturaGoogle(origemGestor === 'google' ? produtoGoogleGestor : produtoGoogleConsultor) })}
                              className="text-[11px] font-black uppercase tracking-widest text-lime-400 underline underline-offset-4"
                           >
                              Abrir assinaturas na Play Store
                           </button>
                        </div>
                     )}
                     <div className="flex gap-3">
                        <button
                           onClick={() => setConfirmandoExclusaoConta(false)}
                           disabled={excluindoConta}
                           className="flex-1 bg-white/5 text-white py-4 rounded-full font-black uppercase text-xs border border-white/10 hover:bg-white/10 transition-all disabled:opacity-50"
                        >
                           Cancelar
                        </button>
                        <button
                           onClick={excluirContaDefinitivamente}
                           disabled={excluindoConta}
                           className="flex-1 bg-red-500 text-white py-4 rounded-full font-black uppercase text-xs hover:bg-red-600 active:scale-95 transition-all disabled:opacity-50"
                        >
                           {excluindoConta ? 'Excluindo...' : 'Excluir'}
                        </button>
                     </div>
                  </div>
               </div>
            )}

            {/* ============================================================ */}
            {/* MODAL DE COMPRA (IN-APP PURCHASE)                            */}
            {/* ============================================================ */}
            {showInAppPurchase && selectedPlanDetails && (
               <div className="fixed inset-0 z-[9999] bg-slate-900 overflow-y-auto animate-fade-in">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-lime-400 to-emerald-500"></div>
                  <div
                     className="w-full max-w-sm mx-auto px-6 relative"
                     style={{ paddingTop: 'calc(2rem + env(safe-area-inset-top, 0px))', paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))' }}
                  >
                     {/* Indicador de etapa — deixa claro que é 1 fluxo só de 3 passos,
                         não 3 telas soltas empilhadas. */}
                     <div className="flex items-center justify-center gap-2 mb-6">
                        <span className="text-[11px] font-black uppercase tracking-widest text-lime-400">Etapa 1 de 3</span>
                        <div className="flex gap-1.5">
                           <div className="w-6 h-1 rounded-full bg-lime-400"></div>
                           <div className="w-6 h-1 rounded-full bg-white/10"></div>
                           <div className="w-6 h-1 rounded-full bg-white/10"></div>
                        </div>
                     </div>

                     <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-lime-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
                           <Lock size={32} className="text-lime-400" />
                        </div>
                        <h3 className="text-2xl font-black text-white uppercase tracking-tight italic mb-2">Checkout <span className="text-lime-400">Seguro</span></h3>
                        <p className="text-[11px] text-slate-400 uppercase tracking-widest font-bold">
                           {selectedPlanDetails.type === 'PAGAMENTO ÚNICO' ? 'Confirme seu pagamento único' : 'Confirme sua assinatura'}
                        </p>
                     </div>

                     <div className="bg-slate-800/50 rounded-2xl p-6 mb-6 border border-white/5">
                        <div className="flex justify-between items-center mb-2">
                           <span className="text-slate-300 font-medium">{selectedPlanDetails.name}</span>
                           <span className="text-white font-black text-lg">R$ {selectedPlanDetails.price}</span>
                        </div>
                        <div className="flex justify-between items-center text-[11px] uppercase tracking-widest text-slate-500">
                           <span>Status</span>
                           <span className="text-lime-400">Pronto para confirmar</span>
                        </div>
                     </div>

                     {canalDePagamento() !== 'google' ? (
                     <>
                     {/* E-mail — a sessão pode ser anônima (sem e-mail nenhum
                         vinculado), então precisa coletar aqui pra emitir a
                         cobrança e depois avisar sobre confirmação de pagamento. */}
                     <div className="mb-4">
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Seu e-mail</label>
                        <input
                           type="email"
                           value={emailPagamento}
                           onChange={(e) => setEmailPagamento(e.target.value)}
                           placeholder="seu@email.com"
                           className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50 transition-all"
                        />
                     </div>

                     {/* CPF/CNPJ — exigido pelo Asaas pra emitir a cobrança de verdade */}
                     <div className="mb-4">
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">CPF ou CNPJ</label>
                        <input
                           type="text"
                           value={cpfCnpjPagamento}
                           onChange={(e) => setCpfCnpjPagamento(e.target.value)}
                           onBlur={() => setCpfPagamentoTocado(true)}
                           placeholder="Só números"
                           className={`w-full bg-black/40 border rounded-xl px-4 py-3 text-sm text-white outline-none transition-all ${cpfPagamentoTocado && cpfCnpjPagamento.trim() && !cpfCnpjValido(cpfCnpjPagamento) ? 'border-red-500/60 focus:border-red-500' : 'border-white/10 focus:border-lime-400/50'}`}
                        />
                        {cpfPagamentoTocado && cpfCnpjPagamento.trim() && !cpfCnpjValido(cpfCnpjPagamento) ? (
                           <p className="text-[11px] text-red-400 mt-1.5 px-1">CPF ou CNPJ inválido — confira os números digitados.</p>
                        ) : (
                           <p className="text-[11px] text-slate-600 mt-1.5 px-1">
                              Precisa ser um CPF ou CNPJ válido de verdade (o dígito verificador é conferido).
                           </p>
                        )}
                     </div>

                     {/* Forma de pagamento — Pix não pede dado nenhum aqui, é só
                         gerar o QR Code na página do Asaas. Cartão também é
                         digitado só lá, nunca dentro do Phoenix OS. */}
                     <div className="mb-6">
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">Forma de Pagamento</label>
                        <div className="grid grid-cols-2 gap-3">
                           <button
                              type="button"
                              onClick={() => setFormaPagamentoEscolhida('PIX')}
                              className={`py-3 rounded-xl font-black text-xs uppercase tracking-widest border transition-all ${formaPagamentoEscolhida === 'PIX' ? 'bg-lime-400 text-black border-lime-400' : 'bg-black/30 text-slate-400 border-white/10'}`}
                           >
                              Pix
                           </button>
                           <button
                              type="button"
                              onClick={() => setFormaPagamentoEscolhida('CREDIT_CARD')}
                              className={`py-3 rounded-xl font-black text-xs uppercase tracking-widest border transition-all ${formaPagamentoEscolhida === 'CREDIT_CARD' ? 'bg-lime-400 text-black border-lime-400' : 'bg-black/30 text-slate-400 border-white/10'}`}
                           >
                              Cartão
                           </button>
                        </div>
                        <p className="text-[11px] text-slate-600 mt-2 px-1">
                           Você vai finalizar o pagamento numa página segura do Asaas — o Phoenix OS nunca vê o número do seu cartão.
                        </p>
                     </div>

                     </>
                     ) : (
                        <p className="text-[11px] text-slate-500 leading-relaxed mb-6 px-1">
                           O pagamento é feito pelo Google Play, com a forma de pagamento da sua conta Google. O Phoenix OS não vê os dados do seu cartão.
                        </p>
                     )}

                     <AceiteAtivo checked={aceiteCheckout} onChange={setAceiteCheckout} comReembolso className="px-1 mt-4 mb-4" />

                     <div className="flex flex-col gap-3">
                        <button
                           onClick={handleConfirmPurchase}
                           disabled={processandoPagamento || !aceiteCheckout}
                           className="w-full disabled:opacity-40 bg-lime-400 hover:bg-lime-300 text-black font-black py-5 rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
                        >
                           {processandoPagamento ? (
                              <>Gerando cobrança...</>
                           ) : (
                              <>{selectedPlanDetails.type === 'PAGAMENTO ÚNICO' ? 'GERAR PAGAMENTO' : 'ASSINAR AGORA'}</>
                           )}
                        </button>
                        <button onClick={() => setShowInAppPurchase(false)} disabled={processandoPagamento} className="w-full text-slate-500 font-bold py-3 mt-2 text-xs uppercase tracking-widest hover:text-white transition-colors disabled:opacity-50">
                           Cancelar
                        </button>
                     </div>
                  </div>
               </div>
            )}

            {/* DECISÃO DE DUPLA ASSINATURA — aparece antes de qualquer
                cobrança nova, quando a pessoa já tem um plano pago do
                outro perfil ativo. Evita "cobrança inesperada". */}
            {mostrarDecisaoDuplaAssinatura && (() => {
               // 🔥 Texto dinâmico: descobre a direção com base no plano que
               // ficou pendente, pra nunca mostrar "Gestor" quando na
               // verdade é o Consultor que já está ativo (ou vice-versa).
               const vaiAssinarConsultor = planoPendenteAposAviso
                  ? (['advisor', 'boutique', 'enterprise'].includes(planoPendenteAposAviso.planId) || planoPendenteAposAviso.planType === 'CONSULTOR')
                  : true; // fallback: mantém o comportamento antigo se por algum motivo não houver plano pendente
               const perfilAtivo = vaiAssinarConsultor ? 'Gestor' : 'Consultor';
               const perfilNovo = vaiAssinarConsultor ? 'Consultor' : 'Gestor';
               const nomePlanoAtivo = vaiAssinarConsultor ? String(gestorPlan).toUpperCase() : String(consultorPlan).toUpperCase();

               return (
                  <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[999] flex items-start justify-center p-6 overflow-y-auto">
                     <div className="bg-[#0a0a12] border border-amber-400/20 rounded-[2.5rem] p-8 max-w-md w-full my-10">
                        <div className="w-14 h-14 rounded-full bg-amber-400/10 flex items-center justify-center mb-5">
                           <AlertTriangle size={24} className="text-amber-400" />
                        </div>
                        <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-3">
                           Antes de continuar
                        </h3>
                        <p className="text-sm text-slate-400 mb-4 leading-relaxed">
                           Você já tem o plano <strong className="text-white">{perfilAtivo} {nomePlanoAtivo}</strong> ativo. Se assinar o {perfilNovo} também, vai passar a pagar <strong className="text-white">os dois valores juntos</strong> — o {perfilAtivo} atual + o novo do {perfilNovo} — a partir de agora.
                        </p>
                        {canalDePagamento() !== 'google' && ( // o desconto de dupla assinatura só existe nas compras pelo site (Asaas)
                        <p className="text-sm text-lime-400 mb-4 leading-relaxed">
                           Se quiser manter os dois ativos, damos 15% de desconto no plano {perfilAtivo} pelos primeiros 3 meses.
                        </p>
                        )}
                        <div className="bg-slate-900/50 border border-white/5 rounded-2xl p-4 mb-4">
                           <p className="text-[11px] text-slate-400 leading-relaxed">
                              <strong className="text-white">Qual escolher?</strong> Se você administra só empresas suas (uma de cada vez, sem filiais), o <strong className="text-white">Gestor</strong> já basta. Se atende clientes externos diferentes, ou uma empresa com vários CNPJs/filiais, o <strong className="text-white">Consultor</strong> é o certo.
                           </p>
                        </div>

                        <div className="bg-amber-950/30 border border-amber-400/20 rounded-2xl p-4 mb-6">
                           <p className="text-[11px] text-amber-200 leading-relaxed mb-2">
                              <strong className="text-white">Se você cancelar o {perfilAtivo}:</strong> você mantém o acesso até o fim do período já pago. Depois disso, os relatórios de {perfilAtivo} ficam bloqueados (não somem, mas não podem mais ser acessados) até você assinar esse perfil de novo. Pra continuar usando esses dados no {perfilNovo}, será preciso adicionar as empresas de novo na Carteira (Consultor) ou iniciar uma nova análise atualizada (Gestor).
                           </p>
                           <p className="text-[11px] text-amber-200 leading-relaxed font-black">
                              Essa ação não pode ser desfeita com um clique — só assinando de novo.
                           </p>
                        </div>

                        <label className="flex items-start gap-3 mb-4 cursor-pointer select-none">
                           <input
                              type="checkbox"
                              checked={confirmouIrreversibilidade}
                              onChange={(e) => setConfirmouIrreversibilidade(e.target.checked)}
                              className="mt-0.5 w-4 h-4 accent-lime-400 shrink-0"
                           />
                           <span className="text-[11px] text-slate-400 leading-relaxed">
                              Entendi que cancelar o {perfilAtivo} bloqueia o acesso aos relatórios desse perfil até eu assinar de novo.
                           </span>
                        </label>

                        <div className="flex flex-col gap-3">
                           <button
                              onClick={() => {
                                 setMostrarDecisaoDuplaAssinatura(false);
                                 setPlanoPendenteAposAviso(null);
                                 setConfirmouIrreversibilidade(false);
                              }}
                              disabled={processandoDecisaoDupla}
                              className="w-full bg-white/5 hover:bg-white/10 text-white py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all border border-white/10 disabled:opacity-50"
                           >
                              Manter meu plano atual de {perfilAtivo}
                           </button>
                           <button
                              onClick={async () => {
                                 await cancelarPlanoAtualEIrParaCheckoutPendente();
                                 setConfirmouIrreversibilidade(false);
                              }}
                              disabled={processandoDecisaoDupla || !confirmouIrreversibilidade}
                              className="w-full bg-white/5 hover:bg-white/10 text-white py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                           >
                              {processandoDecisaoDupla ? 'Processando...' : `Quero só o ${perfilNovo} — cancelar o ${perfilAtivo}`}
                           </button>
                           <button
                              onClick={async () => {
                                 // 🔥 NOVO: antes essa escolha só seguia pro checkout do
                                 // plano novo — o desconto de 15% prometido no texto acima
                                 // nunca era aplicado de verdade na assinatura que já
                                 // estava ativa (alguém precisava entrar no Asaas e reduzir
                                 // na mão). Agora aplica automaticamente aqui, antes de
                                 // seguir pro checkout. Best effort: se o desconto falhar
                                 // por algum motivo, ainda assim segue pro checkout — não
                                 // faz sentido travar a pessoa de assinar por causa disso.
                                 setProcessandoDecisaoDupla(true);
                                 try {
                                    const perfilSnap = await getDoc(doc(db, 'usuarios', user.uid));
                                    const dadosPerfil = perfilSnap.exists() ? perfilSnap.data() : {};
                                    const subscriptionIdAtivo = vaiAssinarConsultor
                                       ? dadosPerfil?.gestorAsaasSubscriptionId
                                       : dadosPerfil?.consultorAsaasSubscriptionId;
                                    if (subscriptionIdAtivo && canalDePagamento() !== 'google') { // o desconto de dupla assinatura só existe no Asaas
                                       await aplicarDescontoDuplaAssinatura({ subscriptionId: subscriptionIdAtivo });
                                    }
                                 } catch (e) {
                                    console.error('[PHOENIX] Erro ao aplicar desconto de dupla assinatura (seguindo pro checkout mesmo assim):', e);
                                 } finally {
                                    setProcessandoDecisaoDupla(false);
                                 }

                                 // 🔥 CORREÇÃO: segue pro checkout de verdade (cobrança via
                                 // Asaas) com o plano que a pessoa tinha escolhido — antes
                                 // isso ativava o Consultor sem cobrar nada, o que é
                                 // diferente de "assinar os dois valores juntos".
                                 setMostrarDecisaoDuplaAssinatura(false);
                                 if (planoPendenteAposAviso) {
                                    setSelectedPlanDetails({
                                       id: planoPendenteAposAviso.planId,
                                       name: planoPendenteAposAviso.planName,
                                       price: planoPendenteAposAviso.planPrice,
                                       type: planoPendenteAposAviso.planType
                                    });
                                    setShowInAppPurchase(true);
                                    setPlanoPendenteAposAviso(null);
                                    setConfirmouIrreversibilidade(false);
                                 }
                              }}
                              disabled={processandoDecisaoDupla}
                              className="w-full bg-lime-400 hover:bg-lime-300 text-black py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all active:scale-95 disabled:opacity-50"
                           >
                              {processandoDecisaoDupla ? 'Processando...' : 'Assinar os dois — tenho ciência do valor'}
                           </button>
                           <button
                              onClick={() => {
                                 setMostrarDecisaoDuplaAssinatura(false);
                                 setPlanoPendenteAposAviso(null);
                                 setConfirmouIrreversibilidade(false);
                              }}
                              disabled={processandoDecisaoDupla}
                              className="w-full text-slate-500 font-bold py-2 text-xs uppercase tracking-widest hover:text-white transition-colors disabled:opacity-50"
                           >
                              Cancelar
                           </button>
                        </div>
                     </div>
                  </div>
               );
            })()}

            {/* LOGIN SEMPRE ACESSÍVEL — pra quem está numa sessão anônima
                e já tem conta de verdade, mas não achava caminho de volta. */}
            {mostrarLoginModal && (
               <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[999] flex items-center justify-center p-6">
                  <div className="bg-[#0a0a12] border border-lime-400/20 rounded-[2.5rem] p-8 max-w-sm w-full">
                     <div className="w-14 h-14 rounded-full bg-lime-400/10 flex items-center justify-center mb-5">
                        <Lock size={24} className="text-lime-400" />
                     </div>
                     <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-2">
                        Fazer Login
                     </h3>
                     <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                        Entre com sua conta pra acessar seus planos e relatórios de qualquer aparelho.
                     </p>

                     <div className="space-y-3 mb-2">
                        <input
                           type="email"
                           value={loginModalEmail}
                           onChange={(e) => setLoginModalEmail(e.target.value)}
                           placeholder="Seu e-mail"
                           className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50 transition-all"
                        />
                        <input
                           type="password" autoComplete="current-password"
                           value={loginModalSenha}
                           onChange={(e) => setLoginModalSenha(e.target.value)}
                           placeholder="Sua senha"
                           className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50 transition-all"
                        />
                     </div>

                     {loginModalErro && (
                        <p className="text-xs text-red-400 mb-4">{loginModalErro}</p>
                     )}
                     {loginModalMsg && (
                        <p className="text-xs text-lime-400 mb-2">{loginModalMsg}</p>
                     )}
                     <button
                        onClick={async () => {
                           setLoginModalErro('');
                           if (!loginModalEmail) { setLoginModalMsg('Digite seu e-mail acima.'); return; }
                           try {
                              await sendPasswordResetEmail(auth, loginModalEmail, { url: 'https://app.phoenixos.com.br' });
                              setLoginModalMsg('Enviamos um link de redefinição para o seu e-mail.');
                           } catch (error) {
                              console.error('[PHOENIX] Erro ao enviar redefinição:', error.code);
                              setLoginModalMsg('Não foi possível enviar o e-mail. Verifique o endereço.');
                           }
                        }}
                        disabled={loginModalCarregando}
                        className="w-full text-center text-[13px] text-slate-400 hover:text-lime-400 font-semibold transition-colors mt-1"
                     >
                        Esqueci minha senha
                     </button>

                     <div className="flex flex-col gap-3 mt-4">
                        <button
                           onClick={fazerLoginModal}
                           disabled={loginModalCarregando}
                           className="w-full bg-lime-400 hover:bg-lime-300 text-black font-black py-4 rounded-2xl transition-all active:scale-95 disabled:opacity-50"
                        >
                           {loginModalCarregando ? 'Entrando...' : 'Entrar'}
                        </button>
                        <button
                           onClick={() => setMostrarLoginModal(false)}
                           disabled={loginModalCarregando}
                           className="w-full text-slate-500 font-bold py-2 text-xs uppercase tracking-widest hover:text-white transition-colors disabled:opacity-50"
                        >
                           Cancelar
                        </button>
                     </div>
                  </div>
               </div>
            )}

            {/* CONVITE PRA VIRAR CONTA DE VERDADE — aparece só depois do
                pagamento confirmado, nunca antes. É um convite, não uma
                barreira: dá pra fechar e continuar usando normalmente. */}
            {showEditEntryWarning && (
               <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[999] flex items-center justify-center p-6">
                  <div className="bg-[#0a0a12] border border-amber-500/30 rounded-[2.5rem] p-8 max-w-sm w-full">
                     <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mb-5">
                        <AlertTriangle size={24} className="text-amber-400" />
                     </div>
                     <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-3">
                        Editar gera um novo relatório
                     </h3>
                     <p className="text-sm text-slate-400 mb-4 leading-relaxed">
                        O Phoenix Entry é um diagnóstico avulso: <strong className="text-white">cada versão do relatório é uma nova compra de R$ 49,90</strong>. Se você editar os dados, vamos gerar um novo relatório e o pagamento será solicitado novamente. O relatório atual continua salvo e liberado.
                     </p>
                     <div className="bg-lime-400/5 border border-lime-400/20 rounded-2xl p-4 mb-6">
                        <p className="text-xs text-slate-300 leading-relaxed">
                           <strong className="text-lime-400">Quem assina um plano edita e gera relatórios ilimitados</strong>, sem pagar de novo a cada versão — além de projeções, Dashboard no computador e muito mais. A partir de R$ 189/mês.
                        </p>
                     </div>
                     <div className="flex flex-col gap-3">
                        <button
                           onClick={() => { setShowEditEntryWarning(false); handleProtocolSelection('tactical', 'Protocolo Tactical', '189,00', 'ASSINATURA'); }}
                           className="w-full bg-lime-400 hover:bg-lime-300 text-black font-black py-4 rounded-2xl transition-all active:scale-95 text-sm"
                        >
                           Assinar e editar sem limites
                        </button>
                        <button
                           onClick={() => {
                              // Nova versão = novo dossiê (novo id), pra não
                              // mexer no relatório já pago e cobrar a nova versão.
                              setShowEditEntryWarning(false);
                              setActiveDossierId(null);
                              setStep(2);
                              setView('wizard');
                           }}
                           className="w-full bg-white/5 border border-white/10 text-white font-black py-4 rounded-2xl transition-all active:scale-95 text-sm"
                        >
                           Editar mesmo assim (R$ 49,90)
                        </button>
                        <button
                           onClick={() => setShowEditEntryWarning(false)}
                           className="w-full text-slate-500 font-bold py-2 text-xs uppercase tracking-widest hover:text-white transition-colors"
                        >
                           Cancelar
                        </button>
                     </div>
                  </div>
               </div>
            )}

            {mostrarConviteConta && (
               <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[999] flex items-center justify-center p-6">
                  <div className="bg-[#0a0a12] border border-lime-400/20 rounded-[2.5rem] p-8 max-w-sm w-full">
                     <div className="w-14 h-14 rounded-full bg-lime-400/10 flex items-center justify-center mb-5">
                        <ShieldCheck size={24} className="text-lime-400" />
                     </div>
                     <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-2">
                        Salve seu relatório
                     </h3>
                     <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                        Crie sua conta em 2 passos e tenha seu relatório salvo pra consultar de qualquer aparelho, quando quiser.
                     </p>

                     <div className="space-y-3 mb-2">
                        <input
                           type="email"
                           value={emailConviteConta}
                           onChange={(e) => setEmailConviteConta(e.target.value)}
                           placeholder="Seu e-mail"
                           className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50 transition-all"
                        />
                        {emailConviteConta && (
                           <p className="text-[11px] text-slate-500 -mt-1 px-1">Use o mesmo e-mail da sua compra, pra tudo ficar na mesma conta.</p>
                        )}
                        <input
                           type="password" autoComplete="new-password"
                           value={senhaConviteConta}
                           onChange={(e) => setSenhaConviteConta(e.target.value)}
                           placeholder="Crie uma senha"
                           className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50 transition-all"
                        />
                     </div>

                     {erroConviteConta && (
                        <p className="text-xs text-red-400 mb-4">{erroConviteConta}</p>
                     )}

                     <div className="flex flex-col gap-3 mt-4">
                        <AceiteAtivo checked={aceiteConvite} onChange={setAceiteConvite} />
                        <button
                           onClick={promoverContaAnonima}
                           disabled={salvandoConviteConta || !aceiteConvite}
                           className="w-full bg-lime-400 hover:bg-lime-300 text-black font-black py-4 rounded-2xl transition-all active:scale-95 disabled:opacity-50"
                        >
                           {salvandoConviteConta ? 'Criando conta...' : 'Criar Conta e Salvar'}
                        </button>
                        <button
                           onClick={() => setMostrarConviteConta(false)}
                           disabled={salvandoConviteConta}
                           className="w-full text-slate-500 font-bold py-2 text-xs uppercase tracking-widest hover:text-white transition-colors disabled:opacity-50"
                        >
                           Agora não
                        </button>
                     </div>
                  </div>
               </div>
            )}

            {/* MODAL: CONFIRMAR CANCELAMENTO DE ASSINATURA */}
            {cancelarAssinaturaAlvo && (
               <div className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-md flex items-center justify-center p-6">
                  <div className="bg-[#0a0a12] border border-white/10 rounded-[2.5rem] p-8 max-w-sm w-full">
                     <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-3">Cancelar assinatura?</h3>
                     <p className="text-sm text-slate-400 leading-relaxed mb-3">
                        Você deixa de ser cobrado nas próximas renovações do plano <strong className="text-white">{cancelarAssinaturaAlvo === 'consultor' ? 'Consultor' : 'Gestor'}</strong>.
                     </p>
                     <p className="text-sm text-slate-400 leading-relaxed mb-3">
                        Seu acesso <strong className="text-white">continua até o fim do período que você já pagou</strong>.
                     </p>
                     <div className="bg-amber-950/30 border border-amber-400/20 rounded-2xl p-4 mb-6">
                        <p className="text-[11px] text-amber-200 leading-relaxed">
                           <strong className="text-white">O que acontece com seus relatórios:</strong>{' '}
                           {cancelarAssinaturaAlvo === 'consultor'
                              ? 'depois dessa data, a Carteira e os relatórios dos seus clientes ficam bloqueados. Nada é apagado: tudo volta a abrir se você assinar de novo.'
                              : 'depois dessa data, você continua com acesso aos relatórios que comprou pelo Entry. Os relatórios gerados durante o plano ficam bloqueados (não são apagados) e voltam a abrir se você assinar de novo. O painel no computador também deixa de abrir.'}
                        </p>
                     </div>
                     <div className="flex flex-col gap-3">
                        <button onClick={confirmarCancelamentoAssinatura} disabled={cancelandoAssinatura} className="w-full bg-red-500/90 text-white py-4 rounded-full font-black uppercase text-xs disabled:opacity-50 active:scale-95 transition-all">
                           {cancelandoAssinatura ? 'Cancelando…' : 'Sim, cancelar assinatura'}
                        </button>
                        <button onClick={() => setCancelarAssinaturaAlvo(null)} disabled={cancelandoAssinatura} className="w-full bg-white/5 text-white py-4 rounded-full font-black uppercase text-xs border border-white/10 active:scale-95 transition-all">
                           Manter assinatura
                        </button>
                     </div>
                  </div>
               </div>
            )}

            {/* MODAL DE PAREAMENTO SEM SENHA — QR Code */}
            {showPareamento && (
               <div className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-md flex items-center justify-center p-6">
                  <div className="bg-[#0a0a12] border border-lime-400/20 rounded-[2.5rem] p-8 max-w-sm w-full text-center">
                     <div className="w-14 h-14 rounded-full bg-lime-400/10 flex items-center justify-center mb-5 mx-auto">
                        <Monitor size={24} className="text-lime-400" />
                     </div>
                     <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-2">
                        Conectar Painel Desktop
                     </h3>
                     <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                        Escaneie esse código com a câmera do computador (ou abra a câmera do celular apontando pra tela dele) — sem digitar senha nenhuma.
                     </p>

                     <div className="bg-white rounded-3xl p-5 mb-5 flex items-center justify-center min-h-[220px]">
                        {gerandoPareamento ? (
                           <div className="w-10 h-10 border-4 border-lime-500 border-t-transparent rounded-full animate-spin"></div>
                        ) : qrPareamentoImg ? (
                           <img
                              src={qrPareamentoImg}
                              alt="QR Code de pareamento"
                              width={200}
                              height={200}
                           />
                        ) : null}
                     </div>

                     <p className="text-[11px] text-slate-600 uppercase tracking-widest font-bold mb-4">
                        Válido por 5 minutos
                     </p>

                     {/* 🔥 CORRIGIDO: antes só mostrava o link do tipo de conta que
                         estava selecionado no app no momento (formData.userType) —
                         quem tem os DOIS perfis ativos (Gestor + Consultor) via só um
                         link, mesmo tendo acesso aos dois Dashboards. Agora mostra um
                         link pra cada plano que a pessoa realmente tem ativo. Também
                         corrigi o texto: não é "peça sua senha" (dá a entender que é
                         outra senha) — é o mesmo e-mail e senha da conta, então deixamos
                         isso claro. */}
                     <p className="text-xs text-slate-500 mb-6 leading-relaxed">
                        Ou acesse pelo navegador do computador:
                        {gestorPlan && gestorPlan !== 'entry' && (
                           <>
                              <br />
                              <span className="text-lime-400 font-bold break-all">app.phoenixos.com.br/dashboard-gestor</span>
                           </>
                        )}
                        {consultorPlan && consultorPlan !== 'free' && (
                           <>
                              <br />
                              <span className="text-purple-400 font-bold break-all">app.phoenixos.com.br/dashboard-consultor</span>
                           </>
                        )}
                        <br />
                        (use o mesmo e-mail e senha da sua conta)
                     </p>

                     <button
                        onClick={() => setShowPareamento(false)}
                        className="w-full text-slate-500 font-bold py-2 text-xs uppercase tracking-widest hover:text-white transition-colors"
                     >
                        Fechar
                     </button>
                  </div>
               </div>
            )}

         </div>
      </div>
   );
}